import { alertInGrain } from "../../utils/grainDialogs.js";
import usePlanningWriteActivity from "../../hooks/usePlanningWriteActivity.js";
import PlanningWriteActivityNotice from "../PlanningWriteActivityNotice.jsx";
import SourceChangeApplicationFeedback from "./SourceChangeApplicationFeedback.jsx";
import { applicationFeedbackMessage } from "../../utils/sourceChangeApplicationFeedback.js";
// Read-only presentation helpers shared with the complete printed lists.
export {
  formatDetailLabel, formatSourceChangeDetailReason, resolveProtectedFieldChanges,
  formatProtectedSourceChangeState, resolveProtectedSourceChangesSectionTitle,
  isDateChangedEndOfDayLoadDetail, isRemovableOrphanEndOfDayLoadDetail, isMixedOrphanEndOfDayLoadDetail,
  resolveMixedEndOfDayLoadOrderGroups, formatRemovedEndOfDayLoadTitle,
  formatExecutionRecoveryBlockReason,
  formatSourceChangeIntegrityConflicts, isNonRenderableTripDetail, buildNonRenderableTripGroups,
  resolveEndOfDayLoadIntegrityIssues, resolveEndOfDayLoadIntegrityOrderIds,
  resolveEndOfDayLoadIntegrityReasonLabel, resolveEndOfDayLoadIntegrityTransition, resolveEndOfDayLoadIntegrityTruckLabel,
  formatTruckSourceChangeValue, resolveTruckSourceChangeLabel, formatTruckSourceChangeReason,
  formatLoadingConstraintValue, LOADING_CONSTRAINT_FIELD_LABELS, LOADING_CONSTRAINT_CONFLICT_LABELS,
  formatLoadingConstraintConflictLimit, formatTruckRecoveryLoadingTime,
};
import TruckCompartmentChanges from "./TruckCompartmentChanges.jsx";
import CompartmentFillChanges from "./CompartmentFillChanges.jsx";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router";
import { getReactPortalRoot } from "../../utils/reactPortalRoot.js";
import { autoUpdate, flip, offset, shift, useFloating } from "@floating-ui/react-dom";
import {
  ArrowsRightLeftIcon,
  ArrowPathIcon,
  ArrowUturnLeftIcon,
  BookmarkIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ClockIcon,
  CubeIcon,
  ExclamationTriangleIcon,
  PauseCircleIcon,
  PaperAirplaneIcon,
  ScaleIcon,
  TrashIcon,
  TruckIcon,
  UserIcon,
  WrenchScrewdriverIcon,
  XCircleIcon,
  EyeSlashIcon,
  LockClosedIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import ConfirmationModal from "../ConfirmationModal.jsx";
import LoadingConstraintRecoveryConfirmation from "./LoadingConstraintRecoveryConfirmation.jsx";
import useLoadingConstraintRecovery from "../../hooks/useLoadingConstraintRecovery.js";
import { acceptTruckDompeurOverride, hasDompeurMismatch } from "../../utils/dompeurAcceptance.js";
import OptimizationModeSplitButton from "../OptimizationModeSplitButton.jsx";
import { buildEveningOptimizationTarget, buildScopedOptimizationConfirmation } from "../../utils/eveningOptimizationTarget.js";
import RestartServiceButton from "../RestartServiceButton.jsx";
import DispatchDayPicker from "./DispatchDayPicker.jsx";
import { DispatchGeotabCheckIcon } from "./DispatchGeotabCheck.jsx";
import DispatchHeaderMoreMenu, { EveningOrderPoolMenuItem } from "./DispatchHeaderMoreMenu.jsx";
import DispatchHeaderActionMenu, {
  DispatchHeaderMenuSection,
  DISPATCH_MENU_ACTION_CLASS_NAME,
} from "./DispatchHeaderActionMenu.jsx";
import {
  ActionButton,
  HoverCard,
  joinClasses,
} from "../ResultsPrimitives.jsx";
import { buttonToneClasses } from "../resultsPrimitives/shared.js";
import {
  DEFAULT_OPTIMIZE_CONFIRMATION,
  dataAttributeProps,
  resolveMetricAction,
  resolveTruckCardAction,
} from "./shared.js";
import useOptimizationLaunchAction from "../../hooks/useOptimizationLaunchAction.js";
import usePopoverDismiss from "../../hooks/usePopoverDismiss.js";
import useAccess from "../../hooks/useAccess.jsx";
import { ACCESS_PERMISSION_KEYS, hasPermission } from "../../lib/accessControl.js";
import {
  buildPortalPublicationActionHoverCardModel,
  buildPortalPublicationDriverHoverCardModel,
  normalizePortalPublications,
  PORTAL_PUBLICATION_STATUSES,
  resolvePortalPublishDisabledReason,
} from "../../lib/portalPublicationsApi.js";
import { Moon, Smartphone, Sun } from "lucide-react";
import {
} from "../../utils/driverPortalPublicationStyles.js";
import { selectRemoveModifiedSourceChangeDetails } from "../../utils/sourceChangeRemovalSelection.js";
import {
  isSourceChangesActionPreviewFresh,
  isSourceChangesPreviewFresh,
  resolveSourceChangesAggregateCount,
} from "../../utils/sourceChangesRefreshPolicy.js";
import {
  normalizeTruckRefreshRecovery,
  resolveDispatchSourceStaleNotice,
  resolveTruckRecoveryPendingDetail,
} from "../../utils/resultsDispatchBootstrap.js";
import {
  DISPATCH_REASSIGNMENT_MODE_COMPLETE,
  DISPATCH_REASSIGNMENT_MODE_FAST,
  normalizeDispatchReassignmentMode,
  resolveDispatchFastReassignmentEnabled,
} from "../../utils/dispatchReassignmentMode.js";
import { resolveNonRenderableConflictAffectedIds } from "../../utils/resultsChromeData.js";
import {
  formatDataManagerAuditFieldLabel,
  formatDataManagerAuditSafeFieldValue,
} from "../../utils/dataManagerAuditLabels.js";
import {
  canRefreshTruckSourceChange, getTruckSourceChangePresentation, compactTruckDiagnostic,
  summarizeTruckSourceFieldLines, keepTruckUnitsTogether, splitTruckSourceLineLead,
  groupTruckSourceChangesBySection, TRUCK_SOURCE_CHANGE_SECTIONS, formatCouplingChangeLine,
} from "../../utils/truckSourceChangePresentation.js";

const DISPATCH_RESTORE_MODAL_PRIMARY_BUTTON_CLASS_NAME = joinClasses([
  "inline-flex h-8 items-center rounded-sm px-3 text-[0.72rem] font-bold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/35 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
  buttonToneClasses.primary,
]);
const SOURCE_CHANGES_MENU_CONFIRM_BUTTON_CLASS_NAME = joinClasses([
  "h-8! min-h-8! rounded-sm! px-2.75! shadow-none!",
  buttonToneClasses.optimizer,
]);
const SOURCE_CHANGES_MENU_INLINE_ACTION_BUTTON_CLASS_NAME = joinClasses([
  "shrink-0 rounded-[7px] px-2 py-1 font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/35 disabled:cursor-not-allowed disabled:opacity-50",
  buttonToneClasses.optimizer,
]);
const SOURCE_CHANGE_GROUP_SECTION_CLASS_NAME = "overflow-hidden rounded-[4px] border border-[var(--dispatch-update-border)] bg-white";
const SOURCE_CHANGE_GROUP_SECTION_BODY_CLASS_NAME = "grid gap-1.5 border-t border-[var(--dispatch-update-border)] bg-[#f6f6f4] px-1 py-1";
const SOURCE_CHANGE_GROUP_HEADER_CLASS_NAME = "flex w-full items-center gap-2 bg-[var(--dispatch-update-fill)] px-3 py-2.25 text-left text-[0.78rem] font-semibold text-white";
const SOURCE_CHANGE_GROUP_HEADER_ICON_CLASS_NAME = "h-4 w-4 flex-none text-white";
const SOURCE_CHANGE_GROUP_HEADER_CHEVRON_CLASS_NAME = "h-3.75 w-3.75 flex-none text-white opacity-80";
const SOURCE_CHANGE_COUNT_BADGE_CLASS_NAME = "inline-flex items-center rounded-[4px] border border-white bg-white px-1.75 py-0.5 text-[0.58rem] font-black uppercase tracking-[0.12em] text-[var(--dispatch-update-fill)]";
const SOURCE_CHANGE_INNER_CARD_BORDER_CLASS_NAME = "border border-black/25";
const SOURCE_CHANGE_PREVIEW_STACK_OUTLINE_CLASS_NAME = "overflow-hidden rounded-[4px] border border-black/25";
const SOURCE_CHANGE_PREVIEW_STACK_SEPARATOR_CLASS_NAME = "border-t border-black/6";
const SOURCE_CHANGE_ITEM_CARD_SURFACE_CLASS_NAME = "bg-slate-100";
const SOURCE_CHANGE_TRUCK_BADGE_CLASS_NAME = "shrink-0 rounded-[4px] border border-amber-200 bg-white px-1.5 pt-[3px] pb-px text-[0.56rem] font-black uppercase tracking-[0.04em] text-amber-800";
const SOURCE_CHANGE_ITEM_TITLE_CLASS_NAME = "min-w-0 truncate text-[0.7rem] font-semibold text-slate-900";
const SOURCE_CHANGE_ITEM_BODY_CLASS_NAME = "text-[0.64rem] leading-4 text-slate-700";
const SOURCE_CHANGE_REFRESH_ACTIVE_BUTTON_CLASS_NAME = joinClasses([
  buttonToneClasses.optimizer,
  "disabled:opacity-100!",
]);
const SOURCE_CHANGE_REFRESH_INACTIVE_BUTTON_CLASS_NAME = "border-slate-200 bg-slate-100 text-slate-400 shadow-none hover:border-slate-200 hover:bg-slate-100 hover:text-slate-400 disabled:opacity-100";
const STANDARD_OPTIMIZATION_MODE = "standard";

const dispatchMetricIconsByKey = {
  "assignment-progress": CheckCircleIcon,
  "confirmed-orders": CheckCircleIcon,
  "manual-count": ArrowsRightLeftIcon,
  "non-assignable-orders": XCircleIcon,
  "suspended-deliveries": PauseCircleIcon,
  "total-trips": ArrowPathIcon,
};

const dispatchMetricDisplayOrder = [
  "total-trips",
  "assignment-progress",
  "confirmed-orders",
  "non-assignable-orders",
  "suspended-deliveries",
];

function isPlanningPillMetric(item) {
  return item?.metricKind === "planning-pill";
}

const dispatchSectionIconsByKey = {
  orders: CheckCircleIcon,
};

function normalizeInlineText(value) {
  return typeof value === "string" ? value.trim() : "";
}

function looksLikeInternalTruckSourceChangeId(value) {
  const text = normalizeInlineText(value);
  if (!text) {
    return false;
  }
  const upperValue = text.toUpperCase();
  return upperValue.startsWith("TRK-") || upperValue.startsWith("TRK_");
}

function formatTruckSourceChangeLabel(truckId, truckName = "") {
  const resolvedId = normalizeInlineText(truckId);
  const resolvedName = normalizeInlineText(truckName);
  const displayValue = resolvedName
    && !looksLikeInternalTruckSourceChangeId(resolvedName)
    && resolvedName.toUpperCase() !== resolvedId.toUpperCase()
    ? resolvedName
    : resolvedId;
  if (!looksLikeInternalTruckSourceChangeId(displayValue)) {
    return displayValue || resolvedId;
  }
  const strippedPrefix = displayValue.replace(/^TRK[-_]/i, "").trim();
  return strippedPrefix || displayValue || resolvedId;
}

function resolveTruckSourceChangeLabel(truckId, truckLabelById = {}) {
  const resolvedId = normalizeInlineText(truckId);
  const mappedLabel = truckLabelById && typeof truckLabelById === "object"
    ? normalizeInlineText(truckLabelById[resolvedId])
    : "";
  return formatTruckSourceChangeLabel(resolvedId, mappedLabel);
}

function resolveReadyTruckChangeCount(sourceChanges, key) {
  const truckChanges = sourceChanges?.truckChanges;
  const count = truckChanges?.counts?.[key];
  return truckChanges?.enabled === true
    && truckChanges?.state === "ready"
    && Number.isInteger(count)
    && count >= 0
    ? count
    : 0;
}

function formatDispatchSnapshotTimestamp(value) {
  const rawValue = normalizeInlineText(value);
  if (!rawValue) {
    return "";
  }
  const parsedDate = new Date(rawValue);
  if (Number.isNaN(parsedDate.getTime())) {
    return rawValue;
  }
  return new Intl.DateTimeFormat("fr-CA", {
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    month: "2-digit",
  }).format(parsedDate);
}

function formatDispatchDateLabel(value) {
  const rawValue = normalizeInlineText(value);
  if (!rawValue) {
    return "";
  }
  const parsedDate = new Date(`${rawValue.slice(0, 10)}T00:00:00`);
  if (Number.isNaN(parsedDate.getTime())) {
    return rawValue;
  }
  return new Intl.DateTimeFormat("fr-CA", {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(parsedDate);
}

function getDispatchSectionChrome(section = {}) {
  if (section?.key === "trucks") {
    return {
      accent: "before:bg-[linear-gradient(180deg,rgba(15,92,87,0.64),rgba(115,132,112,0.18))]",
      shell: "relative overflow-visible rounded-[8px] border border-[rgba(15,92,87,0.18)] bg-[linear-gradient(180deg,rgba(255,255,255,0.98),rgba(244,248,245,0.94))] px-2.25 pt-3 pb-2.25 shadow-none before:absolute before:left-0 before:top-6 before:bottom-4 before:w-1 before:rounded-r-sm before:content-['']",
      tag: "border-[rgba(71,85,105,0.28)] bg-[rgba(226,232,240,0.98)] text-[#334155]",
    };
  }

  if (section?.key === "orders") {
    return {
      accent: "before:bg-[linear-gradient(180deg,rgba(80,96,118,0.42),rgba(148,163,184,0.14))]",
      shell: "relative overflow-visible rounded-[8px] border border-[rgba(15,92,87,0.11)] bg-[linear-gradient(180deg,rgba(255,255,255,0.98),rgba(246,248,251,0.95))] px-2.25 pt-3 pb-2.25 shadow-none before:absolute before:left-0 before:top-6 before:bottom-4 before:w-1 before:rounded-r-sm before:content-['']",
      tag: "border-[rgba(71,85,105,0.34)] bg-white text-[#334155]",
    };
  }

  return {
    accent: "before:bg-[rgba(148,163,184,0.22)]",
    shell: "relative overflow-visible rounded-[8px] border border-[rgba(148,163,184,0.18)] bg-[rgba(255,255,255,0.72)] px-2.25 pt-3 pb-2.25 shadow-none before:absolute before:left-0 before:top-6 before:bottom-4 before:w-1 before:rounded-r-sm before:content-['']",
    tag: "border-[rgba(148,163,184,0.28)] bg-[rgba(248,250,252,0.98)] text-slate-600",
  };
}

function getDispatchCardToneClassNames(tone = "neutral") {
  if (tone === "success") {
    return {
      accent: "before:bg-[var(--dispatch-card-success-accent)]",
      card: "border-[var(--dispatch-card-success-border)] bg-white",
      icon: "border-[var(--dispatch-card-success-border)] bg-[image:var(--dispatch-card-success-icon)] text-shell-status-success-ink",
      value: "text-shell-status-success-ink",
      badge: "border-border-header-success bg-white text-shell-status-success-ink",
    };
  }

  if (tone === "warning") {
    return {
      accent: "before:bg-[var(--dispatch-card-warning-accent)]",
      card: "border-[var(--dispatch-card-warning-border)] bg-[image:var(--dispatch-card-warning-surface)]",
      icon: "border-[var(--dispatch-card-warning-icon-border)] bg-[image:var(--dispatch-card-warning-icon)] text-amber-700",
      value: "text-[#8a4f08]",
      badge: "border-[rgba(217,119,6,0.16)] bg-white text-[#8a4f08]",
    };
  }

  if (tone === "danger") {
    return {
      accent: "before:bg-[var(--dispatch-card-danger-accent)]",
      card: "border-[var(--dispatch-card-danger-border)] bg-[image:var(--dispatch-card-danger-surface)]",
      icon: "border-[var(--dispatch-card-danger-icon-border)] bg-[image:var(--dispatch-card-danger-icon)] text-[var(--dispatch-card-danger-ink)]",
      value: "text-[var(--dispatch-card-danger-ink)]",
      badge: "border-[var(--dispatch-badge-danger-border)] bg-white text-[var(--dispatch-card-danger-ink)]",
    };
  }

  return {
    accent: "before:bg-[var(--dispatch-card-neutral-accent)]",
    card: "border-[var(--dispatch-card-neutral-border)] bg-white",
    icon: "border-[var(--dispatch-card-neutral-icon-border)] bg-[image:var(--dispatch-card-neutral-icon)] text-slate-600",
    value: "text-slate-900",
    badge: "border-[rgba(148,163,184,0.22)] bg-white text-slate-700",
  };
}

export function DispatchIndicatorCard({
  Icon = null,
  alertNote = "",
  alertTone = "",
  ariaControls,
  ariaExpanded,
  ariaLabel,
  badge = null,
  cardClassName = "",
  dataProps = {},
  iconContainerClassName = "",
  iconClassName = "",
  interactive = false,
  label,
  onClick = undefined,
  tone = "neutral",
  value,
}) {
  const Component = interactive ? "button" : "div";
  const toneClasses = getDispatchCardToneClassNames(tone);
  const hasAlertNote = typeof alertNote === "string"
    ? Boolean(alertNote.trim())
    : Boolean(alertNote);

  const alertBadge = hasAlertNote ? (
    <span
      className={joinClasses([
        "inline-flex shrink-0 max-w-full items-center rounded-[4px] border px-1.25 py-0.5 text-[0.62rem] font-black leading-none",
        alertTone === "info"
          ? "border-blue-300 bg-blue-50 text-blue-700"
          : tone === "danger"
          ? "border-[var(--dispatch-badge-danger-border)] bg-[var(--dispatch-badge-danger-fill)] text-[var(--dispatch-card-danger-ink)] uppercase tracking-[0.04em]"
          : tone === "warning"
            ? "border-[var(--dispatch-badge-warning-border)] bg-[var(--dispatch-badge-warning-fill)] text-[#8a4f08] uppercase tracking-[0.04em]"
            : "border-[rgba(15,92,87,0.2)] bg-[rgba(15,92,87,0.08)] text-shell-status-success-ink uppercase tracking-[0.04em]",
      ])}
    >
      {alertNote}
    </span>
  ) : null;

  return (
    <Component
      data-dispatch-indicator-card="true"
      data-dispatch-indicator-tone={tone}
      type={interactive ? "button" : undefined}
      onClick={interactive ? onClick : undefined}
      aria-controls={ariaControls || undefined}
      aria-expanded={ariaExpanded || undefined}
      aria-label={ariaLabel || undefined}
      className={joinClasses([
        "relative grid min-h-15 w-full min-w-0 grid-cols-[minmax(0,1fr)_auto] items-stretch gap-1.75 overflow-hidden rounded-[4px] border px-2.5 py-1.75 pl-3 text-left shadow-none before:absolute before:inset-y-2.5 before:left-0 before:w-0.75 before:rounded-r-sm before:content-['']",
        toneClasses.card,
        toneClasses.accent,
        cardClassName,
        interactive
          ? "appearance-none transition duration-150 hover:border-[rgba(15,92,87,0.22)] hover:bg-white active:translate-y-0 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2"
          : "",
      ])}
      {...dataProps}
    >
      <span className="grid min-w-0 content-start gap-0.75">
        <span className="block truncate whitespace-nowrap text-[0.66rem] font-black uppercase tracking-[0.12em] text-slate-500/90">
          {label}
        </span>
        <span className="flex min-w-0 flex-nowrap items-center gap-1.5">
          <strong className={joinClasses(["text-[0.9rem] font-semibold leading-[1.05] tracking-[-0.015em]", toneClasses.value])}>
            {value}
          </strong>
          {alertBadge}
        </span>
      </span>

      <span className="flex min-h-full items-stretch gap-2">
        {badge ? (
          <span className={joinClasses([
            "inline-flex h-fit shrink-0 items-center justify-center rounded-[4px] border px-2 py-1 text-[0.7rem] font-semibold shadow-page-loading-spinner",
            toneClasses.badge,
          ])}>
            {badge}
          </span>
        ) : null}
        {Icon ? (
          <span className={joinClasses([
            "flex min-h-full w-8.5 items-center justify-center self-stretch rounded-[4px] border shadow-none",
            toneClasses.icon,
            iconContainerClassName,
            iconClassName,
          ])}>
            <Icon className="h-4 w-4" aria-hidden="true" />
          </span>
        ) : null}
      </span>
    </Component>
  );
}

function DispatchTruckListHoverCardContent({ hoverCard = {} }) {
  const description = typeof hoverCard?.description === "string" && hoverCard.description.trim()
    ? hoverCard.description.trim()
    : "";
  const items = Array.isArray(hoverCard?.items) ? hoverCard.items : [];
  const note = typeof hoverCard?.note === "string" && hoverCard.note.trim()
    ? hoverCard.note.trim()
    : "";
  const intro = description || note
    ? (
      <div className="grid gap-1">
        {description ? <p className="m-0 max-w-80 text-[11px] leading-[1.55] text-content-muted">{description}</p> : null}
        {note ? <p className="m-0 max-w-80 text-[11px] leading-[1.55] text-content-muted">{note}</p> : null}
      </div>
    )
    : null;

  if (!items.length) {
    return (
      <div className="grid gap-2">
        {intro}
        <p className="m-0 max-w-64 text-[11px] leading-[1.55] text-content-muted">{hoverCard?.emptyLabel || "Aucun camion"}</p>
      </div>
    );
  }

  return (
    <div className="grid min-w-64 gap-1.5">
      {intro}
      {items.map((item) => (
        <div key={item.key} className="grid gap-px rounded-[12px] border border-black/6 bg-slate-50/80 px-3 py-2">
          <span className="text-[11px] font-semibold leading-[1.35] text-content-ink">{item.label}</span>
          {item.meta ? <span className="text-[10px] leading-[1.4] text-content-muted">{item.meta}</span> : null}
        </div>
      ))}
    </div>
  );
}

function DispatchSourceStaleHoverCardContent({ notice = null }) {
  if (!notice || typeof notice !== "object") {
    return null;
  }

  const causeText = typeof notice.causeTooltip === "string" && notice.causeTooltip.trim()
    ? notice.causeTooltip.trim()
    : typeof notice.causeLine === "string" && notice.causeLine.trim()
      ? notice.causeLine.trim()
      : "";
  const causeLines = Array.isArray(notice.causeLines)
    ? notice.causeLines.filter((line) => typeof line === "string" && line.trim()).map((line) => line.trim())
    : [];
  const reasonLines = causeLines.length > 0
    ? causeLines
    : [causeText || (typeof notice.reasonLabel === "string" && notice.reasonLabel.trim()
      ? notice.reasonLabel.trim()
      : "Le planning a été modifié depuis le dernier calcul.")];

  return (
    <div className="grid max-w-96 gap-2" data-results-source-stale-hover-card="true">
      <p className="m-0 text-[11px] leading-[1.5] text-content-muted">
        Ce changement n'est pas encore dans les tournées. Optimiser pour les mettre à jour, ou garder le dispatch tel quel.
      </p>
      <ul className="m-0 grid list-none gap-1 border-t border-black/6 p-0 pt-2">
        {reasonLines.map((reason, index) => (
          <li key={`${index}-${reason}`} className="grid grid-cols-[auto_minmax(0,1fr)] gap-1.5 text-[11px] font-semibold leading-[1.5] text-content-copy">
            <span aria-hidden="true" className="text-content-muted">–</span>
            <span>{reason}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function DispatchInvalidateActionHoverCardContent() {
  return (
    <div className="grid min-w-72 gap-1.75">
      <p className="m-0 max-w-80 text-[11px] leading-[1.55] text-content-muted">
        Invalider marque le snapshot dispatch courant comme obsolète et replace la journée en état À revalider. Les tournées visibles restent consultables.
      </p>

      <div className="grid gap-1 rounded-[12px] border border-black/6 bg-slate-50/80 px-3 py-2">
        <span className="text-[11px] font-semibold leading-[1.35] text-content-ink">Ce que fait Invalider</span>
        <span className="text-[10px] leading-[1.45] text-content-muted">
          Met à jour le statut opérationnel de la journée sans supprimer affectations, arrêts ni protections déjà visibles.
        </span>
      </div>

      <div className="grid gap-1 rounded-[12px] border border-black/6 bg-slate-50/80 px-3 py-2">
        <span className="text-[11px] font-semibold leading-[1.35] text-content-ink">Différence avec les resets</span>
        <span className="text-[10px] leading-[1.45] text-content-muted">
          Contrairement à Cache, HERE ou Réinitialiser, cette action ne vide aucun cache ni planning : elle signale seulement que le calcul courant doit être revalidé avant export ou reprise opérationnelle.
        </span>
      </div>

      <div className="grid gap-1 rounded-[12px] border border-black/6 bg-slate-50/80 px-3 py-2">
        <span className="text-[11px] font-semibold leading-[1.35] text-content-ink">Quand l'utiliser</span>
        <span className="text-[10px] leading-[1.45] text-content-muted">
          Après une anomalie métier, un doute sur la fraîcheur du calcul ou avant d'exiger une nouvelle optimisation sans toucher au dispatch visible.
        </span>
      </div>
    </div>
  );
}

export function DispatchResetRevalidationActionHoverCardContent() {
  return (
    <div className="grid min-w-72 gap-1.75">
      <p className="m-0 max-w-80 text-[11px] leading-[1.55] text-content-muted">
        Acquitter retire « À revalider » de la journée, sans relancer « Optimiser ».
      </p>

      <div className="grid gap-1 rounded-[12px] border border-black/6 bg-slate-50/80 px-3 py-2">
        <span className="text-[11px] font-semibold leading-[1.35] text-content-ink">Ce que fait Acquitter</span>
        <span className="text-[10px] leading-[1.45] text-content-muted">
          La journée redevient courante et les tournées, affectations et protections visibles restent inchangées.
        </span>
      </div>

      <div className="grid gap-1 rounded-[12px] border border-black/6 bg-slate-50/80 px-3 py-2">
        <span className="text-[11px] font-semibold leading-[1.35] text-content-ink">À utiliser avec prudence</span>
        <span className="text-[10px] leading-[1.45] text-content-muted">
          Cette action ne vérifie pas le contenu du dispatch : elle confirme seulement que l'avertissement ne doit plus bloquer la journée.
        </span>
      </div>
    </div>
  );
}

export function DispatchSnapshotDeleteActionHoverCardContent({ disabledReason = "" }) {
  return (
    <div className="grid min-w-72 gap-1.75">
      <p className="m-0 max-w-80 text-[11px] leading-[1.55] text-content-muted">
        Supprimer le snapshot Dispatch — Jour + Soir
      </p>
      <div className="grid gap-1 rounded-[12px] border border-red-200/70 bg-red-50/70 px-3 py-2">
        <span className="text-[11px] font-semibold leading-[1.35] text-red-900">Suppression complète et irréversible</span>
        <span className="text-[10px] leading-[1.45] text-red-800">
          Retire les affectations, arrêts, protections, états et sauvegardes de la date, puis retire automatiquement les publications portail. Les commandes Data Manager restent intactes.
        </span>
      </div>
      {disabledReason ? (
        <span className="text-[10px] leading-[1.45] text-content-muted">{disabledReason}</span>
      ) : null}
    </div>
  );
}

// Bulles des boutons « Cache » et « HERE » : ni l'un ni l'autre ne met la journée à revalider.
const OPTIMIZATION_CACHE_RESET_HELP = "Efface les calculs gardés en mémoire. Le prochain « Optimiser » repart de zéro. Le Dispatch affiché et la journée ne changent pas.";
const HERE_CACHE_RESET_HELP = "Efface les trajets HERE gardés. Les prochains calculs les redemandent à HERE (appels facturés). Le Dispatch affiché ne change pas.";

export function DispatchResetActionHoverCardContent({ mode = "dispatch" }) {
  return (
    <div className="grid min-w-72 gap-1.75">
      <p className="m-0 max-w-80 text-[11px] leading-[1.55] text-content-muted">
        {mode === "here" ? HERE_CACHE_RESET_HELP : OPTIMIZATION_CACHE_RESET_HELP}
      </p>
    </div>
  );
}

function DispatchPortalPublicationDriverSection({ emptyLabel = "", items = [], title = "" }) {
  const safeItems = Array.isArray(items) ? items : [];

  return (
    <div className="grid gap-1">
      <span className="text-[11px] font-semibold leading-[1.35] text-content-ink">{title}</span>
      {safeItems.length ? (
        <div className="grid gap-1">
          {safeItems.map((item) => (
            <div key={item.key} className="grid gap-px rounded-[12px] border border-black/6 bg-slate-50/80 px-3 py-2">
              <span className="text-[11px] font-semibold leading-[1.35] text-content-ink">{item.label}</span>
              {item.meta ? <span className="text-[10px] leading-[1.4] text-content-muted">{item.meta}</span> : null}
            </div>
          ))}
        </div>
      ) : (
        <p className="m-0 text-[10px] leading-[1.45] text-content-muted">{emptyLabel}</p>
      )}
    </div>
  );
}

export function DispatchPortalPublicationSummaryHoverCardContent({ model = {} }) {
  const statusMessage = typeof model?.statusMessage === "string" ? model.statusMessage.trim() : "";
  const summary = typeof model?.summary === "string" ? model.summary.trim() : "";
  const publishedDrivers = Array.isArray(model?.publishedDrivers) ? model.publishedDrivers : [];
  const eligibleUnpublishedDrivers = Array.isArray(model?.eligibleUnpublishedDrivers) ? model.eligibleUnpublishedDrivers : [];

  if (statusMessage) {
    return (
      <div className="grid min-w-64 gap-2">
        <p className="m-0 max-w-80 text-[11px] leading-[1.55] text-content-muted">{statusMessage}</p>
      </div>
    );
  }

  return (
    <div className="grid min-w-64 gap-2">
      {summary ? <p className="m-0 max-w-80 text-[11px] leading-[1.55] text-content-muted">{summary}</p> : null}
      <DispatchPortalPublicationDriverSection
        emptyLabel={model?.emptyPublishedLabel || "Aucun chauffeur publié pour le moment."}
        items={publishedDrivers}
        title="Publiés sur le portail"
      />
      <DispatchPortalPublicationDriverSection
        emptyLabel={model?.emptyEligibleLabel || "Tous les chauffeurs éligibles sont publiés."}
        items={eligibleUnpublishedDrivers}
        title="Éligibles, non publiés"
      />
    </div>
  );
}

export function DispatchPublishPortalActionHoverCardContent({ model = null, reason = "" }) {
  const safeModel = model && typeof model === "object" ? model : {};
  const blockers = Array.isArray(safeModel.blockers) ? safeModel.blockers : [];
  const notices = Array.isArray(safeModel.notices) ? safeModel.notices : [];
  const normalizedReason = normalizeInlineText(safeModel.reason || reason)
    || (notices.length ? "" : "La publication portail n'est pas disponible pour le moment.");
  const showRevalidationGuidance = /revalider/i.test(normalizedReason);

  return (
    <div className="grid min-w-72 gap-1.75">
      {normalizedReason ? (
        <p className="m-0 max-w-80 text-[11px] leading-[1.55] text-content-muted">
          {normalizedReason}
        </p>
      ) : null}

      {notices.length ? (
        <div className="grid gap-1.5" data-portal-publication-notices>
          {notices.map((notice) => (
            <div
              key={`notice:${notice.truckId}`}
              className="grid gap-0.5 rounded-[4px] border border-[rgba(217,119,6,0.22)] bg-[rgba(255,251,235,0.9)] px-3 py-2"
              data-portal-publication-notice={notice.truckId}
            >
              <span className="text-[11px] font-semibold leading-[1.35] text-content-ink">
                Rappel · Camion {notice.truckLabel || notice.truckId}
              </span>
              <span className="text-[10px] leading-[1.45] text-content-muted">
                {notice.reason} La publication reste permise.
              </span>
              {notice.targetLabel ? (
                <span className="text-[10px] leading-[1.45] text-content-muted">Cible PM : {notice.targetLabel}</span>
              ) : null}
              {notice.actionRequired ? (
                <span className="text-[10px] font-semibold leading-[1.45] text-content-ink">À faire : {notice.actionRequired}</span>
              ) : null}
            </div>
          ))}
        </div>
      ) : null}

      {blockers.length ? (
        <div className="grid gap-1.5" data-portal-publication-blockers>
          {blockers.map((blocker) => (
            <div
              key={`${blocker.truckId}:${blocker.reasonCode}`}
              className="grid gap-0.5 rounded-[12px] border border-black/6 bg-slate-50/80 px-3 py-2"
              data-portal-publication-blocker={blocker.truckId}
            >
              <span className="text-[11px] font-semibold leading-[1.35] text-content-ink">
                Camion {blocker.truckLabel || blocker.truckId}
              </span>
              {blocker.reason ? (
                <span className="text-[10px] leading-[1.45] text-content-muted">{blocker.reason}</span>
              ) : null}
              {blocker.targetLabel ? (
                <span className="text-[10px] leading-[1.45] text-content-muted">
                  Cible PM : {blocker.targetLabel}
                </span>
              ) : null}
              {blocker.actionRequired ? (
                <span className="text-[10px] font-semibold leading-[1.45] text-content-ink">
                  Action : {blocker.actionRequired}
                </span>
              ) : null}
            </div>
          ))}
        </div>
      ) : null}

      {showRevalidationGuidance ? (
        <div className="grid gap-1 rounded-[12px] border border-black/6 bg-slate-50/80 px-3 py-2">
          <span className="text-[11px] font-semibold leading-[1.35] text-content-ink">Que faire</span>
          <span className="text-[10px] leading-[1.45] text-content-muted">
            La publication globale reste bloquée jusqu'à la prochaine optimisation. Un camion confirmé non touché peut toutefois être publié depuis son menu.
          </span>
        </div>
      ) : null}
    </div>
  );
}

function DispatchMetricSummaryHoverCardContent({ metric }) {
  const label = typeof metric?.metricLabel === "string" && metric.metricLabel.trim()
    ? metric.metricLabel.trim()
    : typeof metric?.label === "string" && metric.label.trim()
      ? metric.label.trim()
      : "Indicateur";
  const value = typeof metric?.metricValue === "string" && metric.metricValue.trim()
    ? metric.metricValue.trim()
    : typeof metric?.value === "string" && metric.value.trim()
      ? metric.value.trim()
      : "";
  const note = typeof metric?.note === "string" && metric.note.trim()
    ? metric.note.trim()
    : typeof metric?.ariaLabel === "string" && metric.ariaLabel.trim()
      ? metric.ariaLabel.trim()
      : "";

  if (!value && !note) {
    return null;
  }

  return (
    <div className="grid min-w-56 gap-2">
      {value ? (
        <div className="grid gap-0.5 rounded-[12px] border border-black/6 bg-slate-50/80 px-3 py-2">
          <span className="text-[10px] font-black uppercase tracking-[0.12em] text-content-faint">{label}</span>
          <strong className="text-[14px] leading-tight text-content-ink">{value}</strong>
        </div>
      ) : null}
      {note ? <p className="m-0 max-w-64 text-[11px] leading-[1.55] text-content-muted">{note}</p> : null}
    </div>
  );
}

function DispatchMetricFormulaHoverCardContent({ details = {} }) {
  const lines = Array.isArray(details?.lines) ? details.lines : [];
  const note = typeof details?.note === "string" && details.note.trim() ? details.note.trim() : "";
  const total = typeof details?.total === "string" && details.total.trim() ? details.total.trim() : "";
  const totalLabel = typeof details?.totalLabel === "string" && details.totalLabel.trim() ? details.totalLabel.trim() : "Total";

  return (
    <div className="flex min-w-64 flex-col gap-1.5">
      {lines.map((line, index) => {
        if (!line?.label && !line?.value) {
          return <div key={index} className="h-1" />;
        }

        return (
          <div
            key={index}
            className="grid items-center gap-2 text-[12px]"
            style={{
              gridTemplateColumns: "minmax(0,1fr) auto",
            }}
          >
            <span className="min-w-0 font-semibold text-content-ink">{line.label}</span>
            <span className="text-right text-content-muted">
              {line.operator ? <span className="mr-1 text-content-faint">{line.operator}</span> : null}
              {line.value}
            </span>
          </div>
        );
      })}

      {total ? (
        <div className="mt-0.5 flex items-center justify-between border-t-[0.5px] border-[rgba(0,0,0,0.08)] pt-2">
          <span className="text-[11px] text-content-muted">{totalLabel}</span>
          <span className="text-[14px] font-bold text-content-ink">= {total}</span>
        </div>
      ) : null}

      {note ? (
        <div className="border-t-[0.5px] border-[rgba(0,0,0,0.08)] pt-2">
          <p className="m-0 max-w-80 text-[11px] leading-[1.55] text-content-muted">{note}</p>
        </div>
      ) : null}
    </div>
  );
}

function DispatchMetricOrderGroupsHoverCardContent({ details = {} }) {
  const groups = Array.isArray(details?.groups) ? details.groups : [];
  const note = typeof details?.note === "string" && details.note.trim() ? details.note.trim() : "";
  const summaryLines = Array.isArray(details?.summaryLines) ? details.summaryLines : [];
  const total = typeof details?.total === "string" && details.total.trim() ? details.total.trim() : "";
  const totalLabel = typeof details?.totalLabel === "string" && details.totalLabel.trim() ? details.totalLabel.trim() : "Total";
  const alertGroups = groups.filter((group) => group?.tone === "danger");
  const otherGroups = groups.filter((group) => group?.tone !== "danger");

  const renderGroup = (group) => {
    const orderIds = Array.isArray(group?.orderIds) ? group.orderIds : [];
    const toneClassName = group?.tone === "success"
      ? "border-[rgba(15,118,110,0.16)] bg-[rgba(240,253,250,0.96)]"
      : group?.tone === "danger"
        ? "border-[rgba(197,82,74,0.2)] bg-[rgba(255,241,240,0.98)]"
        : group?.tone === "warning"
          ? "border-[rgba(217,119,6,0.16)] bg-[rgba(255,251,235,0.98)]"
          : "border-black/6 bg-slate-50/82";
    const badgeClassName = group?.tone === "success"
      ? "border-[rgba(15,118,110,0.16)] bg-white text-[#0f5c57]"
      : group?.tone === "danger"
        ? "border-[rgba(197,82,74,0.22)] bg-white text-[#972d24]"
        : group?.tone === "warning"
          ? "border-[rgba(217,119,6,0.16)] bg-white text-[#9a670f]"
          : "border-[rgba(148,163,184,0.18)] bg-white text-slate-600";

    return (
      <div key={group?.key || group?.title || "group"} className={joinClasses([
        "grid min-w-0 gap-2 rounded-[12px] border px-3 py-2",
        toneClassName,
      ])}>
        <div className="flex items-center justify-between gap-2">
          <span className="text-[10px] font-black uppercase tracking-[0.12em] text-content-faint">{group?.title || "Groupe"}</span>
          <span className={joinClasses([
            "inline-flex items-center rounded-full border px-2 py-0.75 text-[0.62rem] font-black uppercase tracking-widest",
            badgeClassName,
          ])}>
            {group?.countLabel || String(orderIds.length)}
          </span>
        </div>

        {orderIds.length ? (
          <div
            className="grid grid-cols-4 gap-1"
          >
            {orderIds.map((orderId) => (
              <span
                key={orderId}
                className="truncate rounded-[8px] border border-black/6 bg-white/90 px-1.5 py-1 text-center font-mono text-[10px] font-semibold tabular-nums text-content-ink"
                title={orderId}
              >
                {orderId}
              </span>
            ))}
          </div>
        ) : (
          <p className="m-0 text-[10px] leading-[1.45] text-content-muted">{group?.emptyLabel || "Aucune commande"}</p>
        )}
      </div>
    );
  };

  return (
    <div className="grid min-w-[24rem] gap-2">
      {summaryLines.length ? (
        <div className="grid gap-1 rounded-[12px] border border-black/6 bg-slate-50/82 px-3 py-2">
          {summaryLines.map((line, index) => (
            <div
              key={`${line?.label || "line"}-${index}`}
              className="grid items-center gap-2 text-[11px]"
              style={{
                gridTemplateColumns: "minmax(0,1fr) auto",
              }}
            >
              <span className="min-w-0 font-semibold text-content-ink">{line?.label || ""}</span>
              <span className={joinClasses([
                "text-right font-semibold",
                line?.label === "Nouvelles (Data Manager)" && Number(line?.value) > 0
                  ? "text-[#972d24]"
                  : "text-content-muted",
              ])}>{line?.value || ""}</span>
            </div>
          ))}

          {total ? (
            <div className="mt-1 flex items-center justify-between border-t border-black/6 pt-2">
              <span className="text-[10px] font-black uppercase tracking-[0.12em] text-content-faint">{totalLabel}</span>
              <span className="text-[13px] font-bold text-content-ink">{total}</span>
            </div>
          ) : null}
        </div>
      ) : null}

      {alertGroups.length ? (
        <div className="grid gap-2">
          {alertGroups.map(renderGroup)}
        </div>
      ) : null}

      {otherGroups.length ? (
        <div className={joinClasses([
          "grid gap-2",
          otherGroups.length > 1 ? "md:grid-cols-2" : "",
        ])}>
          {otherGroups.map(renderGroup)}
        </div>
      ) : null}

      {note ? (
        <p className="m-0 max-w-md text-[11px] leading-normal text-content-muted">{note}</p>
      ) : null}
    </div>
  );
}

function resolveDispatchMetricHoverCardContent(metric) {
  if (metric?.hoverCard) {
    return <DispatchTruckListHoverCardContent hoverCard={metric.hoverCard} />;
  }

  if (metric?.hoverCardDetails?.type === "order-groups") {
    return <DispatchMetricOrderGroupsHoverCardContent details={metric.hoverCardDetails} />;
  }

  if (metric?.hoverCardDetails?.type === "formula") {
    return <DispatchMetricFormulaHoverCardContent details={metric.hoverCardDetails} />;
  }

  if (metric?.hoverCardDetails?.type === "note" && typeof metric.hoverCardDetails.note === "string" && metric.hoverCardDetails.note.trim()) {
    return <p className="m-0 max-w-80 text-[11px] leading-[1.55] text-content-muted">{metric.hoverCardDetails.note.trim()}</p>;
  }

  return <DispatchMetricSummaryHoverCardContent metric={metric} />;
}

function resolveDispatchHoverCardAccentColor(metric) {
  if (metric?.tone === "danger") {
    return "rgba(151,45,36,0.85)";
  }

  if (metric?.tone === "success") {
    return "var(--color-kpi-accent)";
  }

  if (metric?.tone === "warning") {
    return "rgba(154,103,15,0.85)";
  }

  return "rgba(95,108,97,0.85)";
}

export function DispatchMetricCard({ interactions = {}, metric }) {
  const onClick = resolveMetricAction(metric, interactions);
  const MetricIcon = metric.hideMetricIcon ? null : (dispatchMetricIconsByKey[metric.key] || null);
  const tone = metric.tone === "danger" || metric.tone === "warning" || metric.tone === "success"
    ? metric.tone
    : "neutral";
  const cardClassName = metric.emphasis === "blocking"
    ? "border-[var(--dispatch-card-blocking-border)] bg-[image:var(--dispatch-card-blocking-surface)] shadow-[0_8px_20px_rgba(15,23,42,0.04)]"
    : "";
  const iconClassName = metric.emphasis === "blocking"
    ? "motion-safe:[animation:dispatch-kpi-alert-blink_1.05s_ease-in-out_infinite]"
    : "";
  const iconContainerClassName = metric.emphasis === "blocking"
    ? "border-[var(--dispatch-card-blocking-icon-border)] bg-[image:var(--dispatch-card-blocking-icon-fill)] text-[var(--dispatch-card-blocking-icon-ink)] shadow-[0_0_0_3px_var(--dispatch-card-blocking-ring)]"
    : "";
  const badge = typeof metric?.badgeValue === "string" && metric.badgeValue.trim()
    ? (
      <span className="inline-flex items-center gap-2 whitespace-nowrap">
        {metric.badgeIconKey === "scale" ? <ScaleIcon className="h-4.5 w-4.5" aria-hidden="true" /> : null}
        <span>{metric.badgeValue.trim()}</span>
      </span>
    )
    : null;
  const hoverCardContent = resolveDispatchMetricHoverCardContent(metric);
  const hoverCardMaxWidth = Number.isFinite(Number(metric?.hoverCardMaxWidth)) ? Number(metric.hoverCardMaxWidth) : 340;
  const card = (
    <DispatchIndicatorCard
      aria-controls={metric.ariaControls || undefined}
      aria-expanded={metric.ariaExpanded || undefined}
      aria-label={metric.ariaLabel || undefined}
      badge={badge}
      cardClassName={cardClassName}
      interactive={metric.isInteractive}
      dataProps={{
        "data-detail-force-open": metric.detailForceOpen ? "true" : undefined,
        "data-detail-view-link": metric.detailViewLink || undefined,
        "data-error-filter": metric.errorFilter || undefined,
        "data-error-source": metric.errorSource || undefined,
        "data-error-trigger": metric.errorTrigger ? "true" : undefined,
      }}
      Icon={MetricIcon}
      iconContainerClassName={iconContainerClassName}
      iconClassName={iconClassName}
      label={metric.label}
      onClick={onClick || undefined}
      tone={tone}
      value={<span {...dataAttributeProps(metric.valueDataAttribute)}>{metric.value}</span>}
    />
  );

  if (hoverCardContent == null) {
    return card;
  }

  return (
    <HoverCard
      title={metric.hoverCardTitle || metric.label}
      content={hoverCardContent}
      placement="bottom-start"
      delay={140}
      maxWidth={hoverCardMaxWidth}
      accentColor={resolveDispatchHoverCardAccentColor(metric)}
    >
      {card}
    </HoverCard>
  );
}

export function enrichConfirmedOrdersMetric(item, interactions = {}) {
  if (!item || item.key !== "confirmed-orders") {
    return item;
  }

  const canApplyDispatchSourceChanges = interactions.canApplyDispatchSourceChanges === true;
  const sourceChanges = interactions.sourceChanges && typeof interactions.sourceChanges === "object"
    ? interactions.sourceChanges
    : {};
  const sourceChangeCounts = sourceChanges.counts && typeof sourceChanges.counts === "object"
    ? sourceChanges.counts
    : {};
  const sourceChangeDetails = sourceChanges.details && typeof sourceChanges.details === "object"
    ? sourceChanges.details
    : {};
  const sourceRemovedEndOfDayLoadDetails = Array.isArray(sourceChangeDetails.removed_end_of_day_loads)
    ? sourceChangeDetails.removed_end_of_day_loads.filter(isRemovableOrphanEndOfDayLoadDetail)
    : [];
  const sourceDateChangedEndOfDayLoadDetails = Array.isArray(sourceChangeDetails.removed_end_of_day_loads)
    ? sourceChangeDetails.removed_end_of_day_loads.filter(isDateChangedEndOfDayLoadDetail)
    : [];
  const sourceNewCount = Number.isFinite(Number(sourceChangeCounts.new))
    ? Math.max(0, Math.round(Number(sourceChangeCounts.new)))
    : 0;
  const newOrderIds = [...new Set(
    (Array.isArray(sourceChangeDetails.new) ? sourceChangeDetails.new : [])
      .map((detail) => normalizeInlineText(detail?.order_id || detail?.orderId))
      .filter(Boolean),
  )];
  const resolvedNewOrderCount = Math.max(sourceNewCount, newOrderIds.length);
  const hasMissingNewOrders = resolvedNewOrderCount > 0;
  // Tournée à vérifier : « Commandes » passe en ambre avec « N à vérifier » (le clic reste vers « Mise à jour »).
  const reviewOrderCount = Number.isFinite(Number(item.nonRenderableOrderCount))
    ? Math.max(0, Math.round(Number(item.nonRenderableOrderCount)))
    : 0;
  const hasTripToReview = reviewOrderCount > 0;
  const metricAlertNote = hasTripToReview
    ? `${reviewOrderCount} à vérifier`
    : hasMissingNewOrders
    ? `${resolvedNewOrderCount} nouvelle${resolvedNewOrderCount > 1 ? "s" : ""}`
    : "";
  const existingHoverDetails = item.hoverCardDetails && typeof item.hoverCardDetails === "object"
    ? item.hoverCardDetails
    : {};
  const existingGroups = Array.isArray(existingHoverDetails.groups) ? existingHoverDetails.groups : [];
  const protectedGroup = existingGroups.find((group) => group?.key === "protected") || null;
  const protectedOrderIds = Array.isArray(protectedGroup?.orderIds) ? protectedGroup.orderIds : [];
  const confirmedCount = Number.isFinite(Number(item.confirmedCount))
    ? Math.max(0, Math.round(Number(item.confirmedCount)))
    : protectedOrderIds.length;
  const pendingCount = Number.isFinite(Number(item.pendingCount))
    ? Math.max(0, Math.round(Number(item.pendingCount)))
    : 0;
  const visibleOrderCount = Number.isFinite(Number(item.visibleOrderCount))
    ? Math.max(0, Math.round(Number(item.visibleOrderCount)))
    : Math.max(0, confirmedCount + pendingCount);
  const suspendedCount = Number.isFinite(Number(item.suspendedCount))
    ? Math.max(0, Math.round(Number(item.suspendedCount)))
    : 0;
  const orderTotalCount = Number.isFinite(Number(item.metricValue))
    ? Math.max(0, Math.round(Number(item.metricValue)))
    : visibleOrderCount + suspendedCount;
  return {
    ...item,
    // Les nouvelles commandes sont déjà signalées en rouge par « Mise à jour » : ici, simple information.
    tone: hasTripToReview ? "warning" : hasMissingNewOrders ? "neutral" : "success",
    metricAlertNote,
    metricAlertTone: hasTripToReview ? "warning" : hasMissingNewOrders ? "info" : "",
    hoverCardDetails: {
      ...existingHoverDetails,
      groups: [],
      note: "",
      summaryLines: [
        {
          label: "Commandes",
          value: String(orderTotalCount),
        },
        {
          label: "Visibles",
          value: String(visibleOrderCount),
        },
        {
          label: "Suspendues",
          value: String(suspendedCount),
        },
        {
          label: "Nouvelles (Data Manager)",
          value: String(resolvedNewOrderCount),
        },
        {
          label: "Protégées",
          value: String(confirmedCount),
        },
        {
          label: "Non protégées",
          value: String(pendingCount),
        },
        ...(hasTripToReview ? [{
          label: "À vérifier",
          value: String(reviewOrderCount),
        }] : []),
      ],
      total: String(orderTotalCount),
      totalLabel: "Commandes",
      type: "order-groups",
    },
    isInteractive: canApplyDispatchSourceChanges,
    sourceChangesMenuTrigger: canApplyDispatchSourceChanges,
    ariaLabel: canApplyDispatchSourceChanges
      ? (hasMissingNewOrders
        ? `Ouvrir M-à-J commandes — ${resolvedNewOrderCount} nouvelle${resolvedNewOrderCount > 1 ? "s" : ""} commande${resolvedNewOrderCount > 1 ? "s" : ""} manquante${resolvedNewOrderCount > 1 ? "s" : ""}`
        : "Ouvrir M-à-J commandes")
      : "",
  };
}

function enrichDispatchSectionForSourceChanges(section, interactions = {}) {
  if (!section || section.key !== "orders" || !Array.isArray(section.items)) {
    return section;
  }

  const items = section.items.map((item) => enrichConfirmedOrdersMetric(item, interactions));
  const hasWarningOrDanger = items.some((item) => item?.tone === "warning" || item?.tone === "danger");

  return {
    ...section,
    items,
    tone: hasWarningOrDanger || section.tone === "warning" || section.tone === "danger"
      ? "warning"
      : section.tone,
  };
}

function DispatchSection({ interactions = {}, section, compact = false, trailingMetrics = null }) {
  const items = Array.isArray(section?.items) ? section.items : [];
  const cards = Array.isArray(section?.truckCards) ? section.truckCards : [];
  const SectionIcon = dispatchSectionIconsByKey[section?.key] || CheckCircleIcon;
  const sectionChrome = getDispatchSectionChrome(section);
  const shellClassName = joinClasses([
    "grid w-full max-w-full min-w-0 content-start gap-2",
    compact ? "" : sectionChrome.shell,
    compact ? "" : sectionChrome.accent,
  ]);
  const metricsGridClassName = joinClasses([
    "grid w-full gap-2",
    trailingMetrics ? "auto-rows-fr" : "",
    items.length + Number(Boolean(trailingMetrics)) > 1 ? "grid-cols-2" : "grid-cols-1",
    trailingMetrics
      ? "xl:grid-flow-col xl:auto-cols-fr xl:grid-cols-none"
      : items.length >= 5
        ? "xl:grid-cols-[repeat(5,minmax(0,1fr))]"
        : items.length >= 4
          ? "xl:grid-cols-[repeat(4,minmax(0,1fr))]"
          : items.length === 3
            ? "xl:grid-cols-[repeat(3,minmax(0,1fr))]"
            : items.length === 2
              ? "xl:grid-cols-[repeat(2,minmax(0,1fr))]"
              : "",
  ]);
  const truckCardsGridClassName = cards.length > 1
    ? "grid gap-2 md:grid-cols-2"
    : "grid gap-2";

  return (
    <section className={shellClassName} aria-label={section?.label || "Section"}>
      {!compact ? <div className="pointer-events-none absolute left-2.5 top-0 z-20 flex -translate-y-1/2">
        <span className={joinClasses([
          "inline-flex w-fit items-center gap-1.5 rounded-[4px] border px-2.5 py-0.5 text-[0.61rem] font-black uppercase tracking-[0.14em] shadow-[0_6px_14px_rgba(15,23,42,0.1)]",
          sectionChrome.tag,
        ])}>
          <SectionIcon className="h-3.5 w-3.5" aria-hidden="true" />
          {section?.label || "Section"}
        </span>
      </div> : null}
      <h2 className="sr-only">{section?.label || "Section"}</h2>
      {items.length > 0 || trailingMetrics ? (
        <div className={metricsGridClassName}>
          {items.map((item) => (
            isPlanningPillMetric(item)
              ? <DispatchPlanningMetric key={item.key} pill={{ ...item, interactions }} />
              : <DispatchMetricCard key={item.key} metric={item} interactions={interactions} />
          ))}
          {trailingMetrics}
        </div>
      ) : null}

      {cards.length > 0 ? (
        <div className={truckCardsGridClassName}>
          {cards.map((card) => (
            <DispatchTruckCard key={card.key} card={card} interactions={interactions} />
          ))}
        </div>
      ) : null}
    </section>
  );
}

function DispatchTruckCard({ card, interactions = {} }) {
  const pills = Array.isArray(card.pills) ? card.pills : [];
  const onClick = resolveTruckCardAction(card, interactions);

  const statusLabel = !card.idleCard
    ? "Opérationnel"
    : card.isDanger
      ? "Non affectés"
      : "En attente";

  const tone = card.idleCard && card.isDanger ? "warning" : "neutral";
  const toneClasses = getDispatchCardToneClassNames(tone);
  const shellBase = joinClasses([
    "grid min-h-18 min-w-0 gap-2 rounded-[8px] border px-3 py-2.25 text-left shadow-none",
    toneClasses.card,
    card.asButton
      ? "appearance-none transition duration-150 hover:border-[rgba(15,92,87,0.22)] hover:bg-white focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2"
      : "",
  ]);

  const statusColor = card.idleCard && card.isDanger
    ? "text-[#9a670f]"
    : !card.idleCard
      ? "text-slate-500"
      : "text-slate-400";

  const content = (
    <>
      <div className="flex items-start justify-between gap-3">
        <div className="grid min-w-0 gap-0.5">
          <span className="text-[0.56rem] font-black uppercase tracking-[0.16em] text-slate-500">
            {card.label}
          </span>
          <span className={joinClasses([
            "text-[0.68rem] font-semibold uppercase tracking-[0.14em]",
            statusColor,
          ])}>
            {statusLabel}
          </span>
        </div>
        <span className={joinClasses([
          "inline-flex min-w-10.5 items-center justify-center rounded-[8px] border px-2.5 py-1 text-[1rem] font-black leading-none shadow-none",
          toneClasses.badge,
        ])} {...dataAttributeProps(card.countDataAttribute)}>
          {card.count}
        </span>
      </div>
      <div
        className="flex flex-wrap gap-1.5"
        {...dataAttributeProps(card.pillsDataAttribute)}
      >
        {pills.map((pill) => (
          <span
            key={pill.key}
            className={joinClasses([
              "inline-flex max-w-full items-center whitespace-nowrap rounded-[7px] border px-2.5 py-1 text-[0.68rem] font-medium text-slate-700 shadow-none",
              pill.tone === "warning"
                ? "border-border-warning-badge bg-[rgba(254,243,199,0.88)] text-alert-warning-ink"
                : "border-[rgba(203,213,225,0.8)] bg-white",
            ])}
            title={pill.label}
          >
            <span className="truncate">{pill.label}</span>
          </span>
        ))}
      </div>
    </>
  );

  if (!card.asButton) {
    return <div className={shellBase}>{content}</div>;
  }

  return (
    <button
      type="button"
      className={shellBase}
      data-error-filter={card.errorFilter || undefined}
      data-error-source={card.errorSource || undefined}
      data-error-trigger={card.errorTrigger ? "true" : undefined}
      data-idle-truck-card={card.idleCard ? "true" : undefined}
      aria-disabled={card.disabled ? "true" : "false"}
      aria-label={card.ariaLabel || undefined}
      disabled={card.disabled}
      onClick={onClick || undefined}
    >
      {content}
    </button>
  );
}

export function DispatchPlanningMetric({ pill }) {
  const tone = pill?.tone === "warning"
    ? "warning"
    : pill?.tone === "danger"
      ? "danger"
      : pill?.tone === "success"
        ? "success"
      : "neutral";
  const hoverCardContent = resolveDispatchMetricHoverCardContent(pill);
  const hoverCardMaxWidth = Number.isFinite(Number(pill?.hoverCardMaxWidth)) ? Number(pill.hoverCardMaxWidth) : 340;
  const hasAlertNote = typeof pill?.metricAlertNote === "string" && pill.metricAlertNote.trim();
  const hasBlockingAlert = hasAlertNote && pill?.metricAlertTone !== "info";
  const MetricIcon = tone === "danger" && pill?.key === "confirmed-orders"
    ? ExclamationTriangleIcon
    : (dispatchMetricIconsByKey[pill?.key] || CheckCircleIcon);

  const card = (
    <DispatchIndicatorCard
      alertNote={hasAlertNote ? pill.metricAlertNote.trim() : ""}
      alertTone={pill?.metricAlertTone || ""}
      aria-label={pill?.ariaLabel || undefined}
      interactive={pill?.isInteractive === true}
      dataProps={dataAttributeProps(pill?.dataAttribute)}
      Icon={MetricIcon}
      iconClassName={hasBlockingAlert ? "motion-safe:[animation:dispatch-kpi-alert-blink_1.05s_ease-in-out_infinite]" : ""}
      iconContainerClassName={hasBlockingAlert
        ? "border-[var(--dispatch-card-blocking-icon-border)] bg-[image:var(--dispatch-card-blocking-icon-fill)] text-[var(--dispatch-card-blocking-icon-ink)] shadow-[0_0_0_3px_var(--dispatch-card-blocking-ring)]"
        : ""}
      label={pill?.metricLabel || "Commandes"}
      onClick={resolveMetricAction(pill, pill?.interactions || {}) || undefined}
      tone={tone}
      value={pill?.metricValue || pill?.label || "0/0"}
    />
  );

  if (hoverCardContent == null) {
    return card;
  }

  return (
    <HoverCard
      title={pill?.hoverCardTitle || pill?.metricLabel || pill?.label || "Commandes"}
      content={hoverCardContent}
      placement="bottom-start"
      delay={140}
      maxWidth={hoverCardMaxWidth}
      accentColor={resolveDispatchHoverCardAccentColor(pill)}
      closeOnReferenceClick={pill?.isInteractive === true}
      disabled={pill?.interactions?.isSourceChangesMenuOpen === true}
    >
      {card}
    </HoverCard>
  );
}

function buildCompanionSourceChangesMenuModel({
  activeDispatchDate = "",
  activeDispatchHasSnapshot = false,
  canApplyDispatchSourceChanges = false,
  disableMutationActions = false,
  isClosedDispatchDay = false,
  mutationActionDisabledReason = "",
  sourceChanges = null,
} = {}) {
  const scopedSourceChanges = sourceChanges && typeof sourceChanges === "object" ? sourceChanges : {};
  const counts = scopedSourceChanges.counts && typeof scopedSourceChanges.counts === "object"
    ? scopedSourceChanges.counts
    : {};
  const details = scopedSourceChanges.details && typeof scopedSourceChanges.details === "object"
    ? scopedSourceChanges.details
    : {};
  const removedEndOfDayLoadDetails = Array.isArray(details.removed_end_of_day_loads)
    ? details.removed_end_of_day_loads.filter(isRemovableOrphanEndOfDayLoadDetail)
    : [];
  const dateChangedEndOfDayLoadDetails = Array.isArray(details.removed_end_of_day_loads)
    ? details.removed_end_of_day_loads.filter(isDateChangedEndOfDayLoadDetail)
    : [];
  const newCount = Math.max(0, Math.round(Number(counts.new) || 0));
  const modifiedCount = selectRemoveModifiedSourceChangeDetails(details).length;
  const dateChangedOrderCount = Math.max(0, Math.round(Number(counts.date_changed_remove) || 0));
  const dateChangedCount = dateChangedOrderCount + dateChangedEndOfDayLoadDetails.length;
  const missingDispatchOrderCount = Math.max(0, Math.round(Number(counts.missing_dispatch_orders) || 0));
  const removedEndOfDayLoadCount = removedEndOfDayLoadDetails.length;
  const ghostOrderCount = missingDispatchOrderCount + removedEndOfDayLoadCount;
  const suspendableErrorCount = Math.max(
    0,
    Math.round(Number(scopedSourceChanges.suspendableErrors?.counts?.total) || 0),
  );
  const endOfDayLoadIntegrityCount = resolveEndOfDayLoadIntegrityIssues(scopedSourceChanges).length;
  const inconsistentExecutionRemovalDetails = Array.isArray(details.inconsistent_execution_removals)
    ? details.inconsistent_execution_removals
    : [];
  const recoverableExecutionCount = inconsistentExecutionRemovalDetails.filter(
    (detail) => detail?.recovery_eligible === true,
  ).length;
  const unresolvedCount = Math.max(0, Math.round(Number(counts.unresolved_total) || 0));
  const truckActionableCount = resolveReadyTruckChangeCount(scopedSourceChanges, "actionable");
  const computedPendingCount = resolveSourceChangesAggregateCount(scopedSourceChanges);
  const retainedPendingCount = Number(scopedSourceChanges.lastVerifiedPendingCount);
  const shouldRetainPendingCount = (
    scopedSourceChanges.needsVerification === true
    || scopedSourceChanges.isLoading === true
    || scopedSourceChanges.isVerifying === true
    || Boolean(normalizeInlineText(scopedSourceChanges.errorMessage))
  ) && Number.isFinite(retainedPendingCount);
  const badgePendingCount = shouldRetainPendingCount
    ? Math.max(0, Math.round(retainedPendingCount))
    : Math.max(0, Math.round(Number(computedPendingCount) || 0));
  const dispatchPeriod = normalizeInlineText(
    scopedSourceChanges.activeScopeDispatchPeriod || scopedSourceChanges.dispatchPeriod || "evening",
  ).toLowerCase();
  const dispatchDate = normalizeInlineText(
    scopedSourceChanges.activeScopeDispatchDate || scopedSourceChanges.dispatchDate || activeDispatchDate,
  );
  const hasVerifiedPreview = isSourceChangesPreviewFresh(scopedSourceChanges, {
    dispatchDate,
    dispatchPeriod,
  });
  const hasActionablePreview = isSourceChangesActionPreviewFresh(scopedSourceChanges, {
    dispatchDate,
    dispatchPeriod,
  });
  const isBusy = scopedSourceChanges.isLoading === true
    || scopedSourceChanges.isMutating === true
    || scopedSourceChanges.isVerifying === true;
  const mutationActionsDisabled = disableMutationActions || !hasActionablePreview;
  const unavailableMessage = normalizeInlineText(scopedSourceChanges.unavailableMessage);
  const disabledReason = !dispatchDate
    ? "Sélectionnez une journée de dispatch."
    : !activeDispatchHasSnapshot
      ? "Aucun dispatch calculé pour cette journée."
      : unavailableMessage
        ? unavailableMessage
      : !hasActionablePreview
        ? "Vérifiez M-à-J commandes avant d'appliquer une action."
        : mutationActionDisabledReason || "Aucun changement Data Manager à appliquer.";
  const canRunAction = canApplyDispatchSourceChanges
    && activeDispatchHasSnapshot
    && !mutationActionsDisabled
    && !isBusy;
  const actionableCommandCount = newCount
    + modifiedCount
    + dateChangedCount
    + ghostOrderCount
    + recoverableExecutionCount
    + suspendableErrorCount
    + endOfDayLoadIntegrityCount
    + (scopedSourceChanges.compartmentFillViolations?.details?.length || 0);
  const actionablePendingCount = Math.min(
    badgePendingCount,
    actionableCommandCount + truckActionableCount,
  );

  return {
    activeDispatchPeriod: dispatchPeriod,
    canImportSourceChanges: canRunAction && newCount > 0 && typeof scopedSourceChanges.onImportNew === "function",
    canImportSuspendableErrors: canRunAction
      && suspendableErrorCount > 0
      && typeof scopedSourceChanges.onImportSuspendableErrors === "function",
    canRemoveDateChangedSourceChanges: canRunAction
      && hasVerifiedPreview
      && dateChangedCount > 0
      && typeof scopedSourceChanges.onRemoveDateChanged === "function",
    canRemoveSourceChanges: canRunAction
      && hasVerifiedPreview
      && modifiedCount > 0
      && typeof scopedSourceChanges.onRemoveModified === "function",
    canRepairGhostOrders: canRunAction
      && hasVerifiedPreview
      && ghostOrderCount > 0
      && typeof scopedSourceChanges.onRepairGhostOrders === "function",
    canRestoreExecutions: canRunAction
      && recoverableExecutionCount > 0
      && typeof scopedSourceChanges.onRestoreExecutions === "function",
    disableMutationActions: mutationActionsDisabled,
    dispatchPeriodLabel: dispatchPeriod === "evening" || dispatchPeriod === "soir" ? "Soir" : "Jour",
    hasVerifiedSourceChangesPreview: hasVerifiedPreview,
    isClosedDispatchDay,
    sourceActionablePendingCount: actionablePendingCount,
    sourceBadgePendingCount: badgePendingCount,
    sourceChangeDisabledReason: disabledReason,
    sourceChanges: scopedSourceChanges,
    sourceChangesBusy: isBusy,
    sourceDateChangedCount: dateChangedCount,
    sourceEndOfDayLoadIntegrityCount: endOfDayLoadIntegrityCount,
    sourceGhostOrderCount: ghostOrderCount,
    sourceMissingDispatchOrderCount: missingDispatchOrderCount,
    sourceNewCount: newCount,
    sourceRemovedEndOfDayLoadCount: removedEndOfDayLoadCount,
    sourceSuspendableErrorCount: suspendableErrorCount,
    sourceUnresolvedCount: unresolvedCount,
  };
}

// Shared with the visual report so its badge includes the same overlaps and reviews.
export function buildDispatchSourceChangesBadgeModel(sourceChanges = {}) {
  const sourceChangeCounts = sourceChanges.counts && typeof sourceChanges.counts === "object"
    ? sourceChanges.counts
    : {};
  const sourceChangeDetails = sourceChanges.details && typeof sourceChanges.details === "object"
    ? sourceChanges.details
    : {};
  const sourceRemovedEndOfDayLoadDetails = Array.isArray(sourceChangeDetails.removed_end_of_day_loads)
    ? sourceChangeDetails.removed_end_of_day_loads.filter(isRemovableOrphanEndOfDayLoadDetail)
    : [];
  const sourceDateChangedEndOfDayLoadDetails = Array.isArray(sourceChangeDetails.removed_end_of_day_loads)
    ? sourceChangeDetails.removed_end_of_day_loads.filter(isDateChangedEndOfDayLoadDetail)
    : [];
  const sourceMixedEndOfDayLoadDetails = Array.isArray(sourceChangeDetails.removed_end_of_day_loads)
    ? sourceChangeDetails.removed_end_of_day_loads.filter(isMixedOrphanEndOfDayLoadDetail)
    : [];
  const sourceNewCount = Number.isFinite(Number(sourceChangeCounts.new))
    ? Math.max(0, Math.round(Number(sourceChangeCounts.new)))
    : 0;
  const sourceRemoveModifiedDetails = selectRemoveModifiedSourceChangeDetails(sourceChangeDetails);
  const sourceRemoveModifiedOrderIds = new Set(sourceRemoveModifiedDetails.map(
    (detail) => normalizeInlineText(detail?.order_id || detail?.orderId),
  ));
  const sourceModifiedCount = sourceRemoveModifiedDetails.length;
  const sourceDateChangedOrderCount = Number.isFinite(Number(sourceChangeCounts.date_changed_remove))
    ? Math.max(0, Math.round(Number(sourceChangeCounts.date_changed_remove)))
    : 0;
  const sourceDateChangedCount = sourceDateChangedOrderCount + sourceDateChangedEndOfDayLoadDetails.length;
  const sourceMissingDispatchOrderCount = Number.isFinite(Number(sourceChangeCounts.missing_dispatch_orders))
    ? Math.max(0, Math.round(Number(sourceChangeCounts.missing_dispatch_orders)))
    : 0;
  const sourceRemovedEndOfDayLoadCount = sourceRemovedEndOfDayLoadDetails.length;
  const sourceEndOfDayLoadIntegrityIssues = resolveEndOfDayLoadIntegrityIssues(sourceChanges);
  const sourceEndOfDayLoadIntegrityCount = sourceEndOfDayLoadIntegrityIssues.length;
  const sourceEndOfDayLoadIntegrityIdentityKeys = new Set(
    sourceEndOfDayLoadIntegrityIssues.flatMap(resolveEndOfDayLoadIntegrityIdentityKeys),
  );
  const sourceProtectedExecutionDetails = Array.isArray(sourceChangeDetails.protected_execution)
    ? sourceChangeDetails.protected_execution.filter(Boolean)
    : [];
  const sourceIntegrityConflictDetails = Array.isArray(sourceChangeDetails.integrity_conflicts)
    ? sourceChangeDetails.integrity_conflicts.filter(Boolean)
    : [];
  const sourceIntegrityConflictCountFromPayload = Number.isFinite(
    Number(sourceChangeCounts.integrity_conflicts),
  )
    ? Math.max(0, Math.round(Number(sourceChangeCounts.integrity_conflicts)))
    : 0;
  const sourceIntegrityConflictCount = Math.max(
    sourceIntegrityConflictCountFromPayload,
    sourceIntegrityConflictDetails.length,
  );
  const sourceProtectedRemovalOverlapDetails = sourceProtectedExecutionDetails.filter((detail) => (
    sourceRemoveModifiedOrderIds.has(normalizeInlineText(detail?.order_id || detail?.orderId))
  ));
  const sourceProtectedRemovalOverlapOrderIds = new Set(sourceProtectedRemovalOverlapDetails.map(
    (detail) => normalizeInlineText(detail?.order_id || detail?.orderId),
  ));
  const sourceBlockingProtectedRemovalOverlapOrderIds = new Set(
    sourceProtectedRemovalOverlapDetails
      .filter((detail) => (
        detail?.blocks_publication === true || detail?.blocksPublication === true
      ))
      .map((detail) => normalizeInlineText(detail?.order_id || detail?.orderId)),
  );
  const sourceBlockingProtectedExecutionDetails = sourceProtectedExecutionDetails.filter(
    (detail) => (
      (detail?.blocks_publication === true || detail?.blocksPublication === true)
      && !sourceRemoveModifiedOrderIds.has(normalizeInlineText(detail?.order_id || detail?.orderId))
    ),
  );
  const sourceNewPmOverlapCount = countSourceChangeDetailsMatchingIntegrityIdentities(
    sourceChangeDetails.new,
    sourceEndOfDayLoadIntegrityIdentityKeys,
  );
  const sourceModifiedPmOverlapCount = countSourceChangeDetailsMatchingIntegrityIdentities(
    sourceRemoveModifiedDetails,
    sourceEndOfDayLoadIntegrityIdentityKeys,
  );
  const sourceDateChangedPmOverlapCount = countSourceChangeDetailsMatchingIntegrityIdentities(
    sourceChangeDetails.date_changed_remove,
    sourceEndOfDayLoadIntegrityIdentityKeys,
  );
  const sourceDateChangedEndOfDayLoadPmOverlapCount = countSourceChangeDetailsMatchingIntegrityIdentities(
    sourceDateChangedEndOfDayLoadDetails,
    sourceEndOfDayLoadIntegrityIdentityKeys,
  );
  const sourceMissingDispatchPmOverlapCount = countSourceChangeDetailsMatchingIntegrityIdentities(
    sourceChangeDetails.missing_dispatch_orders,
    sourceEndOfDayLoadIntegrityIdentityKeys,
  );
  const sourceRemovedEndOfDayLoadPmOverlapCount = countSourceChangeDetailsMatchingIntegrityIdentities(
    sourceRemovedEndOfDayLoadDetails,
    sourceEndOfDayLoadIntegrityIdentityKeys,
  );
  const sourceMixedEndOfDayLoadPmOverlapCount = countSourceChangeDetailsMatchingIntegrityIdentities(
    sourceMixedEndOfDayLoadDetails,
    sourceEndOfDayLoadIntegrityIdentityKeys,
  );
  const sourceAllRemovedEndOfDayLoadPmOverlapCount = countSourceChangeDetailsMatchingIntegrityIdentities(
    sourceChangeDetails.removed_end_of_day_loads,
    sourceEndOfDayLoadIntegrityIdentityKeys,
  );
  const sourceBlockingProtectedPmOverlapCount = countSourceChangeDetailsMatchingIntegrityIdentities(
    sourceBlockingProtectedExecutionDetails,
    sourceEndOfDayLoadIntegrityIdentityKeys,
  );
  const sourceGenericPmOverlapCount = sourceNewPmOverlapCount
    + sourceModifiedPmOverlapCount
    + sourceDateChangedPmOverlapCount
    + sourceMissingDispatchPmOverlapCount
    + sourceAllRemovedEndOfDayLoadPmOverlapCount
    + sourceBlockingProtectedPmOverlapCount;
  const sourceNewBadgeCount = Math.max(0, sourceNewCount - sourceNewPmOverlapCount);
  const sourceModifiedBadgeCount = Math.max(0, sourceModifiedCount - sourceModifiedPmOverlapCount);
  const sourceDateChangedBadgeCount = Math.max(
    0,
    sourceDateChangedCount
      - sourceDateChangedPmOverlapCount
      - sourceDateChangedEndOfDayLoadPmOverlapCount,
  );
  const sourceMissingDispatchBadgeCount = Math.max(
    0,
    sourceMissingDispatchOrderCount - sourceMissingDispatchPmOverlapCount,
  );
  const sourceRemovedEndOfDayLoadBadgeCount = Math.max(
    0,
    sourceRemovedEndOfDayLoadCount - sourceRemovedEndOfDayLoadPmOverlapCount,
  );
  const sourceMixedEndOfDayLoadReviewCount = Math.max(
    0,
    sourceMixedEndOfDayLoadDetails.length - sourceMixedEndOfDayLoadPmOverlapCount,
  );
  const sourceProtectedExecutionCountFromPayload = Number.isFinite(
    Number(sourceChangeCounts.protected_execution),
  )
    ? Math.max(0, Math.round(Number(sourceChangeCounts.protected_execution)))
    : 0;
  const sourceProtectedExecutionCountBeforePmDeduplication = Math.max(
    sourceProtectedExecutionCountFromPayload,
    sourceProtectedExecutionDetails.length,
  );
  const sourceProtectedReviewCount = Math.max(
    0,
    sourceProtectedExecutionCountBeforePmDeduplication - sourceProtectedRemovalOverlapOrderIds.size,
  );
  const sourceBlockingProtectedExecutionCountFromPayload = Number.isFinite(
    Number(sourceChangeCounts.blocking_protected_execution),
  )
    ? Math.max(0, Math.round(Number(sourceChangeCounts.blocking_protected_execution)))
    : 0;
  const sourceBlockingProtectedExecutionCountBeforePmDeduplication = Math.max(
    Math.max(
      0,
      sourceBlockingProtectedExecutionCountFromPayload - sourceBlockingProtectedRemovalOverlapOrderIds.size,
    ),
    sourceBlockingProtectedExecutionDetails.length,
  );
  const sourceBlockingProtectedExecutionCount = Math.max(
    0,
    sourceBlockingProtectedExecutionCountBeforePmDeduplication - sourceBlockingProtectedPmOverlapCount,
  );
  const sourceUnresolvedCountFromPayload = Number.isFinite(Number(sourceChangeCounts.unresolved_total))
    ? Math.max(0, Math.round(Number(sourceChangeCounts.unresolved_total)))
    : 0;
  const sourceUnresolvedCount = Math.max(
    0,
    sourceUnresolvedCountFromPayload - sourceGenericPmOverlapCount,
  );
  const sourceInconsistentExecutionRemovalDetails = Array.isArray(sourceChangeDetails.inconsistent_execution_removals)
    ? sourceChangeDetails.inconsistent_execution_removals
    : [];
  const sourceRecoverableExecutionCount = sourceInconsistentExecutionRemovalDetails.filter(
    (detail) => detail?.recovery_eligible === true,
  ).length;
  const sourceBlockedExecutionReviewCount = Math.max(
    0,
    sourceInconsistentExecutionRemovalDetails.length - sourceRecoverableExecutionCount,
  );
  const sourceActionableUnresolvedCount = Math.max(
    0,
    sourceUnresolvedCount
      - sourceBlockingProtectedExecutionCount
      - sourceIntegrityConflictCount
      - sourceBlockedExecutionReviewCount
      - sourceMixedEndOfDayLoadReviewCount,
  );
  const sourceGhostOrderCount = sourceMissingDispatchOrderCount + sourceRemovedEndOfDayLoadCount;
  const sourceBadgeGhostOrderCount = sourceMissingDispatchBadgeCount
    + sourceRemovedEndOfDayLoadBadgeCount;
  const suspendableErrorCounts = sourceChanges.suspendableErrors?.counts && typeof sourceChanges.suspendableErrors.counts === "object"
    ? sourceChanges.suspendableErrors.counts
    : {};
  const sourceSuspendableErrorCount = Number.isFinite(Number(suspendableErrorCounts.total))
    ? Math.max(0, Math.round(Number(suspendableErrorCounts.total)))
    : 0;
  const sourceComputedActionableBadgeCount = sourceNewBadgeCount
    + sourceModifiedBadgeCount
    + sourceDateChangedBadgeCount
    + sourceBadgeGhostOrderCount
    + sourceRecoverableExecutionCount
    + sourceSuspendableErrorCount
    + sourceEndOfDayLoadIntegrityCount;
  const sourceCommandActionableBadgePendingCount = Math.max(
    sourceActionableUnresolvedCount,
    sourceComputedActionableBadgeCount,
  );
  const sourceTruckActionableCount = resolveReadyTruckChangeCount(sourceChanges, "actionable");
  const sourceTruckUnresolvedCount = resolveReadyTruckChangeCount(sourceChanges, "unresolved");
  // Camions hors limite EXP : catégorie actionnable hors counts du backend.
  const sourceCompartmentFillCount = sourceChanges.compartmentFillViolations?.details?.length || 0;
  const sourceActionableBadgePendingCount = sourceCommandActionableBadgePendingCount
    + sourceTruckActionableCount
    + sourceCompartmentFillCount;
  const computedSourceBadgePendingCount = sourceCommandActionableBadgePendingCount
    + sourceProtectedReviewCount
    + sourceIntegrityConflictCount
    + sourceBlockedExecutionReviewCount
    + sourceMixedEndOfDayLoadReviewCount
    + sourceTruckUnresolvedCount
    + sourceCompartmentFillCount;
  const lastVerifiedSourceBadgePendingCount = Number(sourceChanges.lastVerifiedPendingCount);
  const hasLastVerifiedSourceBadgePendingCount = sourceChanges.lastVerifiedPendingCount != null
    && Number.isFinite(lastVerifiedSourceBadgePendingCount);
  const shouldRetainLastVerifiedSourceBadge = (
    sourceChanges.needsVerification === true
    || sourceChanges.needsPlanningContextVerification === true
    || sourceChanges.isLoading === true
    || sourceChanges.isVerifying === true
    || Boolean(normalizeInlineText(sourceChanges.errorMessage))
    || Boolean(normalizeInlineText(sourceChanges.planningContextVerificationErrorMessage))
  ) && hasLastVerifiedSourceBadgePendingCount;
  const sourceBadgePendingCount = shouldRetainLastVerifiedSourceBadge
    ? Math.max(0, Math.round(lastVerifiedSourceBadgePendingCount))
    : computedSourceBadgePendingCount;
  return {
    sourceNewCount,
    sourceModifiedCount,
    sourceDateChangedCount,
    sourceMissingDispatchOrderCount,
    sourceRemovedEndOfDayLoadCount,
    sourceEndOfDayLoadIntegrityCount,
    sourceUnresolvedCount,
    sourceRecoverableExecutionCount,
    sourceActionableUnresolvedCount,
    sourceGhostOrderCount,
    sourceSuspendableErrorCount,
    sourceActionableBadgePendingCount,
    computedSourceBadgePendingCount,
    sourceBadgePendingCount,
  };
}

export function DispatchPlanningActions({ interactions = {}, model = {}, headerSlots = null, fillSourceChangesIndicator = Boolean(headerSlots) }) {
  const navigate = useNavigate();
  const location = useLocation();
  const optimizeConfirmation = model.optimizeConfirmation && typeof model.optimizeConfirmation === "object"
    ? model.optimizeConfirmation
    : DEFAULT_OPTIMIZE_CONFIRMATION;
  const optimizationMode = model.optimizationMode && typeof model.optimizationMode === "object"
    ? model.optimizationMode
    : null;
  const optimizationLaunchPreflight = model.optimizationLaunchPreflight && typeof model.optimizationLaunchPreflight === "object"
    ? model.optimizationLaunchPreflight
    : null;
  const optimizationExcludedTrucks = Array.isArray(model.optimizationExcludedTrucks)
    ? model.optimizationExcludedTrucks.filter(Boolean)
    : [];
  const includeSuspendedDeliveriesInOptimization = model.includeSuspendedDeliveriesInOptimization === true;
  const { access, isReady: isAccessReady } = useAccess();
  const canRestartRailwayService = isAccessReady
    && hasPermission(access, ACCESS_PERMISSION_KEYS.technicalRailwayRestart);
  const canSeeGeotabCheck = isAccessReady && hasPermission(access, ACCESS_PERMISSION_KEYS.dispatchGeotabRealRead);
  const sourceStaleNotice = model.sourceStaleNotice && typeof model.sourceStaleNotice === "object"
    ? model.sourceStaleNotice
    : null;
  const dispatchDayControls = model.dispatchDayControls && typeof model.dispatchDayControls === "object"
    ? model.dispatchDayControls
    : null;
  const truckLabelById = model.truckLabelById && typeof model.truckLabelById === "object"
    ? model.truckLabelById
    : {};
  const truckDriverById = model.truckDriverById && typeof model.truckDriverById === "object"
    ? model.truckDriverById
    : null;
  const optimizeFormRef = useRef(null);
  const [isOptimizeConfirmOpen, setIsOptimizeConfirmOpen] = useState(false);
  const [requestedOptimizationPeriod, setRequestedOptimizationPeriod] = useState("");
  const { isFinalizing, isOptimizing, launchOptimization } = useOptimizationLaunchAction({});
  useEffect(() => {
    interactions.onOptimizationStateChange?.(isOptimizing);
  }, [interactions.onOptimizationStateChange, isOptimizing]);
  const pendingPlanningMutationCount = Number.isFinite(Number(model.pendingPlanningMutationCount))
    ? Number(model.pendingPlanningMutationCount)
    : 0;
  const hasPendingPlanningMutations = pendingPlanningMutationCount > 0;
  const isSameOdGroupingMutationPending = interactions.isSameOdGroupingMutationPending === true;
  const isProductMixingEnabled = interactions.manualProductMixing?.enabled === true;
  const isProductMixingSaving = interactions.manualProductMixing?.isSaving === true;
  const hasPlanningActionInProgress = isOptimizing || hasPendingPlanningMutations;
  const activeDispatchStatusKey = normalizeInlineText(
    dispatchDayControls?.activeDispatchStatus
    || dispatchDayControls?.activeStatus?.key,
  ).toLowerCase();
  const activeDispatchState = normalizeInlineText(dispatchDayControls?.activeDispatchState).toLowerCase();
  const isClosedDispatchDay = dispatchDayControls?.activeItem?.isClosed === true
    || activeDispatchStatusKey === "cloture"
    || activeDispatchStatusKey === "closed"
    || activeDispatchState === "closed";
  // Journée livrée : le serveur refuse tout changement, « Optimiser » et « Réinitialiser » sont grisés.
  const isDeliveredDispatchDay = activeDispatchStatusKey === "livre";
  const disableMutationActions = isClosedDispatchDay || hasPlanningActionInProgress;
  const disableOptimizationLaunch = interactions.eveningOrderPool?.isSaving === true || isSameOdGroupingMutationPending || isProductMixingSaving
    || hasPendingPlanningMutations
    || (interactions.optimizationScope?.dispatchPeriod === "evening" && Boolean(interactions.eveningOrderPool?.disabledReason))
    || ((isClosedDispatchDay || isDeliveredDispatchDay) && !isOptimizing);
  const mutationActionDisabledReason = isProductMixingSaving
    ? "Le mélange de produits est en cours d’enregistrement."
    : isSameOdGroupingMutationPending
    ? "Le réglage Groupes inséparables est en cours d’enregistrement. Attendez sa confirmation avant de relancer l’optimisation."
    : hasPendingPlanningMutations
      ? "Une mise à jour du planning est en cours. Attendez sa fin avant de relancer l'optimisation ou de réinitialiser le planning."
      : isOptimizing
        ? "Une optimisation est en cours. Attendez sa fin avant de modifier le planning."
        : isClosedDispatchDay
          ? "Journée clôturée : rouvrez-la avant de réinitialiser le planning ou de relancer l'optimisation."
          : isDeliveredDispatchDay
            ? "Journée livrée : le Dispatch ne peut plus changer."
            : "";
  const activeDispatchDate = normalizeInlineText(
    dispatchDayControls?.activeItem?.dispatchDate
    || dispatchDayControls?.activeDispatchDate,
  );
  const optimizationScope = interactions.optimizationScope || {};
  const optimizationDispatchDate = optimizationScope.dispatchDate || activeDispatchDate;
  const optimizationDispatchPeriod = optimizationScope.dispatchPeriod || "day";
  const showOptimizationPeriodMenu = optimizationScope.showPeriodMenu === true;
  const optimizationContextKey = `${optimizationDispatchDate}:${optimizationDispatchPeriod}:${showOptimizationPeriodMenu}:${interactions.activeDetailView || ""}`;
  const eveningOptimizationTarget = useMemo(() => buildEveningOptimizationTarget({
    dispatchDate: optimizationDispatchDate,
    railState: optimizationScope.eveningGanttRailState,
    includeSuspendedDeliveries: includeSuspendedDeliveriesInOptimization,
    eveningOrderPool: interactions.eveningOrderPool,
  }), [optimizationDispatchDate, optimizationScope.eveningGanttRailState, includeSuspendedDeliveriesInOptimization, interactions.eveningOrderPool]);
  const eveningOptimizationDisabledReason = hasPendingPlanningMutations || isSameOdGroupingMutationPending
    || isProductMixingSaving || interactions.eveningOrderPool?.isSaving === true
    ? mutationActionDisabledReason || "Une mise à jour du planning est en cours."
    : eveningOptimizationTarget.disabledReason;
  const isEveningShortcutTarget = requestedOptimizationPeriod === "evening" && optimizationDispatchPeriod === "day";
  const selectedOptimizationModel = isEveningShortcutTarget ? eveningOptimizationTarget.model : model;
  const selectedOptimizationDisabled = isEveningShortcutTarget
    ? !showOptimizationPeriodMenu || Boolean(eveningOptimizationDisabledReason)
    : disableOptimizationLaunch;
  const selectedOptimizeConfirmation = buildScopedOptimizationConfirmation(
    selectedOptimizationModel?.optimizeConfirmation || optimizeConfirmation,
    optimizationDispatchDate,
    requestedOptimizationPeriod || optimizationDispatchPeriod,
  );
  useEffect(() => {
    setIsOptimizeConfirmOpen(false);
    setRequestedOptimizationPeriod("");
  }, [optimizationContextKey]);
  const openOptimizationConfirmation = (period) => {
    if (isOptimizing || (period === "evening" && optimizationDispatchPeriod === "day"
      ? !showOptimizationPeriodMenu || Boolean(eveningOptimizationDisabledReason)
      : disableOptimizationLaunch)) return;
    setRequestedOptimizationPeriod(period);
    setIsOptimizeConfirmOpen(true);
  };
  const activeDispatchLabel = normalizeInlineText(
    dispatchDayControls?.activeItem?.label
    || dispatchDayControls?.selectedLabel
    || activeDispatchDate,
  );
  const activeDispatchHasSnapshot = dispatchDayControls?.activeItem?.hasSnapshot === true
    || Boolean(dispatchDayControls?.activeLastOptimizedAt);
  const activeDispatchRequiresRevalidation = dispatchDayControls?.activeRequiresRevalidation === true;
  const canCloseActiveDay = interactions?.canCloseDispatchDay === true
    && dispatchDayControls?.activeItem?.hasDispatch === true
    && activeDispatchState !== "closed";
  const canReopenActiveDay = interactions?.canReopenDispatchDay === true
    && Boolean(activeDispatchDate)
    && activeDispatchState === "closed";
  const hasDayStateActions = canCloseActiveDay || canReopenActiveDay;
  const canInvalidateDispatchDay = interactions.canInvalidateDispatchDay === true;
  const canResetDispatchRevalidation = interactions.canResetDispatchRevalidation === true;
  const canResetOptimizationRuntimeCache = interactions.canResetOptimizationRuntimeCache === true;
  const canResetHereRouteCache = interactions.canResetHereRouteCache === true;
  const canDeleteDispatchSnapshot = interactions.canDeleteDispatchSnapshot === true;
  const canResetPlanning = interactions.canResetPlanning === true;
  const planningResetProtection = model.planningResetProtection && typeof model.planningResetProtection === "object"
    ? model.planningResetProtection
    : {};
  const planningResetBlocked = planningResetProtection.blocked === true;
  const blockedOrderCount = Math.max(0, Number(planningResetProtection.order_count) || 0);
  const blockedOrderIds = Array.isArray(planningResetProtection.order_ids)
    ? planningResetProtection.order_ids.filter(Boolean).slice(0, 3)
    : [];
  const blockedOrderLabel = blockedOrderCount === 1 ? "commande" : "commandes";
  const blockedOrderDetails = blockedOrderIds.length > 0 ? ` (${blockedOrderIds.join(", ")})` : "";
  const planningResetBlockedReason = planningResetProtection.reason_code === "planning_reset_execution_unavailable"
    ? "Les exécutions Data Manager ne peuvent pas être vérifiées. Réinitialisation indisponible."
    : `${blockedOrderCount || "Des"} ${blockedOrderLabel} avec chargement ou livraison réels${blockedOrderDetails}. Réinitialisation impossible. Utilisez Optimiser pour la suite ; corrigez les tonnages dans Data Manager ou déconfirmez explicitement dans le portail pour modifier l'exécution.`;
  const reassignmentRebuildMode = normalizeDispatchReassignmentMode(interactions.reassignmentRebuildMode);
  const isFastReassignmentEnabled = resolveDispatchFastReassignmentEnabled(
    import.meta.env?.VITE_DISPATCH_FAST_REASSIGNMENT_ENABLED,
  );
  const canApplyDispatchSourceChanges = interactions.canApplyDispatchSourceChanges === true;
  const canRunOptimization = interactions.canRunOptimization === true;
  const sourceChanges = interactions.sourceChanges && typeof interactions.sourceChanges === "object"
    ? interactions.sourceChanges
    : {};
  const {
    sourceNewCount,
    sourceModifiedCount,
    sourceDateChangedCount,
    sourceMissingDispatchOrderCount,
    sourceRemovedEndOfDayLoadCount,
    sourceEndOfDayLoadIntegrityCount,
    sourceUnresolvedCount,
    sourceRecoverableExecutionCount,
    sourceActionableUnresolvedCount,
    sourceGhostOrderCount,
    sourceSuspendableErrorCount,
    sourceActionableBadgePendingCount,
    computedSourceBadgePendingCount,
    sourceBadgePendingCount,
  } = buildDispatchSourceChangesBadgeModel(sourceChanges);
  const dispatchActionableSourceChangeCount = sourceActionableBadgePendingCount;
  const sourceChangesBusy = sourceChanges.isLoading === true
    || sourceChanges.isMutating === true
    || sourceChanges.isVerifying === true;
  const sourceActiveScopeDispatchDate = normalizeInlineText(
    sourceChanges.activeScopeDispatchDate || activeDispatchDate,
  );
  const sourceActiveScopeDispatchPeriod = normalizeInlineText(
    sourceChanges.activeScopeDispatchPeriod,
  ).toLowerCase();
  const sourceDispatchPeriodLabel = sourceActiveScopeDispatchPeriod === "evening" || sourceActiveScopeDispatchPeriod === "soir"
    ? "Soir"
    : "Jour";
  const canScheduleOptimization = canRunOptimization
    && isAccessReady
    && hasPermission(access, ACCESS_PERMISSION_KEYS.automationsManage)
    && Boolean(activeDispatchDate);
  const eveningSourceChanges = interactions.sourceChangesByPeriod?.evening
    && typeof interactions.sourceChangesByPeriod.evening === "object"
    ? interactions.sourceChangesByPeriod.evening
    : {};
  const showEveningSourceChangesShortcut = interactions.showEveningSourceChangesShortcut === true
    && sourceDispatchPeriodLabel === "Jour";
  const eveningSourceChangesMenuModel = buildCompanionSourceChangesMenuModel({
    activeDispatchDate,
    activeDispatchHasSnapshot,
    canApplyDispatchSourceChanges,
    disableMutationActions,
    isClosedDispatchDay,
    mutationActionDisabledReason,
    sourceChanges: eveningSourceChanges,
  });
  const sourceChangesMenuOpenPeriod = normalizeInlineText(
    interactions.sourceChangesMenuOpenPeriod,
  ).toLowerCase();
  const hasVerifiedSourceChangesPreview = isSourceChangesPreviewFresh(sourceChanges, {
    dispatchDate: sourceActiveScopeDispatchDate,
    dispatchPeriod: sourceActiveScopeDispatchPeriod,
  });
  const hasActionableSourceChangesPreview = isSourceChangesActionPreviewFresh(sourceChanges, {
    dispatchDate: sourceActiveScopeDispatchDate,
    dispatchPeriod: sourceActiveScopeDispatchPeriod,
  });
  useEffect(() => {
    if (
      !hasVerifiedSourceChangesPreview
      || typeof sourceChanges.onVerifiedPendingCount !== "function"
    ) {
      return;
    }
    sourceChanges.onVerifiedPendingCount(computedSourceBadgePendingCount);
  }, [
    computedSourceBadgePendingCount,
    hasVerifiedSourceChangesPreview,
    sourceChanges.onVerifiedPendingCount,
  ]);
  useEffect(() => {
    if (
      !showEveningSourceChangesShortcut
      || !eveningSourceChangesMenuModel.hasVerifiedSourceChangesPreview
      || typeof eveningSourceChanges.onVerifiedPendingCount !== "function"
    ) {
      return;
    }
    eveningSourceChanges.onVerifiedPendingCount(
      eveningSourceChangesMenuModel.sourceBadgePendingCount,
    );
  }, [
    eveningSourceChanges.onVerifiedPendingCount,
    eveningSourceChangesMenuModel.hasVerifiedSourceChangesPreview,
    eveningSourceChangesMenuModel.sourceBadgePendingCount,
    showEveningSourceChangesShortcut,
  ]);
  const isSourceChangesMutationInProgress = sourceChanges.isMutating === true;
  // Pendant une vérification M-à-J, l'avis n'est pas encore connu : ni « ! » ni Acquitter/Invalider.
  const isSourceChangesVerificationInProgress = canApplyDispatchSourceChanges
    && (sourceChanges.isVerifying === true || sourceChanges.isLoading === true)
    && !normalizeInlineText(sourceChanges.errorMessage);
  const resolvedSourceStaleNotice = resolveDispatchSourceStaleNotice({
    sourceStaleNotice,
    sourceChanges,
    hasVerifiedPreview: hasVerifiedSourceChangesPreview,
  });
  const hasBlockingSourceStaleNotice = Boolean(resolvedSourceStaleNotice);
  const shouldShowSourceStaleNotice = hasBlockingSourceStaleNotice
    && !isSourceChangesMutationInProgress
    && !isSourceChangesVerificationInProgress;
  const canResetRuntimeCache = Boolean(activeDispatchDate)
    && !hasPlanningActionInProgress;
  const sourceChangesMutationActionsDisabled = disableMutationActions
    || !hasActionableSourceChangesPreview;
  const sourceChangeUnavailableMessage = normalizeInlineText(sourceChanges.unavailableMessage);
  const sourceChangeDisabledReason = !activeDispatchDate
    ? "Sélectionnez une journée de dispatch."
    : !activeDispatchHasSnapshot
      ? "Aucun dispatch calculé pour cette journée."
      : sourceChangeUnavailableMessage
        ? sourceChangeUnavailableMessage
      : sourceChanges.needsPlanningContextVerification === true
        ? "Ouvrez M-à-J commandes pour actualiser le contexte avant d'appliquer une action."
        : !hasActionableSourceChangesPreview
          ? "Vérifiez M-à-J commandes avant d'appliquer une action."
          : mutationActionDisabledReason || "Aucun changement Data Manager à appliquer.";
  const canImportSourceChanges = canApplyDispatchSourceChanges
    && activeDispatchHasSnapshot
    && !sourceChangesMutationActionsDisabled
    && !sourceChangesBusy
    && sourceNewCount > 0
    && typeof sourceChanges.onImportNew === "function";
  const canRemoveSourceChanges = canApplyDispatchSourceChanges
    && activeDispatchHasSnapshot
    && !sourceChangesMutationActionsDisabled
    && !sourceChangesBusy
    && hasVerifiedSourceChangesPreview
    && sourceModifiedCount > 0
    && typeof sourceChanges.onRemoveModified === "function";
  const canRemoveDateChangedSourceChanges = canApplyDispatchSourceChanges
    && activeDispatchHasSnapshot
    && !sourceChangesMutationActionsDisabled
    && !sourceChangesBusy
    && hasVerifiedSourceChangesPreview
    && sourceDateChangedCount > 0
    && typeof sourceChanges.onRemoveDateChanged === "function";
  const canImportSuspendableErrors = canApplyDispatchSourceChanges
    && activeDispatchHasSnapshot
    && !sourceChangesMutationActionsDisabled
    && !sourceChangesBusy
    && sourceSuspendableErrorCount > 0
    && typeof sourceChanges.onImportSuspendableErrors === "function";
  const canRepairGhostOrders = canApplyDispatchSourceChanges
    && activeDispatchHasSnapshot
    && !sourceChangesMutationActionsDisabled
    && !sourceChangesBusy
    && hasVerifiedSourceChangesPreview
    && sourceGhostOrderCount > 0
    && typeof sourceChanges.onRepairGhostOrders === "function";
  const canRestoreExecutions = canApplyDispatchSourceChanges
    && activeDispatchHasSnapshot
    && !sourceChangesMutationActionsDisabled
    && !sourceChangesBusy
    && sourceRecoverableExecutionCount > 0
    && typeof sourceChanges.onRestoreExecutions === "function";
  const resetRuntimeCacheDisabledReason = !activeDispatchDate
    ? "Sélectionnez une journée de dispatch avant de vider le cache d’optimisation."
    : hasPendingPlanningMutations
      ? mutationActionDisabledReason
      : isOptimizing
        ? "Arrêtez l'optimisation en cours avant de vider le cache d’optimisation."
        : OPTIMIZATION_CACHE_RESET_HELP;
  const resetHereDisabledReason = hasPendingPlanningMutations
    ? mutationActionDisabledReason
    : isOptimizing
      ? "Arrêtez l'optimisation en cours avant de vider le cache HERE."
      : HERE_CACHE_RESET_HELP;
  const deleteDispatchSnapshotDisabledReason = !activeDispatchDate
    ? "Sélectionnez une journée de dispatch avant de supprimer son snapshot."
    : isClosedDispatchDay
      // Les photos de clôture servent au Calibrage Geotab : une journée clôturée ne se vide pas.
      ? "Journée clôturée : rouvrez-la d’abord."
      : isSourceChangesMutationInProgress
      ? "Une mise à jour Data Manager est en cours."
      : hasPendingPlanningMutations
      ? mutationActionDisabledReason
      : isOptimizing
        ? "Arrêtez l'optimisation en cours avant de supprimer le snapshot."
        : "Supprimer tout l'état Dispatch de cette date pour Jour et Soir.";
  const canDeleteActiveDispatchSnapshot = canDeleteDispatchSnapshot
    && Boolean(activeDispatchDate)
    && !isClosedDispatchDay
    && !hasPlanningActionInProgress
    && !isSourceChangesMutationInProgress;
  const canInvalidateActiveDispatch = canInvalidateDispatchDay
    && Boolean(activeDispatchDate)
    && activeDispatchHasSnapshot
    && !hasPlanningActionInProgress
    && !isSourceChangesMutationInProgress
    && !isSourceChangesVerificationInProgress
    && !hasBlockingSourceStaleNotice;
  const canResetActiveDispatchRevalidation = canResetDispatchRevalidation
    && Boolean(activeDispatchDate)
    && activeDispatchHasSnapshot
    && activeDispatchRequiresRevalidation
    && hasBlockingSourceStaleNotice
    && !hasPlanningActionInProgress
    && !isSourceChangesMutationInProgress
    && !isSourceChangesVerificationInProgress;
  const resetRevalidationDisabledReason = !activeDispatchDate
    ? "Sélectionnez une journée de dispatch."
    : !activeDispatchHasSnapshot
      ? "Aucun dispatch calculé pour cette journée."
      : !activeDispatchRequiresRevalidation
        ? "Cette journée n'est pas « À revalider »."
      : isSourceChangesVerificationInProgress
        ? "Vérification M-à-J en cours."
      : !hasBlockingSourceStaleNotice
        ? "Acquitter sert seulement quand le « ! » d'« Optimiser » est affiché."
        : isSourceChangesMutationInProgress
          ? "Une mise à jour Data Manager est en cours."
        : hasPendingPlanningMutations
          ? mutationActionDisabledReason
          : isOptimizing
            ? "Arrêtez d'abord l'optimisation en cours."
            : "Retire « À revalider » sans relancer « Optimiser ».";
  const invalidateDisabledReason = !activeDispatchDate
    ? "Sélectionnez une journée de dispatch."
    : !activeDispatchHasSnapshot
      ? "Aucun dispatch calculé n'est disponible pour invalider cette journée."
      : isSourceChangesVerificationInProgress
        ? "Vérification M-à-J en cours."
      : hasBlockingSourceStaleNotice
        ? "Cette journée est déjà marquée à revalider."
        : isSourceChangesMutationInProgress
          ? "Une mise à jour Data Manager est en cours."
        : hasPendingPlanningMutations
          ? mutationActionDisabledReason
          : isOptimizing
            ? "Arrêtez l'optimisation en cours avant d'invalider le dispatch."
            : "Marquer le dispatch calculé comme obsolète pour exiger une nouvelle validation métier.";
  const {
    canCancelOptimization,
    isCancelling,
    requestOptimizationCancel,
  } = useOptimizationLaunchAction({});

  useEffect(() => {
    if (!isFastReassignmentEnabled && reassignmentRebuildMode === DISPATCH_REASSIGNMENT_MODE_FAST) {
      interactions.onReassignmentRebuildModeChange?.(DISPATCH_REASSIGNMENT_MODE_COMPLETE);
    }
  }, [interactions, isFastReassignmentEnabled, reassignmentRebuildMode]);

  const handleOptimizeConfirm = () => {
    const form = optimizeFormRef.current;
    if (!(form instanceof HTMLFormElement) || isOptimizing || isSameOdGroupingMutationPending || selectedOptimizationDisabled) {
      return;
    }

    setIsOptimizeConfirmOpen(false);
    const launchAccepted = launchOptimization({
      form,
      dispatchDate: optimizationDispatchDate || undefined,
      dispatchPeriod: requestedOptimizationPeriod || optimizationDispatchPeriod,
      includeSuspendedDeliveries: includeSuspendedDeliveriesInOptimization,
      optimizationMode: STANDARD_OPTIMIZATION_MODE,
      preflight: {
        ...(selectedOptimizationModel?.optimizationLaunchPreflight || optimizationLaunchPreflight),
        pendingPlanningMutationCount,
        allowEveningOrderPool: isEveningShortcutTarget
          ? eveningOptimizationTarget.allowEveningOrderPool
          : interactions.eveningOrderPool?.enabled === true,
      },
      schedule(runLaunch) {
        window.requestAnimationFrame(() => {
          void runLaunch();
        });
      },
    });
    if (launchAccepted) {
      interactions.onOptimizationStateChange?.(true);
    }
  };

  const handleOptimizationCancel = async () => {
    try {
      await requestOptimizationCancel();
    } catch (error) {
      await alertInGrain(error instanceof Error && error.message
        ? error.message
        : "Impossible d'arrêter l'optimisation en cours.");
    }
  };

  const showDispatchRevalidationActionGroup = canResetDispatchRevalidation || canInvalidateDispatchDay;
  const showResetActionGroup = canDeleteDispatchSnapshot
    || canResetOptimizationRuntimeCache
    || canResetHereRouteCache
    || canRestartRailwayService;

  const menuContextKey = `${activeDispatchDate}:${sourceActiveScopeDispatchPeriod}:${interactions.activeDetailView || "route"}:${location.key}`;
  const dayActions = (
    <div className="dispatch-header-day-context">
      {isClosedDispatchDay ? <span className="inline-flex items-center gap-1 rounded-[4px] border border-red-200 bg-red-50 px-2 py-1 text-[0.68rem] font-bold text-red-800" data-dispatch-day-closed-indicator="true"><LockClosedIcon className="h-3.5 w-3.5" aria-hidden="true" />Journée fermée</span> : null}
      {hasDayStateActions || showDispatchRevalidationActionGroup || canResetPlanning || canScheduleOptimization ? (
        <DispatchHeaderActionMenu label="Actions de journée" contextGroup={headerSlots ? "day" : null} contextKey={menuContextKey}>
          {({ isOpen }) => <>
          {canScheduleOptimization ? (
            <DispatchHeaderMenuSection label="Planification">
              <ActionButton
                role="menuitem"
                tabIndex={-1}
                aria-label="Programmer cette optimisation"
                className={DISPATCH_MENU_ACTION_CLASS_NAME}
                onClick={() => {
                  const searchParams = new URLSearchParams({
                    new: "dispatch.optimize",
                    dispatch_date: activeDispatchDate,
                    dispatch_period: ["evening", "soir"].includes(sourceActiveScopeDispatchPeriod) ? "evening" : "day",
                    optimization_mode: STANDARD_OPTIMIZATION_MODE,
                  });
                  navigate(`/admin/automations?${searchParams.toString()}`);
                }}
                size="sm"
                title="Choisir une date et une heure pour lancer cette optimisation automatiquement."
                tone="ghost"
              >
                <ClockIcon aria-hidden="true" className="h-3.5 w-3.5 flex-none" />
                <span>Programmer l’optimisation…</span>
              </ActionButton>
            </DispatchHeaderMenuSection>
          ) : null}
          {hasDayStateActions ? (
            <DispatchHeaderMenuSection label="Gestion de journée">
            <div
              aria-label="État de la journée de dispatch"
              className="flex min-w-0 flex-wrap items-center justify-end gap-1"
              data-dispatch-day-state-actions
              role="group"
            >
              {canCloseActiveDay ? (
                <ActionButton
                  role="menuitem"
                  tabIndex={-1}
                  size="sm"
                  tone="ghost"
                  className="dispatch-header-menu-action h-9! min-h-9! rounded-[4px]! border border-[rgba(148,163,184,0.22)]! bg-white! px-4! text-[0.84rem]! font-semibold! text-content-copy! shadow-none! hover:border-[rgba(100,116,139,0.3)]! hover:bg-slate-50! hover:text-text! hover:shadow-none!"
                  onClick={() => {
                    interactions.onCloseDispatchDay?.(activeDispatchDate);
                  }}
                >
                  Clore la journée
                </ActionButton>
              ) : null}
              {canReopenActiveDay ? (
                <ActionButton
                  role="menuitem"
                  tabIndex={-1}
                  size="sm"
                  tone="ghost"
                  className="dispatch-header-menu-action h-9! min-h-9! rounded-[4px]! border border-[rgba(197,82,74,0.32)]! bg-[rgba(254,242,242,0.98)]! px-3! text-[0.84rem]! font-semibold! text-[#a03a31]! shadow-none! hover:border-[rgba(197,82,74,0.45)]! hover:bg-[rgba(254,226,226,0.98)]! hover:text-[#8f2f28]! hover:shadow-none!"
                  onClick={() => {
                    interactions.onReopenDispatchDay?.(activeDispatchDate);
                  }}
                  title="Journée fermée — cliquez pour la rouvrir et réautoriser les modifications"
                  aria-label="Journée fermée — Rouvrir la journée"
                  data-dispatch-day-reopen-action="true"
                  data-dispatch-day-closed-indicator="true"
                >
                  <span className="inline-flex items-center gap-2">
                    <span
                      className="inline-flex items-center gap-1 rounded-[4px] border border-[rgba(197,82,74,0.24)] bg-white/92 px-1.5 py-0.5 text-[0.62rem] font-black uppercase tracking-[0.1em] text-[#a03a31]"
                      aria-hidden="true"
                    >
                      <LockClosedIcon className="h-3 w-3 flex-none" />
                      Fermée
                    </span>
                    Rouvrir la journée
                  </span>
                </ActionButton>
              ) : null}
            </div>
            </DispatchHeaderMenuSection>
          ) : null}
          {showDispatchRevalidationActionGroup ? <DispatchHeaderMenuSection label="Validation">
            {canResetDispatchRevalidation ? (
              <HoverCard disabled={!isOpen} title="Acquitter" content={<DispatchResetRevalidationActionHoverCardContent />} placement="left-start" delay={140} maxWidth={380} accentColor="var(--hovercard-accent-neutral)">
                <ActionButton role="menuitem"
                  tabIndex={-1} size="sm" tone="ghost"
                className={DISPATCH_MENU_ACTION_CLASS_NAME}
                disabled={!canResetActiveDispatchRevalidation}
                title={resetRevalidationDisabledReason}
                onClick={() => {
                  interactions.onResetDispatchRevalidation?.(activeDispatchDate);
                }}
              >
                  <span>Acquitter</span>
                </ActionButton>
              </HoverCard>
            ) : null}
            {canInvalidateDispatchDay ? (
              <HoverCard disabled={!isOpen} title="Invalider" content={<DispatchInvalidateActionHoverCardContent />} placement="left-start" delay={140} maxWidth={380} accentColor="var(--hovercard-accent-neutral)">
                <ActionButton role="menuitem"
                  tabIndex={-1} size="sm" tone="ghost"
                className={DISPATCH_MENU_ACTION_CLASS_NAME}
                disabled={!canInvalidateActiveDispatch}
                title={invalidateDisabledReason}
                onClick={() => {
                  interactions.onInvalidateDispatchDay?.(activeDispatchDate);
                }}
              >
                  <span>Invalider</span>
                </ActionButton>
              </HoverCard>
            ) : null}
          </DispatchHeaderMenuSection> : null}
          {canResetPlanning ? (
            <DispatchHeaderMenuSection label="Réinitialisation" danger>
                <ActionButton role="menuitem"
                  tabIndex={-1} size="sm" tone="ghost"
                className={DISPATCH_MENU_ACTION_CLASS_NAME}
                disabled={disableMutationActions || disableOptimizationLaunch || isProductMixingEnabled || planningResetBlocked}
                title={planningResetBlocked && !isDeliveredDispatchDay ? planningResetBlockedReason : (mutationActionDisabledReason || (isProductMixingEnabled ? "Désactivez « Autoriser le mélange de produits » dans Plus avant de réinitialiser le planning." : "Réinitialiser le planning"))}
                onClick={() => { interactions.onPlanningReset?.({
                  includeSuspendedDeliveries: includeSuspendedDeliveriesInOptimization,
                  optimizationMode: STANDARD_OPTIMIZATION_MODE,
                }); }}
              ><ArrowPathIcon className="h-4 w-4 flex-none" aria-hidden="true" />
                  <span>Réinitialiser le planning</span>
                </ActionButton>
                {planningResetBlocked && !isDeliveredDispatchDay ? <span className="block px-3 pb-2 text-xs text-amber-800" role="status">{planningResetBlockedReason}</span> : null}
            </DispatchHeaderMenuSection>
          ) : null}
          </>}
        </DispatchHeaderActionMenu>
      ) : null}
      <DispatchGeotabCheckIcon
        date={activeDispatchDate}
        revision={model.runtimeRevision || ""}
        enabled={canSeeGeotabCheck && !["evening", "soir"].includes(sourceActiveScopeDispatchPeriod)}
        paused={sourceChanges.isMutating === true}
      />
      {showResetActionGroup ? (
        <DispatchHeaderActionMenu label="Outils avancés" contextKey={menuContextKey} icon={WrenchScrewdriverIcon}>
          {({ isOpen }) => <>
          {canResetOptimizationRuntimeCache || canResetHereRouteCache || canRestartRailwayService ? <DispatchHeaderMenuSection label="Maintenance">
            {canResetOptimizationRuntimeCache ? (
              <HoverCard disabled={!isOpen} title="Vider le cache d’optimisation" content={<DispatchResetActionHoverCardContent mode="dispatch" />} placement="left-start" delay={140} maxWidth={380} accentColor="var(--hovercard-accent-neutral)">
                <ActionButton role="menuitem"
                  tabIndex={-1} size="sm" tone="ghost"
                className={DISPATCH_MENU_ACTION_CLASS_NAME}
                disabled={!canResetRuntimeCache}
                title={resetRuntimeCacheDisabledReason}
                onClick={() => {
                  interactions.onResetOptimizationRuntimeCache?.();
                }}
              >
                  <span>Vider le cache d’optimisation</span>
                </ActionButton>
              </HoverCard>
            ) : null}
            {canResetHereRouteCache ? (
              <HoverCard disabled={!isOpen} title="Vider le cache des trajets HERE" content={<DispatchResetActionHoverCardContent mode="here" />} placement="left-start" delay={140} maxWidth={380} accentColor="var(--hovercard-accent-neutral)">
                <ActionButton role="menuitem"
                  tabIndex={-1} size="sm" tone="ghost"
                className={DISPATCH_MENU_ACTION_CLASS_NAME}
                disabled={hasPlanningActionInProgress}
                title={resetHereDisabledReason}
                onClick={() => {
                  interactions.onResetHereRouteCache?.();
                }}
              >
                  <span>Vider le cache des trajets HERE</span>
                </ActionButton>
              </HoverCard>
            ) : null}
            {canRestartRailwayService ? <RestartServiceButton
              label="Redéployer le service Railway" role="menuitem"
                  tabIndex={-1}
              className={DISPATCH_MENU_ACTION_CLASS_NAME}
              disabled={hasPlanningActionInProgress}
              disabledTitle={hasPlanningActionInProgress ? mutationActionDisabledReason : undefined}
            /> : null}
          </DispatchHeaderMenuSection> : null}
          {canDeleteDispatchSnapshot ? (
            <DispatchHeaderMenuSection label="Suppression du Dispatch" danger><HoverCard disabled={!isOpen} title="Supprimer le snapshot Dispatch — Jour + Soir" content={<DispatchSnapshotDeleteActionHoverCardContent disabledReason={deleteDispatchSnapshotDisabledReason} />} placement="left-start" delay={140} maxWidth={380} accentColor="var(--hovercard-accent-danger)">
                <ActionButton role="menuitem"
                  tabIndex={-1} size="sm" tone="ghost"
                className={DISPATCH_MENU_ACTION_CLASS_NAME}
                disabled={!canDeleteActiveDispatchSnapshot}
                title={deleteDispatchSnapshotDisabledReason}
                onClick={() => {
                  interactions.onDeleteDispatchSnapshot?.(activeDispatchDate);
                }}
              >
                  <span>Supprimer le snapshot Dispatch — Jour + Soir</span>
                </ActionButton>
              </HoverCard>
            </DispatchHeaderMenuSection>
          ) : null}
          </>}
        </DispatchHeaderActionMenu>
      ) : null}
      {canResetDispatchRevalidation && hasBlockingSourceStaleNotice && !isSourceChangesVerificationInProgress ? (
        <ActionButton size="sm" tone="ghost" className="h-8.25! min-h-8.25! rounded-[4px]!"
          disabled={!canResetActiveDispatchRevalidation} title={resetRevalidationDisabledReason}
          data-dispatch-contextual-acknowledge="true"
          onClick={() => interactions.onResetDispatchRevalidation?.(activeDispatchDate)}>Acquitter</ActionButton>
      ) : null}
    </div>
  );
  const primaryActions = (
    <div className="dispatch-header-primary">
          {canRunOptimization ? (
            <form
              ref={optimizeFormRef}
              method="post"
              action="/optimize"
              data-loading-overlay-title="Loading page"
              data-loading-overlay-message="Nous préparons les affectations, la carte de routage et les indicateurs de flotte."
            >
              <OptimizationModeSplitButton
                cancelLabel="Arrêter"
                cancelLoadingLabel="Annulation demandée..."
                disabled={!isOptimizing && disableOptimizationLaunch}
                disabledTitle={disableOptimizationLaunch ? (mutationActionDisabledReason || undefined) : undefined}
                excludedTrucks={optimizationExcludedTrucks}
                isCancelling={isCancelling}
                isFinalizing={isFinalizing}
                isLoading={isOptimizing}
                loadingLabel="Optimisation en cours..."
                onCancel={handleOptimizationCancel}
                onLaunch={() => openOptimizationConfirmation(optimizationDispatchPeriod)}
                periodMenu={showOptimizationPeriodMenu ? (
                  <DispatchHeaderActionMenu
                    label="Choisir la période à optimiser"
                    shouldIgnoreTarget={(target) => Boolean(target?.closest?.('[data-evening-order-pool-help="true"]'))}
                    iconOnly
                    contextKey={optimizationContextKey}
                    disabled={isOptimizing}
                    triggerClassName={`dispatch-optimization-period-trigger${shouldShowSourceStaleNotice ? " has-status-notice" : ""}`}
                  >
                    {({ isOpen }) => <>
                    <button type="button" role="menuitem" className={DISPATCH_MENU_ACTION_CLASS_NAME}
                      disabled={disableOptimizationLaunch} title={mutationActionDisabledReason || undefined}
                      onClick={() => openOptimizationConfirmation("day")}>Optimiser le jour</button>
                    <button type="button" role="menuitem" className={DISPATCH_MENU_ACTION_CLASS_NAME}
                      disabled={Boolean(eveningOptimizationDisabledReason)} title={eveningOptimizationDisabledReason || undefined}
                      onClick={() => openOptimizationConfirmation("evening")}>Optimiser le soir</button>
                    {eveningOptimizationDisabledReason ? (
                      <p className="m-0 px-2 py-1 text-xs text-slate-600" role="status">{eveningOptimizationDisabledReason}</p>
                    ) : null}
                    {interactions.canMutatePlanning === true ? <EveningOrderPoolMenuItem
                      compact
                      isMenuOpen={isOpen}
                      policy={{ ...interactions.eveningOrderPool,
                        visible: true,
                        disabledReason: eveningOptimizationTarget.settingsDisabledReason || interactions.eveningOrderPool?.disabledReason,
                      }}
                      disabled={isOptimizing || hasPendingPlanningMutations || isSameOdGroupingMutationPending
                        || isProductMixingSaving || Boolean(eveningOptimizationTarget.settingsDisabledReason)
                        || interactions.isOptimizationPreviewInteractionLocked === true}
                    /> : null}
                    </>}
                  </DispatchHeaderActionMenu>
                ) : null}
                showCancel={canCancelOptimization}
                statusNotice={shouldShowSourceStaleNotice ? {
                  ariaLabel: "Afficher pourquoi une réoptimisation est suggérée",
                  content: <DispatchSourceStaleHoverCardContent notice={resolvedSourceStaleNotice} />,
                  title: "Pourquoi réoptimiser ?",
                } : null}
              />
            </form>
          ) : null}
    </div>
  );
  const sourceChangesActions = canApplyDispatchSourceChanges ? (
    <>
      <SourceChangesMenuButton
        fillIndicator={fillSourceChangesIndicator}
        activeDispatchPeriod={sourceActiveScopeDispatchPeriod}
        dispatchPeriodLabel={sourceDispatchPeriodLabel}
        truckDriverById={truckDriverById}
        truckLabelById={truckLabelById}
        sourceNewCount={sourceNewCount}
        sourceDateChangedCount={sourceDateChangedCount}
        sourceMissingDispatchOrderCount={sourceMissingDispatchOrderCount}
        sourceRemovedEndOfDayLoadCount={sourceRemovedEndOfDayLoadCount}
        sourceGhostOrderCount={sourceGhostOrderCount}
        sourceSuspendableErrorCount={sourceSuspendableErrorCount}
        sourceEndOfDayLoadIntegrityCount={sourceEndOfDayLoadIntegrityCount}
        sourceUnresolvedCount={sourceActionableUnresolvedCount}
        sourceBadgePendingCount={sourceBadgePendingCount}
        sourceActionablePendingCount={dispatchActionableSourceChangeCount}
        sourceChanges={sourceChanges}
        sourceChangeDisabledReason={sourceChangeDisabledReason}
        hasVerifiedSourceChangesPreview={hasVerifiedSourceChangesPreview}
        activeDispatchHasSnapshot={activeDispatchHasSnapshot}
        disableMutationActions={sourceChangesMutationActionsDisabled}
        isClosedDispatchDay={isClosedDispatchDay}
        sourceChangesBusy={sourceChangesBusy}
        canImportSourceChanges={canImportSourceChanges}
        canImportSuspendableErrors={canImportSuspendableErrors}
        canRemoveSourceChanges={canRemoveSourceChanges}
        canRemoveDateChangedSourceChanges={canRemoveDateChangedSourceChanges}
        canRepairGhostOrders={canRepairGhostOrders}
        canRestoreExecutions={canRestoreExecutions}
        isOpen={sourceChangesMenuOpenPeriod
          ? sourceChangesMenuOpenPeriod === sourceActiveScopeDispatchPeriod
          : typeof interactions.isSourceChangesMenuOpen === "boolean"
            ? interactions.isSourceChangesMenuOpen
            : null}
        onOpenChange={typeof interactions.onSourceChangesMenuOpenChange === "function"
          ? (nextOpen) => interactions.onSourceChangesMenuOpenChange(
              nextOpen,
              sourceActiveScopeDispatchPeriod,
            )
          : null}
        openRequestedPeriod={sourceChanges.openRequestedPeriod}
        onOpenRequestConsumed={sourceChanges.onOpenRequestConsumed}
      />
      {showEveningSourceChangesShortcut ? (
        <SourceChangesMenuButton
          {...eveningSourceChangesMenuModel}
          fillIndicator={fillSourceChangesIndicator}
          activeDispatchHasSnapshot={activeDispatchHasSnapshot}
          truckLabelById={truckLabelById}
          isOpen={sourceChangesMenuOpenPeriod
            ? sourceChangesMenuOpenPeriod === "evening"
            : false}
          onOpenChange={typeof interactions.onSourceChangesMenuOpenChange === "function"
            ? (nextOpen) => interactions.onSourceChangesMenuOpenChange(nextOpen, "evening")
            : null}
        />
      ) : null}
    </>
  ) : null;
  return (
    <>
      {headerSlots ? <>
        {headerSlots.dayActionsRoot ? createPortal(dayActions, headerSlots.dayActionsRoot) : null}
        {headerSlots.primaryActionsRoot ? createPortal(primaryActions, headerSlots.primaryActionsRoot) : null}
        {headerSlots.sourceChangesActionsRoot ? createPortal(sourceChangesActions, headerSlots.sourceChangesActionsRoot) : null}
      </> : <div className="dispatch-header-work">{dayActions}{sourceChangesActions}{primaryActions}</div>}

      <ConfirmationModal
        {...selectedOptimizeConfirmation}
        isOpen={isOptimizeConfirmOpen}
        confirmDisabled={selectedOptimizationDisabled || isOptimizing}
        onCancel={() => setIsOptimizeConfirmOpen(false)}
        onClose={() => setIsOptimizeConfirmOpen(false)}
        onConfirm={handleOptimizeConfirm}
      />
    </>
  );
}

const SOURCE_CHANGE_REASON_LABELS = {
  "address": "adresse",
  "order_address": "adresse commande",
  "loading_address": "adresse chargement",
  "delivery_silo": "silo livraison",
  "delivery_period": "Soir",
  "is_dompeur": "commande Dompeur",
  "ontario": "Ontario",
  "loading_client_code": "code client chargement",
  "planning_note": "note de planification",
  "building": "bâtiment",
  "quantity_tons": "quantité",
  "loading_silo": "silo",
  "grain_type": "produit",
  "grain_code": "code produit",
  "client_code": "code client",
  "client_name": "nom client",
  "loading_client_name": "nom client chargement",
  "comment": "commentaire",
  "loading_priority": "priorité chargement",
  "delivery_priority": "priorité déchargement",
  "loading_window_start": "heure de priorité chargement",
  "delivery_window_start": "heure de priorité déchargement",
	"loading_is_meunerie": "statut meunerie du chargement",
	"meunerie_loading_extra_minutes": "surtemps meunerie",
	"client_loading_opening_time": "ouverture de la meunerie",
	"client_loading_closing_time": "fermeture de la meunerie",
  "delivery_date": "date de livraison",
  "service_time_minutes": "temps de service",
  "order_date": "date commande",
  "aspirator": "aspirateur",
  "work_order_number": "numéro bon travail",
  "locked_truck_id": "camion verrouillé",
  "is_preloaded_previous_day": "préchargé la veille",
  "lifecycle_status": "état de la commande",
  // Champs d'exécution comparés par le serveur (portail chauffeur, tonnage réel).
  "confirmation_portail": "confirmation du chauffeur",
  "actual_delivered_quantity_tons": "tonnage livré",
  "delivered_by_driver_name": "chauffeur",
  "delivered_by_driver_user_id": "chauffeur",
  "driver_completed_at": "heure de livraison",
  "driver_confirmation_source": "source de la confirmation",
  "driver_status": "statut chauffeur",
};
// Repli quand un champ n'a pas de libellé : jamais le nom technique.
const SOURCE_CHANGE_UNKNOWN_FIELD_LABEL = "autre donnée";

const SOURCE_CHANGE_MEUNERIE_FIELDS = new Set([
  "client_loading_closing_time",
  "client_loading_opening_time",
  "meunerie_loading_extra_minutes",
]);

// La raison affichée doit nommer un champ qui déplace la commande, pas une métadonnée.
function resolveSourceChangeFieldsByImpact(detail) {
  const changedFields = Array.isArray(detail?.changed_fields) ? detail.changed_fields : [];
  const isMetadata = (field) => detail?.field_changes?.[field]?.category === "metadata";
  return [...changedFields.filter((field) => !isMetadata(field)), ...changedFields.filter(isMetadata)];
}

const SOURCE_CHANGE_PRIORITY_OF_WINDOW = { delivery_window_start: "delivery_priority", loading_window_start: "loading_priority" };

// Le menu M-à-J nomme toutes les raisons qui déplacent la commande (une métadonnée seulement à défaut).
// Une priorité couvre son heure.
function resolveSourceChangeReasonFields(detail) {
  const fields = resolveSourceChangeFieldsByImpact(detail);
  const material = fields.filter((field) => detail?.field_changes?.[field]?.category !== "metadata");
  const shown = material.length > 0 ? material : fields.slice(0, 1);
  const unscoped = (field) => normalizeInlineText(field).split(".").filter(Boolean).at(-1);
  const names = new Set(shown.map(unscoped));
  return shown.filter((field) => !names.has(SOURCE_CHANGE_PRIORITY_OF_WINDOW[unscoped(field)]));
}

function resolveSourceChangePrimaryField(changedFields) {
  if (!Array.isArray(changedFields) || changedFields.length === 0) {
    return "";
  }
  return normalizeInlineText(changedFields[0]).split(".").filter(Boolean).at(-1);
}

function resolveSourceChangeMeunerieName(detail) {
  const primaryField = resolveSourceChangePrimaryField(resolveSourceChangeFieldsByImpact(detail));
  if (!SOURCE_CHANGE_MEUNERIE_FIELDS.has(primaryField)) {
    return "";
  }
  const fieldChanges = detail?.field_changes && typeof detail.field_changes === "object" && !Array.isArray(detail.field_changes)
    ? detail.field_changes
    : {};
  const loadingChange = fieldChanges.loading_client_name || fieldChanges.loadingClientName;
  return normalizeInlineText(loadingChange?.source_value ?? loadingChange?.sourceValue)
    || normalizeInlineText(detail?.loading_client_name);
}

function formatSourceChangeReason(changedFields, { reasonOnly = false, showAllFields = false, meunerieName = "" } = {}) {
  if (!Array.isArray(changedFields) || changedFields.length === 0) {
    return null;
  }
  const labels = changedFields.map((field) => {
    const unscopedField = normalizeInlineText(field).split(".").filter(Boolean).at(-1);
    return SOURCE_CHANGE_REASON_LABELS[field]
      || SOURCE_CHANGE_REASON_LABELS[unscopedField]
      || SOURCE_CHANGE_UNKNOWN_FIELD_LABEL;
  });
  const meunerieSuffix = reasonOnly && meunerieName ? ` · ${meunerieName}` : "";
  if (reasonOnly) {
    const reasons = [...new Set(labels)];
    return `Raison${reasons.length > 1 ? "s" : ""} : ${reasons.join(", ")}${meunerieSuffix}`;
  }
  if (changedFields.length === 1) {
    return `Champ modifié\u00A0: ${labels[0]}`;
  }
  if (showAllFields) {
    return `Champs modifiés\u00A0: ${labels.join(" · ")}`;
  }
  return `Champs modifiés\u00A0: ${labels[0]} +${changedFields.length - 1}`;
}

function resolveProtectedSourceChangeFieldLabels(changedFields) {
  if (!Array.isArray(changedFields) || changedFields.length === 0) {
    return [];
  }
  return changedFields.map((field) => {
    const unscopedField = normalizeInlineText(field).split(".").filter(Boolean).at(-1);
    return SOURCE_CHANGE_REASON_LABELS[field]
      || SOURCE_CHANGE_REASON_LABELS[unscopedField]
      || SOURCE_CHANGE_UNKNOWN_FIELD_LABEL;
  });
}

function formatDetailLabel(detail) {
  const client = detail.delivery_client_name
    || detail.client_name
    || detail.order_id
    || (detail.marker_key ? formatRemovedEndOfDayLoadTitle(detail) : "")
    || "—";
  const shortId = detail.order_id ? ` · ${detail.order_id}` : "";
  return `${client}${shortId}`;
}
const PROTECTED_SOURCE_CHANGE_STATE_LABELS = Object.freeze({
  manual_freeze: "Gelée manuellement",
  confirmation_or_portal: "Confirmée / Portail",
  execution_started: "Exécution commencée",
  end_of_day_load: "Chargé PM",
  preloaded_previous_day: "Chargée la veille",
  delivered_or_completed: "Livrée / complétée",
  planning_protection: "Protection du planning",
  other: "Autre protection",
});

const PROTECTED_SOURCE_CHANGE_TYPE_LABELS = Object.freeze({
  date_changed_remove: "Date de livraison déplacée",
  missing_dispatch_order: "Commande absente du Data Manager",
  missing_dispatch_orders: "Commande absente du Data Manager",
  loading_constraint_change: "Contrainte de meunerie modifiée",
  modified_same_date: "Commande modifiée",
  new: "Nouvelle commande",
  removed_end_of_day_load: "Chargé PM retiré",
});

const SOURCE_CHANGE_CATEGORY_LABELS = Object.freeze({
  execution: "Exécution",
  integrity: "Intégrité",
  material: "Planification",
  metadata: "Métadonnées",
});

const SOURCE_CHANGE_EXECUTION_PROOF_LABELS = Object.freeze({
  actual_delivered_quantity_tons: "tonnage livré réel",
  actual_delivered_tonnage: "tonnage livré réel",
  actual_loaded_quantity_tons: "tonnage chargé réel",
  actual_loaded_tonnage: "tonnage chargé réel",
  delivery_completed: "livraison complétée",
  delivered_or_completed: "livrée / complétée",
  driver_portal: "confirmation Portail",
  portal_confirmation: "confirmation Portail",
  portal_confirmed: "confirmation Portail",
  source_completed: "commande complétée",
});

const SOURCE_CHANGE_PROTECTION_REASON_LABELS = Object.freeze({
  actual_delivered_tonnage: "tonnage livré réel",
  actual_loaded_tonnage: "tonnage chargé réel",
  actual_tonnage: "tonnage réel",
  confirmed_or_delivered: "confirmée / livrée",
  delivery_completed: "livraison complétée",
  dispatcher_frozen: "gel dispatcher",
  driver_portal: "confirmation Portail",
  end_of_day_load: "Chargé PM",
  locked_truck: "camion verrouillé",
  manual_assignment: "affectation manuelle",
  manual_freeze: "gel manuel",
  planning_protection: "protection planning",
  preloaded_previous_day: "chargée la veille",
  source_completed: "commande complétée",
  truck_protected: "camion protégé",
  truck_or_dispatcher_protected: "camion ou dispatch protégé",
});

function normalizeBackendSourceChangeLabels(value, labelMap) {
  const values = Array.isArray(value) ? value : value ? [value] : [];
  return [...new Set(values.map((item) => {
    if (item && typeof item === "object" && !Array.isArray(item)) {
      const explicitLabel = normalizeInlineText(item.label || item.message || item.display_label);
      if (explicitLabel) {
        return explicitLabel;
      }
      const code = normalizeInlineText(item.code || item.reason || item.type).toLowerCase();
      return labelMap[code] || code;
    }
    const code = normalizeInlineText(item).toLowerCase();
    return labelMap[code] || code;
  }).filter(Boolean))];
}

function formatSourceChangeCategory(detail) {
  const explicitLabel = normalizeInlineText(
    detail?.change_category_label || detail?.category_label,
  );
  if (explicitLabel) {
    return explicitLabel;
  }
  const category = normalizeInlineText(
    detail?.change_category || detail?.category,
  ).toLowerCase();
  return SOURCE_CHANGE_CATEGORY_LABELS[category] || category;
}

function resolveSourceChangeBackendEvidence(detail) {
  return {
    executionProofs: normalizeBackendSourceChangeLabels(
      detail?.execution_proofs || detail?.executionProofs,
      SOURCE_CHANGE_EXECUTION_PROOF_LABELS,
    ),
    protectionReasons: normalizeBackendSourceChangeLabels(
      detail?.protection_reasons || detail?.protectionReasons,
      SOURCE_CHANGE_PROTECTION_REASON_LABELS,
    ),
  };
}

function resolveProtectedSourceChangeState(detail) {
  const state = normalizeInlineText(detail?.protected_state).toLowerCase();
  return PROTECTED_SOURCE_CHANGE_STATE_LABELS[state]
    ? state
    : "other";
}

const PROTECTED_SOURCE_CHANGE_FREEZE_REASONS = Object.freeze([
  "manual_freeze",
  "dispatcher_frozen",
]);

function resolveProtectedSourceChangesSectionTitle(details) {
  const states = new Set(
    (Array.isArray(details) ? details : [])
      .map((detail) => resolveProtectedSourceChangeState(detail))
      .filter(Boolean),
  );
  if (states.size === 1 && states.has("confirmation_or_portal")) {
    return "Écart avec commandes Confirmée / Portail";
  }
  if (states.size === 1 && states.has("manual_freeze")) {
    return "Écart avec Commandes 'Gelée'";
  }
  return "Écart avec commandes figées";
}

function protectedSourceChangeRequiresUnfreeze(details) {
  return (Array.isArray(details) ? details : []).some((detail) => {
    const state = resolveProtectedSourceChangeState(detail);
    if (state === "manual_freeze") {
      return true;
    }
    const reasons = Array.isArray(detail?.protection_reasons)
      ? detail.protection_reasons
      : [];
    return reasons.some((reason) => (
      PROTECTED_SOURCE_CHANGE_FREEZE_REASONS.includes(normalizeInlineText(reason).toLowerCase())
    ));
  });
}

function formatProtectedSourceChangeState(detail) {
  return PROTECTED_SOURCE_CHANGE_STATE_LABELS[resolveProtectedSourceChangeState(detail)];
}

function formatProtectedSourceChangeType(detail) {
  const ghostReason = normalizeInlineText(detail?.ghost_reason).toLowerCase();
  if (ghostReason === "source_inactive") {
    return "Commande inactive / annulée";
  }
  if (ghostReason === "source_missing") {
    return "Commande absente du Data Manager";
  }
  const changeType = normalizeInlineText(
    detail?.protected_change_type || detail?.change_type,
  ).toLowerCase();
  return PROTECTED_SOURCE_CHANGE_TYPE_LABELS[changeType]
    || formatGhostReason(detail?.ghost_reason)
    || "Écart Data Manager";
}

function formatProtectedFieldValue(value) {
  if (value === null || value === undefined || value === "") {
    return "Vide";
  }
  if (typeof value === "boolean") {
    return value ? "Oui" : "Non";
  }
  if (value === "active_in_dispatch") {
    return "Présente dans le dispatch";
  }
  if (value === "cancelled") {
    return "Annulée";
  }
  if (value === "inactive") {
    return "Inactive";
  }
  if (value === "missing") {
    return "Absente du Data Manager";
  }
  if (value === "completed") {
    return "Complétée";
  }
  if (Array.isArray(value)) {
    return value.length > 0 ? value.map(formatProtectedFieldValue).join(", ") : "Vide";
  }
  if (typeof value === "object") {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value);
}

function resolveProtectedFieldChanges(detail) {
  const rawFieldChanges = detail?.field_changes || detail?.fieldChanges;
  const fieldChangeEntries = Array.isArray(rawFieldChanges)
    ? rawFieldChanges.map((fieldChange, index) => [
      normalizeInlineText(fieldChange?.path || fieldChange?.field_name || fieldChange?.fieldName)
        || `field-${index}`,
      fieldChange,
    ])
    : rawFieldChanges && typeof rawFieldChanges === "object"
      ? Object.entries(rawFieldChanges)
      : [];
  const fieldChanges = Object.fromEntries(fieldChangeEntries);
  const changedFields = Array.isArray(detail?.changed_fields)
    ? detail.changed_fields.map(normalizeInlineText).filter(Boolean)
    : [];
  const fieldNames = [...new Set([...changedFields, ...Object.keys(fieldChanges)])];
  return fieldNames.map((fieldName) => {
    const values = fieldChanges[fieldName] && typeof fieldChanges[fieldName] === "object"
      ? fieldChanges[fieldName]
      : {};
    const hasDispatchValue = Object.prototype.hasOwnProperty.call(values, "dispatch_value")
      || Object.prototype.hasOwnProperty.call(values, "dispatchValue");
    const hasSourceValue = Object.prototype.hasOwnProperty.call(values, "source_value")
      || Object.prototype.hasOwnProperty.call(values, "sourceValue");
    const unscopedFieldName = fieldName.split(".").filter(Boolean).at(-1) || fieldName;
    return {
      fieldName,
      label: normalizeInlineText(values.label || values.display_label)
        || SOURCE_CHANGE_REASON_LABELS[fieldName]
        || SOURCE_CHANGE_REASON_LABELS[unscopedFieldName]
        || SOURCE_CHANGE_UNKNOWN_FIELD_LABEL,
      dispatchValue: formatProtectedFieldValue(values.dispatch_value ?? values.dispatchValue),
      sourceValue: formatProtectedFieldValue(values.source_value ?? values.sourceValue),
      hasValues: hasDispatchValue || hasSourceValue,
    };
  });
}

function SourceChangeFieldTransitions({ detail, className = "" }) {
  const fieldChanges = resolveProtectedFieldChanges(detail).filter((fieldChange) => fieldChange.hasValues);
  if (fieldChanges.length === 0) {
    return null;
  }
  return (
    <span className={joinClasses(["min-w-0", className])} data-source-change-field-transitions="true">
      {fieldChanges.map((fieldChange) => (
        <span key={fieldChange.fieldName} className="block truncate">
          {fieldChange.label} : {fieldChange.dispatchValue} → {fieldChange.sourceValue}
        </span>
      ))}
    </span>
  );
}


function normalizeSourceChangeGroupText(value) {
  return normalizeInlineText(value).toLowerCase();
}

function resolveSuspendableErrorGroupIdentity(detail) {
  const label = normalizeInlineText(detail?.delivery_client_name || detail?.client_name);
  if (!label) {
    return null;
  }
  const address = normalizeInlineText(detail?.delivery_address || detail?.address);
  return {
    address,
    key: normalizeInlineText(detail?.suspended_group_key) || `${normalizeSourceChangeGroupText(label)}|${normalizeSourceChangeGroupText(address)}`,
    label,
  };
}

function resolveSourceChangeGroupIdentity(detail) {
  const label = normalizeInlineText(detail?.suspended_group_label || detail?.client_name);
  const suspendedGroupKey = normalizeInlineText(detail?.suspended_group_key);
  if (suspendedGroupKey && label) {
    return { key: suspendedGroupKey, label };
  }
  const clientLabel = normalizeInlineText(detail?.client_name);
  if (!clientLabel) {
    return null;
  }
  const address = normalizeInlineText(detail?.address);
  const loadingClient = normalizeInlineText(detail?.loading_client_name);
  const loadingAddress = normalizeInlineText(detail?.loading_address);
  const loadingKey = normalizeInlineText(detail?.loading_group_key || detail?.same_site_group_key)
    || (loadingClient && loadingAddress ? `${normalizeSourceChangeGroupText(loadingClient)}|${normalizeSourceChangeGroupText(loadingAddress)}` : "");
  if (!address || !loadingKey) {
    return null;
  }
  return {
    key: `${normalizeSourceChangeGroupText(clientLabel)}|${normalizeSourceChangeGroupText(address)}|${loadingKey}`,
    label: clientLabel,
  };
}

function buildGroupedSourceChangeRows(details) {
  const safeDetails = Array.isArray(details) ? details.filter(Boolean) : [];
  const groupsByKey = new Map();
  safeDetails.forEach((detail) => {
    const identity = resolveSourceChangeGroupIdentity(detail);
    if (!identity?.key) {
      return;
    }
    const group = groupsByKey.get(identity.key) || {
      details: [],
      identity,
    };
    group.details.push(detail);
    groupsByKey.set(identity.key, group);
  });

  const emittedGroupKeys = new Set();
  const rows = [];
  safeDetails.forEach((detail) => {
    const identity = resolveSourceChangeGroupIdentity(detail);
    const group = identity?.key ? groupsByKey.get(identity.key) : null;
    if (!group || group.details.length < 2) {
      rows.push({
        detail,
        rowKey: detail.delivery_id || detail.order_id || `detail-${rows.length}`,
        rowType: "detail",
      });
      return;
    }
    if (emittedGroupKeys.has(identity.key)) {
      return;
    }
    emittedGroupKeys.add(identity.key);
    rows.push({
      details: group.details,
      rowKey: `source-change-group-${identity.key}`,
      rowType: "group",
      subtitle: `${group.details.length} commande${group.details.length > 1 ? "s" : ""}`,
      title: identity.label,
      truckLabel: resolveGroupedSourceChangeTruckLabel(group.details),
    });
  });
  return rows;
}

function buildGroupedSuspendableErrorRows(details) {
  const safeDetails = Array.isArray(details) ? details.filter(Boolean) : [];
  const groupsByKey = new Map();
  safeDetails.forEach((detail) => {
    const identity = resolveSuspendableErrorGroupIdentity(detail);
    if (!identity?.key) {
      return;
    }
    const group = groupsByKey.get(identity.key) || {
      details: [],
      identity,
    };
    group.details.push(detail);
    groupsByKey.set(identity.key, group);
  });

  const emittedGroupKeys = new Set();
  const rows = [];
  safeDetails.forEach((detail) => {
    const identity = resolveSuspendableErrorGroupIdentity(detail);
    const group = identity?.key ? groupsByKey.get(identity.key) : null;
    if (!group || group.details.length < 2) {
      rows.push({
        detail,
        rowKey: detail.delivery_id || detail.order_id || `detail-${rows.length}`,
        rowType: "detail",
      });
      return;
    }
    if (emittedGroupKeys.has(identity.key)) {
      return;
    }
    emittedGroupKeys.add(identity.key);
    rows.push({
      details: group.details,
      rowKey: `suspendable-error-group-${identity.key}`,
      rowType: "group",
      subtitle: `${group.details.length} commande${group.details.length > 1 ? "s" : ""}`,
      title: identity.label,
    });
  });
  return rows;
}

function formatSourceChangeTruckLabel(detail) {
  const truckLabel = typeof detail?.truck_label === "string" ? detail.truck_label.trim() : "";
  return truckLabel || "Non assigné";
}

function resolveGroupedSourceChangeTruckLabel(details) {
  const safeDetails = Array.isArray(details) ? details.filter(Boolean) : [];
  const uniqueLabels = [];
  safeDetails.forEach((detail) => {
    const truckLabel = typeof detail?.truck_label === "string" ? detail.truck_label.trim() : "";
    if (!truckLabel || uniqueLabels.includes(truckLabel)) {
      return;
    }
    uniqueLabels.push(truckLabel);
  });
  if (uniqueLabels.length === 0) {
    return null;
  }
  return uniqueLabels.join(" / ");
}

function resolveRemoveModifiedDestinationLabel(detail) {
  if (detail?.change_type === "date_changed_remove") {
    return "Sera retirée du dispatch (date de livraison modifiée).";
  }
  if (detail?.already_suspended === true) {
    return "Sera actualisée dans Suspendues.";
  }

  return "Sera transférée vers Suspendues.";
}

export function resolveRemoveModifiedPreviewActionLabel(detail) {
  if (detail?.change_type === "date_changed_remove") {
    return resolveRemoveModifiedDestinationLabel(detail);
  }
  return null;
}

function formatRemoveModifiedActionSummary(details) {
  const safeDetails = Array.isArray(details) ? details.filter(Boolean) : [];
  const alreadySuspendedCount = safeDetails.filter(
    (detail) => detail?.already_suspended === true,
  ).length;
  const assignedCount = Math.max(0, safeDetails.length - alreadySuspendedCount);
  const summaries = [];

  if (assignedCount === 1) {
    summaries.push("1 commande sera retirée de son camion et transférée vers Suspendues.");
  } else if (assignedCount > 1) {
    summaries.push(`${assignedCount} commandes seront retirées de leur camion et transférées vers Suspendues.`);
  }
  if (alreadySuspendedCount === 1) {
    summaries.push("1 commande déjà suspendue sera actualisée sur place.");
  } else if (alreadySuspendedCount > 1) {
    summaries.push(`${alreadySuspendedCount} commandes déjà suspendues seront actualisées sur place.`);
  }

  return summaries.join(" ");
}

function resolveSourceChangePreviewActionLabel(actionLabel, detail) {
  return typeof actionLabel === "function" ? actionLabel(detail) : actionLabel;
}

function formatGhostReason(reason) {
  const normalizedReason = normalizeInlineText(reason);
  if (normalizedReason === "source_missing") {
    return "commande supprimée";
  }
  if (normalizedReason === "source_inactive") {
    return "commande inactive";
  }
  if (normalizedReason === "source_date_changed") {
    return "date de livraison déplacée";
  }
  if (normalizedReason === "source_completed") {
    return "commande livrée / complétée";
  }
  return "";
}

function formatSourceChangeInitialReasonLabel(reason) {
  const normalizedReason = normalizeInlineText(reason);
  if (!normalizedReason) {
    return "";
  }
  if (normalizedReason.toLowerCase() === "unassigned") {
    return "Non assignée";
  }
  if (/garde-fou final|groupe same o\/d/i.test(normalizedReason)) {
    return "Tonnage trop élevé pour les camions disponibles";
  }
  return normalizedReason;
}

function normalizeEndOfDayLoadOrderIds(value) {
  return Array.isArray(value)
    ? [...new Set(value.map(normalizeInlineText).filter(Boolean))]
    : [];
}

function isMixedOrphanEndOfDayLoadDetail(detail) {
  const reason = normalizeInlineText(detail?.ghost_reason).toLowerCase();
  return reason === "source_mixed" || (
    normalizeEndOfDayLoadOrderIds(detail?.missing_order_ids).length > 0
    && normalizeEndOfDayLoadOrderIds(detail?.date_changed_order_ids).length > 0
  );
}

function isRemovableOrphanEndOfDayLoadDetail(detail) {
  if (isMixedOrphanEndOfDayLoadDetail(detail)) {
    return false;
  }
  const reason = normalizeInlineText(detail?.ghost_reason).toLowerCase();
  return reason === "source_missing" || reason === "source_inactive";
}

function isDateChangedEndOfDayLoadDetail(detail) {
  return !isMixedOrphanEndOfDayLoadDetail(detail)
    && normalizeInlineText(detail?.ghost_reason).toLowerCase() === "source_date_changed";
}

function formatExecutionRecoveryBlockReason(reason) {
  const normalizedReason = normalizeInlineText(reason);
  if (!normalizedReason) {
    return "";
  }
  const labels = {
    actual_delivered_tonnage_missing: "tonnage livré réel incomplet",
    actual_loaded_tonnage_missing: "tonnage chargé réel absent",
    active_assignment_missing: "affectation active absente",
    active_assignment_ambiguous: "affectation active en double",
    active_truck_ambiguous: "camion actif en double",
    active_truck_missing: "camion actif absent",
    active_trip_ambiguous: "tournée active en double",
    active_trip_missing: "tournée active absente",
    execution_in_progress: "exécution en cours",
    execution_not_removed: "l'exécution n'est plus retirée",
    snapshot_assignment_ambiguous: "affectation en double dans le plan gardé",
    snapshot_assignment_missing: "affectation absente du plan gardé",
    snapshot_delivery_loading_mismatch: "chargement associé incompatible dans le plan gardé",
    snapshot_delivery_missing: "livraison absente du plan gardé",
    snapshot_delivery_step_ambiguous: "étape de livraison en double dans le plan gardé",
    snapshot_delivery_step_missing: "étape de livraison absente du plan gardé",
    snapshot_loading_step_ambiguous: "étape de chargement en double dans le plan gardé",
    snapshot_loading_step_missing: "étape de chargement absente du plan gardé",
    snapshot_order_ambiguous: "commande en double dans le plan gardé",
    snapshot_order_missing: "commande absente du plan gardé",
    snapshot_trip_ambiguous: "tournée en double dans le plan gardé",
    snapshot_trip_mismatch: "tournée différente dans le plan gardé",
    snapshot_trip_missing: "tournée absente du plan gardé",
    snapshot_truck_mismatch: "camion différent dans le plan gardé",
    snapshot_truck_missing: "camion absent du plan gardé",
    snapshot_unavailable: "plan gardé indisponible",
    source_date_mismatch: "date différente dans le Data Manager",
    source_delivery_missing: "livraison absente du Data Manager",
    source_delivery_not_completed: "livraison non livrée dans le Data Manager",
    source_not_completed: "commande non terminée dans le Data Manager",
    source_order_missing: "commande absente du Data Manager",
  };
  return labels[normalizedReason] || "restauration automatique non admissible";
}

function formatSourceChangeDetailReason(
  detail,
  includeChangedFields = true,
  { reasonOnly = false, showAllFields = false } = {},
) {
  if (includeChangedFields && reasonOnly) {
    const fieldChanges = resolveProtectedFieldChanges(detail);
    const loadingClientChange = fieldChanges.find((change) => (
      change.fieldName.split(".").at(-1) === "loading_client_name"
      && change.hasValues && change.dispatchValue !== change.sourceValue
    ));
    const hasMeunerieConstraintChange = fieldChanges.some((change) => (
      SOURCE_CHANGE_MEUNERIE_FIELDS.has(change.fieldName.split(".").at(-1))
    ));
    if (loadingClientChange && hasMeunerieConstraintChange) {
      return `Meunerie : ${loadingClientChange.dispatchValue} → ${loadingClientChange.sourceValue}`;
    }
  }
  const changedFieldReason = includeChangedFields
    ? formatSourceChangeReason(reasonOnly ? resolveSourceChangeReasonFields(detail) : detail?.changed_fields, {
      meunerieName: reasonOnly ? resolveSourceChangeMeunerieName(detail) : "",
      reasonOnly,
      showAllFields,
    })
    : "";
  if (changedFieldReason) {
    return changedFieldReason;
  }
  const changeType = normalizeInlineText(
    detail?.change_type || detail?.protected_change_type,
  ).toLowerCase();
  if (changeType === "new") {
    return PROTECTED_SOURCE_CHANGE_TYPE_LABELS.new;
  }
  const categoryLabel = formatSourceChangeCategory(detail);
  const reason = formatGhostReason(detail?.ghost_reason)
    || formatSourceChangeInitialReasonLabel(detail?.initial_reason_label)
    || formatExecutionRecoveryBlockReason(detail?.recovery_block_reason);
  return [categoryLabel, reason].filter(Boolean).join(" · ");
}

const MAX_VISIBLE_DETAILS = 3;

// Menu M-à-J : les commandes d'un groupe qui ont les mêmes raisons les partagent sur une seule ligne.
// Une commande avec un conflit à expliquer garde sa ligne.
function groupDetailsBySharedReason(details, actionLabel, reasonOnly) {
  const lines = [];
  const lineByReason = new Map();
  details.forEach((detail) => {
    const label = detail.order_id || formatDetailLabel(detail);
    const reasonKey = reasonOnly && !applicationFeedbackMessage(detail?.application_feedback)
      ? `${resolveSourceChangePreviewActionLabel(actionLabel, detail) || ""}|${formatSourceChangeDetailReason(detail, true, { reasonOnly })}`
      : null;
    const sharedLine = reasonKey === null ? null : lineByReason.get(reasonKey);
    if (sharedLine) {
      sharedLine.labels.push(label);
      return;
    }
    const line = { detail, labels: [label] };
    lines.push(line);
    if (reasonKey !== null) {
      lineByReason.set(reasonKey, line);
    }
  });
  return lines;
}

// A Data Manager save that could not be applied explains why under its order.
function SourceChangeConflictLine({ detail }) {
  const message = applicationFeedbackMessage(detail?.application_feedback);
  return message ? (
    <span className="min-w-0 basis-full break-words text-[0.64rem] font-semibold leading-tight text-[#972d24]" data-source-change-conflict-reason="true">
      Conflit : {message}
    </span>
  ) : null;
}

export function SourceChangeDetailsPreview({
  details,
  actionLabel,
  embedded = false,
  groupSuspendableErrors = false,
  groupSourceChanges = false,
  reasonOnly = false,
  splitMultipleChangedFields = false,
}) {
  const [showAllDetails, setShowAllDetails] = useState(false);

  if (!Array.isArray(details) || details.length === 0) {
    return null;
  }

  const rows = groupSuspendableErrors
    ? buildGroupedSuspendableErrorRows(details)
    : groupSourceChanges
      ? buildGroupedSourceChangeRows(details)
      : details.map((detail, idx) => ({
        detail,
        rowKey: detail.delivery_id || detail.order_id || idx,
        rowType: "detail",
      }));
  const hiddenCount = Math.max(0, rows.length - MAX_VISIBLE_DETAILS);
  const visibleRows = showAllDetails ? rows : rows.slice(0, MAX_VISIBLE_DETAILS);

  return (
    <div
      className={joinClasses([
        "grid gap-0",
        embedded
          ? ""
          : "mt-1 border-t border-[rgba(148,163,184,0.1)] pt-1.5",
      ])}
    >
      <div className={SOURCE_CHANGE_PREVIEW_STACK_OUTLINE_CLASS_NAME}>
        {visibleRows.map((row, rowIndex) => {
          const rowSeparatorClassName = rowIndex > 0
            ? SOURCE_CHANGE_PREVIEW_STACK_SEPARATOR_CLASS_NAME
            : "";
          if (row.rowType === "group") {
            const detailLines = groupDetailsBySharedReason(row.details, actionLabel, reasonOnly);
            const hiddenDetailCount = detailLines.slice(MAX_VISIBLE_DETAILS).reduce((count, line) => count + line.labels.length, 0);
            return (
              <div
                key={row.rowKey}
                className={joinClasses([
                  "grid gap-1 px-2 py-0.5",
                  rowSeparatorClassName,
                  SOURCE_CHANGE_ITEM_CARD_SURFACE_CLASS_NAME,
                ])}
              >
              <div className="flex min-w-0 items-center gap-2">
                <span className={joinClasses([SOURCE_CHANGE_ITEM_TITLE_CLASS_NAME, "flex-1"])}>
                  {row.title}
                </span>
                <span className="shrink-0 rounded-[4px] border border-slate-200 bg-white px-1.25 py-px text-[0.58rem] font-bold text-slate-700">
                  {row.subtitle}
                </span>
                {row.truckLabel ? (
                  <span className={SOURCE_CHANGE_TRUCK_BADGE_CLASS_NAME}>
                    {row.truckLabel}
                  </span>
                ) : null}
              </div>
              <div className="grid gap-0.5 border-l border-slate-200 pl-1.5">
                {detailLines.slice(0, MAX_VISIBLE_DETAILS).map(({ detail, labels }, idx) => {
                  const detailKey = detail.delivery_id || detail.order_id || idx;
                  const detailLabel = labels.join(", ");
                  const resolvedActionLabel = resolveSourceChangePreviewActionLabel(actionLabel, detail);
                  const hasMultipleChangedFields = Array.isArray(detail?.changed_fields)
                    && detail.changed_fields.length > 1;
                  const detailReason = splitMultipleChangedFields && hasMultipleChangedFields && !reasonOnly
                    ? formatSourceChangeReason(detail.changed_fields, { showAllFields: true })
                    : formatSourceChangeDetailReason(detail, groupSourceChanges, { reasonOnly });
                  const showReasonOnSeparateLine = splitMultipleChangedFields
                    && hasMultipleChangedFields
                    && !reasonOnly
                    && Boolean(detailReason);

                  if (showReasonOnSeparateLine) {
                    return (
                      <div key={detailKey} className={joinClasses(["grid gap-0", SOURCE_CHANGE_ITEM_BODY_CLASS_NAME])}>
                        <span className="truncate">
                          {detailLabel}
                          {resolvedActionLabel ? ` · ${resolvedActionLabel}` : ""}
                        </span>
                        <span className="ml-2 border-l border-slate-200 pl-2 break-words leading-tight">
                          {detailReason}
                        </span>
                      </div>
                    );
                  }

                  return (
                    <div key={detailKey} className="grid min-w-0 gap-0">
                      {/* Toutes les raisons restent lisibles : la ligne passe à la ligne au lieu d'être coupée. */}
                      <span className={joinClasses([reasonOnly ? "break-words" : "truncate", SOURCE_CHANGE_ITEM_BODY_CLASS_NAME])}>
                        {detailLabel}
                        {resolvedActionLabel ? ` · ${resolvedActionLabel}` : ""}
                        {detailReason ? ` · ${detailReason}` : ""}
                      </span>
                      <SourceChangeConflictLine detail={detail} />
                    </div>
                  );
                })}
                {hiddenDetailCount > 0 ? (
                  <span className="text-[0.58rem] font-medium text-slate-600">
                    + {hiddenDetailCount} autre{hiddenDetailCount > 1 ? "s" : ""}
                  </span>
                ) : null}
              </div>
            </div>
          );
        }

        const detail = row.detail;
        const detailReason = formatSourceChangeDetailReason(
          detail,
          true,
          { reasonOnly, showAllFields: splitMultipleChangedFields && !reasonOnly },
        );
        const resolvedActionLabel = resolveSourceChangePreviewActionLabel(actionLabel, detail);
        const hasDetailReason = Boolean(
          detail.changed_fields?.length > 0
          || detail.ghost_reason
          || detail.initial_reason_label
          || detail.recovery_block_reason,
        );
        return (
          <div
            key={row.rowKey}
            className={joinClasses([
              "flex min-w-0 items-center gap-1.5 px-2 py-0.5",
              rowSeparatorClassName,
              SOURCE_CHANGE_ITEM_CARD_SURFACE_CLASS_NAME,
            ])}
          >
            <div className="grid min-w-0 flex-1 gap-0 leading-tight">
              <span className={SOURCE_CHANGE_ITEM_TITLE_CLASS_NAME}>
                {formatDetailLabel(detail)}
              </span>
              {resolvedActionLabel || hasDetailReason ? (
                <div className="flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-0">
                  {resolvedActionLabel ? (
                    <span className={SOURCE_CHANGE_ITEM_BODY_CLASS_NAME}>
                      {resolvedActionLabel}
                    </span>
                  ) : null}
                  {hasDetailReason ? (
                    <span
                      className="min-w-0 max-w-full basis-full overflow-hidden break-words text-[0.64rem] leading-tight text-slate-700"
                      title={detailReason || undefined}
                    >
                      {detailReason}
                    </span>
                  ) : null}
                </div>
              ) : null}
              <SourceChangeConflictLine detail={detail} />
            </div>
            {detail.truck_label ? (
              <span className={SOURCE_CHANGE_TRUCK_BADGE_CLASS_NAME}>
                {detail.truck_label}
              </span>
            ) : null}
          </div>
        );
        })}
      </div>
      {hiddenCount > 0 ? (
        <button
          type="button"
          className="flex w-full items-center justify-start gap-1 rounded-[4px] px-2 py-0.5 text-left text-[0.62rem] font-medium text-slate-600 hover:text-slate-800 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-sky-300"
          aria-expanded={showAllDetails}
          onClick={() => setShowAllDetails((currentValue) => !currentValue)}
        >
          <span>
            {showAllDetails
              ? "Masquer les différences supplémentaires"
              : `+ ${hiddenCount} autre${hiddenCount > 1 ? "s" : ""}`}
          </span>
        </button>
      ) : null}
    </div>
  );
}

const END_OF_DAY_LOAD_INTEGRITY_REASON_LABELS = Object.freeze({
  target_materialization_mismatch: "Répartition du Chargé PM modifiée",
  link_incomplete: "Lien PM incomplet",
  mixed_with_preload: "Mélangé à un preload",
  target_snapshot_missing: "Dispatch cible absent",
  wrong_target_truck: "Mauvais camion cible",
});

export const END_OF_DAY_LOAD_INTEGRITY_ACTION_LABELS = Object.freeze({
  repair_target_materialization: "Rétablir le Chargé PM",
  optimize_target_dispatch: "Optimiser le dispatch cible",
  repair_link: "Réparer le lien PM",
  suspend_target: "Mettre dans Suspendues",
});

export function resolveEndOfDayLoadIntegrity(sourceChanges = {}) {
  const integrity = sourceChanges?.endOfDayLoadIntegrity || sourceChanges?.end_of_day_load_integrity;
  return integrity && typeof integrity === "object" ? integrity : {};
}

function resolveEndOfDayLoadIntegrityOrderIds(issue) {
  const orderIds = Array.isArray(issue?.order_ids)
    ? issue.order_ids
    : Array.isArray(issue?.orderIds)
      ? issue.orderIds
      : [];
  const fallbackOrderId = normalizeInlineText(issue?.order_id || issue?.orderId);
  return [...new Set([...orderIds.map(normalizeInlineText), fallbackOrderId].filter(Boolean))];
}

function resolveEndOfDayLoadIntegrityDeliveryIds(issue) {
  const deliveryIds = Array.isArray(issue?.delivery_ids)
    ? issue.delivery_ids
    : Array.isArray(issue?.deliveryIds)
      ? issue.deliveryIds
      : [];
  const fallbackDeliveryId = normalizeInlineText(issue?.delivery_id || issue?.deliveryId);
  return [...new Set([...deliveryIds.map(normalizeInlineText), fallbackDeliveryId].filter(Boolean))];
}

function resolveEndOfDayLoadIntegrityIdentityKeys(issue) {
  return [
    ...resolveEndOfDayLoadIntegrityOrderIds(issue).map((orderId) => `order:${orderId}`),
    ...resolveEndOfDayLoadIntegrityDeliveryIds(issue).map((deliveryId) => `delivery:${deliveryId}`),
  ];
}

function resolveSourceChangeDetailIdentityKeys(detail) {
  if (!detail || typeof detail !== "object") {
    return [];
  }
  const orderIds = [
    ...(Array.isArray(detail.order_ids) ? detail.order_ids : []),
    ...(Array.isArray(detail.orderIds) ? detail.orderIds : []),
    detail.order_id,
    detail.orderId,
  ].map(normalizeInlineText).filter(Boolean);
  const deliveryIds = [
    ...(Array.isArray(detail.delivery_ids) ? detail.delivery_ids : []),
    ...(Array.isArray(detail.deliveryIds) ? detail.deliveryIds : []),
    detail.delivery_id,
    detail.deliveryId,
  ].map(normalizeInlineText).filter(Boolean);
  return [
    ...new Set(orderIds.map((orderId) => `order:${orderId}`)),
    ...new Set(deliveryIds.map((deliveryId) => `delivery:${deliveryId}`)),
  ];
}

function countSourceChangeDetailsMatchingIntegrityIdentities(details, integrityIdentityKeys) {
  if (!Array.isArray(details) || !(integrityIdentityKeys instanceof Set) || integrityIdentityKeys.size === 0) {
    return 0;
  }
  return details.filter((detail) => resolveSourceChangeDetailIdentityKeys(detail).some(
    (identityKey) => integrityIdentityKeys.has(identityKey),
  )).length;
}

function resolveEndOfDayLoadIntegrityMarkerKey(issue, index) {
  const markerKey = normalizeInlineText(issue?.marker_key || issue?.markerKey);
  if (markerKey) {
    return markerKey;
  }
  const orderKey = resolveEndOfDayLoadIntegrityOrderIds(issue).join("|");
  const sourceDate = normalizeInlineText(issue?.source?.dispatch_date || issue?.source?.dispatchDate);
  const targetDate = normalizeInlineText(issue?.target?.dispatch_date || issue?.target?.dispatchDate);
  return orderKey || [sourceDate, targetDate, index].join("|");
}

function resolveEndOfDayLoadIntegrityIssues(sourceChanges = {}) {
  const integrity = resolveEndOfDayLoadIntegrity(sourceChanges);
  const rawIssues = Array.isArray(integrity?.issues)
    ? integrity.issues.filter(Boolean)
    : Array.isArray(integrity?.anomalies)
      ? integrity.anomalies.filter(Boolean)
      : [];
  const issuesByMarker = new Map();

  rawIssues.forEach((issue, index) => {
    const markerKey = resolveEndOfDayLoadIntegrityMarkerKey(issue, index);
    if (!issuesByMarker.has(markerKey)) {
      issuesByMarker.set(markerKey, issue);
    }
  });
  return [...issuesByMarker.values()];
}

function formatCompactDispatchDateLabel(value) {
  const rawValue = normalizeInlineText(value);
  if (!rawValue) {
    return "Date inconnue";
  }
  const parsedDate = new Date(rawValue.slice(0, 10) + "T00:00:00");
  if (Number.isNaN(parsedDate.getTime())) {
    return rawValue;
  }
  return new Intl.DateTimeFormat("fr-CA", {
    day: "numeric",
    month: "short",
  }).format(parsedDate);
}

function formatEndOfDayLoadIntegrityPeriod(value, fallback = "Jour") {
  const normalizedValue = normalizeInlineText(value).toLowerCase();
  if (["evening", "soir"].includes(normalizedValue)) {
    return "Soir";
  }
  if (["day", "jour", "next_day"].includes(normalizedValue)) {
    return "Jour";
  }
  return fallback;
}

function resolveEndOfDayLoadIntegrityTransition(issue) {
  const source = issue?.source && typeof issue.source === "object" ? issue.source : {};
  const target = issue?.target && typeof issue.target === "object" ? issue.target : {};
  const sourceDate = source.dispatch_date || source.dispatchDate || issue?.source_dispatch_date || issue?.sourceDispatchDate;
  const targetDate = target.dispatch_date || target.dispatchDate || issue?.target_dispatch_date || issue?.targetDispatchDate;
  const sourcePeriod = source.dispatch_period || source.dispatchPeriod || issue?.source_dispatch_period || issue?.sourceDispatchPeriod;
  const targetPeriod = target.dispatch_period || target.dispatchPeriod || issue?.target_dispatch_period || issue?.targetDispatchPeriod;
  return [
    formatCompactDispatchDateLabel(sourceDate),
    formatEndOfDayLoadIntegrityPeriod(sourcePeriod),
    "→",
    formatCompactDispatchDateLabel(targetDate),
    formatEndOfDayLoadIntegrityPeriod(targetPeriod),
  ].join(" ");
}

function resolveEndOfDayLoadIntegrityTruckLabel(issue) {
  const target = issue?.target && typeof issue.target === "object" ? issue.target : {};
  const source = issue?.source && typeof issue.source === "object" ? issue.source : {};
  return normalizeInlineText(
    target.truck_label
    || target.truckLabel
    || target.truck_id
    || target.truckId
    || issue?.target_truck_label
    || issue?.targetTruckLabel
    || source.truck_label
    || source.truckLabel
    || source.truck_id
    || source.truckId,
  ) || "Camion inconnu";
}

export function formatEndOfDayLoadIntegrityOrderLabel(issue) {
  const orderIds = resolveEndOfDayLoadIntegrityOrderIds(issue);
  if (!orderIds.length) {
    return "Chargé PM";
  }
  const visibleOrderIds = orderIds.slice(0, 2);
  const remainingCount = orderIds.length - visibleOrderIds.length;
  return visibleOrderIds.join(", ") + (remainingCount > 0 ? " +" + remainingCount : "");
}

function resolveEndOfDayLoadIntegrityReasonLabel(issue) {
  const reasonCode = normalizeInlineText(
    issue?.primary_reason_code
    || issue?.primaryReasonCode
    || issue?.reason_code
    || issue?.reasonCode,
  ).toLowerCase();
  return END_OF_DAY_LOAD_INTEGRITY_REASON_LABELS[reasonCode]
    || END_OF_DAY_LOAD_INTEGRITY_REASON_LABELS.link_incomplete;
}

function resolveEndOfDayLoadIntegrityAction(issue, onResolve) {
  const action = normalizeInlineText(
    issue?.repair_action
    || issue?.repairAction
    || issue?.recommended_action
    || issue?.recommendedAction,
  ).toLowerCase();
  const isMutationAction = ["repair_link", "suspend_target", "repair_target_materialization"].includes(action);
  const repairAllowed = issue?.repair_allowed ?? issue?.repairAllowed;
  if (
    action === "manual_intervention"
    || !END_OF_DAY_LOAD_INTEGRITY_ACTION_LABELS[action]
    || (isMutationAction && repairAllowed === false)
    || typeof onResolve !== "function"
  ) {
    return { action: "manual_intervention", label: "Intervention requise" };
  }
  return { action, label: END_OF_DAY_LOAD_INTEGRITY_ACTION_LABELS[action] };
}

/**
 * Compact M-à-J contract:
 * sourceChanges.endOfDayLoadIntegrity (or end_of_day_load_integrity) contains
 * { status, preview_revision, issues[] }. Each issue exposes marker_key,
 * order_ids, source/target dispatch scopes, primary_reason_code, repair_action
 * and repair_allowed. Actions are delegated without mutation to
 * sourceChanges.onResolveEndOfDayLoadIntegrity(issue, action).
 */
function EndOfDayLoadIntegritySection({
  sourceChanges,
  actionsDisabled = false,
  isBusy = false,
  onResolve,
}) {
  const issues = resolveEndOfDayLoadIntegrityIssues(sourceChanges);
  if (!issues.length) {
    return null;
  }

  return (
    <section
      aria-label="Chargé PM à corriger"
      className={SOURCE_CHANGE_GROUP_SECTION_CLASS_NAME}
      data-end-of-day-load-integrity-issues
    >
      <div className={SOURCE_CHANGE_GROUP_HEADER_CLASS_NAME}>
        <ExclamationTriangleIcon className={SOURCE_CHANGE_GROUP_HEADER_ICON_CLASS_NAME} aria-hidden="true" />
        <span className="min-w-0 flex-1 truncate">Chargé PM à corriger</span>
        <span className={SOURCE_CHANGE_COUNT_BADGE_CLASS_NAME}>
          {issues.length}
        </span>
      </div>
      <div className="grid gap-1 border-t border-[var(--dispatch-update-border)] bg-white p-2">
        {issues.map((issue, index) => {
          const markerKey = resolveEndOfDayLoadIntegrityMarkerKey(issue, index);
          const resolution = resolveEndOfDayLoadIntegrityAction(issue, onResolve);
          const isManualIntervention = resolution.action === "manual_intervention";
          return (
            <div
              key={markerKey}
              className={joinClasses([
                "flex min-w-0 items-center justify-between gap-2 rounded-[4px] bg-white/96 px-2 py-1.5",
                SOURCE_CHANGE_INNER_CARD_BORDER_CLASS_NAME,
              ])}
              data-end-of-day-load-integrity-marker={markerKey}
            >
              <div className="grid min-w-0 gap-0.5">
                <span className="truncate text-[0.68rem] font-semibold text-slate-800">
                  {formatEndOfDayLoadIntegrityOrderLabel(issue)} · {resolveEndOfDayLoadIntegrityTruckLabel(issue)}
                </span>
                <span className="truncate text-[0.59rem] text-slate-500">
                  {resolveEndOfDayLoadIntegrityTransition(issue)}
                </span>
                <span
                  className="whitespace-normal break-words text-[0.59rem] font-semibold leading-tight text-[#a03a31]"
                  data-end-of-day-load-integrity-reason
                >
                  {resolveEndOfDayLoadIntegrityReasonLabel(issue)}
                </span>
              </div>
              {isManualIntervention ? (
                <span className="shrink-0 text-[0.59rem] font-semibold text-[#a03a31]">
                  Intervention requise
                </span>
              ) : (
                <button
                  type="button"
                  className={joinClasses([
                    SOURCE_CHANGES_MENU_INLINE_ACTION_BUTTON_CLASS_NAME,
                    "text-[0.6rem]",
                  ])}
                  data-end-of-day-load-integrity-action={resolution.action}
                  disabled={actionsDisabled || isBusy}
                  onClick={() => onResolve(issue, resolution.action)}
                >
                  {resolution.label}
                </button>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

// Rappel non bloquant : Chargé PM vers une date cible jamais optimisée.
function EndOfDayLoadIntegrityNoticesSection({ sourceChanges, isBusy = false, onResolve }) {
  const integrity = resolveEndOfDayLoadIntegrity(sourceChanges);
  const notices = Array.isArray(integrity.notices) ? integrity.notices.filter(Boolean) : [];
  if (!notices.length) {
    return null;
  }

  return (
    <section
      aria-label="Rappel Chargé PM"
      className={SOURCE_CHANGE_GROUP_SECTION_CLASS_NAME}
      data-end-of-day-load-integrity-notices
    >
      <div className={SOURCE_CHANGE_GROUP_HEADER_CLASS_NAME}>
        <ExclamationTriangleIcon className={SOURCE_CHANGE_GROUP_HEADER_ICON_CLASS_NAME} aria-hidden="true" />
        <span className="min-w-0 flex-1 truncate">Rappel Chargé PM</span>
      </div>
      <div className="grid gap-1 border-t border-[var(--dispatch-update-border)] bg-white p-2">
        {notices.map((notice, index) => (
          <div
            key={resolveEndOfDayLoadIntegrityMarkerKey(notice, index)}
            className={joinClasses([
              "flex min-w-0 items-center justify-between gap-2 rounded-[4px] bg-white/96 px-2 py-1.5",
              SOURCE_CHANGE_INNER_CARD_BORDER_CLASS_NAME,
            ])}
          >
            <div className="grid min-w-0 gap-0.5">
              <span className="truncate text-[0.68rem] font-semibold text-slate-800">
                {formatEndOfDayLoadIntegrityOrderLabel(notice)} · {resolveEndOfDayLoadIntegrityTruckLabel(notice)}
              </span>
              <span className="truncate text-[0.59rem] text-slate-500">{resolveEndOfDayLoadIntegrityTransition(notice)}</span>
              <span className="text-[0.59rem] font-semibold leading-tight text-[rgb(154,52,18)]">
                {resolveEndOfDayLoadIntegrityReasonLabel(notice)} — {END_OF_DAY_LOAD_INTEGRITY_ACTION_LABELS.optimize_target_dispatch}
              </span>
            </div>
            {typeof onResolve === "function" ? (
              <button
                type="button"
                className={joinClasses([SOURCE_CHANGES_MENU_INLINE_ACTION_BUTTON_CLASS_NAME, "text-[0.6rem]"])}
                disabled={isBusy}
                onClick={() => onResolve(notice, "optimize_target_dispatch")}
              >
                Ouvrir la cible
              </button>
            ) : null}
          </div>
        ))}
      </div>
    </section>
  );
}

function normalizeSourceChangeCount(counts, key) {
  const numericValue = Number(counts?.[key]);
  return Number.isFinite(numericValue) ? Math.max(0, Math.round(numericValue)) : 0;
}

function getRestoreExecutionPreservation(preview = null) {
  const executionPreservation = preview?.execution_preservation && typeof preview.execution_preservation === "object"
    ? preview.execution_preservation
    : null;
  const counts = executionPreservation?.counts && typeof executionPreservation.counts === "object"
    ? executionPreservation.counts
    : {};
  const details = executionPreservation?.details && typeof executionPreservation.details === "object"
    ? executionPreservation.details
    : {};
  const preservableDetails = Array.isArray(details.preservable) ? details.preservable.filter(Boolean) : [];
  const blockedDetails = Array.isArray(details.blocked) ? details.blocked.filter(Boolean) : [];
  const preservableCount = Math.max(
    normalizeSourceChangeCount(counts, "preservable"),
    preservableDetails.length,
  );
  const blockedCount = Math.max(
    normalizeSourceChangeCount(counts, "blocked"),
    blockedDetails.length,
  );
  const blocking = executionPreservation?.blocking === true;
  const total = Math.max(
    normalizeSourceChangeCount(counts, "total"),
    preservableCount + blockedCount,
    blocking ? 1 : 0,
  );

  return {
    blockedCount,
    blockedDetails,
    blocking,
    preservableCount,
    preservableDetails,
    total,
  };
}

function restorePreviewHasBlockingExecutionPreservation(preview = null) {
  return getRestoreExecutionPreservation(preview).blocking;
}

function getRestoreExecutionPreservationBlockingMessage(preview = null) {
  const executionPreservation = getRestoreExecutionPreservation(preview);
  if (!executionPreservation.blocking) {
    return "";
  }
  if (executionPreservation.blockedCount > 1) {
    return `${executionPreservation.blockedCount} exécutions réelles doivent être corrigées avant de restaurer.`;
  }
  return "Une exécution réelle doit être corrigée avant de restaurer.";
}

function getRestoreIntegrityConflicts(preview = null) {
  const directConflicts = Array.isArray(preview?.integrity_conflicts)
    ? preview.integrity_conflicts
    : [];
  const sourceChangeConflicts = Array.isArray(preview?.details?.integrity_conflicts)
    ? preview.details.integrity_conflicts
    : [];
  const conflicts = directConflicts.length > 0 ? directConflicts : sourceChangeConflicts;
  return conflicts.filter((conflict) => conflict && typeof conflict === "object");
}

function getRestoreIntegrityConflictBlockingMessage(preview = null) {
  const conflicts = getRestoreIntegrityConflicts(preview);
  if (!conflicts.length) {
    return "";
  }
  const truckLabels = Array.from(new Set(
    conflicts
      .map((conflict) => normalizeInlineText(
        conflict?.truck_name
        || conflict?.truck_label
        || conflict?.truck_id,
      ))
      .filter(Boolean),
  ));
  const affectedOrderIds = Array.from(new Set(
    conflicts.flatMap((conflict) => {
      const orderIds = Array.isArray(conflict?.order_ids)
        ? conflict.order_ids
        : conflict?.order_id
          ? [conflict.order_id]
          : [];
      return orderIds.map(normalizeInlineText).filter(Boolean);
    }),
  ));
  const affectedLabels = [
    truckLabels.length > 0 ? `Camions : ${truckLabels.join(", ")}` : "",
    affectedOrderIds.length > 0 ? `Commandes : ${affectedOrderIds.join(", ")}` : "",
  ].filter(Boolean);
  return [
    `Restauration impossible : ${conflicts.length} tournée${conflicts.length > 1 ? "s" : ""} non rendable${conflicts.length > 1 ? "s" : ""}.`,
    affectedLabels.join(" · "),
  ].filter(Boolean).join(" ");
}

function formatTruckRecoveryOrderLine(orderId, deliveryClientName) {
  const normalizedOrderId = normalizeInlineText(orderId);
  const clientName = normalizeInlineText(deliveryClientName);
  if (!normalizedOrderId) {
    return "";
  }
  return clientName ? `${normalizedOrderId} · ${clientName}` : normalizedOrderId;
}

function formatExecutionPreservationOrderLabel(detail) {
  const clientLabel = normalizeInlineText(detail?.delivery_client_name || detail?.client_name || detail?.customer_name);
  const orderId = normalizeInlineText(detail?.order_id || detail?.order_number || detail?.order_reference || detail?.delivery_id);

  if (clientLabel && orderId) {
    return `${clientLabel} · ${orderId}`;
  }
  return orderId || clientLabel || "Commande non identifiée";
}

function formatExecutionPreservationTruckLabel(detail) {
  return normalizeInlineText(
    detail?.truck_label
    || detail?.snapshot_truck_label
    || detail?.execution_truck_label
    || detail?.current_truck_label
    || detail?.truck_number
    || detail?.truck_id,
  ) || "Camion non identifié";
}

function formatExecutionPreservationReason(detail, fallbackReason = "") {
  const explicitReason = normalizeInlineText(
    detail?.reason_label
    || detail?.reason_message
    || detail?.blocking_reason_label
    || detail?.message,
  );
  if (explicitReason) {
    return explicitReason;
  }

  const reasonCode = normalizeInlineText(detail?.reason || detail?.blocking_reason || detail?.recovery_block_reason);
  return formatExecutionRecoveryBlockReason(reasonCode) || fallbackReason;
}

function ExecutionPreservationDetailsPreview({ details, fallbackReason }) {
  const [showAllDetails, setShowAllDetails] = useState(false);
  const safeDetails = Array.isArray(details) ? details.filter(Boolean) : [];

  if (safeDetails.length === 0) {
    return null;
  }

  const visibleDetails = showAllDetails ? safeDetails : safeDetails.slice(0, MAX_VISIBLE_DETAILS);
  const hiddenCount = Math.max(0, safeDetails.length - MAX_VISIBLE_DETAILS);

  return (
    <div className="grid gap-1 border-t border-[rgba(148,163,184,0.12)] pt-1.5">
      {visibleDetails.map((detail, index) => {
        const orderLabel = formatExecutionPreservationOrderLabel(detail);
        const truckLabel = formatExecutionPreservationTruckLabel(detail);
        const reasonLabel = formatExecutionPreservationReason(detail, fallbackReason);
        const rowKey = normalizeInlineText(detail?.order_id || detail?.delivery_id || detail?.execution_id)
          || `${truckLabel}-${index}`;

        return (
          <div
            key={rowKey}
            className="grid gap-0.5 rounded-sm border border-[rgba(148,163,184,0.14)] bg-white/90 px-2 py-1.5"
          >
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-[0.62rem]">
              <span className="font-semibold text-slate-700">
                Commande · {orderLabel}
              </span>
              <span className="text-slate-500">
                Camion · {truckLabel}
              </span>
            </div>
            {reasonLabel ? (
              <span className="text-[0.6rem] leading-4 text-slate-500">
                Raison · {reasonLabel}
              </span>
            ) : null}
          </div>
        );
      })}
      {hiddenCount > 0 ? (
        <button
          type="button"
          className="flex w-full items-center justify-start gap-1 rounded-[4px] px-2 py-0.5 text-left text-[0.62rem] font-medium text-slate-500 hover:text-slate-700 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-sky-300"
          aria-expanded={showAllDetails}
          onClick={() => setShowAllDetails((currentValue) => !currentValue)}
        >
          {showAllDetails
            ? "Masquer les exécutions supplémentaires"
            : `+ ${hiddenCount} autre${hiddenCount > 1 ? "s" : ""} exécution${hiddenCount > 1 ? "s" : ""}`}
        </button>
      ) : null}
    </div>
  );
}

function restorePreviewNeedsFinalConfirmation(preview = null) {
  const relatedDispatches = preview?.related_dispatches && typeof preview.related_dispatches === "object"
    ? preview.related_dispatches
    : null;
  const addressRevalidation = preview?.address_revalidation && typeof preview.address_revalidation === "object"
    ? preview.address_revalidation
    : null;
  return relatedDispatches?.requires_confirmation === true
    || relatedDispatches?.requires_removed_end_of_day_load_confirmation === true
    || addressRevalidation?.confirmation_required === true;
}

function DispatchRestoreImpactPreview({ isLoading = false, preview = null }) {
  if (isLoading) {
    return (
      <p className="m-0 rounded-sm bg-slate-50 px-2 py-1.5 text-[0.64rem] font-medium text-slate-500">
        Vérification des changements Data Manager...
      </p>
    );
  }

  if (preview?.previewError === true) {
    return (
      <p className="m-0 rounded-sm bg-amber-50 px-2 py-1.5 text-[0.64rem] font-medium text-amber-800">
        Prévisualisation indisponible. La restauration vérifiera quand même les commandes à jour.
      </p>
    );
  }

  if (preview?.previewLoaded !== true) {
    return (
      <p className="m-0 rounded-sm bg-slate-50 px-2 py-1.5 text-[0.64rem] font-medium text-slate-500">
        Vérification des changements Data Manager...
      </p>
    );
  }

  const counts = preview?.counts && typeof preview.counts === "object" ? preview.counts : {};
  const details = preview?.details && typeof preview.details === "object" ? preview.details : {};
  const newCount = normalizeSourceChangeCount(counts, "new");
  const removedCount = normalizeSourceChangeCount(counts, "date_changed_remove");
  const newDetails = Array.isArray(details.new) ? details.new : [];
  const removedDetails = Array.isArray(details.date_changed_remove) ? details.date_changed_remove : [];
  const relatedDispatches = preview?.related_dispatches && typeof preview.related_dispatches === "object"
    ? preview.related_dispatches
    : null;
  const affectedDispatchDates = Array.isArray(relatedDispatches?.affected_dispatch_dates)
    ? relatedDispatches.affected_dispatch_dates.map(normalizeInlineText).filter(Boolean)
    : [];
  const endOfDayLoads = Array.isArray(relatedDispatches?.end_of_day_loads)
    ? relatedDispatches.end_of_day_loads.filter(Boolean)
    : [];
  const removedEndOfDayLoads = Array.isArray(relatedDispatches?.removed_end_of_day_loads)
    ? relatedDispatches.removed_end_of_day_loads.filter(Boolean)
    : [];
  const addressRevalidation = preview?.address_revalidation && typeof preview.address_revalidation === "object"
    ? preview.address_revalidation
    : null;
  const addressCounts = addressRevalidation?.counts && typeof addressRevalidation.counts === "object"
    ? addressRevalidation.counts
    : {};
  const addressDetails = Array.isArray(addressRevalidation?.details)
    ? addressRevalidation.details.filter(Boolean)
    : [];
  const keptAddressCount = normalizeSourceChangeCount(addressCounts, "kept_as_routing_errors");
  const changedValidAddressCount = normalizeSourceChangeCount(addressCounts, "changed_valid_confirmation_required");
  const keptAddressDetails = addressDetails.filter((detail) => detail?.restore_action === "keep_address_routing_error");
  const changedValidAddressDetails = addressDetails.filter((detail) => detail?.restore_action === "restore_as_suspended_after_confirmation");
  const executionPreservation = getRestoreExecutionPreservation(preview);
  const integrityConflicts = getRestoreIntegrityConflicts(preview);
  const hasExecutionPreservationImpact = executionPreservation.total > 0;
  const hasIntegrityConflictImpact = integrityConflicts.length > 0;
  const hasRelatedDispatchImpact = relatedDispatches?.requires_confirmation === true && affectedDispatchDates.length > 1;
  const hasRemovedEndOfDayLoadImpact = relatedDispatches?.requires_removed_end_of_day_load_confirmation === true
    && removedEndOfDayLoads.length > 0;
  const hasAddressImpact = keptAddressCount > 0 || changedValidAddressCount > 0;
  const hasImpact = newCount > 0
    || removedCount > 0
    || hasRelatedDispatchImpact
    || hasRemovedEndOfDayLoadImpact
    || hasAddressImpact
    || hasExecutionPreservationImpact
    || hasIntegrityConflictImpact;
  const newVerb = newCount > 1 ? "seront" : "sera";
  const removedVerb = removedCount > 1 ? "seront" : "sera";

  if (!hasImpact) {
    return (
      <p className="m-0 rounded-sm bg-emerald-50 px-2 py-1.5 text-[0.64rem] font-medium text-emerald-800">
        Aucun écart Data Manager détecté pour cette sauvegarde.
      </p>
    );
  }

  return (
    <div className="grid gap-1.5 rounded-sm border border-[rgba(148,163,184,0.16)] bg-slate-50/80 p-1.5">
      {hasIntegrityConflictImpact ? (
        <div className="grid gap-1 rounded-sm border border-[rgba(197,82,74,0.24)] bg-red-50 px-2 py-1.5">
          <p className="m-0 text-[0.64rem] font-semibold text-[#972d24]">
            {integrityConflicts.length} tournée{integrityConflicts.length > 1 ? "s" : ""} non rendable{integrityConflicts.length > 1 ? "s" : ""} bloque{integrityConflicts.length > 1 ? "nt" : ""} la restauration.
          </p>
          <p className="m-0 text-[0.6rem] leading-4 text-[#b24339]">
            La sauvegarde reste consultable, mais elle ne peut pas remplacer le dispatch courant.
          </p>
          <div className="grid gap-0.5">
            {integrityConflicts.slice(0, MAX_VISIBLE_DETAILS).map((conflict, index) => {
              const truckLabel = normalizeInlineText(
                conflict?.truck_name
                || conflict?.truck_label
                || conflict?.truck_id,
              ) || "Camion non identifié";
              const orderIds = Array.isArray(conflict?.order_ids)
                ? conflict.order_ids.map(normalizeInlineText).filter(Boolean)
                : conflict?.order_id
                  ? [normalizeInlineText(conflict.order_id)].filter(Boolean)
                  : [];
              return (
                <span
                  key={`${normalizeInlineText(conflict?.trip_id) || truckLabel}-${index}`}
                  className="break-words text-[0.6rem] font-medium text-[#b24339]"
                  data-restore-integrity-conflict
                >
                  {truckLabel}
                  {orderIds.length > 0 ? ` · commandes : ${orderIds.join(", ")}` : ""}
                </span>
              );
            })}
          </div>
        </div>
      ) : null}
      {hasExecutionPreservationImpact ? (
        <div className={joinClasses([
          "grid gap-1 rounded-sm px-2 py-1.5",
          executionPreservation.blocking
            ? "border border-[rgba(197,82,74,0.24)] bg-red-50"
            : "border border-[rgba(5,150,105,0.2)] bg-emerald-50",
        ])}>
          {executionPreservation.preservableCount > 0 ? (
            <div className="grid gap-1">
              <p className="m-0 text-[0.64rem] font-semibold text-emerald-900">
                {executionPreservation.preservableCount} exécution{executionPreservation.preservableCount > 1 ? "s" : ""} livrée{executionPreservation.preservableCount > 1 ? "s" : ""} {executionPreservation.preservableCount > 1 ? "seront" : "sera"} préservée{executionPreservation.preservableCount > 1 ? "s" : ""}.
              </p>
              <p className="m-0 text-[0.6rem] leading-4 text-emerald-800">
                Les tonnages réels, confirmations et gel Portail seront réappliqués dans la tournée restaurée.
              </p>
              <ExecutionPreservationDetailsPreview
                details={executionPreservation.preservableDetails}
                fallbackReason="Exécution livrée : les données réelles seront conservées"
              />
            </div>
          ) : null}
          {executionPreservation.blocking ? (
            <div className={joinClasses([
              "grid gap-1",
              executionPreservation.preservableCount > 0 ? "border-t border-[rgba(197,82,74,0.16)] pt-1.5" : "",
            ])}>
              <p className="m-0 text-[0.64rem] font-semibold text-[#972d24]">
                {executionPreservation.blockedCount > 0
                  ? `${executionPreservation.blockedCount} exécution${executionPreservation.blockedCount > 1 ? "s" : ""} réelle${executionPreservation.blockedCount > 1 ? "s" : ""} bloque${executionPreservation.blockedCount > 1 ? "nt" : ""} la restauration.`
                  : "Une exécution réelle bloque la restauration."}
              </p>
              <p className="m-0 text-[0.6rem] leading-4 text-[#b24339]">
                Restaurer est désactivé afin de ne pas masquer une commande chargée, confirmée ou livrée.
              </p>
              <ExecutionPreservationDetailsPreview
                details={executionPreservation.blockedDetails}
                fallbackReason="La sauvegarde ne permet pas de préserver cette exécution en sécurité"
              />
            </div>
          ) : executionPreservation.preservableCount === 0 ? (
            <p className="m-0 text-[0.64rem] font-semibold text-slate-700">
              {executionPreservation.total} exécution{executionPreservation.total > 1 ? "s" : ""} réelle{executionPreservation.total > 1 ? "s" : ""} {executionPreservation.total > 1 ? "seront prises" : "sera prise"} en compte pendant la restauration.
            </p>
          ) : null}
        </div>
      ) : null}
      {hasRelatedDispatchImpact ? (
        <div className="grid gap-1 rounded-sm border border-[rgba(217,119,6,0.2)] bg-amber-50 px-2 py-1.5">
          <p className="m-0 text-[0.64rem] font-semibold text-amber-900">
            Cette sauvegarde contient un Chargé PM. Restaurer écrasera les dispatchs du{" "}
            {affectedDispatchDates.map(formatDispatchDateLabel).join(" et du ")}.
          </p>
          {endOfDayLoads.length > 0 ? (
            <div className="grid gap-0.5">
              {endOfDayLoads.slice(0, MAX_VISIBLE_DETAILS).map((load, index) => {
                const targetTruckLabel = normalizeInlineText(load?.target_truck_label)
                  || normalizeInlineText(load?.target_truck_id)
                  || "camion cible";
                const sourceDateLabel = formatDispatchDateLabel(load?.source_dispatch_date);
                const targetDateLabel = formatDispatchDateLabel(load?.target_dispatch_date);
                const orderCount = Array.isArray(load?.order_ids) ? load.order_ids.length : 0;
                return (
                  <span
                    key={normalizeInlineText(load?.marker_key) || `${targetTruckLabel}-${index}`}
                    className="truncate text-[0.6rem] font-medium text-amber-800"
                  >
                    {targetTruckLabel}
                    {sourceDateLabel && targetDateLabel ? ` · ${sourceDateLabel} → ${targetDateLabel}` : ""}
                    {orderCount > 0 ? ` · ${orderCount} commande${orderCount > 1 ? "s" : ""}` : ""}
                  </span>
                );
              })}
              {endOfDayLoads.length > MAX_VISIBLE_DETAILS ? (
                <span className="text-[0.58rem] font-medium text-amber-700">
                  + {endOfDayLoads.length - MAX_VISIBLE_DETAILS} autre{endOfDayLoads.length - MAX_VISIBLE_DETAILS > 1 ? "s" : ""} Chargé PM
                </span>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
      {hasRemovedEndOfDayLoadImpact ? (
        <div className="grid gap-1 rounded-sm border border-[rgba(197,82,74,0.2)] bg-red-50 px-2 py-1.5">
          <p className="m-0 text-[0.64rem] font-semibold text-[#972d24]">
            Certaines commandes Chargé PM n'existent plus. Restaurer retirera complètement les Chargés PM concernés.
          </p>
          <div className="grid gap-0.5">
            {removedEndOfDayLoads.slice(0, MAX_VISIBLE_DETAILS).map((load, index) => {
              const targetTruckLabel = normalizeInlineText(load?.target_truck_label)
                || normalizeInlineText(load?.target_truck_id)
                || "camion cible";
              const missingOrderIds = Array.isArray(load?.missing_order_ids)
                ? load.missing_order_ids.map(normalizeInlineText).filter(Boolean)
                : [];
              return (
                <span
                  key={normalizeInlineText(load?.marker_key) || `${targetTruckLabel}-missing-${index}`}
                  className="truncate text-[0.6rem] font-medium text-[#b24339]"
                >
                  {targetTruckLabel}
                  {missingOrderIds.length > 0 ? ` · manquante${missingOrderIds.length > 1 ? "s" : ""}: ${missingOrderIds.join(", ")}` : ""}
                </span>
              );
            })}
            {removedEndOfDayLoads.length > MAX_VISIBLE_DETAILS ? (
              <span className="text-[0.58rem] font-medium text-[#b24339]">
                + {removedEndOfDayLoads.length - MAX_VISIBLE_DETAILS} autre{removedEndOfDayLoads.length - MAX_VISIBLE_DETAILS > 1 ? "s" : ""} Chargé PM retiré
              </span>
            ) : null}
          </div>
        </div>
      ) : null}
      {hasAddressImpact ? (
        <div className="grid gap-1 rounded-sm border border-[rgba(197,82,74,0.18)] bg-red-50/80 px-2 py-1.5">
          {keptAddressCount > 0 ? (
            <div className="grid gap-1">
              <p className="m-0 text-[0.64rem] font-semibold text-[#972d24]">
                {keptAddressCount} commande{keptAddressCount > 1 ? "s" : ""} {keptAddressCount > 1 ? "resteront" : "restera"} dans Adresses & Routage, pas dans Suspendues.
              </p>
              <SourceChangeDetailsPreview
                details={keptAddressDetails}
                actionLabel="restera dans Adresses & Routage"
                embedded
              />
            </div>
          ) : null}
          {changedValidAddressCount > 0 ? (
            <div className="grid gap-1">
              <p className="m-0 text-[0.64rem] font-semibold text-amber-900">
                {changedValidAddressCount} adresse{changedValidAddressCount > 1 ? "s" : ""} corrigée{changedValidAddressCount > 1 ? "s" : ""} depuis la sauvegarde demande{changedValidAddressCount > 1 ? "nt" : ""} confirmation.
              </p>
              <SourceChangeDetailsPreview
                details={changedValidAddressDetails}
                actionLabel="adresse actuelle validée; restauration dans Suspendues après confirmation"
                embedded
              />
            </div>
          ) : null}
        </div>
      ) : null}
      {newCount > 0 ? (
        <div className="grid gap-1">
          <p className="m-0 text-[0.64rem] font-semibold text-slate-700">
            {newCount} nouvelle{newCount > 1 ? "s" : ""} commande{newCount > 1 ? "s" : ""} {newVerb} placée{newCount > 1 ? "s" : ""} dans Suspendues.
          </p>
          <SourceChangeDetailsPreview
            details={newDetails}
            actionLabel="sera placée dans Suspendues"
          />
        </div>
      ) : null}
      {removedCount > 0 ? (
        <div className="grid gap-1">
          <p className="m-0 text-[0.64rem] font-semibold text-[#b24339]">
            {removedCount} commande{removedCount > 1 ? "s" : ""} changée{removedCount > 1 ? "s" : ""} de date {removedVerb} retirée{removedCount > 1 ? "s" : ""} des tournées restaurées.
          </p>
          <SourceChangeDetailsPreview
            details={removedDetails}
            actionLabel="sera retirée de la tournée restaurée"
          />
        </div>
      ) : null}
    </div>
  );
}

function DispatchRestoreConfirmationPopover({
  isLoading = false,
  preview = null,
  isConfirmDisabled = false,
  blockedReason = "",
  onCancel,
  onConfirm,
}) {
  const titleId = React.useId();
  const descriptionId = React.useId();

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onCancel?.();
      }
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleEscape);
    };
  }, [onCancel]);

  if (typeof document === "undefined") {
    return null;
  }

  return createPortal(
    <div
      className="fixed inset-0 z-modal isolate flex items-center justify-center bg-slate-950/25 px-4 py-6 font-sans sm:px-6"
      role="presentation"
      data-dispatch-restore-confirm-popover="true"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          onCancel?.();
        }
      }}
    >
      <section
        aria-describedby={descriptionId}
        aria-labelledby={titleId}
        aria-modal="true"
        className="grid max-h-[calc(100dvh-3rem)] w-[min(30rem,calc(100vw-2rem))] grid-rows-[auto_minmax(0,1fr)_auto] overflow-hidden rounded-overlay border border-[rgba(148,163,184,0.22)] bg-white text-slate-900 shadow-floating motion-safe:animate-popover-enter"
        role="dialog"
      >
        <div className="flex items-start justify-between gap-3 border-b border-[rgba(148,163,184,0.16)] px-4 py-3">
          <div className="min-w-0">
            <p className="m-0 text-[0.62rem] font-black uppercase tracking-[0.16em] text-slate-500">
              Sauvegarde dispatch
            </p>
            <h2 id={titleId} className="m-0 mt-1 text-[1rem] font-semibold leading-5 text-slate-900">
              Restaurer cette sauvegarde ?
            </h2>
            <p id={descriptionId} className="m-0 mt-1.5 text-[0.72rem] leading-5 text-slate-500">
              Les attelages et les chauffeurs reviennent aussi à ceux de la sauvegarde. Vérifiez les impacts Data Manager et Chargé PM avant de confirmer la restauration.
            </p>
          </div>
          <button
            type="button"
            className="inline-flex h-8 w-8 flex-none items-center justify-center rounded-full border border-[rgba(71,85,105,0.16)] bg-white text-slate-500 hover:bg-slate-50 hover:text-slate-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-300/40"
            aria-label="Fermer"
            onClick={onCancel}
          >
            <XMarkIcon className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>

        <div className="overflow-y-auto px-4 py-3">
          <DispatchRestoreImpactPreview isLoading={isLoading} preview={preview} />
        </div>

        <div className="flex items-center justify-between gap-3 border-t border-[rgba(148,163,184,0.16)] bg-slate-50/80 px-4 py-3">
          {blockedReason ? (
            <p className="m-0 max-w-[17rem] text-[0.62rem] font-semibold leading-4 text-[#972d24]" role="status">
              {blockedReason}
            </p>
          ) : <span />}
          <div className="flex shrink-0 items-center gap-2">
            <button
              type="button"
              className="inline-flex h-8 items-center rounded-sm border border-[rgba(71,85,105,0.18)] bg-white px-3 text-[0.72rem] font-semibold text-slate-600 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-300"
              onClick={onCancel}
            >
              Annuler
            </button>
            <button
              type="button"
              className={DISPATCH_RESTORE_MODAL_PRIMARY_BUTTON_CLASS_NAME}
              disabled={isConfirmDisabled}
              title={isConfirmDisabled && blockedReason ? blockedReason : undefined}
              onClick={onConfirm}
            >
              Restaurer
            </button>
          </div>
        </div>
      </section>
    </div>,
    getReactPortalRoot(),
  );
}

function DispatchRestoreFinalConfirmationPopover({
  preview = null,
  isConfirmDisabled = false,
  blockedReason = "",
  onCancel,
  onConfirm,
}) {
  const titleId = React.useId();
  const descriptionId = React.useId();

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onCancel?.();
      }
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleEscape);
    };
  }, [onCancel]);

  if (typeof document === "undefined") {
    return null;
  }

  return createPortal(
    <div
      className="fixed inset-0 z-modal isolate flex items-center justify-center bg-slate-950/25 px-4 py-6 font-sans sm:px-6"
      role="presentation"
      data-dispatch-restore-final-popover="true"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          onCancel?.();
        }
      }}
    >
      <section
        aria-describedby={descriptionId}
        aria-labelledby={titleId}
        aria-modal="true"
        className="grid max-h-[calc(100dvh-3rem)] w-[min(30rem,calc(100vw-2rem))] grid-rows-[auto_minmax(0,1fr)_auto] overflow-hidden rounded-overlay border border-[rgba(197,82,74,0.22)] bg-white text-slate-900 shadow-floating motion-safe:animate-popover-enter"
        role="alertdialog"
      >
        <div className="flex items-start justify-between gap-3 border-b border-[rgba(148,163,184,0.16)] px-4 py-3">
          <div className="min-w-0">
            <p className="m-0 text-[0.62rem] font-black uppercase tracking-[0.16em] text-[#b24339]">
              Confirmation requise
            </p>
            <h2 id={titleId} className="m-0 mt-1 text-[1rem] font-semibold leading-5 text-slate-900">
              Confirmer la restauration ?
            </h2>
            <p id={descriptionId} className="m-0 mt-1.5 text-[0.72rem] leading-5 text-slate-500">
              Cette sauvegarde contient un impact à confirmer. Vérifiez les éléments touchés avant de restaurer.
            </p>
          </div>
          <button
            type="button"
            className="inline-flex h-8 w-8 flex-none items-center justify-center rounded-full border border-[rgba(71,85,105,0.16)] bg-white text-slate-500 hover:bg-slate-50 hover:text-slate-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#b24339]/30"
            aria-label="Fermer"
            onClick={onCancel}
          >
            <XMarkIcon className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>

        <div className="grid gap-2 overflow-y-auto px-4 py-3">
          <DispatchRestoreImpactPreview preview={preview} />
          <div className="rounded-sm border border-[rgba(197,82,74,0.18)] bg-red-50 px-3 py-2">
            <p className="m-0 text-[0.72rem] font-semibold leading-5 text-[#972d24]">
              Cette action appliquera la restauration avec les confirmations indiquées ci-dessus.
            </p>
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 border-t border-[rgba(148,163,184,0.16)] bg-slate-50/80 px-4 py-3">
          {blockedReason ? (
            <p className="m-0 max-w-[17rem] text-[0.62rem] font-semibold leading-4 text-[#972d24]" role="status">
              {blockedReason}
            </p>
          ) : <span />}
          <div className="flex shrink-0 items-center gap-2">
            <button
              type="button"
              className="inline-flex h-8 items-center rounded-sm border border-[rgba(71,85,105,0.18)] bg-white px-3 text-[0.72rem] font-semibold text-slate-600 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-300"
              onClick={onCancel}
            >
              Retour
            </button>
            <button
              type="button"
              className="inline-flex h-8 items-center rounded-sm border border-[#b24339] bg-[#b24339] px-3 text-[0.72rem] font-bold text-white hover:bg-[#972d24] disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#b24339]/35 focus-visible:ring-offset-2"
              disabled={isConfirmDisabled}
              title={isConfirmDisabled && blockedReason ? blockedReason : undefined}
              onClick={onConfirm}
            >
              Restaurer
            </button>
          </div>
        </div>
      </section>
    </div>,
    getReactPortalRoot(),
  );
}

const SOURCE_CHANGE_INTEGRITY_CONFLICT_LABELS = Object.freeze({
  canonical_delivery_id_duplicate: "Identifiant de livraison canonique dupliqué",
  canonical_delivery_id_missing: "Identifiant de livraison canonique absent",
  dispatch_delivery_id_duplicate: "Identifiant de livraison du dispatch dupliqué",
  dispatch_delivery_id_missing: "Identifiant de livraison du dispatch absent",
  dispatch_delivery_not_in_source: "Livraison du dispatch absente du Data Manager",
  source_delivery_id_duplicate: "Identifiant de livraison Data Manager dupliqué",
  source_delivery_id_missing: "Identifiant de livraison Data Manager absent",
  source_delivery_not_in_dispatch: "Livraison Data Manager absente du dispatch",
  source_payload_delivery_not_canonical: "Livraison du payload absente des données canoniques",
  non_renderable_trip: "Étapes de tournée incomplètes",
});

function getSourceChangeIntegrityConflicts(detail) {
  return Array.isArray(detail?.integrity_conflicts)
    ? detail.integrity_conflicts
    : Array.isArray(detail?.integrityConflicts)
      ? detail.integrityConflicts
      : [];
}

function isNonRenderableTripConflict(conflict) {
  const code = normalizeInlineText(
    conflict && typeof conflict === "object" ? conflict.code : conflict,
  ).toLowerCase();
  return code === "non_renderable_trip";
}

function isNonRenderableTripDetail(detail) {
  return normalizeInlineText(detail?.change_type || detail?.changeType).toLowerCase() === "non_renderable_trip"
    || getSourceChangeIntegrityConflicts(detail).some(isNonRenderableTripConflict);
}

function normalizeIntegrityConflictIds(...values) {
  const ids = [];
  values.forEach((value) => {
    (Array.isArray(value) ? value : [value]).forEach((candidate) => {
      const normalized = normalizeInlineText(candidate);
      if (normalized) {
        ids.push(normalized);
      }
    });
  });
  return [...new Set(ids)];
}

function buildNonRenderableTripGroups(details) {
  const groups = new Map();
  (Array.isArray(details) ? details : []).forEach((detail, detailIndex) => {
    if (!detail || !isNonRenderableTripDetail(detail)) {
      return;
    }
    const matchingConflicts = getSourceChangeIntegrityConflicts(detail).filter(isNonRenderableTripConflict);
    const conflicts = matchingConflicts.length > 0 ? matchingConflicts : [{}];
    conflicts.forEach((conflict) => {
      const truckId = normalizeInlineText(conflict?.truck_id || detail?.truck_id);
      const truckLabel = normalizeInlineText(
        conflict?.truck_name
        || conflict?.truck_label
        || detail?.truck_label
        || detail?.truck_name
        || truckId,
      );
      const tripId = normalizeInlineText(conflict?.trip_id || detail?.trip_id);
      const {
        hasMissingOrderIds,
        orderIds,
      } = resolveNonRenderableConflictAffectedIds(conflict, detail);
      const scopeOrderIds = normalizeIntegrityConflictIds(
        conflict?.order_ids,
        conflict?.order_id,
        detail?.order_ids,
        detail?.order_id,
      ).sort();
      const groupKey = `${truckId || truckLabel || "unknown"}::${tripId || scopeOrderIds.join("|") || orderIds.join("|") || detailIndex}`;
      const group = groups.get(groupKey) || {
        activeOrderCount: 0,
        conflictId: "",
        conflictVersion: "",
        fallbackOrderIds: new Set(),
        missingOrderIds: new Set(),
        repairBlockReason: "",
        repairEligible: false,
        suspendTargets: new Map(),
        tripId,
        truckId,
        truckLabel,
      };
      const conflictId = normalizeInlineText(conflict?.conflict_id || detail?.conflict_id);
      const conflictVersion = normalizeInlineText(conflict?.conflict_version || detail?.conflict_version);
      if (!group.conflictId || group.conflictId === conflictId) {
        group.conflictId = conflictId || group.conflictId;
      } else {
        group.conflictId = "";
      }
      if (!group.conflictVersion || group.conflictVersion === conflictVersion) {
        group.conflictVersion = conflictVersion || group.conflictVersion;
      } else {
        group.conflictVersion = "";
      }
      group.repairEligible = group.repairEligible || conflict?.repair_eligible === true || detail?.repair_eligible === true;
      group.repairBlockReason = normalizeInlineText(
        conflict?.repair_block_reason || detail?.repair_block_reason || group.repairBlockReason,
      );
      const suspendTargets = Array.isArray(conflict?.suspend_targets)
        ? conflict.suspend_targets
        : Array.isArray(detail?.suspend_targets) ? detail.suspend_targets : [];
      suspendTargets.forEach((target) => {
        const targetId = normalizeInlineText(target?.target_id || target?.targetId);
        const orderId = normalizeInlineText(target?.order_id || target?.orderId);
        if (targetId && orderId) {
          group.suspendTargets.set(targetId, {
            orderId,
            suspendBlockReason: normalizeInlineText(
              target?.suspend_block_reason || target?.suspendBlockReason,
            ),
            suspendEligible: target?.suspend_eligible !== false,
            targetId,
          });
        }
      });
      orderIds.forEach((orderId) => {
        (hasMissingOrderIds ? group.missingOrderIds : group.fallbackOrderIds).add(orderId);
      });
      group.activeOrderCount = Math.max(
        group.activeOrderCount,
        Number.isFinite(Number(conflict?.active_order_count))
          ? Math.max(0, Math.round(Number(conflict.active_order_count)))
          : 0,
      );
      groups.set(groupKey, group);
    });
  });
  return [...groups.values()].map((group) => {
    const { fallbackOrderIds, missingOrderIds, suspendTargets, ...visibleGroup } = group;
    return {
      ...visibleGroup,
      orderIds: [...(missingOrderIds.size > 0 ? missingOrderIds : fallbackOrderIds)],
      suspendTargets: [...suspendTargets.values()],
    };
  });
}

function formatSourceChangeIntegrityConflicts(detail) {
  const conflicts = getSourceChangeIntegrityConflicts(detail);
  return [...new Set(conflicts.map((conflict) => {
    const code = normalizeInlineText(
      conflict && typeof conflict === "object" ? conflict.code : conflict,
    ).toLowerCase();
    const deliveryId = normalizeInlineText(
      conflict && typeof conflict === "object"
        ? conflict.delivery_id || conflict.deliveryId
        : "",
    );
    const label = SOURCE_CHANGE_INTEGRITY_CONFLICT_LABELS[code] || code || "Correspondance de livraison ambiguë";
    return deliveryId ? `${label} (${deliveryId})` : label;
  }).filter(Boolean))];
}

function formatNonRenderableTripBlockReason(reason, fallback = "Action indisponible.") {
  const normalizedReason = normalizeInlineText(reason).toLowerCase();
  if (normalizedReason === "published_truck") {
    return "Tournée publiée. Dépubliez-la avant de la modifier.";
  }
  if (normalizedReason === "dispatch_day_closed") {
    return "Cette journée est fermée.";
  }
  if (normalizedReason === "conflict_not_targetable") {
    return "La commande ne peut pas être identifiée sans ambiguïté.";
  }
  return fallback;
}

function NonRenderableTripConfirmPanel({
  action,
  actionsDisabled = false,
  group,
  isBusy = false,
  onCancel,
  onConfirm,
  target = null,
}) {
  const truckLabel = group?.truckLabel || group?.truckId || "ce camion";
  const orderId = target?.orderId || "cette commande";
  const isSuspendAction = action === "suspend";
  return (
    <div className="grid gap-2 p-1" data-non-renderable-trip-confirmation={action}>
      <div className="grid gap-1 px-1.5 pt-0.5">
        <p className="m-0 text-[0.8rem] font-semibold text-slate-800">
          {isSuspendAction
            ? `Placer ${orderId} dans Suspendues ?`
            : `Réparer ${truckLabel} ?`}
        </p>
        <p className="m-0 text-[0.68rem] leading-5 text-slate-500">
          {isSuspendAction
            ? `La commande et ses livraisons seront retirées de ${truckLabel} et pourront être réaffectées.`
            : "Seule cette tournée sera recalculée. Les commandes resteront sur ce camion, mais leur ordre et leurs heures peuvent changer."}
        </p>
      </div>
      <div className="flex items-center justify-end gap-2 border-t border-slate-100 px-1.5 pt-2">
        <ActionButton
          type="button"
          tone="ghost"
          size="sm"
          autoFocus
          disabled={isBusy}
          onClick={onCancel}
        >
          Annuler
        </ActionButton>
        <ActionButton
          type="button"
          tone={isSuspendAction ? "danger" : "accent"}
          size="sm"
          disabled={isBusy || actionsDisabled}
          onClick={onConfirm}
        >
          {isBusy
            ? (isSuspendAction ? "Suspension…" : "Réparation…")
            : (isSuspendAction ? "Placer dans Suspendues" : "Réparer la tournée")}
        </ActionButton>
      </div>
    </div>
  );
}

function SourceChangeIntegrityConflictsSection({
  actionsDisabled = false,
  details,
  failedRepairConflictId = "",
  hasRuntimeRevision = false,
  isBusy = false,
  onRequestAction = null,
}) {
  const safeDetails = Array.isArray(details) ? details.filter(Boolean) : [];
  if (safeDetails.length === 0) {
    return null;
  }
  const nonRenderableTripGroups = buildNonRenderableTripGroups(safeDetails);
  const identifierConflictDetails = safeDetails.filter((detail) => !isNonRenderableTripDetail(detail));
  const visibleConflictCount = nonRenderableTripGroups.length + identifierConflictDetails.length;
  const hasNonRenderableTrips = nonRenderableTripGroups.length > 0;
  const hasIdentifierConflicts = identifierConflictDetails.length > 0;
  const visibleNonRenderableTripGroups = nonRenderableTripGroups.slice(0, MAX_VISIBLE_DETAILS);
  const visibleIdentifierConflictDetails = identifierConflictDetails.slice(
    0,
    Math.max(0, MAX_VISIBLE_DETAILS - visibleNonRenderableTripGroups.length),
  );

  return (
    <section
      aria-label="Conflits d’intégrité des commandes"
      className={SOURCE_CHANGE_GROUP_SECTION_CLASS_NAME}
      data-source-change-integrity-conflicts="true"
    >
      <div className={SOURCE_CHANGE_GROUP_HEADER_CLASS_NAME}>
        <ExclamationTriangleIcon className={SOURCE_CHANGE_GROUP_HEADER_ICON_CLASS_NAME} aria-hidden="true" />
        <span className="min-w-0 flex-1 truncate">
          {hasNonRenderableTrips ? "Tournées à vérifier" : "Conflits d’intégrité"}
        </span>
        <span className={SOURCE_CHANGE_COUNT_BADGE_CLASS_NAME}>
          {visibleConflictCount}
        </span>
      </div>
      <div className="grid gap-1 border-t border-[var(--dispatch-update-border)] bg-white p-2">
        {hasNonRenderableTrips ? (
          <>
            <p className="m-0 px-1 text-[0.6rem] font-medium leading-4 text-rose-700">
              Certaines commandes affectées ont des étapes manquantes dans le Gantt.
            </p>
            {visibleNonRenderableTripGroups.map((group, index) => {
              const orderCount = Math.max(group.orderIds.length, group.activeOrderCount, 1);
              const truckLabel = group.truckLabel || group.truckId || "Camion à identifier";
              const hasSecureDescriptor = Boolean(
                group.conflictId && group.conflictVersion && hasRuntimeRevision,
              );
              const canRequestAction = hasSecureDescriptor && typeof onRequestAction === "function";
              const repairDisabled = actionsDisabled || isBusy || group.repairEligible !== true;
              const repairDisabledReason = actionsDisabled || isBusy
                ? "Une autre action planning est en cours ou l’aperçu doit être vérifié."
                : formatNonRenderableTripBlockReason(
                  group.repairBlockReason,
                  "Cette tournée ne peut pas être réparée automatiquement.",
                );
              return (
                <article
                  key={`${group.truckId || truckLabel}-${group.tripId || index}`}
                  className={joinClasses([
                    "grid gap-1.5 rounded-[4px] bg-rose-50/60 px-2 py-1.5",
                    SOURCE_CHANGE_INNER_CARD_BORDER_CLASS_NAME,
                  ])}
                  data-non-renderable-trip="true"
                >
                  <span className="text-[0.68rem] font-semibold text-slate-700">
                    {truckLabel} · étapes manquantes
                  </span>
                  <span className="text-[0.6rem] leading-4 text-rose-700">
                    {group.orderIds.length > 0
                      ? `Commande${group.orderIds.length > 1 ? "s" : ""} : ${group.orderIds.join(", ")}`
                      : `${orderCount} commande${orderCount > 1 ? "s" : ""} concernée${orderCount > 1 ? "s" : ""}`}
                  </span>
                  {canRequestAction ? (
                    <div className="grid gap-1" data-non-renderable-trip-actions="true">
                      <button
                        type="button"
                        className={joinClasses([
                          "w-full rounded-[7px] border px-2 py-1 text-left text-[0.62rem] font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/35",
                          repairDisabled
                            ? "cursor-not-allowed border-slate-200 bg-slate-50 text-slate-400"
                            : "border-accent bg-accent text-white hover:brightness-95",
                        ])}
                        disabled={repairDisabled}
                        title={repairDisabled ? repairDisabledReason : undefined}
                        onClick={(event) => onRequestAction({
                          action: "repair",
                          group,
                          triggerElement: event.currentTarget,
                        })}
                      >
                        {isBusy ? "Réparation…" : "Réparer la tournée"}
                      </button>
                      {group.suspendTargets.map((target) => {
                        const suspendDisabled = actionsDisabled || isBusy || target.suspendEligible !== true;
                        const isPriorityFallback = failedRepairConflictId === group.conflictId;
                        return (
                          <button
                            key={target.targetId}
                            type="button"
                            className={joinClasses([
                              "w-full rounded-[7px] border px-2 py-1 text-left text-[0.62rem] font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-300",
                              suspendDisabled
                                ? "cursor-not-allowed border-slate-200 bg-slate-50 text-slate-400"
                                : isPriorityFallback
                                  ? "border-rose-300 bg-rose-100 text-rose-800 hover:bg-rose-200"
                                  : "border-rose-200 bg-white text-rose-700 hover:bg-rose-50",
                            ])}
                            disabled={suspendDisabled}
                            title={suspendDisabled
                              ? formatNonRenderableTripBlockReason(target.suspendBlockReason)
                              : undefined}
                            onClick={(event) => onRequestAction({
                              action: "suspend",
                              group,
                              target,
                              triggerElement: event.currentTarget,
                            })}
                          >
                            Placer {target.orderId} dans Suspendues
                          </button>
                        );
                      })}
                    </div>
                  ) : null}
                </article>
              );
            })}
          </>
        ) : null}
        {hasIdentifierConflicts ? (
          <p className="m-0 px-1 text-[0.6rem] font-medium leading-4 text-rose-700">
            Corrigez les identifiants de livraison dans le Data Manager avant toute action automatique.
          </p>
        ) : null}
        {visibleIdentifierConflictDetails.map((detail, index) => {
          const conflicts = formatSourceChangeIntegrityConflicts(detail);
          return (
            <article
              key={normalizeInlineText(detail?.change_revision) || normalizeInlineText(detail?.order_id) || index}
              className={joinClasses([
                "grid gap-0.5 rounded-[4px] bg-rose-50/60 px-2 py-1.5",
                SOURCE_CHANGE_INNER_CARD_BORDER_CLASS_NAME,
              ])}
            >
              <span className="text-[0.68rem] font-semibold text-slate-700">
                {formatDetailLabel(detail)}
              </span>
              <span className="text-[0.6rem] leading-4 text-rose-700">
                {conflicts.join(" · ") || "Correspondance de livraison ambiguë"}
              </span>
            </article>
          );
        })}
        {visibleConflictCount > MAX_VISIBLE_DETAILS ? (
          <span className="px-1 text-[0.58rem] font-medium text-rose-600">
            + {visibleConflictCount - MAX_VISIBLE_DETAILS} autre{visibleConflictCount - MAX_VISIBLE_DETAILS > 1 ? "s" : ""} conflit{visibleConflictCount - MAX_VISIBLE_DETAILS > 1 ? "s" : ""}
          </span>
        ) : null}
      </div>
    </section>
  );
}

function resolveMixedEndOfDayLoadOrderGroups(detail) {
  const missingOrderIds = normalizeEndOfDayLoadOrderIds(detail?.missing_order_ids);
  const explicitDateChangedOrderIds = normalizeEndOfDayLoadOrderIds(detail?.date_changed_order_ids);
  if (explicitDateChangedOrderIds.length > 0) {
    return { dateChangedOrderIds: explicitDateChangedOrderIds, missingOrderIds };
  }
  const missingOrderIdSet = new Set(missingOrderIds);
  return {
    dateChangedOrderIds: normalizeEndOfDayLoadOrderIds(detail?.order_ids).filter(
      (orderId) => !missingOrderIdSet.has(orderId),
    ),
    missingOrderIds,
  };
}

function MixedEndOfDayLoadSourceChangesSection({ details }) {
  const safeDetails = Array.isArray(details)
    ? details.filter((detail) => detail && isMixedOrphanEndOfDayLoadDetail(detail))
    : [];
  if (safeDetails.length === 0) {
    return null;
  }

  return (
    <section
      aria-label="Chargés PM mixtes à corriger"
      className={SOURCE_CHANGE_GROUP_SECTION_CLASS_NAME}
      data-source-mixed-end-of-day-loads="true"
    >
      <div className={SOURCE_CHANGE_GROUP_HEADER_CLASS_NAME}>
        <ExclamationTriangleIcon className={SOURCE_CHANGE_GROUP_HEADER_ICON_CLASS_NAME} aria-hidden="true" />
        <span className="min-w-0 flex-1 truncate">
          Chargés PM mixtes
        </span>
        <span className={SOURCE_CHANGE_COUNT_BADGE_CLASS_NAME}>
          {safeDetails.length}
        </span>
      </div>
      <div className="grid gap-1 border-t border-[var(--dispatch-update-border)] bg-white p-2">
        <p className="m-0 px-1 text-[0.6rem] font-medium leading-4 text-amber-800">
          Ce groupe combine des commandes à supprimer et des dates déplacées. Séparez ou corrigez le groupe Chargé PM avant toute action.
        </p>
        {safeDetails.slice(0, MAX_VISIBLE_DETAILS).map((detail, index) => {
          const { dateChangedOrderIds, missingOrderIds } = resolveMixedEndOfDayLoadOrderGroups(detail);
          const targetTruckLabel = normalizeInlineText(
            detail?.target_truck_label || detail?.target_truck_id,
          );
          return (
            <article
              key={normalizeInlineText(detail?.marker_key) || index}
              className={joinClasses([
                "grid gap-0.5 rounded-[4px] bg-amber-50/60 px-2 py-1.5",
                SOURCE_CHANGE_INNER_CARD_BORDER_CLASS_NAME,
              ])}
              data-source-mixed-end-of-day-load={normalizeInlineText(detail?.marker_key) || "mixed"}
            >
              <span className="text-[0.68rem] font-semibold text-slate-700">
                {formatRemovedEndOfDayLoadTitle(detail)}
                {targetTruckLabel ? ` · ${targetTruckLabel}` : ""}
              </span>
              <span className="text-[0.6rem] leading-4 text-amber-800">
                À supprimer&nbsp;: {missingOrderIds.join(", ") || "à vérifier"}
                {" · "}
                Dates déplacées&nbsp;: {dateChangedOrderIds.join(", ") || "à vérifier"}
              </span>
            </article>
          );
        })}
        {safeDetails.length > MAX_VISIBLE_DETAILS ? (
          <span className="px-1 text-[0.58rem] font-medium text-amber-700">
            + {safeDetails.length - MAX_VISIBLE_DETAILS} autre{safeDetails.length - MAX_VISIBLE_DETAILS > 1 ? "s" : ""} groupe{safeDetails.length - MAX_VISIBLE_DETAILS > 1 ? "s" : ""}
          </span>
        ) : null}
      </div>
    </section>
  );
}

function BlockedExecutionRemovalsSection({ details }) {
  const safeDetails = Array.isArray(details) ? details.filter(Boolean) : [];
  if (safeDetails.length === 0) {
    return null;
  }

  return (
    <section
      aria-label="Exécutions à vérifier"
      className={SOURCE_CHANGE_GROUP_SECTION_CLASS_NAME}
      data-blocked-execution-removals="true"
    >
      <div className={SOURCE_CHANGE_GROUP_HEADER_CLASS_NAME}>
        <ExclamationTriangleIcon className={SOURCE_CHANGE_GROUP_HEADER_ICON_CLASS_NAME} aria-hidden="true" />
        <span className="min-w-0 flex-1 truncate">
          Exécutions à vérifier
        </span>
        <span className={SOURCE_CHANGE_COUNT_BADGE_CLASS_NAME}>
          {safeDetails.length}
        </span>
      </div>
      <div className="grid gap-1 border-t border-[var(--dispatch-update-border)] bg-white p-2">
        <p className="m-0 px-1 text-[0.6rem] font-medium leading-4 text-rose-700">
          La restauration automatique est bloquée. Vérifiez ces exécutions avant de publier le dispatch.
        </p>
        {safeDetails.slice(0, MAX_VISIBLE_DETAILS).map((detail, index) => {
          const recoveryBlockReason = formatExecutionRecoveryBlockReason(detail?.recovery_block_reason)
            || "restauration automatique non admissible";
          const truckLabel = formatSourceChangeTruckLabel(detail);
          return (
            <article
              key={normalizeInlineText(detail?.change_revision) || normalizeInlineText(detail?.order_id) || index}
              className={joinClasses([
                "grid gap-0.5 rounded-[4px] bg-rose-50/60 px-2 py-1.5",
                SOURCE_CHANGE_INNER_CARD_BORDER_CLASS_NAME,
              ])}
              data-blocked-execution-order={normalizeInlineText(detail?.order_id)}
            >
              <span className="text-[0.68rem] font-semibold text-slate-700">
                {formatDetailLabel(detail)} · {truckLabel}
              </span>
              <span className="text-[0.6rem] leading-4 text-rose-700">
                {recoveryBlockReason}
              </span>
            </article>
          );
        })}
        {safeDetails.length > MAX_VISIBLE_DETAILS ? (
          <span className="px-1 text-[0.58rem] font-medium text-rose-600">
            + {safeDetails.length - MAX_VISIBLE_DETAILS} autre{safeDetails.length - MAX_VISIBLE_DETAILS > 1 ? "s" : ""} exécution{safeDetails.length - MAX_VISIBLE_DETAILS > 1 ? "s" : ""}
          </span>
        ) : null}
      </div>
    </section>
  );
}

function ProtectedSourceChangesSection({
  details,
  actionsDisabled = false,
  isBusy = false,
  isClosedDispatchDay = false,
  onAcknowledge,
}) {
  const [showAllDetails, setShowAllDetails] = useState(false);
  const safeDetails = Array.isArray(details) ? details.filter(Boolean) : [];
  if (safeDetails.length === 0) {
    return null;
  }
  const hiddenCount = Math.max(0, safeDetails.length - MAX_VISIBLE_DETAILS);
  const visibleDetails = showAllDetails
    ? safeDetails
    : safeDetails.slice(0, MAX_VISIBLE_DETAILS);

  const disabledReason = isClosedDispatchDay
    ? "Journée clôturée : rouvrez-la avant de conserver le dispatch actuel."
    : actionsDisabled
      ? "Une opération de planning est en cours. Attendez sa fin avant de conserver le dispatch actuel."
      : isBusy
        ? "La vérification M-à-J est en cours."
        : "";
  const sectionTitle = resolveProtectedSourceChangesSectionTitle(safeDetails);
  const requiresUnfreeze = protectedSourceChangeRequiresUnfreeze(safeDetails);

  return (
    <section
      aria-label={sectionTitle}
      className={SOURCE_CHANGE_GROUP_SECTION_CLASS_NAME}
      data-protected-source-changes-section="true"
    >
      <div
        className={SOURCE_CHANGE_GROUP_HEADER_CLASS_NAME}
        data-protected-source-changes-header="true"
      >
        <PauseCircleIcon className={SOURCE_CHANGE_GROUP_HEADER_ICON_CLASS_NAME} aria-hidden="true" />
        <span className="min-w-0 flex-1 truncate">
          {sectionTitle}
        </span>
        <span className={SOURCE_CHANGE_COUNT_BADGE_CLASS_NAME}>
          {safeDetails.length}
        </span>
      </div>

      <div className="grid gap-1.25 border-t border-[var(--dispatch-update-border)] bg-white px-1 py-1">
        <p
          className="m-0 flex items-start gap-1.5 text-[0.6rem] font-medium leading-4 text-amber-800"
          data-protected-source-changes-warning="true"
        >
          <ExclamationTriangleIcon className="mt-px h-3.25 w-3.25 flex-none" aria-hidden="true" />
          <span>
            {requiresUnfreeze
              ? "Écart avec des commandes 'Gelée', 'Confirmée' ou 'En cours' : mise à jour automatique impossible. Dégeler les commandes pour pouvoir ensuite les corriger dans « Cmd modifiées vers Suspendues »."
              : "Commandes figées (gel, portail, tonnage réel, Chargé PM) : mise à jour automatique impossible. Conservez le dispatch actuel ou corrigez l’écart dans le Data Manager."}
          </span>
        </p>
        {isClosedDispatchDay ? (
          <p className="m-0 text-[0.6rem] font-semibold leading-4 text-[#b24339]">
            Rouvrez la journée pour utiliser « Conserver Dispatch ».
          </p>
        ) : null}

        {visibleDetails.map((detail, index) => {
          const orderId = normalizeInlineText(detail?.order_id) || "Commande non identifiée";
          const clientLabel = normalizeInlineText(
            detail?.client_name || detail?.delivery_client_name || detail?.customer_name,
          ) || "Client non identifié";
          const truckLabel = formatSourceChangeTruckLabel(detail);
          const changedFieldLabels = resolveProtectedSourceChangeFieldLabels(detail?.changed_fields);
          const fallbackReason = changedFieldLabels.length === 0
            ? formatGhostReason(detail?.ghost_reason)
              || formatSourceChangeInitialReasonLabel(detail?.initial_reason_label)
              || formatExecutionRecoveryBlockReason(detail?.recovery_block_reason)
              || formatProtectedSourceChangeType(detail)
            : null;
          const changeRevision = normalizeInlineText(detail?.change_revision);
          const hasCurrentRevision = /^[a-f0-9]{64}$/i.test(changeRevision);
          const acknowledgeDisabled = isClosedDispatchDay
            || actionsDisabled
            || isBusy
            || !hasCurrentRevision
            || typeof onAcknowledge !== "function";
          const acknowledgeTitle = disabledReason
            || (!hasCurrentRevision
              ? "Rafraîchissez M-à-J commandes pour obtenir la révision actuelle."
              : "Conserver l’affectation et les valeurs actuelles du dispatch pour cette commande.");

          return (
            <article
              key={changeRevision || normalizeInlineText(detail?.delivery_id) || `${orderId}-${index}`}
              className={joinClasses([
                "grid gap-0.25 rounded-[4px] bg-[rgba(255,250,242,0.72)] px-2 py-1.25",
                SOURCE_CHANGE_INNER_CARD_BORDER_CLASS_NAME,
              ])}
              data-protected-source-change-order={orderId}
              data-protected-source-change-state={resolveProtectedSourceChangeState(detail)}
              data-source-change-category={normalizeInlineText(detail?.change_category || detail?.category)}
            >
              <div className="flex min-w-0 items-baseline gap-1.5">
                <span className="min-w-0 truncate text-[0.7rem] font-semibold text-slate-700">
                  {clientLabel}
                </span>
                <span className="shrink-0 text-[0.7rem] font-semibold text-slate-700">
                  · {orderId}
                </span>
                <span
                  className={SOURCE_CHANGE_TRUCK_BADGE_CLASS_NAME}
                  title={`Camion · ${truckLabel}`}
                >
                  {truckLabel}
                </span>
              </div>

              <div className="flex min-w-0 items-start justify-between gap-2">
                <div className="min-w-0 flex-1 overflow-hidden">
                  {changedFieldLabels.length === 1 ? (
                    <span className="block text-[0.64rem] leading-4 text-slate-500">
                      {`Champ modifié\u00A0: ${changedFieldLabels[0]}`}
                    </span>
                  ) : changedFieldLabels.length > 1 ? (
                    <div className="grid gap-0.5">
                      <span className="text-[0.64rem] font-medium leading-4 text-slate-500">
                        Modifiés&nbsp;:
                      </span>
                      {changedFieldLabels.map((label, fieldIndex) => (
                        <span
                          key={`${orderId}-changed-field-${fieldIndex}-${label}`}
                          className="block text-[0.64rem] leading-4 text-slate-500"
                        >
                          {label}
                        </span>
                      ))}
                    </div>
                  ) : fallbackReason ? (
                    <span className="block truncate text-[0.64rem] text-slate-500">
                      {fallbackReason}
                    </span>
                  ) : null}
                </div>
                <button
                  type="button"
                  aria-label={`Conserver Dispatch pour la commande ${orderId}`}
                  className={joinClasses([
                    SOURCE_CHANGES_MENU_INLINE_ACTION_BUTTON_CLASS_NAME,
                    "text-[0.59rem] font-bold",
                  ])}
                  data-acknowledge-protected-source-change={orderId}
                  disabled={acknowledgeDisabled}
                  title={acknowledgeTitle}
                  onClick={() => onAcknowledge?.(detail)}
                >
                  Conserver Dispatch
                </button>
              </div>
            </article>
          );
        })}
        {hiddenCount > 0 ? (
          <button
            type="button"
            className="flex w-full items-center justify-start rounded-[7px] px-2 py-0.5 text-left text-[0.61rem] font-medium text-amber-700 hover:bg-amber-50 hover:text-amber-900 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-amber-300"
            aria-expanded={showAllDetails}
            data-protected-source-changes-toggle="true"
            onClick={() => setShowAllDetails((currentValue) => !currentValue)}
          >
            {showAllDetails
              ? "Réduire la liste"
              : `+ ${hiddenCount} autre${hiddenCount > 1 ? "s" : ""} commande${hiddenCount > 1 ? "s" : ""} protégée${hiddenCount > 1 ? "s" : ""}`}
          </button>
        ) : null}
      </div>
    </section>
  );
}
function AcknowledgeProtectedSourceChangeConfirmPanel({
  detail,
  actionsDisabled = false,
  isBusy = false,
  isClosedDispatchDay = false,
  onCancel,
  onConfirm,
}) {
  const fieldChanges = resolveProtectedFieldChanges(detail);
  const changeCategory = formatSourceChangeCategory(detail);
  const {
    executionProofs,
    protectionReasons,
  } = resolveSourceChangeBackendEvidence(detail);
  const orderId = normalizeInlineText(detail?.order_id) || "Commande non identifiée";
  const clientLabel = normalizeInlineText(
    detail?.client_name || detail?.delivery_client_name || detail?.customer_name,
  ) || "Client non identifié";
  const blockedReason = isClosedDispatchDay
    ? "Rouvrez la journée avant de conserver le dispatch actuel."
    : actionsDisabled
      ? "Une autre modification du Dispatch est en cours. Réessayez dans quelques instants."
      : "";

  return (
    <div className="grid gap-2 p-1" data-acknowledge-protected-confirmation="true">
      <div className="grid gap-1 px-1.5 pt-0.5">
        <p className="m-0 text-[0.8rem] font-semibold text-slate-800">
          Conserver le dispatch actuel ?
        </p>
        <p className="m-0 text-[0.68rem] leading-5 text-slate-500">
          {clientLabel} · {orderId} · {formatSourceChangeTruckLabel(detail)}
        </p>
      </div>

      <div className="grid gap-1 rounded-[4px] border border-[rgba(217,119,6,0.18)] bg-[rgba(255,250,242,0.62)] p-1.5">
        <div className="flex items-center justify-between gap-2 rounded-[4px] bg-white px-2 py-1.5">
          <span className="text-[0.62rem] font-semibold text-slate-700">
            {formatProtectedSourceChangeType(detail)}
          </span>
          <span className="rounded-[4px] border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[0.56rem] font-bold text-amber-800">
            {formatProtectedSourceChangeState(detail)}
          </span>
        </div>
        {changeCategory || executionProofs.length > 0 || protectionReasons.length > 0 ? (
          <div className="grid gap-0.5 rounded-[4px] bg-white px-2 py-1.5 text-[0.6rem] leading-4 text-slate-500">
            {changeCategory ? <span>Catégorie : {changeCategory}</span> : null}
            {executionProofs.length > 0 ? <span>Preuves : {executionProofs.join(", ")}</span> : null}
            {protectionReasons.length > 0 ? <span>Protections : {protectionReasons.join(", ")}</span> : null}
          </div>
        ) : null}
        {fieldChanges.map((fieldChange) => (
          <div key={fieldChange.fieldName} className="grid gap-0.25 rounded-[4px] bg-white px-2 py-1.5 text-[0.61rem] leading-4">
            <span className="font-semibold text-slate-700">{fieldChange.label}</span>
            <span className="break-words text-slate-500">Dispatch actuel : {fieldChange.dispatchValue}</span>
            <span className="break-words text-[#92400e]">Data Manager : {fieldChange.sourceValue}</span>
          </div>
        ))}
      </div>

      <p className="m-0 rounded-[4px] border border-[rgba(13,148,136,0.18)] bg-teal-50 px-2 py-1.75 text-[0.64rem] font-medium leading-5 text-[#0f766e]">
        Les rapports utiliseront les valeurs et l’affectation actuelles du dispatch. Cette action ne dégèle, ne suspend et ne déplace pas la commande.
      </p>
      {blockedReason ? (
        <p className="m-0 px-1 text-[0.61rem] font-semibold text-[#b24339]" role="status">
          {blockedReason}
        </p>
      ) : null}

      <div className="flex items-center justify-end gap-1.5 px-0.5 pb-0.5">
        <ActionButton
          size="sm"
          tone="ghost"
          className="h-8! min-h-8! rounded-sm! px-2.75! shadow-none!"
          disabled={isBusy}
          onClick={onCancel}
        >
          Annuler
        </ActionButton>
        <ActionButton
          size="sm"
          tone="optimizer"
          className={SOURCE_CHANGES_MENU_CONFIRM_BUTTON_CLASS_NAME}
          disabled={isBusy || isClosedDispatchDay || actionsDisabled}
          onClick={onConfirm}
        >
          Conserver Dispatch
        </ActionButton>
      </div>
    </div>
  );
}
export function RemoveModifiedSourceChangesConfirmPanel({
  actionsDisabled = false,
  details,
  isBusy = false,
  onCancel,
  onConfirm,
}) {
  const safeDetails = Array.isArray(details) ? details : [];
  const modifiedDetails = safeDetails.filter((detail) => detail?.change_type !== "date_changed_remove");
  const dateChangedDetails = safeDetails.filter((detail) => detail?.change_type === "date_changed_remove");
  const alreadySuspendedCount = modifiedDetails.filter(
    (detail) => detail?.already_suspended === true,
  ).length;
  const assignedModifiedCount = Math.max(0, modifiedDetails.length - alreadySuspendedCount);
  const hasMixedModifiedDestinations = assignedModifiedCount > 0 && alreadySuspendedCount > 0;
  const previewRows = [
    ...buildGroupedSourceChangeRows(modifiedDetails),
    ...dateChangedDetails.map((detail, idx) => ({
      detail,
      rowKey: detail.delivery_id || detail.order_id || `date-changed-${idx}`,
      rowType: "detail",
    })),
  ];
  const remainingCount = Math.max(0, previewRows.length - MAX_VISIBLE_DETAILS);
  const visibleRows = previewRows.slice(0, MAX_VISIBLE_DETAILS);

  return (
    <div className="grid gap-2 p-1">
      <div className="grid gap-1 px-1.5 pt-0.5">
        <p className="m-0 text-[0.8rem] font-semibold text-slate-800">
          {hasMixedModifiedDestinations
            ? "Traiter les commandes modifiées"
            : alreadySuspendedCount > 0
              ? "Actualiser les commandes suspendues"
              : "Retirer les commandes modifiées"}
        </p>
        <p className="m-0 text-[0.68rem] leading-5 text-slate-500">
          {hasMixedModifiedDestinations
            ? "Les commandes affectées seront retirées de leur camion et transférées vers Suspendues; celles déjà suspendues seront actualisées sur place."
            : alreadySuspendedCount > 0
              ? "Ces commandes sont déjà dans Suspendues et seront actualisées sur place."
              : "Vérifiez le camion actuel de chaque commande avant de la transférer vers Suspendues."}
        </p>
      </div>

      <div className="grid gap-1 rounded-[4px] border border-[rgba(197,82,74,0.14)] bg-[rgba(254,242,242,0.42)] p-1.5">
        {visibleRows.map((row) => {
          if (row.rowType === "group") {
            return (
              <div
                key={row.rowKey}
                className="grid gap-1 rounded-[4px] border border-[rgba(217,119,6,0.16)] bg-[rgba(255,250,242,0.92)] px-2.25 py-2"
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex min-w-0 items-baseline gap-1.5">
                    <span className="min-w-0 truncate text-[0.72rem] font-semibold text-slate-800">
                      {row.title}
                    </span>
                    {row.truckLabel ? (
                      <span className="shrink-0 rounded-[4px] border border-[rgba(148,163,184,0.16)] bg-white px-1.25 py-px text-[0.58rem] font-medium text-slate-400">
                        {row.truckLabel}
                      </span>
                    ) : null}
                  </div>
                  <span className="shrink-0 rounded-[4px] border border-[rgba(217,119,6,0.18)] bg-white px-1.25 py-px text-[0.58rem] font-bold text-[#92400e]">
                    {row.subtitle}
                  </span>
                </div>
                <div className="grid gap-0.5 border-l border-[rgba(217,119,6,0.18)] pl-1.5">
                  {row.details.slice(0, MAX_VISIBLE_DETAILS).map((detail, idx) => {
                    const fieldTransitions = resolveProtectedFieldChanges(detail).filter(
                      (fieldChange) => fieldChange.hasValues,
                    );
                    return (
                      <div
                        key={detail.delivery_id || detail.order_id || idx}
                        className="grid min-w-0 gap-0.5 text-[0.62rem] text-slate-500"
                      >
                        <span className="truncate">
                          {detail.order_id || formatDetailLabel(detail)}
                          {fieldTransitions.length === 0 && detail.changed_fields?.length > 0
                            ? ` · ${formatSourceChangeReason(detail.changed_fields, { showAllFields: true })}`
                            : ""}
                          {detail.already_suspended === true
                            ? " · Sera actualisée dans Suspendues."
                            : ""}
                        </span>
                        <SourceChangeFieldTransitions
                          detail={detail}
                          className="pl-1.5 text-[0.59rem] text-slate-400"
                        />
                      </div>
                    );
                  })}
                  {row.details.length > MAX_VISIBLE_DETAILS ? (
                    <span className="text-[0.58rem] font-medium text-slate-400">
                      + {row.details.length - MAX_VISIBLE_DETAILS} autre{row.details.length - MAX_VISIBLE_DETAILS > 1 ? "s" : ""}
                    </span>
                  ) : null}
                </div>
              </div>
            );
          }

          const detail = row.detail;
          const detailActionLabel =
            detail?.already_suspended === true || detail?.change_type === "date_changed_remove"
              ? resolveRemoveModifiedDestinationLabel(detail)
              : null;
          return (
          <div
            key={row.rowKey}
            className="grid gap-0.75 rounded-[4px] border border-[rgba(197,82,74,0.12)] bg-white/95 px-2.25 py-2"
          >
            <span className="text-[0.72rem] font-semibold text-slate-800">
              {formatDetailLabel(detail)}
            </span>
            {detail.already_suspended === true ? (
              <span className="inline-flex items-center gap-1.5 text-[0.66rem] font-semibold text-amber-700">
                <PauseCircleIcon className="h-3.5 w-3.5 flex-none" aria-hidden="true" />
                Déjà dans Suspendues
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 text-[0.66rem] text-slate-600">
                <TruckIcon className="h-3.5 w-3.5 flex-none text-slate-400" aria-hidden="true" />
                <span>
                  Camion actuel&nbsp;:
                  {" "}
                  <strong className="font-semibold text-slate-800">{formatSourceChangeTruckLabel(detail)}</strong>
                </span>
              </span>
            )}
            {detailActionLabel ? (
              <span className="text-[0.64rem] leading-5 text-[#b24339]">
                {detailActionLabel}
              </span>
            ) : null}
            {resolveProtectedFieldChanges(detail).some((fieldChange) => fieldChange.hasValues) ? (
              <SourceChangeFieldTransitions
                detail={detail}
                className="text-[0.6rem] leading-4 text-slate-500"
              />
            ) : detail.changed_fields?.length > 0 || detail.initial_reason_label ? (
              <span className="text-[0.6rem] text-slate-400">
                {formatSourceChangeReason(detail.changed_fields, { showAllFields: true })
                  || formatSourceChangeInitialReasonLabel(detail.initial_reason_label)}
              </span>
            ) : null}
          </div>
          );
        })}
        {remainingCount > 0 ? (
          <p className="m-0 px-1.5 py-0.5 text-[0.62rem] font-medium text-slate-500">
            + {remainingCount} autre{remainingCount > 1 ? "s" : ""} commande{remainingCount > 1 ? "s" : ""}
          </p>
        ) : null}
      </div>

      <div className="flex items-center justify-end gap-1.5 px-0.5 pb-0.5">
        <ActionButton
          size="sm"
          tone="ghost"
          className="h-8! min-h-8! rounded-sm! px-2.75! shadow-none!"
          disabled={isBusy}
          onClick={onCancel}
        >
          Annuler
        </ActionButton>
        <ActionButton
          size="sm"
          tone="optimizer"
          className={SOURCE_CHANGES_MENU_CONFIRM_BUTTON_CLASS_NAME}
          disabled={actionsDisabled || isBusy}
          onClick={onConfirm}
        >
          {alreadySuspendedCount > 0 ? "Confirmer la mise à jour" : "Confirmer le retrait"}
        </ActionButton>
      </div>
    </div>
  );
}

function RemoveDateChangedSourceChangesConfirmPanel({
  actionsDisabled = false,
  details,
  isBusy = false,
  onCancel,
  onConfirm,
}) {
  const safeDetails = Array.isArray(details) ? details : [];
  const remainingCount = Math.max(0, safeDetails.length - MAX_VISIBLE_DETAILS);
  const visibleDetails = safeDetails.slice(0, MAX_VISIBLE_DETAILS);

  return (
    <div className="grid gap-2 p-1">
      <div className="grid gap-1 px-1.5 pt-0.5">
        <p className="m-0 text-[0.8rem] font-semibold text-slate-800">
          Retirer les dates déplacées
        </p>
        <p className="m-0 text-[0.68rem] leading-5 text-slate-500">
          Les commandes et leurs Chargés PM associés seront retirés du dispatch courant, puis les camions touchés seront recalculés localement.
        </p>
      </div>

      <div className="grid gap-1 rounded-[4px] border border-[rgba(13,148,136,0.18)] bg-[rgba(240,253,250,0.52)] p-1.5">
        {visibleDetails.map((detail, idx) => (
          <div
            key={detail.delivery_id || detail.order_id || idx}
            className="grid gap-0.75 rounded-[4px] border border-[rgba(13,148,136,0.14)] bg-white/95 px-2.25 py-2"
          >
            <span className="text-[0.72rem] font-semibold text-slate-800">
              {formatDetailLabel(detail)}
            </span>
            <span className="inline-flex items-center gap-1.5 text-[0.66rem] text-slate-600">
              <TruckIcon className="h-3.5 w-3.5 flex-none text-slate-400" aria-hidden="true" />
              <span>
                Camion actuel&nbsp;:
                {" "}
                <strong className="font-semibold text-slate-800">{formatSourceChangeTruckLabel(detail)}</strong>
              </span>
            </span>
            <span className="text-[0.64rem] leading-5 text-[#0f766e]">
              Sera retirée du dispatch courant.
            </span>
            <span className="text-[0.6rem] text-slate-400">
              {detail.dispatch_delivery_date && detail.source_delivery_date
                ? `${detail.dispatch_delivery_date} → ${detail.source_delivery_date}`
                : formatSourceChangeReason(detail.changed_fields)}
            </span>
          </div>
        ))}
        {remainingCount > 0 ? (
          <p className="m-0 px-1.5 py-0.5 text-[0.62rem] font-medium text-slate-500">
            + {remainingCount} autre{remainingCount > 1 ? "s" : ""} élément{remainingCount > 1 ? "s" : ""}
          </p>
        ) : null}
      </div>

      <div className="flex items-center justify-end gap-1.5 px-0.5 pb-0.5">
        <ActionButton
          size="sm"
          tone="ghost"
          className="h-8! min-h-8! rounded-sm! px-2.75! shadow-none!"
          disabled={isBusy}
          onClick={onCancel}
        >
          Annuler
        </ActionButton>
        <ActionButton
          size="sm"
          tone="optimizer"
          className={SOURCE_CHANGES_MENU_CONFIRM_BUTTON_CLASS_NAME}
          disabled={actionsDisabled || isBusy || safeDetails.length <= 0}
          onClick={onConfirm}
        >
          Confirmer le retrait
        </ActionButton>
      </div>
    </div>
  );
}

function ImportSuspendableErrorsConfirmPanel({
  actionsDisabled = false,
  details,
  isBusy = false,
  onCancel,
  onConfirm,
}) {
  const visibleDetails = Array.isArray(details) ? details : [];
  const count = visibleDetails.length;

  return (
    <div className="grid gap-2 p-1">
      <div className="grid gap-1 px-1.5 pt-0.5">
        <p className="m-0 text-[0.8rem] font-semibold text-slate-800">
          Importer les erreurs vers Suspendues
        </p>
        <p className="m-0 text-[0.68rem] leading-5 text-slate-500">
          Regroupe les commandes par clients.
        </p>
      </div>

      <div className="rounded-[4px] border border-[rgba(217,119,6,0.16)] bg-[rgba(255,250,242,0.5)] p-1.5">
        <SourceChangeDetailsPreview
          details={visibleDetails}
          groupSuspendableErrors
          embedded
        />
      </div>

      <div className="flex items-center justify-end gap-1.5 px-0.5 pb-0.5">
        <ActionButton
          size="sm"
          tone="ghost"
          className="h-8! min-h-8! rounded-sm! px-2.75! shadow-none!"
          disabled={isBusy}
          onClick={onCancel}
        >
          Annuler
        </ActionButton>
        <ActionButton
          size="sm"
          tone="optimizer"
          className={SOURCE_CHANGES_MENU_CONFIRM_BUTTON_CLASS_NAME}
          disabled={actionsDisabled || isBusy || count <= 0}
          onClick={onConfirm}
        >
          Confirmer le transfert
        </ActionButton>
      </div>
    </div>
  );
}

function formatRemovedEndOfDayLoadOrderIds(detail) {
  const missingOrderIds = Array.isArray(detail?.missing_order_ids)
    ? detail.missing_order_ids.map(normalizeInlineText).filter(Boolean)
    : [];
  const orderIds = Array.isArray(detail?.order_ids)
    ? detail.order_ids.map(normalizeInlineText).filter(Boolean)
    : [];
  const ids = missingOrderIds.length > 0 ? missingOrderIds : orderIds;
  return ids.length > 0 ? ids.join(", ") : normalizeInlineText(detail?.marker_key) || "PM";
}

function formatRemovedEndOfDayLoadTitle(detail) {
  const label = normalizeInlineText(detail?.loading_label || detail?.client_name);
  const ids = formatRemovedEndOfDayLoadOrderIds(detail);
  return label ? `${label} · ${ids}` : ids;
}

function RepairGhostOrdersConfirmPanel({
  actionsDisabled = false,
  orderDetails,
  pmDetails,
  isBusy = false,
  onCancel,
  onConfirm,
}) {
  const safeOrderDetails = Array.isArray(orderDetails) ? orderDetails : [];
  const safePmDetails = Array.isArray(pmDetails) ? pmDetails : [];
  const totalCount = safeOrderDetails.length + safePmDetails.length;
  const visiblePmDetails = safePmDetails.slice(0, MAX_VISIBLE_DETAILS);
  const remainingPmCount = Math.max(0, safePmDetails.length - MAX_VISIBLE_DETAILS);

  return (
    <div className="grid gap-2 p-1">
      <div className="grid gap-1 px-1.5 pt-0.5">
        <p className="m-0 text-[0.8rem] font-semibold text-slate-800">
          Supprimer du dispatch
        </p>
        <p className="m-0 text-[0.68rem] leading-5 text-slate-500">
          Ces éléments seront retirés du dispatch, puis seuls les camions touchés seront recalculés. Aucune donnée ne sera supprimée dans Data Manager.
        </p>
      </div>

      {safeOrderDetails.length > 0 ? (
        <div className="grid gap-1 rounded-[4px] border border-[rgba(13,148,136,0.18)] bg-[rgba(240,253,250,0.52)] p-1.5">
          <SourceChangeDetailsPreview
            details={safeOrderDetails}
            actionLabel="sera retirée du dispatch courant"
            groupSourceChanges
            embedded
          />
        </div>
      ) : null}

      {safePmDetails.length > 0 ? (
        <div className="grid gap-1 rounded-[4px] border border-[rgba(217,119,6,0.18)] bg-[rgba(255,250,242,0.52)] p-1.5">
          <p className="m-0 px-1 text-[0.64rem] font-bold text-slate-600">
            Chargés PM orphelins
          </p>
          {visiblePmDetails.map((detail, idx) => {
            const sourceDateLabel = formatDispatchDateLabel(detail?.source_dispatch_date || detail?.marker_dispatch_date);
            const targetDateLabel = formatDispatchDateLabel(detail?.target_dispatch_date);
            const truckLabel = normalizeInlineText(detail?.target_truck_label || detail?.target_truck_id) || "camion cible";
            const reasonLabel = formatGhostReason(detail?.ghost_reason) || "commande source supprimée";
            return (
              <div
                key={normalizeInlineText(detail?.marker_key) || idx}
                className="grid gap-0.75 rounded-[4px] border border-[rgba(217,119,6,0.14)] bg-white/95 px-2.25 py-2"
              >
                <span className="text-[0.72rem] font-semibold text-slate-800">
                  {formatRemovedEndOfDayLoadTitle(detail)}
                </span>
                <span className="inline-flex items-center gap-1.5 text-[0.66rem] text-slate-600">
                  <TruckIcon className="h-3.5 w-3.5 flex-none text-slate-400" aria-hidden="true" />
                  <span>
                    Camion cible&nbsp;:
                    {" "}
                    <strong className="font-semibold text-slate-800">{truckLabel}</strong>
                  </span>
                </span>
                <span className="text-[0.64rem] leading-5 text-[#92400e]">
                  Chargé PM retiré; {reasonLabel}.
                </span>
                {sourceDateLabel || targetDateLabel ? (
                  <span className="text-[0.6rem] text-slate-400">
                    {[sourceDateLabel ? `source ${sourceDateLabel}` : "", targetDateLabel ? `cible ${targetDateLabel}` : ""].filter(Boolean).join(" → ")}
                  </span>
                ) : null}
              </div>
            );
          })}
          {remainingPmCount > 0 ? (
            <p className="m-0 px-1.5 py-0.5 text-[0.62rem] font-medium text-slate-500">
              + {remainingPmCount} autre{remainingPmCount > 1 ? "s" : ""} Chargé{remainingPmCount > 1 ? "s" : ""} PM
            </p>
          ) : null}
        </div>
      ) : null}

      <div className="flex items-center justify-end gap-1.5 px-0.5 pb-0.5">
        <ActionButton
          size="sm"
          tone="ghost"
          className="h-8! min-h-8! rounded-sm! px-2.75! shadow-none!"
          disabled={isBusy}
          onClick={onCancel}
        >
          Annuler
        </ActionButton>
        <ActionButton
          size="sm"
          tone="optimizer"
          className={SOURCE_CHANGES_MENU_CONFIRM_BUTTON_CLASS_NAME}
          disabled={actionsDisabled || isBusy || totalCount <= 0}
          onClick={onConfirm}
        >
          Tout retirer du dispatch
        </ActionButton>
      </div>
    </div>
  );
}


function RestoreExecutionsConfirmPanel({
  actionsDisabled = false,
  details,
  isBusy = false,
  onCancel,
  onConfirm,
}) {
  const safeDetails = Array.isArray(details) ? details : [];
  return (
    <div className="grid gap-2 p-1">
      <div className="grid gap-1 px-1.5 pt-0.5">
        <p className="m-0 text-[0.8rem] font-semibold text-slate-800">
          Restaurer les exécutions
        </p>
        <p className="m-0 text-[0.68rem] leading-5 text-slate-500">
          Les confirmations Portail seront remises dans le camion et la tournée conservés par le snapshot. Une sauvegarde sera créée avant la modification.
        </p>
      </div>
      <div className="rounded-[4px] border border-[rgba(13,148,136,0.18)] bg-[rgba(240,253,250,0.52)] p-1.5">
        <SourceChangeDetailsPreview
          details={safeDetails}
          actionLabel="sera restaurée dans sa tournée d'origine"
          groupSourceChanges
          embedded
        />
      </div>
      <div className="flex items-center justify-end gap-1.5 px-0.5 pb-0.5">
        <ActionButton
          size="sm"
          tone="ghost"
          className="h-8! min-h-8! rounded-sm! px-2.75! shadow-none!"
          disabled={isBusy}
          onClick={onCancel}
        >
          Annuler
        </ActionButton>
        <ActionButton
          size="sm"
          tone="optimizer"
          className={SOURCE_CHANGES_MENU_CONFIRM_BUTTON_CLASS_NAME}
          disabled={actionsDisabled || isBusy || safeDetails.length <= 0}
          onClick={onConfirm}
        >
          Confirmer la restauration
        </ActionButton>
      </div>
    </div>
  );
}


function DispatchPeriodBadgeIcon({ periodLabel = "Jour" }) {
  const isEvening = periodLabel === "Soir";

  return (
    <span
      aria-hidden="true"
      className={joinClasses([
        "inline-flex h-5.5 w-5.5 flex-none items-center justify-center rounded-full border shadow-[inset_0_1px_0_rgba(255,255,255,0.65)]",
        isEvening
          ? "border-[rgba(99,102,241,0.42)] bg-[rgba(224,231,255,0.98)] text-[rgb(55,48,163)]"
          : "border-[rgba(245,158,11,0.5)] bg-[rgba(255,247,237,0.98)] text-[rgb(180,83,9)]",
      ])}
      data-dispatch-period-badge={isEvening ? "evening" : "day"}
      title={isEvening ? "Soir" : "Jour"}
    >
      {isEvening ? (
        <Moon className="h-3.75 w-3.75" strokeWidth={2.55} />
      ) : (
        <Sun className="h-3.75 w-3.75" strokeWidth={2.55} />
      )}
    </span>
  );
}

const TRUCK_SOURCE_CHANGE_REASON_LABELS = {
	capacity_trim_insufficient: "Retirer les commandes non protégées ne suffit pas.",
	executed_group: "Ce regroupement contient une étape déjà exécutée.",
	load_capacity_exceeded: "Le chargement maximal dépasse la nouvelle capacité.",
	non_contiguous_group: "Ce regroupement n’est pas contigu dans la tournée.",
	protected_or_frozen_group: "Ce regroupement est protégé ou figé.",
	protected_truck: "La tournée est protégée.",
	published_truck: "La tournée est publiée.",
  end_of_day_present: "Un Chargé PM ou une fin de journée est présent.",
  field_not_supported_locally: "Ce champ exige une optimisation complète.",
  fleet_coupled_change: "Le changement dépend d’autres camions.",
  fleet_reference_invalid: "La référence flotte n’est plus valide.",
  fleet_reference_required: "Une référence flotte doit être initialisée.",
  fleet_reference_version_unsupported: "La version de référence flotte n’est pas prise en charge.",
  global_period_change: "Le changement touche une période globale.",
  interday_preload_present: "Les données du préchargement sont incomplètes. Vérifiez le chargement et son lien avec la veille.",
  publication_context_unavailable: "La vérification des publications est indisponible. Rechargez la page pour réessayer.",
  local_feasibility_failed: "Le camion ne peut pas être recalculé localement.",
  manual_sequence_not_replayable: "La séquence manuelle ne peut pas être rejouée à l’identique.",
  max_loadings_per_truck_per_day_exceeded: "La limite de chargements par camion pour la journée est atteinte.",
  max_work_hours_exceeded: "La tournée dépasse les heures max du chauffeur.",
  multi_trip_not_supported: "Les affectations à plusieurs trajets ne sont pas prises en charge.",
	protected_route_changed: "La route protégée ne peut pas être conservée.",
	protected_capacity_trim_unavailable: "Aucune commande non protégée n'est suspendable.",
  route_executed: "La route contient déjà une exécution.",
  route_frozen: "La route est figée.",
  route_published: "La route est publiée.",
  structural_truck_change: "Le changement est structurel.",
  truck_assignment_missing: "L’affectation du camion est absente.",
  time_insufficient: "La tournée dépasse la durée disponible du chauffeur.",
  unknown_truck_field: "Le champ camion n’est pas reconnu.",
};

function formatTruckSourceChangeReason(reasonCode) {
  const normalizedReasonCode = normalizeInlineText(reasonCode).toLowerCase();
  return TRUCK_SOURCE_CHANGE_REASON_LABELS[normalizedReasonCode]
    || normalizedReasonCode.replaceAll("_", " ");
}

function TruckSourceEmphasizedLine({ text, className = "m-0 min-w-0" }) {
  const { lead, rest } = splitTruckSourceLineLead(text);
  return (
    <p className={className}>
      {lead ? <span className="font-bold">{keepTruckUnitsTogether(lead)}</span> : null}
      {lead ? " : " : null}
      {keepTruckUnitsTogether(lead ? rest : text)}
    </p>
  );
}

function formatTruckSourceChangeValue(fieldName, value, truckLabelById = {}) {
  const safeFieldValue = formatDataManagerAuditSafeFieldValue(fieldName, value);
  if (safeFieldValue) {
    return safeFieldValue;
  }
  if (value == null || value === "") {
    return "Aucune";
  }
  if (typeof value === "boolean") {
    return value ? "Oui" : "Non";
  }
  if (Array.isArray(value)) {
    return value.length > 0 ? value.join(", ") : "Aucune";
  }
  if (typeof value === "object") {
    try {
      return JSON.stringify(value);
    } catch {
      return "Valeur structurée";
    }
  }
  if (looksLikeInternalTruckSourceChangeId(value)) {
    return resolveTruckSourceChangeLabel(value, truckLabelById);
  }
  return String(value);
}

function TruckSourceChangedFields({ fields = [], truckLabelById = {} }) {
  const visibleFields = Array.isArray(fields)
    ? fields.filter((field) => normalizeInlineText(field?.field).toLowerCase() !== "truck_id")
    : [];
  if (visibleFields.length === 0) {
    return null;
  }
  return (
    <dl className="grid gap-1">
      {visibleFields.map((field) => (
        <div key={`${field.field}|${field.label}`} className="grid gap-0.5 rounded-[4px] bg-white/85 px-1.5 py-1">
          <dt className="text-[0.6rem] font-bold text-slate-500">
            {formatDataManagerAuditFieldLabel(field.field, { fallbackLabel: field.label })}
          </dt>
          <dd className="text-[0.64rem] font-semibold text-slate-700">
            {field.valueVisibility === "redacted" ? (
              <span data-truck-change-redacted>Valeur masquée</span>
            ) : field.field === "compartments" ? (
              <TruckCompartmentChanges before={field.before} after={field.after} />
            ) : (
              <span>
                {formatTruckSourceChangeValue(field.field, field.before, truckLabelById)} → {formatTruckSourceChangeValue(field.field, field.after, truckLabelById)}
              </span>
            )}
          </dd>
        </div>
      ))}
    </dl>
  );
}

function TruckSourceChangeRejectConfirmPanel({
  actionsDisabled = false,
  isBusy = false,
  onCancel,
  onConfirm,
  selection = null,
  truckLabelById = {},
}) {
  const cancelRef = useRef(null);
  const titleId = React.useId();
  const descriptionId = React.useId();
  const truckLabel = resolveTruckSourceChangeLabel(selection?.truckId, truckLabelById);

  useEffect(() => {
    cancelRef.current?.focus();
  }, []);

  return (
    <section
      aria-describedby={descriptionId}
      aria-labelledby={titleId}
      aria-modal="true"
      className="grid gap-3 rounded-[4px] border border-rose-200 bg-rose-50/70 p-3"
      data-truck-source-change-reject-confirm
      role="dialog"
    >
      <div className="grid gap-1">
        <span id={titleId} className="text-[0.76rem] font-bold text-slate-900">
          Rejeter la mise à jour de {truckLabel} ?
        </span>
        <span id={descriptionId} className="text-[0.64rem] leading-4 text-slate-600">
          Le profil et la route actuels de ce camion resteront inchangés dans ce dispatch. La fiche Data Manager courante sera reprise à la prochaine optimisation complète; une nouvelle modification du camion réapparaîtra ici.
        </span>
      </div>
      <TruckSourceChangedFields fields={selection?.changedFields} truckLabelById={truckLabelById} />
      <div className="flex items-center justify-end gap-2">
        <button
          ref={cancelRef}
          type="button"
          className="h-8 rounded-sm border border-slate-200 bg-white px-2.75 text-[0.68rem] font-semibold text-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/35"
          onClick={onCancel}
        >
          Annuler
        </button>
        <ActionButton
          className={joinClasses([
            SOURCE_CHANGES_MENU_CONFIRM_BUTTON_CLASS_NAME,
            "border-rose-300! bg-rose-600! text-white! hover:bg-rose-700!",
          ])}
          disabled={actionsDisabled || isBusy || !selection?.truckId}
          size="sm"
          tone="optimizer"
          onClick={onConfirm}
        >
          {isBusy ? "Rejet..." : "Rejeter la mise à jour"}
        </ActionButton>
      </div>
    </section>
  );
}

function formatTruckRecoveryMinutes(value) {
  const totalMinutes = Math.max(0, Math.round(Number(value) || 0));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours <= 0) {
    return `${minutes} min`;
  }
  return minutes > 0 ? `${hours} h ${String(minutes).padStart(2, "0")}` : `${hours} h`;
}

function formatTruckRecoveryTons(value) {
	const tons = Number(value);
	return Number.isFinite(tons) ? `${tons.toLocaleString("fr-CA", { maximumFractionDigits: 3 })} t` : "—";
}

function formatTruckRecoveryLoadingTime(value) {
	const text = normalizeInlineText(value);
	const match = text.match(/(?:^|T|\s)(\d{1,2}):(\d{2})(?::|$)/);
	return match ? `${match[1].padStart(2, "0")}h${match[2]}` : "";
}

function formatTruckRecoveryLoadingSequence(group = null) {
	const start = Number(group?.loadingSequenceStart);
	const end = Number(group?.loadingSequenceEnd);
	if (!Number.isInteger(start) || start < 1) {
		return "";
	}
	return Number.isInteger(end) && end > start ? `${start}–${end}` : String(start);
}

function resolveTruckRecoveryCheckpointRemainingTons(checkpoint = null, selectedGroupKeys = []) {
	const selected = selectedGroupKeys instanceof Set
		? selectedGroupKeys
		: new Set(selectedGroupKeys);
	const removedTons = (Array.isArray(checkpoint?.contributions) ? checkpoint.contributions : [])
		.reduce((total, contribution) => (
			selected.has(contribution?.sourceGroupKey)
				? total + Math.max(0, Number(contribution?.quantityTons) || 0)
				: total
		), 0);
	return Math.max(0, (Number(checkpoint?.loadTons) || 0) - removedTons);
}

function resolveTruckRecoveryProjectedPeak(recovery = null, selectedGroupKeys = []) {
	const selected = new Set(selectedGroupKeys);
	const checkpoints = Array.isArray(recovery?.capacityCheckpoints)
		? recovery.capacityCheckpoints
		: [];
	return checkpoints.reduce((peak, checkpoint) => Math.max(
		peak,
		resolveTruckRecoveryCheckpointRemainingTons(checkpoint, selected),
	), 0);
}

function resolveBlockedTruckRecoveryMessage(recovery = null, truckLabel = "ce camion") {
	if (recovery?.constraintKind === "load_capacity") {
		if (recovery?.status === "protected_only") {
			return `Les regroupements qui causent le dépassement sont protégés, figés, publiés ou exécutés. Corrigez ou réaffectez manuellement ${truckLabel}, ou rejetez la modification.`;
		}
		if (recovery?.status === "insufficient") {
			return `Aucune sélection sûre ne ramène tous les chargements sous ${formatTruckRecoveryTons(recovery?.availableTons)} sans vider la tournée. Corrigez ou réaffectez manuellement ${truckLabel}, ou rejetez la modification.`;
		}
		return "Sélectionnez des regroupements entiers à transférer vers Suspendues. Chaque chargement doit respecter la nouvelle capacité.";
	}
  if (recovery?.status === "protected_only") {
    return `Aucune commande non protégée n'est suspendable. Déprotégez ou déplacez une commande de ${truckLabel}, puis cliquez sur Actualiser.`;
  }
  if (recovery?.status === "insufficient") {
    return `Les commandes non protégées ne suffisent pas à ramener la tournée sous ${formatTruckRecoveryMinutes(recovery?.availableMinutes)}. Déplacez une commande vers un autre camion avec Réaffectation rapide, puis cliquez sur Actualiser.`;
  }
  return "Des groupes non protégés peuvent être transférés vers Suspendues pour rendre cette tournée compatible, sans optimisation complète.";
}

export function TruckRefreshRecoveryConfirmPanel({
  actionsDisabled = false,
  isBusy = false,
  onCancel,
  onConfirm,
  selection = null,
  truckLabelById = {},
}) {
  const cancelRef = useRef(null);
  const titleId = React.useId();
  const descriptionId = React.useId();
  const recovery = selection?.recovery;
	const isLoadCapacity = recovery?.constraintKind === "load_capacity";
	const groups = isLoadCapacity && Array.isArray(recovery?.candidateGroups)
		? recovery.candidateGroups
		: Array.isArray(recovery?.proposedGroups) ? recovery.proposedGroups : [];
	const proposedGroupKeys = useMemo(() => (
		(Array.isArray(recovery?.proposedGroups) ? recovery.proposedGroups : [])
			.map((group) => group?.sourceGroupKey)
			.filter(Boolean)
	), [recovery]);
	const [selectedGroupKeys, setSelectedGroupKeys] = useState(proposedGroupKeys);
	const selectedGroupKeySet = useMemo(() => new Set(selectedGroupKeys), [selectedGroupKeys]);
	const selectedGroups = isLoadCapacity
		? groups.filter((group) => selectedGroupKeySet.has(group?.sourceGroupKey))
		: groups;
	const commandCount = selectedGroups.reduce(
		(total, group) => total + Math.max(0, Number(group?.commandCount) || 0),
		0,
	);
	const projectedPeakTons = isLoadCapacity
		? resolveTruckRecoveryProjectedPeak(recovery, selectedGroupKeys)
		: null;
	const selectionIsSafe = !isLoadCapacity
		|| projectedPeakTons <= Number(recovery?.availableTons) + 1e-9;
	const capacityField = (Array.isArray(selection?.changedFields) ? selection.changedFields : [])
		.find((field) => normalizeInlineText(field?.field) === "capacity_tons");
	const previousCapacityTons = Number(capacityField?.before);
	const candidateGroupsByKey = useMemo(() => new Map(
		groups.map((group) => [group?.sourceGroupKey, group]),
	), [groups]);
	const overloadedCheckpoints = useMemo(() => {
		const peakByGroupSet = new Map();
		(Array.isArray(recovery?.capacityCheckpoints) ? recovery.capacityCheckpoints : [])
			.filter((checkpoint) => (
				Number(checkpoint?.loadTons) > Number(recovery?.availableTons) + 1e-9
			))
			.forEach((checkpoint) => {
				const contributions = Array.isArray(checkpoint?.contributions) ? checkpoint.contributions : [];
				const groupSetKey = contributions
					.map((contribution) => normalizeInlineText(contribution?.sourceGroupKey))
					.filter(Boolean)
					.sort()
					.join("|");
				const existing = peakByGroupSet.get(groupSetKey);
				if (!existing || Number(checkpoint?.loadTons) > Number(existing?.loadTons)) {
					peakByGroupSet.set(groupSetKey, checkpoint);
				}
			});
		return [...peakByGroupSet.values()]
			.sort((left, right) => Number(left?.sequence) - Number(right?.sequence))
			.map((checkpoint, index) => {
				const seenGroupKeys = new Set();
				const checkpointGroups = (Array.isArray(checkpoint?.contributions) ? checkpoint.contributions : [])
					.map((contribution) => candidateGroupsByKey.get(contribution?.sourceGroupKey))
					.filter((group) => {
						if (!group?.sourceGroupKey || seenGroupKeys.has(group.sourceGroupKey)) {
							return false;
						}
						seenGroupKeys.add(group.sourceGroupKey);
						return true;
					});
				const loadingStartAt = checkpointGroups
					.map((group) => normalizeInlineText(group?.loadingStartAt))
					.filter(Boolean)
					.sort()[0] || "";
				const loadingTimeLabel = formatTruckRecoveryLoadingTime(loadingStartAt);
				return {
					...checkpoint,
					label: loadingTimeLabel ? `Chargement ${loadingTimeLabel}` : `Chargement ${index + 1}`,
					candidateGroups: checkpointGroups,
				};
			});
	}, [candidateGroupsByKey, recovery?.availableTons, recovery?.capacityCheckpoints]);
	const selectionFeedback = !isLoadCapacity
		? ""
		: selectedGroupKeys.length === 0
			? overloadedCheckpoints.length === 1
				? "Aucune sélection. Choisissez au moins un regroupement dans ce chargement."
				: `Aucune sélection. Choisissez au moins un regroupement dans chacun des ${overloadedCheckpoints.length || 1} chargements.`
			: selectionIsSafe
				? `Sélection valide : le chargement maximal restant sera de ${formatTruckRecoveryTons(projectedPeakTons)}.`
				: `Sélection incomplète : un chargement de ${formatTruckRecoveryTons(projectedPeakTons)} dépasse encore la limite de ${formatTruckRecoveryTons(recovery?.availableTons)}.`;
  const truckLabel = resolveTruckSourceChangeLabel(selection?.truckId, truckLabelById);
	const renderRecoveryGroupChoice = (group, keyPrefix = "") => {
		const orderLines = Array.isArray(group?.orderIds)
			? group.orderIds.flatMap((orderId) => {
				const orderLine = formatTruckRecoveryOrderLine(
					orderId,
					group.deliveryClientNameByOrderId?.[orderId],
				);
				return orderLine ? [{ orderId, orderLine }] : [];
			})
			: [];
		return (
		  <li
			key={`${keyPrefix}${group.sourceGroupKey}`}
			className="grid min-w-0 content-start gap-0.5 rounded-[4px] border border-amber-200/80 bg-white px-2.5 py-2 shadow-[0_1px_2px_rgba(15,23,42,0.04)]"
		  >
			<label className={joinClasses([
				"flex min-w-0 items-start gap-2",
				isLoadCapacity
					? group.selectable !== true ? "cursor-not-allowed opacity-60" : "cursor-pointer"
					: "",
			])}>
				{isLoadCapacity ? (
					<input
						type="checkbox"
						className="mt-0.5 h-4 w-4 flex-none accent-teal-700"
						checked={selectedGroupKeySet.has(group.sourceGroupKey)}
						disabled={group.selectable !== true || actionsDisabled || isBusy}
						onChange={(event) => {
							setSelectedGroupKeys((currentKeys) => event.target.checked
								? [...new Set([...currentKeys, group.sourceGroupKey])].sort()
								: currentKeys.filter((key) => key !== group.sourceGroupKey));
						}}
					/>
				) : null}
				<span className="grid min-w-0 gap-0.5">
					<span className="text-[0.72rem] font-bold leading-4 text-slate-800">
						{group.label} · {group.commandCount} commande(s){isLoadCapacity ? ` · ${formatTruckRecoveryTons(group.quantityTons)}` : ""}
					</span>
					{isLoadCapacity && group.selectable !== true ? (
						<span className="text-[0.62rem] leading-4 text-rose-700">
							{group.blockedReason || formatTruckSourceChangeReason(group.blockedReasonCode)}
						</span>
					) : null}
				</span>
			</label>
			{orderLines.length > 0 ? (
			  <ul className="m-0 grid min-w-0 list-none gap-0.5 p-0 text-[0.6rem] leading-4 text-slate-500">
				{orderLines.map(({ orderId, orderLine }) => (
				  <li key={orderId} className="min-w-0 break-words" title={orderLine}>
					{orderLine}
				  </li>
				))}
			  </ul>
			) : null}
		  </li>
		);
	};

  useEffect(() => {
    cancelRef.current?.focus();
  }, []);

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onCancel?.();
      }
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleEscape);
    };
  }, [onCancel]);

	useEffect(() => {
		setSelectedGroupKeys(proposedGroupKeys);
	}, [proposedGroupKeys, recovery?.recoveryRevision]);

	if (typeof document === "undefined") {
		return null;
	}

	return createPortal(
	  <div
		className="fixed inset-0 z-modal isolate flex items-center justify-center bg-slate-950/30 px-4 py-6 font-sans backdrop-blur-xs sm:px-6"
		data-truck-refresh-recovery-modal="true"
		role="presentation"
	  >
    <section
      aria-describedby={descriptionId}
      aria-labelledby={titleId}
	  aria-modal="true"
	  className="grid max-h-[calc(100dvh-3rem)] w-[min(48rem,calc(100vw-2rem))] grid-rows-[auto_auto_minmax(0,1fr)_auto] overflow-hidden rounded-overlay border border-[rgba(148,163,184,0.22)] bg-white text-slate-900 shadow-floating motion-safe:animate-popover-enter"
      data-truck-refresh-recovery-confirm
      role="dialog"
    >
	  <div className="flex items-start justify-between gap-4 border-b border-[rgba(148,163,184,0.16)] px-5 py-3">
		<div className="grid min-w-0 gap-1">
		  <span className="text-[0.62rem] font-black uppercase tracking-[0.16em] text-amber-700">
			{isLoadCapacity ? "Capacité" : "Durée de travail"}
		  </span>
		  <h2 id={titleId} className="m-0 text-[1.05rem] font-semibold leading-6 text-slate-900">
			{isLoadCapacity ? `${truckLabel} dépasse sa nouvelle capacité` : `Rendre ${truckLabel} compatible ?`}
		  </h2>
		  <p id={descriptionId} className="m-0 text-[0.76rem] leading-5 text-slate-600">
			{isLoadCapacity
			  ? Number.isFinite(previousCapacityTons)
				? `La capacité passe de ${formatTruckRecoveryTons(previousCapacityTons)} à ${formatTruckRecoveryTons(recovery?.availableTons)}, mais la tournée atteint actuellement ${formatTruckRecoveryTons(recovery?.requiredTons)}. La modification ne peut donc pas être appliquée telle quelle.`
				: `La nouvelle capacité est de ${formatTruckRecoveryTons(recovery?.availableTons)}, mais la tournée atteint actuellement ${formatTruckRecoveryTons(recovery?.requiredTons)}. La modification ne peut donc pas être appliquée telle quelle.`
			  : "Les groupes non protégés ci-dessous seront transférés ensemble vers Suspendues, puis ce camion sera actualisé sans optimisation complète. L’ordre des commandes restantes sera conservé."}
		  </p>
		</div>
		<button
		  type="button"
		  aria-label="Fermer"
		  className="inline-flex h-9 w-9 flex-none items-center justify-center rounded-full border border-[rgba(71,85,105,0.16)] bg-white text-slate-500 hover:bg-slate-50 hover:text-slate-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-300/40"
		  onClick={onCancel}
		>
		  <XMarkIcon aria-hidden="true" className="h-4.5 w-4.5" />
		</button>
      </div>
	  <dl className={joinClasses([
		"mx-5 mt-3 grid gap-2 rounded-[4px] border border-amber-200/80 bg-amber-50/55 px-3 py-2 text-center",
		isLoadCapacity ? "grid-cols-2" : "grid-cols-3",
	  ])}>
        <div>
			  <dt className="text-[0.61rem] font-bold uppercase tracking-[0.07em] text-slate-500">{isLoadCapacity ? "Capacité avant" : "Tournée"}</dt>
			  <dd className="m-0 mt-0.5 text-[0.82rem] font-bold text-slate-800">{isLoadCapacity && Number.isFinite(previousCapacityTons) ? formatTruckRecoveryTons(previousCapacityTons) : formatTruckRecoveryMinutes(recovery?.requiredMinutes)}</dd>
        </div>
        <div>
			  <dt className="text-[0.61rem] font-bold uppercase tracking-[0.07em] text-slate-500">{isLoadCapacity ? "Nouvelle capacité" : "Maximum"}</dt>
			  <dd className="m-0 mt-0.5 text-[0.82rem] font-bold text-slate-800">{isLoadCapacity ? formatTruckRecoveryTons(recovery?.availableTons) : formatTruckRecoveryMinutes(recovery?.availableMinutes)}</dd>
        </div>
		{!isLoadCapacity ? (
		  <div>
			<dt className="text-[0.61rem] font-bold uppercase tracking-[0.07em] text-slate-500">Après</dt>
			<dd className="m-0 mt-0.5 text-[0.82rem] font-bold text-rose-700">{formatTruckRecoveryMinutes(recovery?.projectedRequiredMinutes)}</dd>
		  </div>
		) : null}
      </dl>
	  {isLoadCapacity && overloadedCheckpoints.length > 0 ? (
		<div className="mx-5 min-h-0 overflow-y-auto py-3">
		  <p className="m-0 text-[0.69rem] leading-5 text-slate-600">
			{overloadedCheckpoints.length} chargement{overloadedCheckpoints.length > 1 ? "s dépassent" : " dépasse"} la nouvelle capacité de {formatTruckRecoveryTons(recovery?.availableTons)}. Pour chaque chargement, cochez au moins un regroupement à déplacer vers Suspendues.
		  </p>
		  <ol className="m-0 mt-2 grid list-none gap-2 p-0" aria-label="Choix par chargement">
			{overloadedCheckpoints.map((checkpoint, checkpointIndex) => {
			  const remainingTons = resolveTruckRecoveryCheckpointRemainingTons(
				checkpoint,
				selectedGroupKeySet,
			  );
			  const loadIsCompatible = remainingTons <= Number(recovery?.availableTons) + 1e-9;
			  return (
			  <li
				key={checkpoint.checkpointKey}
				className="overflow-hidden rounded-[4px] border border-slate-200 bg-slate-50/70"
				data-truck-recovery-load={checkpointIndex + 1}
			  >
				<div className="flex items-center justify-between gap-3 border-b border-slate-200 px-3 py-1.5">
				  <div className="flex min-w-0 items-baseline gap-2">
					<h3 className="m-0 text-[0.73rem] font-bold text-slate-900">{checkpoint.label}</h3>
					<span className={joinClasses([
					  "truncate text-[0.62rem]",
					  loadIsCompatible ? "font-semibold text-emerald-700" : "text-slate-500",
					])}>
					  {loadIsCompatible ? "Capacité respectée" : "Choisissez au moins un regroupement"}
					</span>
				  </div>
				  <span
					aria-label={`${formatTruckRecoveryTons(remainingTons)}${loadIsCompatible ? ", capacité respectée" : ", capacité dépassée"}`}
					className={joinClasses([
					  "inline-flex flex-none items-center gap-1 text-[0.72rem] font-bold",
					  loadIsCompatible ? "text-emerald-700" : "text-rose-700",
					])}
					data-truck-recovery-remaining-tons
				  >
					{formatTruckRecoveryTons(remainingTons)}
					{loadIsCompatible ? <CheckCircleIcon aria-hidden="true" className="h-4 w-4" /> : null}
				  </span>
				</div>
				<div className="mx-2 mt-2 flex min-w-0 items-center gap-2" data-truck-recovery-gantt={checkpointIndex + 1}>
				  <span className="flex-none text-[0.6rem] font-bold text-slate-600">{truckLabel}</span>
				  <div className="flex min-w-0 flex-1 gap-0.5 rounded-[6px] border-2 border-rose-500 bg-white p-1 shadow-[0_0_0_1px_rgba(244,63,94,0.08)]">
					{checkpoint.candidateGroups.map((group) => {
					  const sequenceLabel = formatTruckRecoveryLoadingSequence(group);
					  return (
						<span
						  key={`${checkpoint.checkpointKey}:gantt:${group.sourceGroupKey}`}
						  className="flex min-w-0 items-center gap-1 rounded-[4px] border border-[#7f96bd] bg-[#b7c7e3] px-1.5 py-1 text-[0.61rem] font-semibold text-slate-700"
						  style={{ flexGrow: Math.max(1, Number(group.commandCount) || 1), flexBasis: 0 }}
						  title={[sequenceLabel, group.label, formatTruckRecoveryLoadingTime(group.loadingStartAt)].filter(Boolean).join(" · ")}
						>
						  {sequenceLabel ? (
							<span className="flex-none rounded-[4px] bg-white/90 px-1 py-0.5 font-bold text-slate-800">{sequenceLabel}</span>
						  ) : null}
						  <span className="truncate">{group.label}</span>
						</span>
					  );
					})}
				  </div>
				</div>
				<ul className="m-0 grid list-none gap-2 p-2 sm:grid-cols-2">
				  {checkpoint.candidateGroups.map((group) => renderRecoveryGroupChoice(group, `${checkpoint.checkpointKey}:`))}
				</ul>
			  </li>
			  );
			})}
		  </ol>
		</div>
	  ) : (
		<ul className="grid min-h-0 gap-2 overflow-y-auto px-5 py-3 sm:grid-cols-2" aria-label="Groupes transférés vers Suspendues">
		  {groups.map((group) => renderRecoveryGroupChoice(group))}
		</ul>
	  )}
	  <div className="flex flex-wrap items-center justify-end gap-2 border-t border-[rgba(148,163,184,0.16)] bg-slate-50/80 px-5 py-3">
		{selectionFeedback ? (
		  <p className={joinClasses([
			"m-0 mr-auto max-w-[24rem] text-[0.69rem] font-semibold leading-5",
			selectionIsSafe && selectedGroupKeys.length > 0 ? "text-emerald-700" : "text-amber-800",
		  ])} role="status">
			{selectionFeedback}
		  </p>
		) : <span className="mr-auto" />}
        <button
          ref={cancelRef}
          type="button"
		  className="h-9 rounded-sm border border-slate-200 bg-white px-3.5 text-[0.74rem] font-semibold text-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/35"
          onClick={onCancel}
        >
          Annuler
        </button>
        <ActionButton
          className={joinClasses([
            SOURCE_CHANGES_MENU_CONFIRM_BUTTON_CLASS_NAME,
			"h-auto! min-h-9! whitespace-normal! px-4! py-2.5! text-center text-[0.74rem]! leading-4!",
          ])}
		  disabled={actionsDisabled || isBusy || commandCount <= 0 || groups.length === 0 || !selectionIsSafe}
          size="sm"
          tone="optimizer"
		  onClick={() => onConfirm?.({ selectedGroupKeys: [...selectedGroupKeys].sort() })}
        >
          {isBusy
            ? "Actualisation..."
			: isLoadCapacity && commandCount <= 0
			  ? "Sélection requise"
			  : isLoadCapacity && !selectionIsSafe
				? "Complétez la sélection"
				: `Suspendre ${commandCount} commande(s) et actualiser ${truckLabel}`}
        </ActionButton>
      </div>
    </section>
	  </div>,
	  getReactPortalRoot(),
	);
}

const LOADING_CONSTRAINT_FIELD_LABELS = {
  client_loading_opening_time: "Ouverture",
  client_loading_closing_time: "Fermeture",
  meunerie_loading_extra_minutes: "Surtemps",
};

const LOADING_CONSTRAINT_CONFLICT_LABELS = {
  client_loading_opening_time: "Ouverture meunerie",
  client_loading_closing_time: "Fermeture meunerie",
  delivery_window: "Fenêtre de livraison",
  loading_window: "Fenêtre de chargement",
  max_work_hours: "Heures maximales",
  operational_sequence: "Séquence opérationnelle",
  time_window: "Fenêtre horaire",
};

function formatLoadingConstraintValue(fieldName, value) {
  const auditFieldName = fieldName.replace(/^client_/, "");
  const formatted = formatDataManagerAuditSafeFieldValue(auditFieldName, value);
  if (formatted) {
    return fieldName === "meunerie_loading_extra_minutes" && formatted !== "—"
      ? `${formatted} min`
      : formatted;
  }
  return value == null || value === "" ? "Aucune" : String(value);
}

function formatLoadingConstraintConflictLimit(conflict) {
  if (conflict?.newLimit == null || conflict.newLimit === "") {
    return "";
  }
  if (conflict.constraintType === "max_work_hours") {
    return formatTruckRecoveryMinutes(conflict.newLimit);
  }
  const formattedTime = formatTruckRecoveryLoadingTime(conflict.newLimit);
  return formattedTime || String(conflict.newLimit);
}

function SourceChangeGroupSectionHeader({
  count = 0,
  headingId,
  icon: Icon,
  label,
}) {
  const numericCount = Number(count);
  const hasCount = Number.isFinite(numericCount) && numericCount > 0;

  return (
    <div
      className={SOURCE_CHANGE_GROUP_HEADER_CLASS_NAME}
      data-source-change-group-header="true"
    >
      <Icon
        aria-hidden="true"
        className={SOURCE_CHANGE_GROUP_HEADER_ICON_CLASS_NAME}
      />
      <span id={headingId} className="min-w-0 flex-1 truncate whitespace-nowrap">
        {label}
      </span>
      {hasCount ? (
        <span className={SOURCE_CHANGE_COUNT_BADGE_CLASS_NAME}>
          {numericCount}
        </span>
      ) : null}
      <ChevronRightIcon
        aria-hidden="true"
        className={SOURCE_CHANGE_GROUP_HEADER_CHEVRON_CLASS_NAME}
      />
    </div>
  );
}

// Commande de l'autre type (Citerne/Dompeur) sur ce camion : même case que la réaffectation.
function DompeurAcceptanceCheckbox({ truckId, checked, disabled, onChange }) {
  return (
    <label className="flex min-w-0 items-start gap-1.5 text-[0.64rem] font-semibold leading-4 text-slate-700" data-dompeur-acceptance={truckId}>
      <input type="checkbox" className="mt-0.5 h-3.5 w-3.5 shrink-0 accent-accent" checked={checked} disabled={disabled}
        onChange={(event) => onChange(event.target.checked)} />
      Garder ces commandes sur ce camion malgré le type Citerne/Dompeur
    </label>
  );
}

// Case cochée : l'autorisation est enregistrée avant l'actualisation de la carte.
const refreshWithDompeurAcceptance = (accepted, onAccept, truckId, refresh) => (accepted && typeof onAccept === "function"
  ? onAccept(truckId).then(refresh, (error) => { void alertInGrain(error.message); })
  : refresh());

function LoadingConstraintChangesSection({
  actionsDisabled = false,
  changes = null,
  isBusy = false,
  onOpenTruck,
  onAcceptDompeur,
  onRefresh,
  onRefreshAll,
  onReject,
  onRecoveryOpenChange,
  previewRevision,
  scopeKey,
  truckLabelById = {},
}) {
  const headingId = React.useId();
  const [dompeurAccepted, setDompeurAccepted] = useState({});
  const [activeRejectTruckId, setActiveRejectTruckId] = useState("");
  const [rejectionTruckId, setRejectionTruckId] = useState("");
  const [isRefreshingAll, setIsRefreshingAll] = useState(false);
  const details = Array.isArray(changes?.details) ? changes.details : [];
  // « Actualiser les meuneries » : toutes les cartes sauf une suspension à confirmer, temps supplémentaire accepté.
  // Masqué s'il ne vise qu'une carte : son propre bouton Actualiser suffit.
  const refreshableCount = details.filter((detail) => detail.applicationFeedback?.outcome !== "confirmation_required").length;
  const { activeTruckId, recovery, refreshTruck, closeRecovery, confirmRecovery } = useLoadingConstraintRecovery({
    actionsDisabled, details, isBusy, onRefresh, onRecoveryOpenChange, previewRevision, scopeKey,
  });
  useEffect(() => {
    if (
      rejectionTruckId
      && !details.some((detail) => normalizeInlineText(detail?.truckId) === rejectionTruckId)
    ) {
      setRejectionTruckId("");
    }
  }, [details, rejectionTruckId]);
  if (details.length === 0) {
    return null;
  }

  const rejectTruck = async (detail) => {
    if (typeof onReject !== "function" || activeRejectTruckId || activeTruckId || isBusy) {
      return;
    }
    const truckId = normalizeInlineText(detail?.truckId);
    setActiveRejectTruckId(truckId);
    try {
      const payload = await onReject(detail);
      if (payload) {
        setRejectionTruckId("");
      }
    } finally {
      setActiveRejectTruckId("");
    }
  };

  return (
    <section
      aria-labelledby={headingId}
      className={SOURCE_CHANGE_GROUP_SECTION_CLASS_NAME}
      data-loading-constraint-changes-section
    >
      <LoadingConstraintRecoveryConfirmation
        recovery={recovery}
        truckLabel={recovery ? resolveTruckSourceChangeLabel(recovery.truckId, truckLabelById) : ""}
        isBusy={Boolean(activeTruckId) || isBusy}
        onCancel={closeRecovery}
        onConfirm={confirmRecovery}
      />
      <SourceChangeGroupSectionHeader
        count={changes?.counts?.trucks || details.length}
        headingId={headingId}
        icon={ClockIcon}
        label="Meuneries"
      />
      <div className={SOURCE_CHANGE_GROUP_SECTION_BODY_CLASS_NAME}>
      {refreshableCount > 1 && typeof onRefreshAll === "function" ? (
        <button
          type="button" role="menuitem"
          className="inline-flex items-center justify-center gap-1.5 rounded-[4px] border border-accent bg-accent px-2 py-1 text-[0.65rem] font-bold text-white hover:border-accent-strong hover:bg-accent-strong focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/35 disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-slate-50 disabled:text-slate-400"
          data-loading-constraint-refresh-all
          disabled={actionsDisabled || isBusy || isRefreshingAll || Boolean(activeTruckId) || Boolean(activeRejectTruckId)}
          onClick={() => {
            setIsRefreshingAll(true);
            void Promise.resolve(onRefreshAll()).finally(() => setIsRefreshingAll(false));
          }}
        >
          <ArrowPathIcon aria-hidden="true" className={joinClasses(["h-3.5 w-3.5", isRefreshingAll ? "motion-safe:animate-spin" : ""])} />
          {isRefreshingAll ? "Actualisation des meuneries…" : `Actualiser les meuneries (${refreshableCount})`}
        </button>
      ) : null}
      {details.map((detail) => {
        const truckId = normalizeInlineText(detail?.truckId);
        const truckLabel = resolveTruckSourceChangeLabel(
          truckId,
          { ...truckLabelById, [truckId]: detail?.truckLabel },
        );
        const conflicts = Array.isArray(detail?.conflicts) ? detail.conflicts : [];
        const presentation = getTruckSourceChangePresentation({
          ...detail,
          status: conflicts.length || detail.applicationFeedback?.outcome === "blocked" ? "blocked" : "actionable",
          retryable: true,
          applicationFeedback: detail.applicationFeedback || (conflicts.length ? { kind: "business", diagnostics: [] } : null),
        }, {
          diagnostics: conflicts.map((conflict) => ({
            ...conflict,
            label: conflict.constraintType?.startsWith("client_loading") || conflict.constraintType === "loading_window"
              ? (detail.mills || []).find((mill) => mill.orderIds?.includes(conflict.orderId))?.name || "" : "",
            message: conflict.message || [
              LOADING_CONSTRAINT_CONFLICT_LABELS[conflict.constraintType] || "Horaire incompatible",
              formatTruckRecoveryLoadingTime(conflict.plannedTime) ? `prévu à ${formatTruckRecoveryLoadingTime(conflict.plannedTime)}` : "",
              formatLoadingConstraintConflictLimit(conflict) ? `limite ${formatLoadingConstraintConflictLimit(conflict)}` : "",
            ].filter(Boolean).join(" · ") + ".",
          })),
        });
        const isActive = activeTruckId === truckId;
        const isInactiveRefresh = !isActive
          && (isBusy || Boolean(activeTruckId) || Boolean(activeRejectTruckId));
        const isRejectActive = activeRejectTruckId === truckId;
        const isRejecting = rejectionTruckId === truckId;
        return (
          <article
            key={`${truckId}|${normalizeInlineText(detail?.changeRevision)}`}
            className={joinClasses([
              "grid min-w-0 gap-1 rounded-[4px] px-2 py-1.5",
              SOURCE_CHANGE_INNER_CARD_BORDER_CLASS_NAME,
              presentation.label === "À actualiser" ? "bg-[#f6f6f4]" : SOURCE_CHANGE_ITEM_CARD_SURFACE_CLASS_NAME,
            ])}
            data-loading-constraint-truck={truckId}
          >
            <div className="flex min-w-0 items-center gap-2">
              <button
                type="button" role="menuitem"
                className="min-w-0 flex-1 rounded-[4px] text-left text-[0.72rem] font-bold leading-tight text-slate-900 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/35"
                aria-label={`Ouvrir ${truckLabel}`}
                disabled={isBusy || typeof onOpenTruck !== "function"}
                onClick={() => onOpenTruck?.(detail)}
              >{truckLabel}</button>
              <span className={joinClasses([
                "shrink-0 rounded-[4px] border px-1.5 pt-[3px] pb-px text-[0.56rem] font-black uppercase tracking-[0.04em]",
                presentation.label === "Bloqué" ? "border-rose-200 bg-rose-50 text-rose-700"
                  : presentation.canRefresh ? "border-amber-200 bg-white text-amber-800"
                    : "border-slate-200 bg-white text-slate-600",
              ])}>{presentation.label}</span>
            </div>

            <div className="grid gap-1">
              {(detail.mills || []).map((mill) => (
                <div
                  key={`${mill.name}|${mill.loadingAddress || ""}`}
                  className="grid min-w-0 gap-0.5"
                  data-loading-constraint-mill
                >
                  <div className="flex items-center gap-2">
                    <span className="min-w-0 flex-1 truncate text-[0.66rem] font-bold text-slate-800">
                      {mill.name}
                    </span>
                    <span className="text-[0.58rem] font-semibold text-slate-500">
                      {mill.orderCount} cmd
                    </span>
                  </div>
                  {(mill.fieldChanges || []).map((change) => (
                    <div key={change.field} className="grid min-w-0 gap-0.5">
                      {(change.valueChanges?.length ? change.valueChanges : [change]).map((valueChange, index) => (
                        <TruckSourceEmphasizedLine
                          key={`${change.field}|${index}`}
                          className="m-0 min-w-0 text-[0.64rem] leading-4 text-slate-700"
                          text={`${LOADING_CONSTRAINT_FIELD_LABELS[change.field] || formatDataManagerAuditFieldLabel(change.field)} : ${formatLoadingConstraintValue(change.field, valueChange.dispatchValue)} → ${formatLoadingConstraintValue(change.field, valueChange.sourceValue)}${change.valueChanges?.length > 1 && valueChange.orderCount > 0 ? ` (${valueChange.orderCount} cmd)` : ""}`}
                        />
                      ))}
                    </div>
                  ))}
                </div>
              ))}
            </div>

            {presentation.items.length > 0 || presentation.guidance ? (
              <div role="status" className="grid min-w-0 gap-0.5 text-[0.66rem] leading-4 text-slate-600" data-loading-constraint-summary>
                {presentation.items.map((item, index) => (
                  <TruckSourceEmphasizedLine
                    key={`${truckId}|${item.reasonCode}|${index}`}
                    text={[compactTruckDiagnostic(item), item.resolutionMessage].filter(Boolean).join(" ")}
                  />
                ))}
                {presentation.guidance ? <p className="m-0 min-w-0">{presentation.guidance}</p> : null}
              </div>
            ) : null}
            {hasDompeurMismatch(presentation.items) && typeof onAcceptDompeur === "function" ? (
              <DompeurAcceptanceCheckbox truckId={truckId} checked={Boolean(dompeurAccepted[truckId])} disabled={isBusy || Boolean(activeTruckId)}
                onChange={(checked) => setDompeurAccepted((current) => ({ ...current, [truckId]: checked }))} />
            ) : null}

            {isRejecting ? (
              <div
                className="grid gap-1 rounded-[4px] border border-rose-200 bg-white/95 p-1.5"
                data-loading-constraint-reject-confirm={truckId}
                aria-label={`Confirmer le rejet des contraintes de ${truckLabel}`}
                role="alertdialog"
              >
                <span className="text-[0.61rem] font-semibold leading-4 text-rose-800">
                  Conserver les contraintes actuelles de ce camion dans ce dispatch ?
                </span>
                <div className="grid grid-cols-2 gap-1">
                  <button
                    type="button"
                    className="rounded-[4px] border border-slate-200 bg-white px-2 py-1 text-[0.62rem] font-bold text-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40"
                    disabled={isRejectActive}
                    onClick={() => setRejectionTruckId("")}
                  >
                    Annuler
                  </button>
                  <button
                    type="button"
                    className="inline-flex items-center justify-center gap-1 rounded-[4px] border border-rose-200 bg-white px-2 py-1 text-[0.62rem] font-bold text-rose-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-300 disabled:cursor-not-allowed disabled:opacity-50"
                    disabled={actionsDisabled || isBusy || isRejectActive}
                    onClick={() => void rejectTruck(detail)}
                  >
                    <XCircleIcon aria-hidden="true" className="h-3.5 w-3.5" />
                    {isRejectActive ? "Rejet…" : "Confirmer"}
                  </button>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-1" data-loading-constraint-actions={truckId}>
                <button
                  type="button"
                  role="menuitem"
                  aria-busy={isActive}
                  aria-label={isActive ? `Actualisation de ${truckLabel} en cours` : `Actualiser ${truckLabel}`}
                  className={joinClasses([
                    "inline-flex items-center justify-center gap-1 rounded-[4px] border px-2 py-1 text-[0.65rem] font-bold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40 disabled:cursor-not-allowed",
                    isInactiveRefresh
                      ? SOURCE_CHANGE_REFRESH_INACTIVE_BUTTON_CLASS_NAME
                      : isActive
                        ? SOURCE_CHANGE_REFRESH_ACTIVE_BUTTON_CLASS_NAME
                        : "border-accent bg-accent text-white hover:border-accent-strong hover:bg-accent-strong",
                  ])}
                  data-loading-constraint-refresh={truckId}
                  disabled={actionsDisabled || isBusy || Boolean(activeTruckId) || Boolean(activeRejectTruckId) || typeof onRefresh !== "function"}
                  onClick={() => void refreshWithDompeurAcceptance(hasDompeurMismatch(presentation.items) && dompeurAccepted[truckId], onAcceptDompeur, truckId, () => refreshTruck(detail))}
                >
                  {isActive ? (
                    <ArrowPathIcon
                      aria-hidden="true"
                      className="h-3.5 w-3.5 motion-safe:animate-spin"
                      data-loading-constraint-spinner
                    />
                  ) : (
                    <ArrowPathIcon
                      aria-hidden="true"
                      className={joinClasses(["h-3.5 w-3.5", activeTruckId ? "invisible" : ""])}
                    />
                  )}
                  {isActive ? "Actualisation…" : detail.applicationFeedback?.outcome === "confirmation_required" ? "Confirmer la suspension" : "Actualiser"}
                </button>
                <button
                  type="button"
                  role="menuitem"
                  aria-haspopup="dialog"
                  className="inline-flex items-center justify-center gap-1 rounded-[4px] border border-rose-200 bg-white px-2 py-1 text-[0.65rem] font-bold text-rose-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-300 disabled:cursor-not-allowed disabled:opacity-50"
                  data-loading-constraint-reject={truckId}
                  disabled={actionsDisabled || isBusy || Boolean(activeTruckId) || Boolean(activeRejectTruckId) || typeof onReject !== "function"}
                  onClick={() => setRejectionTruckId(truckId)}
                >
                  <XCircleIcon aria-hidden="true" className="h-3.5 w-3.5" />
                  Rejeter
                </button>
              </div>
            )}
          </article>
        );
      })}
      </div>
    </section>
  );
}


const TRUCK_SOURCE_CHANGE_SECTION_ICONS = { truck: TruckIcon, trailer: CubeIcon, driver: UserIcon };

export function TruckSourceChangesSection({
  dispatchPeriod = "day",
  activeTruckRefreshId = "",
  actionsDisabled = false,
  isBusy = false,
  onOpenTruck,
  onRequestReject,
  onRequestRefresh,
  onRequestRefreshAll,
  onAcceptDompeur,
  truckChanges = null,
  truckDriverById = null,
  truckLabelById = {},
}) {
  const headingId = React.useId();
  const [dompeurAccepted, setDompeurAccepted] = useState({});
  if (truckChanges?.enabled !== true) {
    return null;
  }
  const state = normalizeInlineText(truckChanges?.state).toLowerCase();
  const normalizedActiveTruckRefreshId = normalizeInlineText(activeTruckRefreshId);
  const hasActiveTruckRefresh = Boolean(normalizedActiveTruckRefreshId);
  const unresolvedCount = resolveReadyTruckChangeCount({ truckChanges }, "unresolved");
  const details = state === "ready" && Array.isArray(truckChanges?.details)
    ? truckChanges.details
    : [];
  const globalChangedFields = state === "ready" && Array.isArray(truckChanges?.globalChangedFields)
    ? truckChanges.globalChangedFields
    : [];
  const shouldShow = state === "reference_required"
    || state === "unavailable"
    || (state === "ready" && (unresolvedCount > 0 || globalChangedFields.length > 0));
  if (!shouldShow) {
    return null;
  }

  // Avis de toute la flotte (référence, indisponibilité, paramètres globaux) : section Camions.
  const fleetNotices = (
    <>
      {state === "reference_required" ? (
        <SourceChangeApplicationFeedback fallbackDiagnostics={[{
          reasonCode: "fleet_reference_required", message: "Une optimisation complète est requise pour initialiser la référence des camions.",
        }]} />
      ) : null}
      {state === "unavailable" ? (
        <SourceChangeApplicationFeedback role="alert" fallbackDiagnostics={[{
          reasonCode: "fleet_unavailable", message: "La vérification des changements camion est indisponible. Rouvrez Mise à jour pour réessayer.",
        }]} />
      ) : null}

      {state === "ready" && globalChangedFields.length > 0 ? (
        <article
          className={joinClasses([
            "grid gap-1.5 rounded-[4px] p-2",
            SOURCE_CHANGE_ITEM_CARD_SURFACE_CLASS_NAME,
            SOURCE_CHANGE_INNER_CARD_BORDER_CLASS_NAME,
          ])}
          data-truck-global-source-changes
        >
          <div className="flex items-center gap-2">
            <span className="min-w-0 flex-1 text-[0.72rem] font-bold text-slate-800">
              Paramètres globaux de la période
            </span>
            <span className="rounded-[4px] border border-slate-200 bg-white px-1.75 py-0.5 text-[0.56rem] font-black uppercase tracking-[0.08em] text-slate-600">
              Optimisation requise
            </span>
          </div>
          <TruckSourceChangedFields fields={globalChangedFields} truckLabelById={truckLabelById} />
          <SourceChangeApplicationFeedback fallbackDiagnostics={[{
            reasonCode: "global_period_change", message: formatTruckSourceChangeReason("global_period_change"),
          }]} />
        </article>
      ) : null}
    </>
  );

  const renderTruckChange = (detail) => {
        const truckId = normalizeInlineText(detail?.truckId);
        const truckLabel = resolveTruckSourceChangeLabel(truckId, truckLabelById);
        // Chauffeur actuel du Gantt ; une ligne connue sans chauffeur l'indique.
        const driverLabel = truckDriverById && Object.prototype.hasOwnProperty.call(truckDriverById, truckId)
          ? normalizeInlineText(truckDriverById[truckId]) || "Aucun chauffeur"
          : "";
        const isActionable = detail?.status === "actionable";
        const isTruckRefreshActive = truckId === normalizedActiveTruckRefreshId;
        const isInactiveTruckRefresh = !isTruckRefreshActive
          && (isBusy || hasActiveTruckRefresh);
        const reasonCodes = Array.isArray(detail?.reasonCodes) ? detail.reasonCodes : [];
        const changedFields = Array.isArray(detail?.changedFields) ? detail.changedFields : [];
        const recovery = detail?.status === "blocked" && detail?.recovery
          ? detail.recovery
          : null;
        const visibleReasonCodes = recovery
          ? reasonCodes.filter((reasonCode) => ![
              "local_feasibility_failed",
              "max_work_hours_exceeded",
              "time_insufficient",
            ].includes(reasonCode))
          : reasonCodes;
        const presentation = getTruckSourceChangePresentation(detail, {
          fallbackDiagnostics: visibleReasonCodes.map((reasonCode) => ({ reasonCode, message: formatTruckSourceChangeReason(reasonCode) })),
          diagnostics: recovery ? [{
            summary: true,
            reasonCode: recovery.constraintKind === "load_capacity" ? "load_capacity_exceeded" : "max_work_hours_exceeded",
            plannedValue: recovery.constraintKind === "load_capacity" ? recovery.requiredTons : recovery.requiredMinutes,
            limit: recovery.constraintKind === "load_capacity" ? recovery.availableTons : recovery.availableMinutes,
            unit: recovery.constraintKind === "load_capacity" ? "tons" : "minutes",
            durationBasis: "retained_route",
            message: recovery.constraintKind === "load_capacity" ? "Le chargement dépasse la nouvelle capacité." : "La tournée dépasse la durée disponible.",
            resolutionMessage: resolveBlockedTruckRecoveryMessage(recovery, truckLabel),
            resolutionReasonCode: recovery.status === "protected_only" ? "protected_capacity_trim_unavailable"
              : recovery.status === "insufficient" ? "capacity_trim_insufficient" : "",
          }] : [],
        });
        const preferredSummaryField = detail?.lastChangedField
          || (presentation.items[0]?.unit === "minutes"
            ? "max_work_hours"
            : presentation.items[0]?.unit === "tons"
              ? "capacity_tons"
              : "");
        // Changement d'attelage : la remorque ou le camion remplacé, sans les valeurs « avant → après » du camion.
        const coupling = detail?.couplingChange || null;
        // Un conflit affiché remplace les valeurs « avant → après » des champs : seul le conflit est montré.
        const showsConflict = presentation.items.length > 0 && detail?.status !== "unsupported";
        const fieldSummaryLines = coupling ? [formatCouplingChangeLine(coupling)] : showsConflict ? [] : summarizeTruckSourceFieldLines(changedFields,
          (field, value) => formatTruckSourceChangeValue(field, value, truckLabelById),
          { preferredField: preferredSummaryField, dispatchPeriod, groupBySource: true });
        return (
          <article
            key={`${truckId}|${normalizeInlineText(detail?.changeRevision)}`}
            className={joinClasses([
              "grid min-w-0 gap-1 rounded-[4px] px-2 py-1.5",
              presentation.label === "À ajuster" ? "border border-blue-200 bg-blue-50"
                : joinClasses([SOURCE_CHANGE_INNER_CARD_BORDER_CLASS_NAME,
                  isActionable ? "bg-[#f6f6f4]" : SOURCE_CHANGE_ITEM_CARD_SURFACE_CLASS_NAME]),
            ])}
            data-truck-change-status={detail?.status}
            data-truck-change-presentation={presentation.label}
          >
            <div className="flex min-w-0 items-center gap-2">
              <button
                type="button" role="menuitem"
                className="min-w-0 flex-1 rounded-[4px] text-left text-[0.72rem] font-bold leading-tight text-slate-900 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/35"
                aria-label={`Ouvrir ${truckLabel}`}
                disabled={isBusy || typeof onOpenTruck !== "function"}
                onClick={() => onOpenTruck?.(detail)}
              >{truckLabel}{driverLabel ? (
                <span className="font-medium text-slate-500" data-truck-change-driver={truckId}>{` · ${driverLabel}`}</span>
              ) : null}</button>
              <span className={joinClasses([
                "shrink-0 rounded-[4px] border px-1.5 pt-[3px] pb-px text-[0.56rem] font-black uppercase tracking-[0.04em]",
                presentation.label === "À ajuster" ? "border-blue-200 bg-blue-50 text-blue-800"
                  : presentation.label === "Bloqué" ? "border-rose-200 bg-rose-50 text-rose-700"
                  : presentation.canRefresh ? "border-amber-200 bg-white text-amber-800"
                    : "border-slate-200 bg-white text-slate-600",
              ])}>{presentation.label}</span>
            </div>
            {fieldSummaryLines.length > 0 ? (
              <div className="grid min-w-0 gap-0.5">
                {fieldSummaryLines.map((line, index) => (
                  <TruckSourceEmphasizedLine
                    key={`${truckId}|${line}|${index}`}
                    text={line}
                    className="m-0 min-w-0 text-[0.64rem] leading-4 text-slate-700"
                  />
                ))}
              </div>
            ) : null}
            {presentation.items.length > 0 || presentation.guidance ? (
              <div role="status" className="grid min-w-0 gap-0.5 text-[0.66rem] leading-4 text-slate-600" data-truck-change-summary>
                {presentation.items.map((item, index) => (
                  <TruckSourceEmphasizedLine
                    key={`${truckId}|${item.reasonCode}|${index}`}
                    text={[compactTruckDiagnostic(item), item.resolutionMessage].filter(Boolean).join(" ")}
                  />
                ))}
                {presentation.guidance ? <p className="m-0 min-w-0">{presentation.guidance}</p> : null}
              </div>
            ) : null}
            {hasDompeurMismatch(presentation.items) && typeof onAcceptDompeur === "function" ? (
              <DompeurAcceptanceCheckbox truckId={truckId} checked={Boolean(dompeurAccepted[truckId])} disabled={isBusy || hasActiveTruckRefresh}
                onChange={(checked) => setDompeurAccepted((current) => ({ ...current, [truckId]: checked }))} />
            ) : null}
            <div className="grid grid-cols-2 gap-1" data-truck-source-change-actions={truckId}>
              <button
                type="button" role="menuitem"
                aria-haspopup={detail?.recovery?.status === "confirmation_required" ? "dialog" : undefined}
                aria-busy={isTruckRefreshActive ? "true" : undefined}
                aria-label={isTruckRefreshActive ? `Actualisation de ${truckLabel} en cours` : `Actualiser ${truckLabel}`}
                className={joinClasses([
                  "inline-flex items-center justify-center gap-1.5 rounded-[4px] border px-2 py-1 text-[0.65rem] font-bold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/35 disabled:cursor-not-allowed",
                  isInactiveTruckRefresh || !presentation.canRefresh ? SOURCE_CHANGE_REFRESH_INACTIVE_BUTTON_CLASS_NAME
                    : isTruckRefreshActive ? SOURCE_CHANGE_REFRESH_ACTIVE_BUTTON_CLASS_NAME
                      : "border-accent bg-accent text-white hover:border-accent-strong hover:bg-accent-strong",
                ])}
                data-truck-refresh-active={isTruckRefreshActive ? "true" : undefined}
                data-truck-refresh-trigger={truckId}
                disabled={!presentation.canRefresh || actionsDisabled || isBusy || hasActiveTruckRefresh || typeof onRequestRefresh !== "function"}
                onClick={(event) => {
                  const trigger = event.currentTarget;
                  void refreshWithDompeurAcceptance(hasDompeurMismatch(presentation.items) && dompeurAccepted[truckId], onAcceptDompeur, truckId, () => onRequestRefresh(detail, trigger));
                }}
              >
                <ArrowPathIcon aria-hidden="true" className={joinClasses(["h-3.5 w-3.5", isTruckRefreshActive ? "motion-safe:animate-spin" : ""])} />
                {isTruckRefreshActive ? "Actualisation…" : "Actualiser"}
              </button>
              {typeof onRequestReject === "function" ? (
                <button
                  type="button" role="menuitem" aria-haspopup="dialog"
                  className="inline-flex items-center justify-center gap-1.5 rounded-[4px] border border-rose-200 bg-white px-2 py-1 text-[0.65rem] font-bold text-rose-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-300 disabled:cursor-not-allowed disabled:opacity-50"
                  data-truck-reject-trigger={truckId}
                  disabled={actionsDisabled || isBusy || hasActiveTruckRefresh}
                  onClick={(event) => onRequestReject(detail, event.currentTarget)}
                >
                  <XCircleIcon aria-hidden="true" className="h-3.5 w-3.5" />
                  Rejeter
                </button>
              ) : null}
            </div>
          </article>
        );
  };

  // Une carte par camion, dans la section de sa modification (Camions, puis Remorques, puis Chauffeurs).
  const sections = groupTruckSourceChangesBySection(details);
  if ((state !== "ready" || globalChangedFields.length > 0) && sections[0]?.key !== "truck") {
    sections.unshift({ ...TRUCK_SOURCE_CHANGE_SECTIONS[0], details: [] });
  }
  // Tous les camions à actualiser ou à ajuster d'un coup, toutes sections confondues ; ceux qui ne passent pas gardent leur carte.
  // Masqué s'il ne vise qu'une carte : son propre bouton Actualiser suffit.
  const refreshableCount = details.filter(canRefreshTruckSourceChange).length;
  const refreshAllButton = refreshableCount > 1 && typeof onRequestRefreshAll === "function" ? (
    <button
      type="button" role="menuitem"
      className="inline-flex items-center justify-center gap-1.5 rounded-[4px] border border-accent bg-accent px-2 py-1 text-[0.65rem] font-bold text-white hover:border-accent-strong hover:bg-accent-strong focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/35 disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-slate-50 disabled:text-slate-400"
      data-truck-refresh-all-trigger
      disabled={actionsDisabled || isBusy || hasActiveTruckRefresh}
      onClick={() => onRequestRefreshAll()}
    >
      <ArrowPathIcon aria-hidden="true" className="h-3.5 w-3.5" />
      {`Actualiser les camions (${refreshableCount})`}
    </button>
  ) : null;
  return sections.map((section, index) => (
    <section
      key={section.key}
      aria-labelledby={`${headingId}-${section.key}`}
      className={SOURCE_CHANGE_GROUP_SECTION_CLASS_NAME}
      data-truck-source-changes-section
      data-truck-source-changes-group={section.key}
    >
      <SourceChangeGroupSectionHeader
        count={section.details.length}
        headingId={`${headingId}-${section.key}`}
        icon={TRUCK_SOURCE_CHANGE_SECTION_ICONS[section.key]}
        label={section.label}
      />
      <div className={SOURCE_CHANGE_GROUP_SECTION_BODY_CLASS_NAME}>
        {index === 0 ? refreshAllButton : null}
        {section.key === "truck" ? fleetNotices : null}
        {section.details.map(renderTruckChange)}
      </div>
    </section>
  ));
}


export function SourceChangesMenuButton({
  fillIndicator = false,
  activeDispatchPeriod = "day",
  dispatchPeriodLabel = "Jour",
  sourceNewCount,
  sourceDateChangedCount = 0,
  sourceMissingDispatchOrderCount = 0,
  sourceRemovedEndOfDayLoadCount = 0,
  sourceGhostOrderCount = sourceMissingDispatchOrderCount + sourceRemovedEndOfDayLoadCount,
  sourceSuspendableErrorCount = 0,
  sourceEndOfDayLoadIntegrityCount = 0,
  sourceUnresolvedCount = 0,
  sourceBadgePendingCount = null,
  sourceActionablePendingCount = 0,
  sourceChanges,
  sourceChangeDisabledReason,
  hasVerifiedSourceChangesPreview = false,
  activeDispatchHasSnapshot,
  disableMutationActions,
  isClosedDispatchDay = false,
  sourceChangesBusy,
  canImportSourceChanges,
  canImportSuspendableErrors,
  canRemoveSourceChanges,
  canRemoveDateChangedSourceChanges,
  canRepairGhostOrders,
  canRestoreExecutions,
  isOpen: controlledIsOpen = null,
  onOpenChange = null,
  openRequestedPeriod = "",
  onOpenRequestConsumed = null,
  truckDriverById = null,
  truckLabelById = {},
}) {
  const planningWriteMessage = usePlanningWriteActivity();
  sourceChangesBusy ||= Boolean(planningWriteMessage);
  disableMutationActions ||= Boolean(planningWriteMessage);
  canImportSourceChanges &&= !planningWriteMessage;
  canImportSuspendableErrors &&= !planningWriteMessage;
  canRemoveSourceChanges &&= !planningWriteMessage;
  canRemoveDateChangedSourceChanges &&= !planningWriteMessage;
  canRepairGhostOrders &&= !planningWriteMessage;
  canRestoreExecutions &&= !planningWriteMessage;
  sourceChangeDisabledReason = planningWriteMessage || sourceChangeDisabledReason;
  const anchorRef = useRef(null);
  const menuRef = useRef(null);
  const menuId = React.useId();
  const [uncontrolledIsMenuOpen, setUncontrolledIsMenuOpen] = useState(false);
  const isMenuOpenControlled = typeof controlledIsOpen === "boolean";
  const isMenuOpen = isMenuOpenControlled ? controlledIsOpen : uncontrolledIsMenuOpen;
  const { refs: menuPositionRefs, floatingStyles: menuPositionStyles, isPositioned: isMenuPositioned } = useFloating({
    open: isMenuOpen, placement: "bottom-end", transform: false,
    middleware: [offset(7), shift({ padding: 8 })], whileElementsMounted: autoUpdate,
  });
  useEffect(() => {
    menuPositionRefs.setReference(anchorRef.current);
    menuPositionRefs.setFloating(menuRef.current);
  }, [isMenuOpen, menuPositionRefs]);
  const wasMenuOpenRef = useRef({ isOpen: false, scopeKey: "" });
  const setIsMenuOpen = useCallback((nextValue) => {
    const resolvedValue = typeof nextValue === "function"
      ? nextValue(isMenuOpen)
      : nextValue;
    if (!isMenuOpenControlled) {
      setUncontrolledIsMenuOpen(resolvedValue);
    }
    onOpenChange?.(resolvedValue);
  }, [isMenuOpen, isMenuOpenControlled, onOpenChange]);
  const normalizedActiveDispatchPeriod = ["evening", "soir"].includes(normalizeInlineText(activeDispatchPeriod).toLowerCase())
    ? "evening"
    : "day";
  const requestedPeriodText = normalizeInlineText(openRequestedPeriod).toLowerCase();
  const normalizedOpenRequestedPeriod = ["evening", "soir"].includes(requestedPeriodText)
    ? "evening"
    : ["day", "jour"].includes(requestedPeriodText) ? "day" : "";
  useEffect(() => {
    if (!normalizedOpenRequestedPeriod || normalizedOpenRequestedPeriod !== normalizedActiveDispatchPeriod) {
      return;
    }

    setIsMenuOpen(true);
    onOpenRequestConsumed?.(normalizedOpenRequestedPeriod);
  }, [normalizedActiveDispatchPeriod, normalizedOpenRequestedPeriod, onOpenRequestConsumed, setIsMenuOpen]);
  const [isRemoveModifiedConfirmOpen, setIsRemoveModifiedConfirmOpen] = useState(false);
  const [isRemoveDateChangedConfirmOpen, setIsRemoveDateChangedConfirmOpen] = useState(false);
  const [isRepairGhostOrdersConfirmOpen, setIsRepairGhostOrdersConfirmOpen] = useState(false);
  const [isRestoreExecutionsConfirmOpen, setIsRestoreExecutionsConfirmOpen] = useState(false);
  const [isImportSuspendableErrorsConfirmOpen, setIsImportSuspendableErrorsConfirmOpen] = useState(false);
  const [protectedSourceChangeToAcknowledge, setProtectedSourceChangeToAcknowledge] = useState(null);
  const [nonRenderableTripAction, setNonRenderableTripAction] = useState(null);
  const [failedRepairConflictId, setFailedRepairConflictId] = useState("");
  const [truckRecoveryConfirmation, setTruckRecoveryConfirmation] = useState(null);
  const [isLoadingRecoveryConfirmationOpen, setIsLoadingRecoveryConfirmationOpen] = useState(false);
  const [truckRecoveryPendingTarget, setTruckRecoveryPendingTarget] = useState(null);
  const [truckRejectionConfirmation, setTruckRejectionConfirmation] = useState(null);
  const [truckRefreshPendingTarget, setTruckRefreshPendingTarget] = useState(null);
  const truckRefreshPreparationRef = useRef(null);
  const nonRenderableTripActionTriggerRef = useRef(null);
  const truckRecoveryActionTriggerRef = useRef(null);
  const truckRecoveryFocusTargetRef = useRef("");
  const truckRejectionActionTriggerRef = useRef(null);
  useEffect(() => {
    const menuScopeKey = `${normalizeInlineText(sourceChanges?.activeScopeDispatchDate)}|${normalizedActiveDispatchPeriod}`;
    const previousOpenState = wasMenuOpenRef.current;
    const didOpen = isMenuOpen && (!previousOpenState.isOpen || previousOpenState.scopeKey !== menuScopeKey);
    wasMenuOpenRef.current = { isOpen: isMenuOpen, scopeKey: menuScopeKey };
    const shouldVerifyPendingContext = isMenuOpen
      && sourceChanges?.needsPlanningContextVerification === true;
    if (
      (!didOpen && !shouldVerifyPendingContext)
      || typeof sourceChanges?.onOpen !== "function"
    ) {
      return;
    }
    void Promise.resolve(sourceChanges.onOpen()).catch(() => {
      // Le contrôleur expose déjà l'erreur de vérification dans le menu.
    });
  }, [
    isMenuOpen,
    normalizedActiveDispatchPeriod,
    sourceChanges?.activeScopeDispatchDate,
    sourceChanges?.needsPlanningContextVerification,
    sourceChanges?.onOpen,
    sourceChanges?.planningContextVerificationVersion,
  ]);
  const details = sourceChanges?.details && typeof sourceChanges.details === "object"
    ? sourceChanges.details
    : {};
  const truckChanges = sourceChanges?.truckChanges && typeof sourceChanges.truckChanges === "object"
    ? sourceChanges.truckChanges
    : null;
  const loadingConstraintChanges = sourceChanges?.loadingConstraintChanges
    && typeof sourceChanges.loadingConstraintChanges === "object"
    ? sourceChanges.loadingConstraintChanges
    : null;
  const mergedTruckLabelById = useMemo(() => {
    const merged = truckLabelById && typeof truckLabelById === "object" ? { ...truckLabelById } : {};
    const truckDetails = Array.isArray(truckChanges?.details) ? truckChanges.details : [];
    for (const detail of truckDetails) {
      const truckId = normalizeInlineText(detail?.truckId);
      const truckLabel = normalizeInlineText(detail?.truckLabel);
      if (!truckId || !truckLabel || looksLikeInternalTruckSourceChangeId(truckLabel)) {
        continue;
      }
      merged[truckId] = truckLabel;
    }
    const loadingConstraintDetails = Array.isArray(loadingConstraintChanges?.details)
      ? loadingConstraintChanges.details
      : [];
    for (const detail of loadingConstraintDetails) {
      const truckId = normalizeInlineText(detail?.truckId);
      const truckLabel = normalizeInlineText(detail?.truckLabel);
      if (truckId && truckLabel) {
        merged[truckId] = truckLabel;
      }
    }
    return merged;
  }, [loadingConstraintChanges?.details, truckChanges?.details, truckLabelById]);
  const truckChangesState = normalizeInlineText(truckChanges?.state).toLowerCase();
  const truckChangesUnresolvedCount = resolveReadyTruckChangeCount(sourceChanges, "unresolved");
  const globalTruckChangedFields = truckChangesState === "ready"
    && Array.isArray(truckChanges?.globalChangedFields)
    ? truckChanges.globalChangedFields
    : [];
  const showTruckSourceChangesSection = truckChanges?.enabled === true
    && (
      truckChangesState === "reference_required"
      || truckChangesState === "unavailable"
      || (
        truckChangesState === "ready"
        && (truckChangesUnresolvedCount > 0 || globalTruckChangedFields.length > 0)
      )
    );
  const truckConfirmationContextKey = JSON.stringify({
    dispatchDate: normalizeInlineText(sourceChanges?.dispatchDate),
    dispatchPeriod: normalizedActiveDispatchPeriod,
    featureRevision: normalizeInlineText(truckChanges?.featureRevision),
    hasVerified: sourceChanges?.hasVerified === true,
    isLoading: sourceChanges?.isLoading === true,
    isMutating: sourceChanges?.isMutating === true,
    isVerifying: sourceChanges?.isVerifying === true,
    needsPlanningContextVerification: sourceChanges?.needsPlanningContextVerification === true,
    needsVerification: sourceChanges?.needsVerification === true,
    previewRevision: normalizeInlineText(sourceChanges?.previewRevision),
    truckChanges,
  });
  const activeTruckRefreshId = truckRefreshPendingTarget
    && truckRefreshPendingTarget.dispatchDate === normalizeInlineText(sourceChanges?.dispatchDate)
    && truckRefreshPendingTarget.dispatchPeriod === normalizedActiveDispatchPeriod
    ? normalizeInlineText(truckRefreshPendingTarget.truckId)
    : "";

  const newDetails = Array.isArray(details.new) ? details.new : [];
  const dateChangedOrderDetails = Array.isArray(details.date_changed_remove) ? details.date_changed_remove : [];
  const dateChangedEndOfDayLoadDetails = Array.isArray(details.removed_end_of_day_loads)
    ? details.removed_end_of_day_loads.filter(isDateChangedEndOfDayLoadDetail)
    : [];
  const dateChangedRemoveDetails = [...dateChangedOrderDetails, ...dateChangedEndOfDayLoadDetails];
  const missingDispatchOrderDetails = Array.isArray(details.missing_dispatch_orders) ? details.missing_dispatch_orders : [];
  const removedEndOfDayLoadDetails = Array.isArray(details.removed_end_of_day_loads)
    ? details.removed_end_of_day_loads.filter(isRemovableOrphanEndOfDayLoadDetail)
    : [];
  const mixedEndOfDayLoadDetails = Array.isArray(details.removed_end_of_day_loads)
    ? details.removed_end_of_day_loads.filter(isMixedOrphanEndOfDayLoadDetail)
    : [];
  const inconsistentExecutionRemovalDetails = Array.isArray(details.inconsistent_execution_removals) ? details.inconsistent_execution_removals : [];
  const protectedExecutionDetails = Array.isArray(details.protected_execution)
    ? details.protected_execution.filter(Boolean)
    : [];
  const integrityConflictDetails = Array.isArray(details.integrity_conflicts)
    ? details.integrity_conflicts.filter(Boolean)
    : [];
  const removeModifiedDetails = selectRemoveModifiedSourceChangeDetails(details);
  const removeModifiedOrderIds = new Set(removeModifiedDetails.map(
    (detail) => normalizeInlineText(detail?.order_id || detail?.orderId),
  ));
  useEffect(() => {
    if (!protectedSourceChangeToAcknowledge) {
      return;
    }
    const selectedOrderId = normalizeInlineText(
      protectedSourceChangeToAcknowledge.order_id || protectedSourceChangeToAcknowledge.orderId,
    );
    const selectedChangeRevision = normalizeInlineText(
      protectedSourceChangeToAcknowledge.change_revision || protectedSourceChangeToAcknowledge.changeRevision,
    );
    const selectionIsStillCurrent = protectedExecutionDetails.some((detail) => (
      normalizeInlineText(detail?.order_id || detail?.orderId) === selectedOrderId
      && normalizeInlineText(detail?.change_revision || detail?.changeRevision) === selectedChangeRevision
    ));
    if (!selectionIsStillCurrent) {
      setProtectedSourceChangeToAcknowledge(null);
    }
  }, [protectedExecutionDetails, protectedSourceChangeToAcknowledge]);
  const recoverableExecutionDetails = inconsistentExecutionRemovalDetails.filter((detail) => detail?.recovery_eligible === true);
  const blockedExecutionDetails = inconsistentExecutionRemovalDetails.filter((detail) => detail?.recovery_eligible !== true);
  const recoverableExecutionCount = recoverableExecutionDetails.length;
  const protectedReviewDetails = protectedExecutionDetails.filter((detail) => (
    !removeModifiedOrderIds.has(normalizeInlineText(detail?.order_id || detail?.orderId))
  ));
  const removeModifiedCount = removeModifiedDetails.length;
  const sourceMutationPendingCount = sourceNewCount
    + removeModifiedCount
    + sourceDateChangedCount
    + sourceGhostOrderCount
    + recoverableExecutionCount;
  const computedUnresolvedPendingCount = sourceMutationPendingCount
    + sourceSuspendableErrorCount
    + sourceEndOfDayLoadIntegrityCount
    + protectedReviewDetails.length
    + integrityConflictDetails.length
    + blockedExecutionDetails.length
    + mixedEndOfDayLoadDetails.length;
  const resolvedSourceBadgePendingCount = Number.isFinite(Number(sourceBadgePendingCount))
    ? Math.max(0, Math.round(Number(sourceBadgePendingCount)))
    : null;
  const unresolvedPendingCount = resolvedSourceBadgePendingCount ?? Math.max(
    sourceUnresolvedCount,
    computedUnresolvedPendingCount,
  );
  const actionablePendingCount = Number.isFinite(Number(sourceActionablePendingCount))
    ? Math.max(0, Math.round(Number(sourceActionablePendingCount)))
    : 0;
  const planningContextVerificationErrorMessage = normalizeInlineText(
    sourceChanges?.planningContextVerificationErrorMessage,
  );
  const sourceChangeUnavailableMessage = normalizeInlineText(sourceChanges?.unavailableMessage);
  const sourceChangeErrorMessage = normalizeInlineText(sourceChanges?.errorMessage)
    || planningContextVerificationErrorMessage;
  const sourceChangesNeedVerification = !sourceChangeUnavailableMessage
    && (
      sourceChanges?.needsVerification === true
      || sourceChanges?.isLoading === true
      || sourceChanges?.isVerifying === true
    );
  const sourceChangesActionNeedVerification = sourceChangesNeedVerification
    || sourceChanges?.needsPlanningContextVerification === true;
  const hasPending = unresolvedPendingCount > 0 || Boolean(sourceChangeErrorMessage);
  const shouldPulse = unresolvedPendingCount > 0;
  const pendingCountsLabel = `${unresolvedPendingCount} à examiner, dont ${actionablePendingCount} actionnable${actionablePendingCount > 1 ? "s" : ""}`;
  const buttonDisabled = !activeDispatchHasSnapshot || (sourceChangesBusy && !planningWriteMessage);
  const ghostOrderDetails = missingDispatchOrderDetails;
  const suspendableErrorDetails = Array.isArray(sourceChanges?.suspendableErrors?.details)
    ? sourceChanges.suspendableErrors.details
    : [];

  const closeMenu = useCallback(() => {
    setIsRemoveModifiedConfirmOpen(false);
    setIsRemoveDateChangedConfirmOpen(false);
    setIsRepairGhostOrdersConfirmOpen(false);
    setProtectedSourceChangeToAcknowledge(null);
    setIsRestoreExecutionsConfirmOpen(false);
    setIsImportSuspendableErrorsConfirmOpen(false);
    setNonRenderableTripAction(null);
    setTruckRecoveryConfirmation(null);
    setTruckRecoveryPendingTarget(null);
    setTruckRejectionConfirmation(null);
    setFailedRepairConflictId("");
    setIsMenuOpen(false);
  }, [setIsMenuOpen]);

  const closeSourceChangeConfirm = useCallback(() => {
    setIsRemoveModifiedConfirmOpen(false);
    setIsRemoveDateChangedConfirmOpen(false);
    setIsRepairGhostOrdersConfirmOpen(false);
    setProtectedSourceChangeToAcknowledge(null);
    setIsRestoreExecutionsConfirmOpen(false);
    setIsImportSuspendableErrorsConfirmOpen(false);
    setNonRenderableTripAction(null);
    setTruckRecoveryConfirmation(null);
    setTruckRejectionConfirmation(null);
  }, []);

  const restoreTruckRejectionFocus = useCallback(() => {
    window.requestAnimationFrame(() => {
      const triggerElement = truckRejectionActionTriggerRef.current;
      if (
        triggerElement instanceof HTMLElement
        && triggerElement.isConnected
        && triggerElement.matches(":not(:disabled)")
      ) {
        triggerElement.focus();
      } else {
        menuRef.current?.focus();
      }
    });
  }, []);

  const closeTruckRejectionConfirm = useCallback(() => {
    setTruckRejectionConfirmation(null);
    restoreTruckRejectionFocus();
  }, [restoreTruckRejectionFocus]);

  const restoreTruckRecoveryFocus = useCallback(() => {
    window.requestAnimationFrame(() => {
      const matchingTrigger = (selector, datasetKey) => Array.from(
        menuRef.current?.querySelectorAll(selector) || [],
      ).find((element) => (
        element instanceof HTMLElement
        && element.dataset[datasetKey] === truckRecoveryFocusTargetRef.current
        && element.getClientRects().length > 0
      ));
      const triggerElement = matchingTrigger("[data-truck-refresh-trigger]", "truckRefreshTrigger")
        || truckRecoveryActionTriggerRef.current;
      if (
        triggerElement instanceof HTMLElement
        && triggerElement.isConnected
        && triggerElement.matches(":not(:disabled)")
        && triggerElement.getClientRects().length > 0
      ) {
        triggerElement.focus();
      } else {
        menuRef.current?.focus();
      }
    });
  }, []);

  const closeTruckRecoveryConfirm = useCallback(() => {
    setTruckRecoveryConfirmation(null);
    restoreTruckRecoveryFocus();
  }, [restoreTruckRecoveryFocus]);

  useEffect(() => {
    if (
      truckRejectionConfirmation
      && (
        truckRejectionConfirmation.contextKey !== truckConfirmationContextKey
        || typeof sourceChanges?.onRejectTruck !== "function"
      )
    ) {
      closeTruckRejectionConfirm();
    }
    if (
      truckRecoveryConfirmation
      && typeof sourceChanges?.onRecoverTruck !== "function"
    ) {
      closeTruckRecoveryConfirm();
    }
  }, [
    closeTruckRejectionConfirm,
    closeTruckRecoveryConfirm,
    truckConfirmationContextKey,
    truckRejectionConfirmation,
    truckRecoveryConfirmation,
    sourceChanges?.onRejectTruck,
    sourceChanges?.onRecoverTruck,
  ]);

  const requestTruckRejectionConfirmation = useCallback((detail, triggerElement = null) => {
    const truckId = normalizeInlineText(detail?.truckId);
    const truckChangeRevision = normalizeInlineText(detail?.changeRevision);
    const dispatchDate = normalizeInlineText(sourceChanges?.dispatchDate);
    const previewRevision = normalizeInlineText(sourceChanges?.previewRevision);
    const featureRevision = normalizeInlineText(truckChanges?.featureRevision);
    if (
      !["actionable", "blocked", "unsupported"].includes(detail?.status)
      || typeof sourceChanges?.onRejectTruck !== "function"
      || !truckId
      || !truckChangeRevision
      || !dispatchDate
      || !previewRevision
      || !featureRevision
    ) {
      return;
    }
    truckRejectionActionTriggerRef.current = triggerElement instanceof HTMLElement
      ? triggerElement
      : null;
    setTruckRejectionConfirmation({
      changedFields: Array.isArray(detail?.changedFields)
        ? detail.changedFields.map((field) => ({ ...field }))
        : [],
      contextKey: truckConfirmationContextKey,
      dispatchDate,
      dispatchPeriod: normalizedActiveDispatchPeriod,
      featureRevision,
      previewRevision,
      truckChangeRevision,
      truckLabel: resolveTruckSourceChangeLabel(truckId, mergedTruckLabelById),
      truckId,
    });
  }, [normalizedActiveDispatchPeriod, sourceChanges?.dispatchDate, sourceChanges?.onRejectTruck, sourceChanges?.previewRevision, truckChanges?.featureRevision, truckConfirmationContextKey, mergedTruckLabelById]);

  const requestTruckRecoveryConfirmation = useCallback((detail, triggerElement = null) => {
    const truckId = normalizeInlineText(detail?.truckId);
    const truckChangeRevision = normalizeInlineText(detail?.changeRevision);
    const recovery = detail?.recovery;
    const recoveryRevision = normalizeInlineText(recovery?.recoveryRevision);
    const dispatchDate = normalizeInlineText(sourceChanges?.dispatchDate);
    const previewRevision = normalizeInlineText(sourceChanges?.previewRevision);
    const featureRevision = normalizeInlineText(truckChanges?.featureRevision);
    if (
      detail?.status !== "blocked"
      || typeof sourceChanges?.onRecoverTruck !== "function"
      || recovery?.status !== "confirmation_required"
      || !truckId
      || !truckChangeRevision
      || !recoveryRevision
      || !dispatchDate
      || !previewRevision
      || !featureRevision
      || !Array.isArray(recovery?.proposedGroups)
      || (
        recovery?.constraintKind !== "load_capacity"
        && recovery.proposedGroups.length === 0
      )
      || (
        recovery?.constraintKind === "load_capacity"
        && (!Array.isArray(recovery?.candidateGroups) || recovery.candidateGroups.length === 0)
      )
    ) {
      return false;
    }
    truckRecoveryActionTriggerRef.current = triggerElement instanceof HTMLElement
      ? triggerElement
      : null;
    truckRecoveryFocusTargetRef.current = truckId;
    setTruckRecoveryConfirmation({
	  changedFields: Array.isArray(detail?.changedFields)
		? detail.changedFields.map((field) => ({ ...field }))
		: [],
      contextKey: truckConfirmationContextKey,
      dispatchDate,
      dispatchPeriod: normalizedActiveDispatchPeriod,
      featureRevision,
      previewRevision,
      recovery: {
        ...recovery,
		candidateGroups: Array.isArray(recovery.candidateGroups)
		  ? recovery.candidateGroups.map((group) => ({
			  ...group,
			  deliveryIds: [...group.deliveryIds],
			  groupStepIds: [...group.groupStepIds],
			  orderIds: [...group.orderIds],
			  ...(group.deliveryClientNameByOrderId
				? { deliveryClientNameByOrderId: { ...group.deliveryClientNameByOrderId } }
				: {}),
			}))
		  : undefined,
		capacityCheckpoints: Array.isArray(recovery.capacityCheckpoints)
		  ? recovery.capacityCheckpoints.map((checkpoint) => ({
			  ...checkpoint,
			  contributions: checkpoint.contributions.map((contribution) => ({ ...contribution })),
			}))
		  : undefined,
        proposedGroups: recovery.proposedGroups.map((group) => ({
          ...group,
          deliveryIds: [...group.deliveryIds],
          groupStepIds: [...group.groupStepIds],
          orderIds: [...group.orderIds],
          ...(group.deliveryClientNameByOrderId
            ? { deliveryClientNameByOrderId: { ...group.deliveryClientNameByOrderId } }
            : {}),
        })),
      },
      recoveryRevision,
      truckChangeRevision,
      truckLabel: resolveTruckSourceChangeLabel(truckId, mergedTruckLabelById),
      truckId,
    });
    return true;
  }, [
    normalizedActiveDispatchPeriod,
    sourceChanges?.dispatchDate,
    sourceChanges?.onRecoverTruck,
    sourceChanges?.previewRevision,
    truckChanges?.featureRevision,
    truckConfirmationContextKey,
    mergedTruckLabelById,
  ]);

  useEffect(() => {
    if (!truckRecoveryConfirmation || typeof sourceChanges?.onRecoverTruck !== "function") {
      return;
    }
    const dispatchDate = normalizeInlineText(sourceChanges?.dispatchDate);
    const isSameScope = truckRecoveryConfirmation.dispatchDate === dispatchDate
      && truckRecoveryConfirmation.dispatchPeriod === normalizedActiveDispatchPeriod;
    if (!isSameScope) {
      closeTruckRecoveryConfirm();
      return;
    }
    if (truckRecoveryPendingTarget) {
      return;
    }
    const sourceChangesAreSettling = sourceChanges?.isLoading === true
      || sourceChanges?.isMutating === true
      || sourceChanges?.isVerifying === true
      || sourceChanges?.needsPlanningContextVerification === true
      || sourceChanges?.needsVerification === true;
    if (sourceChangesAreSettling) {
      return;
    }
    const currentDetail = (Array.isArray(truckChanges?.details) ? truckChanges.details : []).find((item) => (
      normalizeInlineText(item?.truckId) === truckRecoveryConfirmation.truckId
    ));
    const currentRecovery = currentDetail?.recovery;
    const recoveryIsStillCurrent = currentDetail?.status === "blocked"
      && currentRecovery?.status === "confirmation_required"
      && normalizeInlineText(currentDetail?.changeRevision) === truckRecoveryConfirmation.truckChangeRevision
      && normalizeInlineText(currentRecovery?.recoveryRevision) === truckRecoveryConfirmation.recoveryRevision;
    if (!recoveryIsStillCurrent) {
      closeTruckRecoveryConfirm();
      return;
    }
    const previewRevision = normalizeInlineText(sourceChanges?.previewRevision);
    const featureRevision = normalizeInlineText(truckChanges?.featureRevision);
    if (
      previewRevision !== truckRecoveryConfirmation.previewRevision
      || featureRevision !== truckRecoveryConfirmation.featureRevision
      || truckRecoveryConfirmation.contextKey !== truckConfirmationContextKey
    ) {
      setTruckRecoveryConfirmation((currentSelection) => (
        currentSelection === truckRecoveryConfirmation
          ? {
              ...currentSelection,
              contextKey: truckConfirmationContextKey,
              featureRevision,
              previewRevision,
            }
          : currentSelection
      ));
    }
  }, [
    closeTruckRecoveryConfirm,
    normalizedActiveDispatchPeriod,
    sourceChanges?.dispatchDate,
    sourceChanges?.isLoading,
    sourceChanges?.isMutating,
    sourceChanges?.isVerifying,
    sourceChanges?.needsPlanningContextVerification,
    sourceChanges?.needsVerification,
    sourceChanges?.onRecoverTruck,
    sourceChanges?.previewRevision,
    truckChanges?.details,
    truckChanges?.featureRevision,
    truckConfirmationContextKey,
    truckRecoveryConfirmation,
    truckRecoveryPendingTarget,
  ]);

  useEffect(() => {
    if (!truckRecoveryPendingTarget) {
      return;
    }
    const dispatchDate = normalizeInlineText(sourceChanges?.dispatchDate);
    if (
      truckRecoveryPendingTarget.dispatchDate !== dispatchDate
      || truckRecoveryPendingTarget.dispatchPeriod !== normalizedActiveDispatchPeriod
    ) {
      setTruckRecoveryPendingTarget(null);
      return;
    }
    const detail = resolveTruckRecoveryPendingDetail(
      Array.isArray(truckChanges?.details) ? truckChanges.details : [],
      truckRecoveryPendingTarget,
    );
    if (!detail) {
      return;
    }
    setIsMenuOpen(true);
    if (
      sourceChanges?.isMutating === true
      || sourceChanges?.isLoading === true
      || sourceChanges?.isVerifying === true
      || sourceChanges?.needsPlanningContextVerification === true
      || sourceChanges?.needsVerification === true
    ) {
      return;
    }
    if (requestTruckRecoveryConfirmation(detail)) {
      setTruckRecoveryPendingTarget(null);
    }
  }, [
    normalizedActiveDispatchPeriod,
    requestTruckRecoveryConfirmation,
    setIsMenuOpen,
    sourceChanges?.dispatchDate,
    sourceChanges?.isLoading,
    sourceChanges?.isMutating,
    sourceChanges?.isVerifying,
    sourceChanges?.needsPlanningContextVerification,
    sourceChanges?.needsVerification,
    truckChanges?.details,
    truckRecoveryPendingTarget,
  ]);

  const submitTruckRefresh = useCallback((frozenSelection) => {
    const refreshTrucks = sourceChanges?.onRefreshTrucks;
    const truckId = normalizeInlineText(frozenSelection?.truckIds?.[0]);
    if (typeof refreshTrucks !== "function" || !truckId) {
      return;
    }
    const pendingTarget = {
      dispatchDate: normalizeInlineText(frozenSelection.dispatchDate),
      dispatchPeriod: normalizeInlineText(frozenSelection.dispatchPeriod).toLowerCase(),
      truckId,
    };
    truckRefreshPreparationRef.current = pendingTarget;
    setTruckRefreshPendingTarget(pendingTarget);
    void Promise.resolve(refreshTrucks(frozenSelection))
      .then((payload) => {
        const blockedTruck = (
          Array.isArray(payload?.blocked_trucks) ? payload.blocked_trucks : []
        ).find((item) => (
          normalizeInlineText(item?.truck_id || item?.truckId) === truckId
          && normalizeInlineText(item?.recovery?.status).toLowerCase() === "confirmation_required"
        ));
        const recovery = normalizeTruckRefreshRecovery(blockedTruck?.recovery);
        const changeRevision = normalizeInlineText(
          blockedTruck?.change_revision || blockedTruck?.changeRevision,
        );
        if (blockedTruck && recovery?.status === "confirmation_required" && changeRevision) {
          setTruckRecoveryPendingTarget({
            ...pendingTarget,
            changeRevision,
            recoveryRevision: recovery.recoveryRevision,
          });
          setIsMenuOpen(true);
          requestTruckRecoveryConfirmation({
            truckId,
            changeRevision,
            status: "blocked",
            recovery,
            changedFields: Array.isArray(frozenSelection?.changedFields)
              ? frozenSelection.changedFields
              : [],
          });
        } else {
          setTruckRecoveryPendingTarget(null);
        }
      })
      .catch(() => {
        // Le contrôleur réconcilie au plus une fois et présente l'erreur sans réémettre le POST.
        setTruckRecoveryPendingTarget(null);
      })
      .finally(() => {
        if (truckRefreshPreparationRef.current === pendingTarget) truckRefreshPreparationRef.current = null;
        setTruckRefreshPendingTarget((currentTarget) => (
          currentTarget === pendingTarget ? null : currentTarget
        ));
      });
  }, [requestTruckRecoveryConfirmation, setIsMenuOpen, sourceChanges?.onRefreshTrucks]);

  const requestTruckRefreshConfirmation = useCallback(async (detail, triggerElement = null) => {
    const truckId = normalizeInlineText(detail?.truckId);
    const truckChangeRevision = normalizeInlineText(detail?.changeRevision);
    const dispatchDate = normalizeInlineText(sourceChanges?.dispatchDate);
    const previewRevision = normalizeInlineText(sourceChanges?.previewRevision);
    const featureRevision = normalizeInlineText(truckChanges?.featureRevision);
    if (
      !canRefreshTruckSourceChange(detail)
      || truckRefreshPreparationRef.current
      || truckRefreshPendingTarget
      || !truckId
      || !truckChangeRevision
      || !dispatchDate
      || !previewRevision
      || !featureRevision
    ) {
      return;
    }
    submitTruckRefresh({
      contextKey: truckConfirmationContextKey,
      verifyFirst: detail.status === "blocked" || detail?.recovery?.status === "confirmation_required",
      truckLabel: detail.truckLabel || truckId,
      changedFields: Array.isArray(detail.changedFields) ? detail.changedFields : [],
      dispatchDate,
      dispatchPeriod: normalizedActiveDispatchPeriod,
      featureRevision,
      previewRevision,
      truckChangeRevisions: { [truckId]: truckChangeRevision },
      truckIds: [truckId],
    });
  }, [
    normalizedActiveDispatchPeriod,
    sourceChanges?.dispatchDate,
    sourceChanges?.previewRevision,
    submitTruckRefresh,
    truckChanges?.featureRevision,
    truckConfirmationContextKey,
    truckRefreshPendingTarget,
  ]);

  const acceptDompeur = useCallback((truckId) => acceptTruckDompeurOverride({
    truckId, dispatchDate: normalizeInlineText(sourceChanges?.dispatchDate), dispatchPeriod: normalizedActiveDispatchPeriod,
  }), [normalizedActiveDispatchPeriod, sourceChanges?.dispatchDate]);

  // « Actualiser les camions » : la même demande que chaque carte, avec tous les camions à actualiser.
  const requestAllTrucksRefresh = useCallback(() => {
    const refreshTrucks = sourceChanges?.onRefreshTrucks;
    const dispatchDate = normalizeInlineText(sourceChanges?.dispatchDate);
    const details = (Array.isArray(truckChanges?.details) ? truckChanges.details : []).filter((detail) => (
      canRefreshTruckSourceChange(detail) && normalizeInlineText(detail?.truckId) && normalizeInlineText(detail?.changeRevision)
    ));
    if (typeof refreshTrucks !== "function" || truckRefreshPreparationRef.current || truckRefreshPendingTarget
      || details.length === 0 || !dispatchDate || !normalizeInlineText(sourceChanges?.previewRevision)
      || !normalizeInlineText(truckChanges?.featureRevision)) {
      return;
    }
    const truckIds = details.map((detail) => normalizeInlineText(detail.truckId));
    void Promise.resolve(refreshTrucks({
      contextKey: truckConfirmationContextKey,
      truckLabel: `${truckIds.length} camions`,
      changedFields: [],
      dispatchDate,
      dispatchPeriod: normalizedActiveDispatchPeriod,
      featureRevision: normalizeInlineText(truckChanges?.featureRevision),
      previewRevision: normalizeInlineText(sourceChanges?.previewRevision),
      truckChangeRevisions: Object.fromEntries(details.map((detail) => [normalizeInlineText(detail.truckId), normalizeInlineText(detail.changeRevision)])),
      truckIds,
    })).catch(() => {
      // Le contrôleur présente l'erreur ; les camions restent dans Mise à jour.
    });
  }, [
    normalizedActiveDispatchPeriod,
    sourceChanges?.dispatchDate,
    sourceChanges?.onRefreshTrucks,
    sourceChanges?.previewRevision,
    truckChanges?.details,
    truckChanges?.featureRevision,
    truckConfirmationContextKey,
    truckRefreshPendingTarget,
  ]);

  const confirmTruckRejection = useCallback(() => {
    const frozenSelection = truckRejectionConfirmation;
    if (!frozenSelection) {
      return;
    }
    const rejectTruck = sourceChanges?.onRejectTruck;
    setTruckRejectionConfirmation(null);
    restoreTruckRejectionFocus();
    if (typeof rejectTruck !== "function") {
      return;
    }
    void Promise.resolve(rejectTruck(frozenSelection)).catch(() => {
      // Le contrôleur présente l'erreur et ne réémet jamais le rejet automatiquement.
    });
  }, [restoreTruckRejectionFocus, sourceChanges?.onRejectTruck, truckRejectionConfirmation]);

  const confirmTruckRecovery = useCallback((confirmation = {}) => {
    const frozenSelection = truckRecoveryConfirmation;
    if (!frozenSelection) {
      return;
    }
    const recoverTruck = sourceChanges?.onRecoverTruck;
    setTruckRecoveryConfirmation(null);
    restoreTruckRecoveryFocus();
    if (typeof recoverTruck !== "function") {
      return;
    }
	const nextSelection = frozenSelection.recovery?.constraintKind === "load_capacity"
	  ? {
		  ...frozenSelection,
		  selectedGroupKeys: Array.isArray(confirmation?.selectedGroupKeys)
			? [...confirmation.selectedGroupKeys]
			: [],
		}
	  : frozenSelection;
    void Promise.resolve(recoverTruck(nextSelection)).catch(() => {
      // Le contrôleur présente l’erreur; aucune relance automatique n’est tentée.
    });
  }, [restoreTruckRecoveryFocus, sourceChanges?.onRecoverTruck, truckRecoveryConfirmation]);

  const closeNonRenderableTripConfirm = useCallback(() => {
    setNonRenderableTripAction(null);
    window.requestAnimationFrame(() => {
      const triggerElement = nonRenderableTripActionTriggerRef.current;
      if (triggerElement instanceof HTMLElement && triggerElement.isConnected) {
        triggerElement.focus();
      } else {
        menuRef.current?.focus();
      }
    });
  }, []);

  usePopoverDismiss({
    anchorElement: anchorRef.current,
    floatingRef: menuRef,
    isOpen: isMenuOpen && !truckRecoveryConfirmation && !isLoadingRecoveryConfirmationOpen,
    onClose: closeMenu,
  });

  const menuItems = [
    {
      key: "import-new",
      label: "Nouvelles cmd vers Suspendues",
      count: sourceNewCount,
      icon: PauseCircleIcon,
      disabled: !canImportSourceChanges,
      details: newDetails,
      groupSourceChanges: true,
      title: canImportSourceChanges
        ? `${sourceNewCount} nouvelle(s) commande(s) seront ajoutée(s) à Suspendues.`
        : sourceChangeDisabledReason,
      action: () => {
        sourceChanges.onImportNew?.();
        closeMenu();
        anchorRef.current?.querySelector("[data-source-changes-trigger='true']")?.focus();
      },
    },
    {
      key: "remove-modified",
      label: "Cmd modifiées vers Suspendues",
      count: removeModifiedCount,
      icon: ExclamationTriangleIcon,
      disabled: !canRemoveSourceChanges,
      details: removeModifiedDetails,
      detailActionLabel: resolveRemoveModifiedPreviewActionLabel,
      groupSourceChanges: true,
      reasonOnly: true,
      splitMultipleChangedFields: true,
      title: canRemoveSourceChanges
        ? formatRemoveModifiedActionSummary(removeModifiedDetails)
        : sourceChangeDisabledReason,
      action: () => {
        setIsRemoveModifiedConfirmOpen(true);
      },
    },
    {
      key: "remove-date-changed",
      label: "Dates déplacées",
      count: sourceDateChangedCount,
      icon: ArrowsRightLeftIcon,
      disabled: !canRemoveDateChangedSourceChanges,
      details: dateChangedRemoveDetails,
      detailActionLabel: "sera retirée du dispatch courant",
      groupSourceChanges: true,
      title: canRemoveDateChangedSourceChanges
        ? `${sourceDateChangedCount} élément(s) seront retiré(s) du dispatch courant et les camions touchés seront recalculés.`
        : sourceChangeDisabledReason,
      action: () => {
        setIsRemoveDateChangedConfirmOpen(true);
      },
    },
    {
      key: "repair-ghost-orders",
      label: "À supprimer",
      count: sourceGhostOrderCount,
      icon: TrashIcon,
      disabled: !canRepairGhostOrders,
      details: [...ghostOrderDetails, ...removedEndOfDayLoadDetails],
      detailActionLabel: "sera retiré du dispatch",
      groupSourceChanges: true,
      deletedOrderDetails: ghostOrderDetails,
      orphanEndOfDayLoadDetails: removedEndOfDayLoadDetails,
      title: canRepairGhostOrders
        ? `${sourceGhostOrderCount} élément(s) seront retiré(s) du dispatch, puis seuls les camions touchés seront recalculés. Aucune donnée ne sera supprimée dans Data Manager.`
        : !hasVerifiedSourceChangesPreview
          ? "Rafraîchissez M-à-J commandes avant de retirer ces éléments du dispatch."
          : sourceChangeDisabledReason,
      action: () => {
        setIsRepairGhostOrdersConfirmOpen(true);
      },
    },
    ...(recoverableExecutionCount > 0 ? [{
      key: "restore-executions",
      label: "Exécutions à restaurer",
      count: recoverableExecutionCount,
      icon: BookmarkIcon,
      disabled: !canRestoreExecutions,
      details: recoverableExecutionDetails,
      detailActionLabel: "sera restaurée dans sa tournée d'origine",
      groupSourceChanges: true,
      title: canRestoreExecutions
        ? "Ces livraisons peuvent être remises dans leur tournée gardée, sans déplacement ni recalcul."
        : sourceChangeDisabledReason,
      action: () => {
        setIsRestoreExecutionsConfirmOpen(true);
      },
    }] : []),
    {
      key: "import-suspendable-errors",
      label: "Importer erreurs vers Suspendues",
      count: sourceSuspendableErrorCount,
      icon: XCircleIcon,
      disabled: !canImportSuspendableErrors,
      details: suspendableErrorDetails,
      title: canImportSuspendableErrors
        ? `${sourceSuspendableErrorCount} commande(s) en erreur métier seront transférée(s) vers Suspendues.`
        : sourceChangeDisabledReason,
      action: () => {
        setIsImportSuspendableErrorsConfirmOpen(true);
      },
      groupSuspendableErrors: true,
    },
  ].filter((item) => Number(item.count) > 0);
  const hasEndOfDayLoadIntegrityIssues = resolveEndOfDayLoadIntegrityIssues(sourceChanges).length > 0;
  const hasLoadingConstraintChanges = Array.isArray(loadingConstraintChanges?.details)
    && loadingConstraintChanges.details.length > 0;
  const compartmentFillDetails = Array.isArray(sourceChanges?.compartmentFillViolations?.details)
    ? sourceChanges.compartmentFillViolations.details
    : [];
  const hasVisibleSourceChangesContent = Boolean(
    sourceChangeUnavailableMessage
    || sourceChangesActionNeedVerification
    || sourceChangeErrorMessage
    || protectedReviewDetails.length > 0
    || blockedExecutionDetails.length > 0
    || integrityConflictDetails.length > 0
    || mixedEndOfDayLoadDetails.length > 0
    || hasEndOfDayLoadIntegrityIssues
    || menuItems.length > 0
    || hasLoadingConstraintChanges
    || compartmentFillDetails.length > 0
    || showTruckSourceChangesSection
  );

  const handleMenuKeyDown = useCallback((event) => {
    if (event.target?.closest?.("[data-react-confirmation-modal='true']")) return;
    const focusableMenuItems = [...(menuRef.current?.querySelectorAll(
      "[role='menuitem']:not(:disabled), [role='menuitemcheckbox']:not(:disabled)",
    ) || [])].filter((element) => element instanceof HTMLElement && element.getClientRects().length > 0);
    const currentIndex = focusableMenuItems.findIndex((element) => element === document.activeElement);
    if (event.key === "Escape") {
      event.preventDefault();
      if (truckRecoveryConfirmation) {
        closeTruckRecoveryConfirm();
        return;
      }
      if (truckRejectionConfirmation) {
        closeTruckRejectionConfirm();
        return;
      }
      if (nonRenderableTripAction) {
        closeNonRenderableTripConfirm();
        return;
      }
      if (protectedSourceChangeToAcknowledge) {
        closeSourceChangeConfirm();
        return;
      }
      if (isRemoveModifiedConfirmOpen) {
        closeSourceChangeConfirm();
        return;
      }
      if (isRemoveDateChangedConfirmOpen) {
        closeSourceChangeConfirm();
        return;
      }
      if (isRepairGhostOrdersConfirmOpen) {
        closeSourceChangeConfirm();
        return;
      }
      if (isRestoreExecutionsConfirmOpen) {
        closeSourceChangeConfirm();
        return;
      }
      if (isImportSuspendableErrorsConfirmOpen) {
        closeSourceChangeConfirm();
        return;
      }
      closeMenu();
      anchorRef.current?.querySelector("[data-source-changes-trigger='true']")?.focus();
      return;
    }

    if (!["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) {
      return;
    }

    event.preventDefault();
    if (!focusableMenuItems.length) {
      return;
    }

    let nextIndex = currentIndex;
    if (event.key === "ArrowDown") {
      nextIndex = currentIndex >= 0 ? (currentIndex + 1) % focusableMenuItems.length : 0;
    } else if (event.key === "ArrowUp") {
      nextIndex = currentIndex >= 0
        ? (currentIndex - 1 + focusableMenuItems.length) % focusableMenuItems.length
        : focusableMenuItems.length - 1;
    } else if (event.key === "Home") {
      nextIndex = 0;
    } else if (event.key === "End") {
      nextIndex = focusableMenuItems.length - 1;
    }

    focusableMenuItems[nextIndex]?.focus();
  }, [closeMenu, closeNonRenderableTripConfirm, closeSourceChangeConfirm, closeTruckRecoveryConfirm, closeTruckRejectionConfirm, isImportSuspendableErrorsConfirmOpen, isRemoveDateChangedConfirmOpen, isRemoveModifiedConfirmOpen, isRepairGhostOrdersConfirmOpen, isRestoreExecutionsConfirmOpen, nonRenderableTripAction, protectedSourceChangeToAcknowledge, truckRecoveryConfirmation, truckRejectionConfirmation]);

  const pulseClassName = shouldPulse
    ? "motion-safe:animate-dispatch-source-changes-pulse"
    : "";

  return (
    <div ref={anchorRef} className="relative inline-flex min-w-0 items-stretch">
      <span className={joinClasses(["inline-flex rounded-[4px]", pulseClassName])}>
        <ActionButton
          data-source-changes-trigger="true"
          aria-controls={menuId}
          aria-expanded={isMenuOpen}
          aria-haspopup="menu"
          size="sm"
          tone="ghost"
          className={joinClasses([
            "rounded-[4px]! px-3.25! text-sm! shadow-none! hover:shadow-none! disabled:border-[rgba(148,163,184,0.16)]! disabled:bg-[rgba(248,250,252,0.8)]! disabled:text-slate-400!",
            fillIndicator ? "h-full! min-h-14!" : "h-8.25! min-h-8.25!",
            hasPending
              ? "border-[var(--dispatch-update-fill)]! bg-[var(--dispatch-update-fill)]! text-white! hover:border-[var(--dispatch-update-hover-fill)]! hover:bg-[var(--dispatch-update-hover-fill)]! hover:text-white!"
              : "border-[rgba(148,163,184,0.16)]! bg-[rgba(248,250,252,0.82)]! text-content-copy! hover:border-[rgba(100,116,139,0.26)]! hover:bg-white! hover:text-text!",
          ])}
          disabled={buttonDisabled}
          aria-label={sourceChangeErrorMessage
            ? `Mise à jour ${dispatchPeriodLabel} — erreur de chargement`
            : sourceChangeUnavailableMessage
              ? `Mise à jour ${dispatchPeriodLabel} — ${sourceChangeUnavailableMessage}`
            : sourceChangesNeedVerification
              ? `Mise à jour ${dispatchPeriodLabel} — ${pendingCountsLabel}; vérification requise`
              : `Mise à jour ${dispatchPeriodLabel} — ${pendingCountsLabel}`}
          title={buttonDisabled
            ? sourceChangeDisabledReason
            : sourceChangeUnavailableMessage
              ? sourceChangeUnavailableMessage
            : (hasPending ? pendingCountsLabel : undefined)}
          onClick={() => setIsMenuOpen((cur) => !cur)}
        >
          <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
            <span>Mise à jour</span>
            <DispatchPeriodBadgeIcon periodLabel={dispatchPeriodLabel} />
            {sourceChangeErrorMessage ? (
              <span className={joinClasses([
                "inline-flex items-center rounded-full border border-[var(--dispatch-badge-danger-border)] bg-[var(--dispatch-badge-danger-fill)] px-1.75 py-0.5 text-[0.58rem] font-black uppercase tracking-[0.12em] text-[var(--dispatch-card-danger-ink)]",
              ])}>
                !
              </span>
            ) : null}
            {unresolvedPendingCount > 0 ? (
              <span
                aria-hidden="true"
                className={joinClasses([
                  "inline-flex min-w-5 items-center justify-center rounded-[4px] border border-white bg-white px-1.5 py-0.5 text-[0.58rem] font-black leading-none text-[var(--dispatch-update-fill)]",
                  pulseClassName,
                ])}
                data-source-changes-counters
              >
                {unresolvedPendingCount}
              </span>
            ) : null}
            {sourceChangesNeedVerification ? (
              <ArrowPathIcon
                aria-label={sourceChangesBusy ? "Vérification M-à-J en cours" : "Vérification M-à-J requise"}
                className={joinClasses([
                  "h-3.25 w-3.25 flex-none",
                  hasPending ? "text-white/85" : "text-slate-500",
                  sourceChangesBusy ? "motion-safe:animate-spin" : "",
                ])}
                data-source-changes-verification-status
              />
            ) : null}
            <ChevronDownIcon
              aria-hidden="true"
              className={joinClasses([
                "h-3.75 w-3.75 transition duration-150",
                isMenuOpen ? "rotate-180" : "rotate-0",
              ])}
            />
          </span>
        </ActionButton>
      </span>

      {isMenuOpen ? (
        <div
          id={menuId}
          ref={menuRef}
          style={isMenuPositioned ? menuPositionStyles : { position: "absolute", top: "100%", right: 0, visibility: "hidden" }}
          role="menu"
          tabIndex={-1}
          aria-label={showTruckSourceChangesSection || loadingConstraintChanges?.details?.length > 0
            ? "Changements commandes et camions Data Manager"
            : "Changements commandes Data Manager"}
          className="z-popover grid max-h-[min(42rem,calc(100vh-8rem))] w-[min(22rem,calc(100vw-1rem))] grid-cols-[minmax(0,1fr)] gap-2 overflow-x-hidden overflow-y-auto rounded-[4px] border border-[rgba(148,163,184,0.18)] bg-[#f6f6f4] p-2 shadow-[0_22px_44px_rgba(15,23,42,0.14)] backdrop-blur-md motion-safe:animate-popover-enter"
          onKeyDown={handleMenuKeyDown}
        >
          <PlanningWriteActivityNotice />
          {truckRecoveryConfirmation
          && truckChanges?.enabled === true
          && typeof sourceChanges?.onRecoverTruck === "function" ? (
            <TruckRefreshRecoveryConfirmPanel
              actionsDisabled={disableMutationActions}
              isBusy={sourceChangesBusy}
              selection={truckRecoveryConfirmation}
              truckLabelById={mergedTruckLabelById}
              onCancel={closeTruckRecoveryConfirm}
              onConfirm={confirmTruckRecovery}
            />
          ) : truckRejectionConfirmation
          && truckChanges?.enabled === true
          && typeof sourceChanges?.onRejectTruck === "function" ? (
            <TruckSourceChangeRejectConfirmPanel
              actionsDisabled={disableMutationActions}
              isBusy={sourceChangesBusy}
              selection={truckRejectionConfirmation}
              truckLabelById={mergedTruckLabelById}
              onCancel={closeTruckRejectionConfirm}
              onConfirm={confirmTruckRejection}
            />
          ) : nonRenderableTripAction ? (
            <NonRenderableTripConfirmPanel
              action={nonRenderableTripAction.action}
              actionsDisabled={disableMutationActions || isClosedDispatchDay}
              group={nonRenderableTripAction.group}
              isBusy={sourceChangesBusy}
              target={nonRenderableTripAction.target}
              onCancel={closeNonRenderableTripConfirm}
              onConfirm={() => {
                void Promise.resolve(sourceChanges.onResolveNonRenderableTrip?.(
                  nonRenderableTripAction.group,
                  nonRenderableTripAction.action,
                  nonRenderableTripAction.target,
                )).then((payload) => {
                  if (!payload) {
                    return;
                  }
                  setNonRenderableTripAction(null);
                  setFailedRepairConflictId("");
                  window.requestAnimationFrame(() => menuRef.current?.focus());
                }).catch((error) => {
                  const reasonCode = normalizeInlineText(error?.guidedRepairReasonCode || error?.code).toLowerCase();
                  if (nonRenderableTripAction.action === "repair" && reasonCode === "repair_blocked") {
                    setFailedRepairConflictId(nonRenderableTripAction.group.conflictId);
                    setNonRenderableTripAction(null);
                    window.requestAnimationFrame(() => menuRef.current?.focus());
                  } else if ([
                    "stale_conflict",
                    "runtime_revision_conflict",
                    "published_truck",
                    "dispatch_day_closed",
                    "conflict_not_targetable",
                  ].includes(reasonCode)) {
                    setNonRenderableTripAction(null);
                    window.requestAnimationFrame(() => menuRef.current?.focus());
                  }
                });
              }}
            />
          ) : protectedSourceChangeToAcknowledge ? (
            <AcknowledgeProtectedSourceChangeConfirmPanel
              actionsDisabled={disableMutationActions}
              detail={protectedSourceChangeToAcknowledge}
              isBusy={sourceChangesBusy}
              isClosedDispatchDay={isClosedDispatchDay}
              onCancel={closeSourceChangeConfirm}
              onConfirm={() => {
                void Promise.resolve(
                  sourceChanges.onAcknowledgeProtected?.(protectedSourceChangeToAcknowledge),
                ).then((payload) => {
                  if (!payload) {
                    return;
                  }
                  closeMenu();
                  anchorRef.current?.querySelector("[data-source-changes-trigger='true']")?.focus();
                }).catch(() => {
                  // Le contrôleur présente déjà l'erreur et conserve la confirmation ouverte.
                });
              }}
            />
          ) : isRemoveModifiedConfirmOpen ? (
            <RemoveModifiedSourceChangesConfirmPanel
              actionsDisabled={disableMutationActions}
              details={removeModifiedDetails}
              isBusy={sourceChangesBusy}
              onCancel={closeSourceChangeConfirm}
              onConfirm={() => {
                void Promise.resolve(sourceChanges.onRemoveModified?.()).then((payload) => {
                  if (!payload) {
                    return;
                  }
                  closeMenu();
                  anchorRef.current?.querySelector("[data-source-changes-trigger='true']")?.focus();
                }).catch(() => {
                  // Le contrôleur conserve le détail de l'erreur et la confirmation ouverte.
                });
              }}
            />
          ) : isRemoveDateChangedConfirmOpen ? (
            <RemoveDateChangedSourceChangesConfirmPanel
              actionsDisabled={disableMutationActions}
              details={dateChangedRemoveDetails}
              isBusy={sourceChangesBusy}
              onCancel={closeSourceChangeConfirm}
              onConfirm={() => {
                void Promise.resolve(sourceChanges.onRemoveDateChanged?.()).then((payload) => {
                  if (!payload) {
                    return;
                  }
                  closeMenu();
                  anchorRef.current?.querySelector("[data-source-changes-trigger='true']")?.focus();
                }).catch(() => {
                  // Le contrôleur présente déjà l'erreur et conserve la confirmation ouverte.
                });
              }}
            />
          ) : isImportSuspendableErrorsConfirmOpen ? (
            <ImportSuspendableErrorsConfirmPanel
              actionsDisabled={disableMutationActions}
              details={suspendableErrorDetails}
              isBusy={sourceChangesBusy}
              onCancel={closeSourceChangeConfirm}
              onConfirm={() => {
                sourceChanges.onImportSuspendableErrors?.();
                closeMenu();
                anchorRef.current?.querySelector("[data-source-changes-trigger='true']")?.focus();
              }}
            />
          ) : isRepairGhostOrdersConfirmOpen ? (
            <RepairGhostOrdersConfirmPanel
              actionsDisabled={disableMutationActions}
              orderDetails={ghostOrderDetails}
              pmDetails={removedEndOfDayLoadDetails}
              isBusy={sourceChangesBusy}
              onCancel={closeSourceChangeConfirm}
              onConfirm={() => {
                void Promise.resolve(sourceChanges.onRepairGhostOrders?.()).then((payload) => {
                  if (!payload) {
                    return;
                  }
                  closeMenu();
                  anchorRef.current?.querySelector("[data-source-changes-trigger='true']")?.focus();
                }).catch(() => {
                  // Le contrôleur actualise M-à-J et conserve la confirmation ouverte.
                });
              }}
            />
          ) : isRestoreExecutionsConfirmOpen ? (
            <RestoreExecutionsConfirmPanel
              actionsDisabled={disableMutationActions}
              details={recoverableExecutionDetails}
              isBusy={sourceChangesBusy}
              onCancel={closeSourceChangeConfirm}
              onConfirm={() => {
                sourceChanges.onRestoreExecutions?.();
                closeMenu();
                anchorRef.current?.querySelector("[data-source-changes-trigger='true']")?.focus();
              }}
            />
          ) : (
            <>
              {sourceChangeUnavailableMessage ? (
                <div
                  className="rounded-[4px] border border-slate-200 bg-slate-50 px-2.5 py-2 text-[0.64rem] font-semibold text-slate-600"
                  data-source-changes-unavailable
                  role="status"
                >
                  {sourceChangeUnavailableMessage}
                </div>
              ) : null}
              {sourceChangesActionNeedVerification && !sourceChangeErrorMessage && !sourceChangeUnavailableMessage ? (
                <div
                  className="rounded-[4px] border border-slate-200 bg-slate-50 px-2.5 py-2 text-[0.64rem] font-semibold text-slate-600"
                  data-source-changes-verification-pending
                  role="status"
                >
                  {sourceChangesBusy
                    ? "Vérification M-à-J en cours. Les actions restent désactivées."
                    : "Cet aperçu doit être vérifié avant toute action M-à-J."}
                </div>
              ) : null}
              {sourceChangeErrorMessage ? (
                <div
                  className="grid gap-1.5 rounded-[4px] border border-rose-200 bg-rose-50/95 p-2"
                  data-source-changes-verification-error
                  role="alert"
                >
                  <div className="grid gap-0.5 px-0.5">
                    <span className="text-[0.7rem] font-bold text-rose-800">
                      Vérification M-à-J impossible
                    </span>
                    <span className="text-[0.61rem] leading-4 text-rose-700">
                      {sourceChangeErrorMessage} {planningContextVerificationErrorMessage
                        ? "Les actions M-à-J restent bloquées jusqu’à une vérification réussie."
                        : "La publication reste bloquée jusqu’à une vérification réussie."}
                    </span>
                  </div>
                  <button
                    type="button"
                    className={joinClasses([
                      "justify-self-start rounded-[7px] px-2 py-1 text-[0.64rem] font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/35",
                      buttonToneClasses.optimizer,
                    ])}
                    disabled={sourceChangesBusy || typeof sourceChanges?.onRefresh !== "function"}
                    onClick={() => {
                      sourceChanges.onRefresh?.();
                    }}
                  >
                    Réessayer la vérification
                  </button>
                </div>
              ) : null}
              {!hasVisibleSourceChangesContent ? (
                <div
                  className="rounded-[4px] border border-slate-200 bg-slate-50 px-2.5 py-2 text-[0.64rem] font-semibold text-slate-600"
                  data-source-changes-empty
                  role="status"
                >
                  Aucune mise à jour à traiter.
                </div>
              ) : null}

              <ProtectedSourceChangesSection
                details={protectedReviewDetails}
                actionsDisabled={disableMutationActions}
                isBusy={sourceChangesBusy}
                isClosedDispatchDay={isClosedDispatchDay}
                onAcknowledge={typeof sourceChanges?.onAcknowledgeProtected === "function"
                  ? (detail) => {
                    setProtectedSourceChangeToAcknowledge(detail);
                  }
                  : undefined}
              />

              <BlockedExecutionRemovalsSection details={blockedExecutionDetails} />

              <SourceChangeIntegrityConflictsSection
                actionsDisabled={disableMutationActions
                  || isClosedDispatchDay
                  || !hasVerifiedSourceChangesPreview
                  || sourceChangesActionNeedVerification}
                details={integrityConflictDetails}
                failedRepairConflictId={failedRepairConflictId}
                hasRuntimeRevision={Boolean(
                  normalizeInlineText(sourceChanges?.runtimeRevision || sourceChanges?.runtime_revision),
                )}
                isBusy={sourceChangesBusy}
                onRequestAction={typeof sourceChanges?.onResolveNonRenderableTrip === "function"
                  ? ({ action, group, target = null, triggerElement = null }) => {
                    nonRenderableTripActionTriggerRef.current = triggerElement;
                    setNonRenderableTripAction({ action, group, target });
                  }
                  : null}
              />

              <MixedEndOfDayLoadSourceChangesSection details={mixedEndOfDayLoadDetails} />

              <EndOfDayLoadIntegritySection
                sourceChanges={sourceChanges}
                actionsDisabled={disableMutationActions}
                isBusy={sourceChangesBusy}
                onResolve={typeof sourceChanges?.onResolveEndOfDayLoadIntegrity === "function"
                  ? (issue, action) => {
                    sourceChanges.onResolveEndOfDayLoadIntegrity(issue, action);
                    closeMenu();
                    anchorRef.current?.querySelector("[data-source-changes-trigger='true']")?.focus();
                  }
                  : undefined}
              />

              <EndOfDayLoadIntegrityNoticesSection
                sourceChanges={sourceChanges}
                isBusy={sourceChangesBusy}
                onResolve={typeof sourceChanges?.onResolveEndOfDayLoadIntegrity === "function"
                  ? (notice, action) => {
                    sourceChanges.onResolveEndOfDayLoadIntegrity(notice, action);
                    closeMenu();
                  }
                  : undefined}
              />

              {menuItems.map((item) => {
            const ItemIcon = item.icon;
            const isActionable = !item.disabled;
            const hasDetails = isActionable && item.details?.length > 0;
            return (
              <div
                key={item.key}
                data-source-delete-card={item.key === "repair-ghost-orders" ? "true" : undefined}
                data-source-changes-actionable={isActionable ? "true" : undefined}
                className={joinClasses([
                  "overflow-hidden rounded-[4px] border",
                  isActionable
                    ? "border-[var(--dispatch-update-border)] bg-white"
                    : "border-[rgba(148,163,184,0.1)] bg-white",
                ])}
              >
                <HoverCard
                  title={item.label}
                  content={item.title}
                  placement="left-start"
                  delay={140}
                  maxWidth={320}
                  accentColor="var(--hovercard-accent-neutral)"
                >
                  <button
                    type="button"
                    role="menuitem"
                    disabled={item.disabled}
                    className={joinClasses([
                      SOURCE_CHANGE_GROUP_HEADER_CLASS_NAME,
                      "group transition duration-150 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2",
                      item.disabled
                        ? "cursor-default bg-white! text-slate-400!"
                        : "cursor-pointer hover:bg-[var(--dispatch-update-hover-fill)]",
                    ])}
                    onClick={item.disabled ? undefined : item.action}
                  >
                    <ItemIcon
                      aria-hidden="true"
                      className={item.disabled
                        ? "h-4 w-4 flex-none text-slate-300"
                        : SOURCE_CHANGE_GROUP_HEADER_ICON_CLASS_NAME}
                    />
                    <span className="min-w-0 flex-1 truncate whitespace-nowrap">{item.label}</span>
                    <span className={item.disabled
                      ? "inline-flex items-center rounded-[4px] border border-[rgba(148,163,184,0.14)] px-1.75 py-0.5 text-[0.58rem] font-black uppercase tracking-[0.12em] text-slate-300"
                      : SOURCE_CHANGE_COUNT_BADGE_CLASS_NAME
                    }>
                      {item.count}
                    </span>
                    {isActionable ? (
                      <ChevronRightIcon
                        aria-hidden="true"
                        className={joinClasses([
                          SOURCE_CHANGE_GROUP_HEADER_CHEVRON_CLASS_NAME,
                          "transition duration-150 group-hover:translate-x-0.5 group-hover:opacity-100",
                        ])}
                      />
                    ) : null}
                  </button>
                </HoverCard>
                {hasDetails ? (
                  <div className={SOURCE_CHANGE_GROUP_SECTION_BODY_CLASS_NAME}>
                    {item.key === "repair-ghost-orders" ? (
                      <>
                        {item.deletedOrderDetails?.length > 0 ? (
                          <div className="grid gap-1" data-source-delete-orders>
                            <SourceChangeDetailsPreview
                              details={item.deletedOrderDetails}
                              actionLabel="sera retirée du dispatch courant"
                              groupSourceChanges
                              embedded
                            />
                          </div>
                        ) : null}
                        {item.orphanEndOfDayLoadDetails?.length > 0 ? (
                          <div className="grid gap-1" data-source-delete-pm>
                            <span className="text-[0.62rem] font-bold text-slate-700">
                              Chargés PM orphelins
                            </span>
                            <SourceChangeDetailsPreview
                              details={item.orphanEndOfDayLoadDetails}
                              actionLabel="sera retiré du dispatch courant"
                              groupSourceChanges
                              embedded
                            />
                          </div>
                        ) : null}
                      </>
                    ) : (
                      <SourceChangeDetailsPreview
                        details={item.details}
                        actionLabel={item.detailActionLabel}
                        groupSuspendableErrors={item.groupSuspendableErrors === true}
                        groupSourceChanges={item.groupSourceChanges === true}
                        reasonOnly={item.reasonOnly === true}
                        splitMultipleChangedFields={item.splitMultipleChangedFields === true}
                        embedded
                      />
                    )}
                  </div>
                ) : null}
              </div>
            );
              })}
              <LoadingConstraintChangesSection
                actionsDisabled={disableMutationActions || isClosedDispatchDay}
                changes={loadingConstraintChanges}
                isBusy={sourceChangesBusy}
                onOpenTruck={(detail) => {
                  sourceChanges?.onOpenTruck?.(detail);
                  closeMenu();
                }}
                onAcceptDompeur={acceptDompeur}
                onRefresh={sourceChanges?.onRefreshLoadingConstraints}
                onRefreshAll={sourceChanges?.onRefreshAllLoadingConstraints}
                onReject={sourceChanges?.onRejectLoadingConstraints}
                onRecoveryOpenChange={setIsLoadingRecoveryConfirmationOpen}
                previewRevision={sourceChanges?.previewRevision}
                scopeKey={`${normalizeInlineText(sourceChanges?.activeScopeDispatchDate || sourceChanges?.dispatchDate)}|${normalizedActiveDispatchPeriod}`}
                truckLabelById={mergedTruckLabelById}
              />
              <CompartmentFillChanges
                Header={SourceChangeGroupSectionHeader}
                actionsDisabled={disableMutationActions || isClosedDispatchDay || sourceChanges?.canMutate !== true}
                bodyClassName={SOURCE_CHANGE_GROUP_SECTION_BODY_CLASS_NAME}
                cardClassName={joinClasses(["grid min-w-0 gap-1 rounded-[4px] px-2 py-1.5", SOURCE_CHANGE_INNER_CARD_BORDER_CLASS_NAME, SOURCE_CHANGE_ITEM_CARD_SURFACE_CLASS_NAME])}
                details={compartmentFillDetails}
                dispatchDate={normalizeInlineText(sourceChanges?.activeScopeDispatchDate || sourceChanges?.dispatchDate)}
                dispatchPeriod={normalizedActiveDispatchPeriod}
                isBusy={sourceChangesBusy}
                onOpenTruck={(detail) => {
                  sourceChanges?.onOpenTruck?.(detail);
                  closeMenu();
                }}
                onRefresh={sourceChanges?.onRefresh}
                runtimeRevision={sourceChanges?.runtimeRevision}
                sectionClassName={SOURCE_CHANGE_GROUP_SECTION_CLASS_NAME}
                truckLabelById={mergedTruckLabelById}
              />
              <TruckSourceChangesSection
                dispatchPeriod={normalizedActiveDispatchPeriod}
                key={`${normalizeInlineText(sourceChanges?.dispatchDate)}|${normalizedActiveDispatchPeriod}`}
                activeTruckRefreshId={activeTruckRefreshId}
                actionsDisabled={disableMutationActions}
                isBusy={sourceChangesBusy}
                onOpenTruck={(detail) => {
                  sourceChanges?.onOpenTruck?.(detail);
                  closeMenu();
                }}
                onRequestReject={typeof sourceChanges?.onRejectTruck === "function"
                  ? requestTruckRejectionConfirmation
                  : undefined}
                onRequestRefresh={requestTruckRefreshConfirmation}
                onRequestRefreshAll={requestAllTrucksRefresh}
                onAcceptDompeur={acceptDompeur}
                truckChanges={truckChanges}
                truckDriverById={truckDriverById}
                truckLabelById={mergedTruckLabelById}
              />
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}

function DispatchDayControls({ controls = null, interactions = {} }) {
  return <DispatchDayPicker controls={controls} interactions={interactions} showDayStateActions={false} compact showStepButtons />;
}

export function DispatchSnapshotsDropdown({
  snapshotItems = [],
  snapshotInteractions = {},
  disableRestoreBase = false,
  disableDeleteBase = false,
  disabled = false,
  disabledReason = "",
  hideDelete = false,
  restoreDisabledReason = "",
  deleteDisabledReason = "",
  restoringSnapshotId = "",
  deletingSnapshotId = "",
  triggerVariant = "labeled",
  triggerClassName = "",
  contextKey = "",
}) {
  const anchorRef = useRef(null);
  const menuId = React.useId();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [confirmingAction, setConfirmingAction] = useState(null);
  useEffect(() => {
    setIsMenuOpen(false);
    setConfirmingAction(null);
  }, [contextKey]);
  const isSplitChevronTrigger = triggerVariant === "split-chevron";
  const { refs, floatingStyles, isPositioned } = useFloating({
    open: isMenuOpen,
    placement: "bottom-end",
    middleware: [
      offset(7),
      flip({ padding: 16 }),
      shift({ padding: 16 }),
    ],
    whileElementsMounted: autoUpdate,
  });

  const closeMenu = useCallback(() => {
    setConfirmingAction(null);
    setIsMenuOpen(false);
  }, []);

  useEffect(() => {
    refs.setReference(anchorRef.current);
  }, [isMenuOpen, refs]);

  usePopoverDismiss({
    anchorElement: anchorRef.current,
    floatingRef: refs.floating,
    isOpen: isMenuOpen,
    onClose: closeMenu,
  });

  useEffect(() => {
    if (disabled) {
      closeMenu();
    }
  }, [closeMenu, disabled]);

  if (!snapshotItems.length && !isSplitChevronTrigger) {
    return (
      <span className="text-[0.68rem] font-semibold text-slate-400">
        Aucune sauvegarde
      </span>
    );
  }

  const snapshotCountLabel = `${snapshotItems.length} sauvegarde${snapshotItems.length > 1 ? "s" : ""}`;
  const restorePreviewById = snapshotInteractions.restorePreviewById && typeof snapshotInteractions.restorePreviewById === "object"
    ? snapshotInteractions.restorePreviewById
    : {};
  const restoreConfirmItemId = confirmingAction?.type === "restore"
    ? normalizeInlineText(confirmingAction?.itemId)
    : "";
  const restoreConfirmPreview = restoreConfirmItemId ? restorePreviewById[restoreConfirmItemId] || null : null;
  const hasLoadedRestoreConfirmPreview = restoreConfirmPreview?.previewLoaded === true || restoreConfirmPreview?.previewError === true;
  const isRestoreConfirmPreviewLoading = Boolean(restoreConfirmItemId)
    && (normalizeInlineText(snapshotInteractions.previewingSnapshotId) === restoreConfirmItemId || !hasLoadedRestoreConfirmPreview);
  const restoreConfirmHasBlockingExecutionPreservation = restorePreviewHasBlockingExecutionPreservation(restoreConfirmPreview);
  const restoreConfirmHasIntegrityConflicts = getRestoreIntegrityConflicts(restoreConfirmPreview).length > 0;
  const restoreConfirmBlockingReason = getRestoreIntegrityConflictBlockingMessage(restoreConfirmPreview)
    || getRestoreExecutionPreservationBlockingMessage(restoreConfirmPreview);
  const needsRestoreConfirmFinalConfirmation = restorePreviewNeedsFinalConfirmation(restoreConfirmPreview);
  const finalRestoreItemId = confirmingAction?.type === "restore-final"
    ? normalizeInlineText(confirmingAction?.itemId)
    : "";
  const finalRestorePreview = finalRestoreItemId ? restorePreviewById[finalRestoreItemId] || null : null;
  const finalRestoreHasBlockingExecutionPreservation = restorePreviewHasBlockingExecutionPreservation(finalRestorePreview);
  const finalRestoreHasIntegrityConflicts = getRestoreIntegrityConflicts(finalRestorePreview).length > 0;
  const finalRestoreBlockingReason = getRestoreIntegrityConflictBlockingMessage(finalRestorePreview)
    || getRestoreExecutionPreservationBlockingMessage(finalRestorePreview);
  const splitChevronDisabled = disabled || snapshotItems.length === 0;
  const splitChevronDisabledReason = disabled
    ? disabledReason
    : snapshotItems.length === 0
      ? "Aucune sauvegarde à restaurer."
      : "";

  return (
    <div ref={anchorRef} className={joinClasses([
      "relative inline-flex min-w-0 items-stretch",
      isSplitChevronTrigger ? "h-full" : "",
    ])}>
      <ActionButton
        data-dispatch-snapshots-trigger="true"
        aria-controls={menuId}
        aria-expanded={isMenuOpen}
        aria-haspopup="menu"
        aria-label={isSplitChevronTrigger ? "Restaurer une sauvegarde" : undefined}
        size="sm"
        tone="ghost"
        className={joinClasses([
          isSplitChevronTrigger
            ? "h-full! min-h-0! w-9.5! min-w-9.5! rounded-none! border-0! bg-transparent! px-0! text-slate-600! shadow-none! hover:bg-slate-50! hover:text-slate-800! hover:shadow-none!"
            : "h-8.75! min-h-8.75! rounded-[4px]! border-[rgba(148,163,184,0.22)]! bg-white! px-2.5! text-slate-700! shadow-none! hover:border-[rgba(100,116,139,0.28)]! hover:bg-slate-50! hover:shadow-none!",
          triggerClassName,
        ])}
        disabled={isSplitChevronTrigger ? splitChevronDisabled : disabled}
        title={isSplitChevronTrigger
          ? (splitChevronDisabled ? splitChevronDisabledReason : "Restaurer une sauvegarde")
          : (disabled ? disabledReason : undefined)}
        onClick={() => {
          setConfirmingAction(null);
          setIsMenuOpen((currentValue) => !currentValue);
        }}
      >
        {isSplitChevronTrigger ? (
          <ChevronDownIcon
            aria-hidden="true"
            className={joinClasses([
              "h-4 w-4 transition duration-150",
              isMenuOpen ? "rotate-180" : "rotate-0",
            ])}
          />
        ) : (
          <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
            <span>{snapshotCountLabel}</span>
            <ChevronDownIcon
              aria-hidden="true"
              className={joinClasses([
                "h-3.5 w-3.5 transition duration-150",
                isMenuOpen ? "rotate-180" : "rotate-0",
              ])}
            />
          </span>
        )}
      </ActionButton>

      {isMenuOpen && getReactPortalRoot() instanceof HTMLElement ? createPortal(
        <div
          id={menuId}
          ref={refs.setFloating}
          data-dispatch-snapshots-menu="true"
          role="menu"
          aria-label="Sauvegardes du dispatch"
          className="z-popover w-[min(24rem,calc(100vw-2rem))] overflow-hidden rounded-lg border border-[rgba(148,163,184,0.18)] bg-white/98 shadow-[0_22px_44px_rgba(15,23,42,0.14)] backdrop-blur-md motion-safe:animate-popover-enter"
          style={{
            ...floatingStyles,
            visibility: isPositioned ? "visible" : "hidden",
          }}
        >
          <div className="max-h-[32rem] overflow-y-auto p-1.5">
            {!snapshotItems.length ? (
              <p className="m-0 px-2 py-2 text-[0.72rem] font-semibold text-slate-500">
                Aucune sauvegarde
              </p>
            ) : null}
            {snapshotItems.map((item) => {
              const itemId = normalizeInlineText(item?.id);
              const isAutoGenerated = item?.is_auto_generated === true
                || normalizeInlineText(item?.origin) === "auto_before_optimize"
                || normalizeInlineText(item?.origin) === "auto_before_reset"
                || normalizeInlineText(item?.origin) === "auto_before_restore";
              const isImported = item?.is_imported === true || Boolean(normalizeInlineText(item?.imported_at));
              const isRestoringThisSnapshot = restoringSnapshotId && restoringSnapshotId === itemId;
              const isDeletingThisSnapshot = deletingSnapshotId && deletingSnapshotId === itemId;
              const savedAtLabel = formatDispatchSnapshotTimestamp(item?.saved_at || item?.created_at);
              const importedAtLabel = isImported
                ? formatDispatchSnapshotTimestamp(item?.imported_at || item?.created_at)
                : "";
              const isConfirmRestore = confirmingAction?.type === "restore" && confirmingAction?.itemId === itemId;
              const isConfirmDelete = confirmingAction?.type === "delete" && confirmingAction?.itemId === itemId;
              const isConfirmingThisItem = isConfirmDelete;

              return (
                <div
                  key={itemId || normalizeInlineText(item?.snapshot_key)}
                  role="menuitem"
                  className="rounded-sm border border-transparent px-1 py-0.5 hover:border-[rgba(148,163,184,0.16)] hover:bg-slate-50"
                  title={normalizeInlineText(item?.label)}
                >
                  {isConfirmingThisItem ? (
                    <div className="grid gap-2 rounded-sm border border-[rgba(71,85,105,0.12)] bg-white px-2 py-1.5">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-[0.68rem] font-semibold text-slate-600">
                          Supprimer cette sauvegarde ?
                        </span>
                      </div>
                      <div className="flex items-center justify-end gap-1.5">
                        <button
                          type="button"
                          className="inline-flex h-6 items-center rounded-sm bg-emerald-600 px-2 text-[0.62rem] font-bold text-white hover:bg-emerald-700"
                          onClick={() => {
                            setConfirmingAction(null);
                            snapshotInteractions.onDelete?.(itemId);
                          }}
                        >
                          Oui
                        </button>
                        <button
                          type="button"
                          className="inline-flex h-6 items-center rounded-sm border border-[rgba(71,85,105,0.18)] bg-white px-2 text-[0.62rem] font-semibold text-slate-600 hover:bg-slate-50"
                          onClick={() => {
                            setConfirmingAction(null);
                          }}
                        >
                          Non
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1.5 px-1 py-1 text-[0.68rem] text-slate-700">
                      <span className={joinClasses([
                        "inline-flex h-5 flex-none items-center rounded-sm border px-1 text-[0.56rem] font-black uppercase tracking-[0.08em]",
                        isAutoGenerated
                          ? "border-sky-200 bg-sky-50 text-sky-800"
                          : isImported
                            ? "border-violet-200 bg-violet-50 text-violet-800"
                            : "border-emerald-200 bg-emerald-50 text-emerald-800",
                      ])}>
                        {isAutoGenerated ? "Auto" : isImported ? "Importée" : "Manuelle"}
                      </span>
                      <span className="inline-flex min-w-0 flex-1 flex-col gap-0.5 font-semibold">
                        <span className="inline-flex min-w-0 items-center gap-1">
                          <ClockIcon className="h-3 w-3 flex-none text-slate-400" aria-hidden="true" />
                          <span className="truncate">
                            {isImported
                              ? `Sauv. ${savedAtLabel || normalizeInlineText(item?.label) || "inconnue"}`
                              : savedAtLabel || normalizeInlineText(item?.label) || "Sauvegarde"}
                          </span>
                        </span>
                        {isImported && importedAtLabel ? (
                          <span className="truncate pl-4 text-[0.62rem] font-medium text-slate-500">
                            Importée {importedAtLabel}
                          </span>
                        ) : null}
                      </span>
                      <button
                        type="button"
                        className="inline-flex h-6 w-6 flex-none items-center justify-center rounded-sm border border-[rgba(71,85,105,0.18)] bg-white text-slate-600 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
                        disabled={disableRestoreBase || Boolean(restoringSnapshotId && !isRestoringThisSnapshot) || Boolean(deletingSnapshotId)}
                        title={restoreDisabledReason || "Restaurer cette sauvegarde"}
                        onClick={() => {
                          if (!itemId) return;
                          setConfirmingAction({ type: "restore", itemId });
                          setIsMenuOpen(false);
                          snapshotInteractions.onPreviewRestore?.(itemId);
                        }}
                      >
                        <ArrowUturnLeftIcon className="h-3.5 w-3.5" aria-hidden="true" />
                      </button>
                      {!hideDelete ? (
                        <button
                          type="button"
                          className="inline-flex h-6 w-6 flex-none items-center justify-center rounded-sm border border-[rgba(239,68,68,0.18)] bg-white text-red-500 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
                          disabled={disableDeleteBase || Boolean(deletingSnapshotId && !isDeletingThisSnapshot) || Boolean(restoringSnapshotId)}
                          title={deleteDisabledReason || "Supprimer cette sauvegarde"}
                          onClick={() => {
                            if (!itemId) return;
                            setConfirmingAction({ type: "delete", itemId });
                          }}
                        >
                          <XMarkIcon className="h-3.5 w-3.5" aria-hidden="true" />
                        </button>
                      ) : null}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>,
        getReactPortalRoot(),
      ) : null}
      {restoreConfirmItemId ? (
        <DispatchRestoreConfirmationPopover
          isLoading={isRestoreConfirmPreviewLoading}
          preview={restoreConfirmPreview}
          isConfirmDisabled={isRestoreConfirmPreviewLoading || restoreConfirmHasBlockingExecutionPreservation || restoreConfirmHasIntegrityConflicts}
          blockedReason={restoreConfirmBlockingReason}
          onCancel={() => {
            setConfirmingAction(null);
          }}
          onConfirm={() => {
            if (isRestoreConfirmPreviewLoading || restoreConfirmHasBlockingExecutionPreservation || restoreConfirmHasIntegrityConflicts) {
              return;
            }
            if (needsRestoreConfirmFinalConfirmation) {
              setConfirmingAction({ type: "restore-final", itemId: restoreConfirmItemId });
              return;
            }
            setConfirmingAction(null);
            snapshotInteractions.onRestore?.(restoreConfirmItemId);
          }}
        />
      ) : null}
      {finalRestoreItemId ? (
        <DispatchRestoreFinalConfirmationPopover
          preview={finalRestorePreview}
          isConfirmDisabled={finalRestoreHasBlockingExecutionPreservation || finalRestoreHasIntegrityConflicts}
          blockedReason={finalRestoreBlockingReason}
          onCancel={() => {
            setConfirmingAction({ type: "restore", itemId: finalRestoreItemId });
          }}
          onConfirm={() => {
            if (finalRestoreHasBlockingExecutionPreservation || finalRestoreHasIntegrityConflicts) {
              return;
            }
            setConfirmingAction(null);
            setIsMenuOpen(false);
            snapshotInteractions.onRestore?.(finalRestoreItemId);
          }}
        />
      ) : null}
    </div>
  );
}

function DispatchRibbonActionsLoadingShell({ className = "" }) {
  const ghostButtonClassName = "pointer-events-none h-7 min-h-7 rounded-[4px] border border-[rgba(148,163,184,0.16)] bg-slate-50/90 opacity-70";
  const ghostRowClassName = "inline-flex min-w-0 flex-wrap items-center justify-end gap-1 rounded-[4px] border border-[rgba(148,163,184,0.16)] bg-white/90 p-1 opacity-70";

  return (
    <div
      aria-busy="true"
      aria-label="Chargement des actions dispatch"
      className={joinClasses([
        "flex min-w-0 flex-col items-end justify-end gap-1.5",
        className,
      ])}
    >
      <div className={ghostRowClassName}>
        <span className={`${ghostButtonClassName} inline-flex w-36 items-center px-2`} />
        <span className={`${ghostButtonClassName} inline-flex w-32 items-center px-2`} />
      </div>
      <div className={ghostRowClassName}>
        <span className={`${ghostButtonClassName} inline-flex w-28 items-center px-2.5`} />
        <span className={`${ghostButtonClassName} inline-flex w-20 items-center px-2`} />
      </div>
    </div>
  );
}

export function ReoptimizationReviewActions({ className = "", interactions = {}, model = {}, headerSlots = null }) {
  const snapshotInteractions = interactions?.dispatchSnapshots && typeof interactions.dispatchSnapshots === "object"
    ? interactions.dispatchSnapshots
    : {};
  const snapshotItems = Array.isArray(snapshotInteractions.items)
    ? snapshotInteractions.items.filter(Boolean)
    : [];
  const pendingPlanningMutationCount = Number.isFinite(Number(model?.pendingPlanningMutationCount))
    ? Math.max(0, Math.round(Number(model.pendingPlanningMutationCount)))
    : 0;
  const hasPendingPlanningMutations = pendingPlanningMutationCount > 0;
  const dispatchDayControls = model?.dispatchDayControls && typeof model.dispatchDayControls === "object"
    ? model.dispatchDayControls
    : null;
  const activeDispatchStatusKey = normalizeInlineText(
    dispatchDayControls?.activeDispatchStatus
    || dispatchDayControls?.activeStatus?.key,
  ).toLowerCase();
  const activeDispatchState = normalizeInlineText(dispatchDayControls?.activeDispatchState).toLowerCase() || "open";
  const isClosedDispatchDay = dispatchDayControls?.activeItem?.isClosed === true
    || activeDispatchStatusKey === "cloture"
    || activeDispatchStatusKey === "closed"
    || activeDispatchState === "closed";
  const {
    isCancelling,
    isOptimizing,
  } = useOptimizationLaunchAction({});
  const isLoading = snapshotInteractions.isLoading === true;
  const isSaving = snapshotInteractions.isSaving === true;
  const restoringSnapshotId = normalizeInlineText(snapshotInteractions.restoringSnapshotId);
  const deletingSnapshotId = normalizeInlineText(snapshotInteractions.deletingSnapshotId);
  const isActionPending = isSaving || Boolean(restoringSnapshotId) || Boolean(deletingSnapshotId);
  const canSaveDispatchSnapshot = interactions?.canSaveDispatchSnapshot === true;
  const canRestoreDispatchSnapshot = interactions?.canRestoreDispatchSnapshot === true;
  const canUseDispatchSnapshots = canSaveDispatchSnapshot || canRestoreDispatchSnapshot;
  const hasPlanningActionInProgress = hasPendingPlanningMutations || isOptimizing || isCancelling;
  const planningActionDisabledReason = hasPendingPlanningMutations
    ? "Une mise à jour du planning est en cours."
    : isOptimizing || isCancelling
      ? "Attendez la fin de l'optimisation en cours."
      : "";
  const disableSave = !canSaveDispatchSnapshot
    || hasPlanningActionInProgress
    || isActionPending;
  const isDeliveredDispatchDay = activeDispatchStatusKey === "livre";
  const disableRestoreBase = !canRestoreDispatchSnapshot
    || hasPlanningActionInProgress
    || isClosedDispatchDay
    || isDeliveredDispatchDay
    || isSaving;
  const disableSnapshotMenu = hasPlanningActionInProgress;
  const saveDisabledReason = !canSaveDispatchSnapshot
    ? "Cette action requiert le droit de sauvegarde dispatch."
    : planningActionDisabledReason
      ? planningActionDisabledReason
      : isSaving
        ? "Sauvegarde du dispatch en cours."
        : restoringSnapshotId
          ? "Restauration de sauvegarde en cours."
          : "";
  const restoreDisabledReason = !canRestoreDispatchSnapshot
    ? "Cette action requiert le droit de restauration dispatch."
    : planningActionDisabledReason
      ? planningActionDisabledReason
      : isClosedDispatchDay
        ? "Journée clôturée : rouvrez-la avant de restaurer une sauvegarde."
        : isDeliveredDispatchDay
          ? "Journée livrée : le Dispatch ne peut plus changer."
          : isSaving
          ? "Sauvegarde du dispatch en cours."
          : restoringSnapshotId
            ? "Restauration de sauvegarde en cours."
            : "";
  const disableDeleteBase = !canSaveDispatchSnapshot
    || hasPlanningActionInProgress
    || isClosedDispatchDay
    || isActionPending;
  const deleteDisabledReason = !canSaveDispatchSnapshot
    ? "Cette action requiert le droit de sauvegarde dispatch."
    : planningActionDisabledReason
      ? planningActionDisabledReason
      : isClosedDispatchDay
        ? "Journée clôturée : rouvrez-la avant de supprimer une sauvegarde."
        : isSaving
          ? "Sauvegarde du dispatch en cours."
          : restoringSnapshotId
            ? "Restauration de sauvegarde en cours."
            : deletingSnapshotId
              ? "Suppression de sauvegarde en cours."
              : "";
  const activeDispatchDate = normalizeInlineText(
    dispatchDayControls?.activeItem?.dispatchDate
    || dispatchDayControls?.activeDispatchDate,
  );
  const optimizationSnapshotRefreshDateRef = useRef(null);
  const refreshSnapshots = snapshotInteractions.onRefresh;
  useEffect(() => {
    const previousOptimizationDate = optimizationSnapshotRefreshDateRef.current;
    optimizationSnapshotRefreshDateRef.current = isOptimizing ? activeDispatchDate : null;
    if (!isOptimizing && previousOptimizationDate === activeDispatchDate) {
      // Launch backups also survive failed and cancelled optimizations.
      void refreshSnapshots?.({ background: true });
    }
  }, [activeDispatchDate, isOptimizing, refreshSnapshots]);
  const portalPublications = normalizePortalPublications(interactions?.portalPublications, {
    dispatchDate: activeDispatchDate,
  });
  const publishedPortalRoutes = portalPublications.routes.filter(
    (route) => route.status === PORTAL_PUBLICATION_STATUSES.published,
  );
  const unpublishedPortalRoutes = portalPublications.routes.filter(
    (route) => route.status === PORTAL_PUBLICATION_STATUSES.unpublished,
  );
  const unavailablePortalRoutes = portalPublications.routes.filter(
    (route) => route.status === PORTAL_PUBLICATION_STATUSES.unavailable,
  );
  const publishableUnpublishedPortalRoutes = unpublishedPortalRoutes.filter(
    (route) => route.publicationAllowed !== false,
  );
  const blockedUnpublishedPortalRoutes = unpublishedPortalRoutes.filter(
    (route) => route.publicationAllowed === false,
  );
  const canPublishDriverPortal = interactions?.canPublishDriverPortal === true;
  const isPortalPublicationsLoading = interactions?.portalPublications?.isLoading === true;
  const portalPublicationErrorMessage = normalizeInlineText(interactions?.portalPublications?.errorMessage);
  const sourceChangesVerification = interactions?.sourceChanges && typeof interactions.sourceChanges === "object"
    ? interactions.sourceChanges
    : {};
  const isSourceChangesVerificationPending = sourceChangesVerification.isLoading === true
    || sourceChangesVerification.isVerifying === true
    || sourceChangesVerification.needsVerification === true;
  const sourceChangesVerificationErrorMessage = normalizeInlineText(sourceChangesVerification.errorMessage);
  const sourceChangesPublicationDisabledReason = isSourceChangesVerificationPending
    ? "Vérification des M-à-J commandes en cours."
    : sourceChangesVerificationErrorMessage
      ? "Impossible de vérifier les M-à-J commandes. Réessayez la vérification avant de publier."
      : "";
  const isPortalPublicationAllowed = portalPublications.publicationAllowed !== false
    && !isSourceChangesVerificationPending
    && !sourceChangesVerificationErrorMessage
    && !portalPublicationErrorMessage;
  const portalPublicationUnavailableReason = portalPublications.publicationUnavailableReason;
  const blockedPortalRouteReason = blockedUnpublishedPortalRoutes
    .map((route) => normalizeInlineText(route.publicationUnavailableReason || route.unavailableReason))
    .find(Boolean) || "";
  const hasPortalPublicationSurface = canPublishDriverPortal
    || portalPublications.routes.length > 0
    || isPortalPublicationsLoading
    || Boolean(portalPublicationErrorMessage);
  const disablePortalPublicationMutation = hasPlanningActionInProgress || isPortalPublicationsLoading;
  const portalPublicationDisabledReason = planningActionDisabledReason
    || (isPortalPublicationsLoading ? "Chargement des publications portail en cours." : "")
    || portalPublicationErrorMessage
    || sourceChangesPublicationDisabledReason
    || (!isPortalPublicationAllowed
      // Dispatch à revalider : les camions non touchés se publient encore depuis leur menu.
      ? portalPublications.publicationUnavailableCode === "stale_snapshot" && publishableUnpublishedPortalRoutes.length > 0
        ? "Dispatch à revalider : publiez chaque camion depuis son menu."
        : portalPublicationUnavailableReason
      : "")
    || (unpublishedPortalRoutes.length > 0 && publishableUnpublishedPortalRoutes.length === 0
      ? blockedPortalRouteReason || "Aucune tournée ne respecte actuellement la politique de publication."
      : "");
  const isPublishPortalDisabled = disablePortalPublicationMutation
    || !isPortalPublicationAllowed
    || publishableUnpublishedPortalRoutes.length === 0;
  const publishPortalDisabledReason = resolvePortalPublishDisabledReason({
    portalPublicationDisabledReason,
    unpublishedPortalRoutes: publishableUnpublishedPortalRoutes,
    isPortalPublicationAllowed,
    portalPublicationUnavailableReason,
    unavailablePortalRoutes,
    publishedPortalRoutes,
  });
  const publishPortalActionHoverCardModel = buildPortalPublicationActionHoverCardModel({
    reason: publishPortalDisabledReason,
    routes: unpublishedPortalRoutes,
    truckIds: blockedUnpublishedPortalRoutes.map((route) => route.truckId),
    noticeTruckIds: publishableUnpublishedPortalRoutes.map((route) => route.truckId),
    truckOutputPolicy: sourceChangesVerification.truckOutputPolicy || portalPublications.truckOutputPolicy,
  });
  const portalPublicationHoverCardModel = buildPortalPublicationDriverHoverCardModel({
    routes: portalPublications.routes,
    publishedCount: publishedPortalRoutes.length,
    availableCount: portalPublications.summary.availableCount,
    errorMessage: portalPublicationErrorMessage,
    isLoading: isPortalPublicationsLoading,
  });
  const hasPublishedPortalRoutes = publishedPortalRoutes.length > 0;
  const isAccessLoading = interactions?.isAccessLoading === true;
  const shouldRenderActions = canUseDispatchSnapshots || hasPortalPublicationSurface;

  const snapshotActions = !shouldRenderActions && isAccessLoading ? <DispatchRibbonActionsLoadingShell className={className} /> : (
    <>
      {canUseDispatchSnapshots ? (
            <div
              aria-label="Sauvegardes du dispatch"
              className="flex min-w-0 flex-wrap items-center justify-end gap-1"
              data-dispatch-snapshot-actions
              role="group"
            >
              <div className="inline-flex h-8.25 min-h-8.25 items-stretch overflow-hidden rounded-[4px] border border-[rgba(148,163,184,0.22)] bg-white shadow-none">
                {canSaveDispatchSnapshot ? (
                  <ActionButton
                    size="sm"
                    tone="ghost"
                    className="h-full! min-h-0! rounded-none! border-0! border-r border-[rgba(148,163,184,0.18)]! bg-transparent! px-3.5! text-[0.84rem]! font-semibold! text-content-copy! shadow-none! hover:bg-slate-50! hover:text-text! hover:shadow-none!"
                    disabled={disableSave}
                    title={saveDisabledReason || undefined}
                    onClick={() => {
                      snapshotInteractions.onSave?.();
                    }}
                  >
                    <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
                      <BookmarkIcon className="h-4 w-4 flex-none" aria-hidden="true" />
                      <span>{isSaving ? "Sauvegarde..." : "Sauvegarder"}</span>
                    </span>
                  </ActionButton>
                ) : null}
                {isLoading ? (
                  <ActionButton
                    size="sm"
                    tone="ghost"
                    className={joinClasses([
                      "h-full! min-h-0! rounded-none! border-0! bg-transparent! text-slate-500! shadow-none!",
                      canSaveDispatchSnapshot ? "w-9! min-w-9! px-0!" : "px-3!",
                    ])}
                    disabled
                    title="Chargement des sauvegardes dispatch"
                  >
                    <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
                      {canSaveDispatchSnapshot ? null : <span>Chargement...</span>}
                      <ChevronDownIcon className="h-4 w-4 flex-none opacity-60" aria-hidden="true" />
                    </span>
                  </ActionButton>
                ) : (
                  <DispatchSnapshotsDropdown
                    contextKey={`${activeDispatchDate}:${interactions.activeDetailView || "route"}:${interactions.sourceChanges?.activeScopeDispatchPeriod || "day"}`}
                    snapshotItems={snapshotItems}
                    snapshotInteractions={snapshotInteractions}
                    disableRestoreBase={disableRestoreBase}
                    disableDeleteBase={disableDeleteBase}
                    disabled={disableSnapshotMenu}
                    disabledReason={planningActionDisabledReason}
                    restoreDisabledReason={restoreDisabledReason}
                    deleteDisabledReason={deleteDisabledReason}
                    restoringSnapshotId={restoringSnapshotId}
                    deletingSnapshotId={deletingSnapshotId}
                    hideDelete={!canSaveDispatchSnapshot}
                    triggerVariant={canSaveDispatchSnapshot ? "split-chevron" : "labeled"}
                    triggerClassName={canSaveDispatchSnapshot
                      ? "w-9! min-w-9!"
                      : "h-full! min-h-0! rounded-none! border-0! bg-transparent! px-3! text-sm! shadow-none! hover:bg-slate-50!"}
                  />
                )}
              </div>
            </div>
          ) : null}
    </>
  );
  const portalActions = (
    <>
      {hasPortalPublicationSurface ? (
        <div className="inline-flex min-w-0 flex-wrap items-center justify-end gap-1 rounded-[4px] bg-white py-0 shadow-none">
          <div
            aria-label="Actions du portail chauffeur"
            className="flex min-w-0 flex-wrap items-center justify-end gap-1"
            data-driver-portal-publication-actions
            role="group"
          >
            <HoverCard
              title="Portail chauffeur"
              content={<DispatchPortalPublicationSummaryHoverCardContent model={portalPublicationHoverCardModel} />}
              placement="bottom-start"
              delay={140}
              maxWidth={360}
              accentColor="var(--color-legacy-teal)"
              referenceAsChild
            >
              <span
                className="dispatch-header-menu-row"
              >
                <span
                  className="inline-flex shrink-0"
                  data-driver-portal-publication-status={hasPublishedPortalRoutes ? "published" : "unpublished"}
                >
                  <Smartphone
                    aria-hidden="true"
                    className="h-4 w-4"
                    strokeWidth={2.2}
                  />
                </span>
                <span>Portail</span>
                <span className="ml-auto tabular-nums text-content-muted">
                  {publishedPortalRoutes.length}/{portalPublications.summary.availableCount}
                </span>
              </span>
            </HoverCard>

            {canPublishDriverPortal ? (
              <HoverCard
                title="Publier"
                content={<DispatchPublishPortalActionHoverCardContent model={publishPortalActionHoverCardModel} />}
                placement="bottom-start"
                delay={140}
                maxWidth={360}
                accentColor="var(--hovercard-accent-warning)"
                disabled={(!publishPortalDisabledReason && !publishPortalActionHoverCardModel.notices.length) || isPortalPublicationsLoading}
              >
                <ActionButton
                  size="sm"
                  tone="ghost"
                  className={DISPATCH_MENU_ACTION_CLASS_NAME}
                  role="menuitem"
                  tabIndex={-1}
                  disabled={isPublishPortalDisabled}
                  onClick={() => {
                    interactions.onPublishPortalRoutes?.(publishableUnpublishedPortalRoutes);
                  }}
                >
                  <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
                    <PaperAirplaneIcon className="h-3.5 w-3.5 flex-none" aria-hidden="true" />
                    <span>{isPortalPublicationsLoading ? "Chargement..." : "Publier"}</span>
                  </span>
                </ActionButton>
              </HoverCard>
            ) : null}

            {canPublishDriverPortal && publishedPortalRoutes.length > 0 ? (
              <ActionButton
                size="sm"
                tone="ghost"
                className={DISPATCH_MENU_ACTION_CLASS_NAME}
                data-destructive="true"
                disabled={disablePortalPublicationMutation}
                title={portalPublicationDisabledReason || "Retirer toutes les publications de cette période"}
                role="menuitem"
                tabIndex={-1}
                onClick={() => {
                  interactions.onUnpublishPortalRoutes?.(publishedPortalRoutes);
                }}
              >
                <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
                  <EyeSlashIcon className="h-3.5 w-3.5 flex-none" aria-hidden="true" />
                  <span>Retirer tout</span>
                </span>
              </ActionButton>
            ) : null}

            {portalPublicationErrorMessage ? (
              <button
                type="button"
                className={DISPATCH_MENU_ACTION_CLASS_NAME}
                onClick={() => {
                  interactions.onRefreshPortalPublications?.();
                }}
                title={portalPublicationErrorMessage}
                role="menuitem"
                tabIndex={-1}
              >
                <ArrowPathIcon className="h-3.5 w-3.5 flex-none" aria-hidden="true" />
                Réessayer
              </button>
            ) : null}
          </div>
        </div>
      ) : null}
    </>
  );
  const moreMenu = (
    <DispatchHeaderMoreMenu
      contextGroup={headerSlots ? "more" : null}
      model={model}
      interactions={interactions}
      contextKey={`${activeDispatchDate}:${interactions.sourceChanges?.activeScopeDispatchPeriod || "day"}:${interactions.activeDetailView || "route"}`}
      isOptimizationInProgress={isOptimizing || interactions.isOptimizationInProgress === true}
      onSameOdGroupingMutationStateChange={interactions.onSameOdGroupingMutationStateChange}
    >
      {portalActions}
    </DispatchHeaderMoreMenu>
  );
  return headerSlots ? <>
    {headerSlots.snapshotActionsRoot ? createPortal(snapshotActions, headerSlots.snapshotActionsRoot) : null}
    {headerSlots.portalActionsRoot ? createPortal(moreMenu, headerSlots.portalActionsRoot) : null}
  </> : <div className={className}>{snapshotActions}{moreMenu}</div>;
}

export default function DispatchRibbonSection({ countersMountNode = null, interactions = {}, model }) {
  const [dayActionsRoot, setDayActionsRoot] = useState(null);
  const [snapshotActionsRoot, setSnapshotActionsRoot] = useState(null);
  const [primaryActionsRoot, setPrimaryActionsRoot] = useState(null);
  const [portalActionsRoot, setPortalActionsRoot] = useState(null);
  const [sourceChangesActionsRoot, setSourceChangesActionsRoot] = useState(null);
  const headerSlots = { dayActionsRoot, snapshotActionsRoot, primaryActionsRoot, portalActionsRoot, sourceChangesActionsRoot };
  const metrics = Array.isArray(model.metrics) ? model.metrics : [];
  const planningPills = Array.isArray(model.planningPills) ? model.planningPills : [];
  const dispatchDayControls = model.dispatchDayControls && typeof model.dispatchDayControls === "object"
    ? model.dispatchDayControls
    : null;
  const hasDispatchDayControls = dispatchDayControls != null;
  const topMetrics = [...metrics, ...planningPills].sort((leftMetric, rightMetric) => {
    const leftIndex = dispatchMetricDisplayOrder.indexOf(leftMetric?.key);
    const rightIndex = dispatchMetricDisplayOrder.indexOf(rightMetric?.key);
    const safeLeftIndex = leftIndex === -1 ? Number.MAX_SAFE_INTEGER : leftIndex;
    const safeRightIndex = rightIndex === -1 ? Number.MAX_SAFE_INTEGER : rightIndex;

    return safeLeftIndex - safeRightIndex;
  });
  const metricsCount = topMetrics.length;
  const metricsGridClassName = metricsCount > 8
    ? "grid w-full gap-1 md:grid-cols-2 xl:grid-cols-9"
    : metricsCount > 7
      ? "grid w-full gap-1 md:grid-cols-2 xl:grid-cols-8"
      : "grid w-full gap-1 md:grid-cols-2 xl:grid-cols-7";
  const legacySections = [
    {
      key: "orders",
      label: "Commandes",
      tone: topMetrics.some((metric) => metric?.tone === "warning" || metric?.tone === "danger") ? "warning" : "neutral",
      items: topMetrics,
      truckCards: [],
    },
  ].filter((section) => (section.items?.length || 0) > 0 || (section.truckCards?.length || 0) > 0);
  const sections = (Array.isArray(model.sections) && model.sections.length > 0 ? model.sections : legacySections)
    .map((section) => enrichDispatchSectionForSourceChanges(section, interactions))
    .filter((section) => section?.key !== "trucks");
  const orderSection = sections.find((section) => section?.key === "orders") || sections[0] || null;
  const enrichedTopMetrics = topMetrics.map((metric) => enrichConfirmedOrdersMetric(metric, interactions));
  // Les indicateurs s'affichent sur la rangée des onglets quand elle est disponible.
  const showsCountersInToolbar = countersMountNode instanceof HTMLElement;
  const sourceChangesSlot = interactions.canApplyDispatchSourceChanges === true
    ? <div ref={setSourceChangesActionsRoot} className={showsCountersInToolbar ? "contents" : "dispatch-header-slot dispatch-header-source-changes-slot"} />
    : null;
  const counters = orderSection ? (
    <div className={joinClasses(["dispatch-header-counters", showsCountersInToolbar ? "dispatch-toolbar-counters" : ""])}>
      <DispatchSection interactions={interactions} section={orderSection} compact trailingMetrics={sourceChangesSlot} />
    </div>
  ) : sourceChangesSlot;

  return (
    <section className="dispatch-header" aria-label="En-tête du Dispatch">
      <div className="dispatch-header-row dispatch-header-day" data-dispatch-header-row="day">
        <div className="dispatch-header-day-context">
          {hasDispatchDayControls ? <DispatchDayControls controls={dispatchDayControls} interactions={interactions} /> : null}
          <div ref={setDayActionsRoot} className="dispatch-header-slot" />
        </div>
        <div className="dispatch-header-primary">
          <div ref={setSnapshotActionsRoot} className="dispatch-header-slot" />
          <div ref={setPrimaryActionsRoot} className="dispatch-header-slot" />
          <div ref={setPortalActionsRoot} className="dispatch-header-slot" />
        </div>
      </div>
      {showsCountersInToolbar ? createPortal(counters, countersMountNode) : (
        <div className="dispatch-header-row dispatch-header-summary" data-dispatch-header-row="summary">{counters}</div>
      )}
      <ReoptimizationReviewActions interactions={interactions} model={model} headerSlots={headerSlots} />
      <DispatchPlanningActions interactions={interactions} model={model} headerSlots={headerSlots} fillSourceChangesIndicator={!showsCountersInToolbar} />

      {!orderSection && sections.length === 0 ? (
        <div className="mt-2">
          <div className={metricsGridClassName}>
            {enrichedTopMetrics.map((metric) => (
              isPlanningPillMetric(metric)
                ? <DispatchPlanningMetric key={metric.key} pill={{ ...metric, interactions }} />
                : <DispatchMetric key={metric.key} metric={metric} interactions={interactions} />
            ))}
          </div>
        </div>
      ) : null}
    </section>
  );
}
